import {
	type TAnySchema,
	Type,
} from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

import { formatAddressLineTwo, formatAttentionLine } from "@ca/formatters/address";
import { formatCurrency } from "@ca/formatters/currency";
import { formatDateFullMonth } from "@ca/formatters/dates";
import { formatInt } from "@ca/formatters/numbers";
import {
	formatPercent,
	formatPercentWhole, 
} from "@ca/formatters/percent";
import {
	formatWrittenDaysParenthetical,
	formatWrittenPercentParenthetical,
	numberToEnglishTitleCase,
} from "@ca/formatters/written-number";
import { renderCheckbox } from "@ca/policies/bool";
import { policyPeriodRevisionError } from "@ca/policies/policy-period-revision";
import { midTermPremiumConsiderationForDoc, midTermPremiumConsiderationFromInvoiced } from "@ca/policies/premium-math";

import type { LOBEnumType } from "../schema";
import type { Endorsement } from "./quote";

import { formatUasDeductible } from "./quote";

export const BaseEndorsementSchema = Type.Object({
	premiumConsideration: Type.Union([
		Type.Literal("the included premium"),
		Type.Number(),
	]),
	namedInsured: Type.String(),
	policyNumber: Type.String(),
	endorsementNumber: Type.Number(),
	effectiveDate: Type.Date(),
});

export const PremiumConsiderationSchema = Type.Object({
	included: Type.Boolean({ default: true }),
	additionalPremium: Type.Union([Type.Null(), Type.Number()], { default: null }),
	/** Line-size-scaled amount invoiced when this clause is added mid-term. */
	invoicedPremium: Type.Optional(Type.Union([Type.Null(), Type.Number()])),
});

/** Optional YYYY-MM-DD; rendered on endorsement forms when set. */
export const OptionalAutoExpirationProperty = {
	autoExpiration: Type.Union([Type.Null(), Type.String()], { default: null }),
};

export const BroadFormEndorsementSchema = Type.Object({
	slug: Type.Literal("broad_form", { default: "broad_form" }),
	hasAirworthiness: Type.Boolean({ default: true }),
	hasAutomaticInsuranceForIncreasedValue: Type.Boolean({ default: true }),
	automaticInsuredValueLimit: Type.Number({ default: 0 }),
	hasBaggageAndHangar: Type.Boolean({ default: true }),
	passengerBaggageLimit: Type.Number({ default: 5_000 }),
	baggagePropertyDamageLimit: Type.Number({ default: 50_000 }),
	hasDefinitionPremises: Type.Boolean({ default: true }),
	definitionPremisesSameLimit: Type.Boolean({ default: true }),
	definitionPremisesPerOccurrence: Type.Boolean({ default: false }),
	definitionPremisesPerOccurrenceLimit: Type.Number({ default: 0 }),
	hasEmergencyOrUnexpectedLanding: Type.Boolean({ default: true }),
	emergencyLandingLimitPercent: Type.Number({ default: 75 }),
	hasSubstituteAircraft: Type.Boolean({ default: true }),
	substituteAircraftDailyLimit: Type.Number({ default: 1_000 }),
	substituteAircraftOccurrenceLimit: Type.Number({ default: 60_000 }),
	substituteAircraftWaitPeriod: Type.Number({
		default: 5, minimum: 0, maximum: 5, 
	}),
	substituteAircraftDaysFromOccurrence: Type.Number({ default: 65, minimum: 1 }),
	hasTempReplacementParts: Type.Boolean({ default: true }),
	replacementPartsLimit: Type.Number({ default: 180_000 }),
	replacementPartsRepairDays: Type.Optional(Type.Number({ default: 5, minimum: 1 })),
	hasHangarkeepers: Type.Boolean({ default: true }),
	hangarkeepersAircraftLimit: Type.Number({ default: 500_000 }),
	hangarkeepersOccurrenceLimit: Type.Number({ default: 1_000_000 }),
	hangarkeepersDeductible: Type.Union([Type.Null(), Type.Number()], { default: 10_000 }),
	hasHostLiquor: Type.Boolean({ default: true }),
	hostLiquorLimit: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
	hasMobileEquipment: Type.Boolean({ default: true }),
	mobileEquipmentLimit: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
	hasPhysicalDamage: Type.Boolean({ default: true }),
	physicalDamageLimit: Type.Number({ default: 50_000 }),
	physicalDamageDeductible: Type.Union([Type.Null(), Type.Number()], { default: 1_000 }),
	hasPolicyTerritory: Type.Boolean({ default: true }),
	hasPremisesMedical: Type.Boolean({ default: true }),
	premisesMedicalLimit: Type.Number({ default: 5_000 }),
	hasProductsLiabilitySale: Type.Boolean({ default: true }),
	productsLiabilitySaleLimit: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
	hasSearchAndRescue: Type.Boolean({ default: true }),
	runwayFoamingLimit: Type.Number({ default: 100_000 }),
	searchAndRescueOccurrenceLimit: Type.Number({ default: 100_000 }),
	hasTripInterruption: Type.Boolean({ default: true }),
	tripInterruptionLimit: Type.Number({ default: 5_000 }),
	hasUnearnedPremium: Type.Boolean({ default: true }),
});

export const BroadNamedInsuredEndorsementSchema = Type.Object({
	slug: Type.Literal("broad_named_insured", { default: "broad_named_insured" }),
	...PremiumConsiderationSchema.properties,
	broadNamedInsured: Type.Optional(Type.String({ default: "" })),
});

export const DateChangeRecognitionEndorsementSchema = Type.Object({
	slug: Type.Literal("date_change_recognition", {
		default: "date_change_recognition",
	}),
	...PremiumConsiderationSchema.properties,
});

export const DeletionOfCoverageCoEmployeesEndorsementSchema = Type.Object({
	slug: Type.Literal("deletion_of_coverage_co_employees", {
		default: "deletion_of_coverage_co_employees",
	}),
	...PremiumConsiderationSchema.properties,
});

export const MentalAnguishLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("mental_anguish_liability", {
		default: "mental_anguish_liability",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglCommunicableDiseaseExclusionEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_communicable_disease_exclusion", {
		default: "cgl_communicable_disease_exclusion",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglDataEventClauseEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_data_event_clause", {
		default: "cgl_data_event_clause",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglDateChangeRecognitionEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_date_change_recognition", {
		default: "cgl_date_change_recognition",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglPfasExclusionEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_pfas_exclusion", {
		default: "cgl_pfas_exclusion",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAmendatoryMoEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amendatory_mo", {
		default: "cgl_amendatory_mo",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAmendatoryNhEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amendatory_nh", {
		default: "cgl_amendatory_nh",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAmendatoryNyEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amendatory_ny", {
		default: "cgl_amendatory_ny",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAmendatorySdEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amendatory_sd", {
		default: "cgl_amendatory_sd",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAmendatoryVaEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amendatory_va", {
		default: "cgl_amendatory_va",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglCommunicableDiseaseExclusionSdEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_communicable_disease_exclusion_sd", {
		default: "cgl_communicable_disease_exclusion_sd",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglCommunicableDiseaseExclusionVtEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_communicable_disease_exclusion_vt", {
		default: "cgl_communicable_disease_exclusion_vt",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglSupplementaryPaymentsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_supplementary_payments", {
		default: "cgl_supplementary_payments",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglHostLiquorExtensionEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_host_liquor_extension", {
		default: "cgl_host_liquor_extension",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglCoverageTerritoryWorldwideEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_coverage_territory_worldwide", {
		default: "cgl_coverage_territory_worldwide",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglReferralLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_referral_liability", {
		default: "cgl_referral_liability",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglPersonalAdvertisingInjuryExtensionEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_personal_advertising_injury_extension", {
		default: "cgl_personal_advertising_injury_extension",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglDeletionExclusionPdProductWorkEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_deletion_exclusion_pd_product_work", {
		default: "cgl_deletion_exclusion_pd_product_work",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglDeletionExclusionCoEmployeesEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_deletion_exclusion_co_employees", {
		default: "cgl_deletion_exclusion_co_employees",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglIncidentalMedicalMalpracticeEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_incidental_medical_malpractice", {
		default: "cgl_incidental_medical_malpractice",
	}),
	...PremiumConsiderationSchema.properties,
	incidentalMalpracticeLimit: Type.Number({ default: 0 }),
});

export const CglDeletionExclusionsRestaurantsCateringEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_deletion_exclusions_restaurants_catering", {
		default: "cgl_deletion_exclusions_restaurants_catering",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAdditionalInsuredDesignatedEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_designated", {
		default: "cgl_additional_insured_designated",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
	designatedPersonsOrganizations: Type.String({ default: "" }),
});

export const CglWaiverTransferRightsRecoveryEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_waiver_transfer_rights_recovery", {
		default: "cgl_waiver_transfer_rights_recovery",
	}),
	...PremiumConsiderationSchema.properties,
	waiverPersonOrOrganization: Type.String({ default: "" }),
});

export const CglExclusionProductsCompletedOpsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_products_completed_ops", {
		default: "cgl_exclusion_products_completed_ops",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglExclusionMedicalPaymentsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_medical_payments", {
		default: "cgl_exclusion_medical_payments",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
});

export const CglExclusionDamageToPremisesRentedEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_damage_to_premises_rented", {
		default: "cgl_exclusion_damage_to_premises_rented",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
});

export const CglExclusionPersonalAdvertisingInjuryEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_personal_advertising_injury", {
		default: "cgl_exclusion_personal_advertising_injury",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
});

export const CglExclusionHangarkeepersLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_hangarkeepers_liability", {
		default: "cgl_exclusion_hangarkeepers_liability",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
});

export const CglExclusionMobileEquipmentEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_mobile_equipment", {
		default: "cgl_exclusion_mobile_equipment",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglLimitedCoverageDesignatedProductsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_limited_coverage_designated_products", {
		default: "cgl_limited_coverage_designated_products",
	}),
	...PremiumConsiderationSchema.properties,
	designatedProductsOrWork: Type.String({ default: "" }),
	yourProductAggregateLimit: Type.Number({ default: 0 }),
	yourWorkAggregateLimit: Type.Number({ default: 0 }),
});

export const CglExclusionHostLiquorEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_host_liquor", {
		default: "cgl_exclusion_host_liquor",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglPollutionProductSoldEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_pollution_product_sold", {
		default: "cgl_pollution_product_sold",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglCancellationClauseAmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_cancellation_clause_amendment", {
		default: "cgl_cancellation_clause_amendment",
	}),
	...PremiumConsiderationSchema.properties,
	nonpaymentNoticeDays: Type.String({ default: "ten (10)" }),
	otherReasonNoticeDays: Type.String({ default: "ninety (90)" }),
});

export const CglSupplementaryPaymentsLimitationEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_supplementary_payments_limitation", {
		default: "cgl_supplementary_payments_limitation",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglOverheadSupervisionEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_overhead_supervision", {
		default: "cgl_overhead_supervision",
	}),
	...PremiumConsiderationSchema.properties,
	locations: Type.String({ default: "" }),
});

export const CglOverheadSupervisionSpecifiedRateEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_overhead_supervision_specified_rate", {
		default: "cgl_overhead_supervision_specified_rate",
	}),
	...PremiumConsiderationSchema.properties,
	locations: Type.String({ default: "" }),
	straightTimeHourlyRate: Type.Number({ default: 0 }),
});

export const CglContractReportingProvisionEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_contract_reporting_provision", {
		default: "cgl_contract_reporting_provision",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglScheduleOfContractsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_schedule_of_contracts", {
		default: "cgl_schedule_of_contracts",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglWaiverGovernmentalImmunityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_waiver_governmental_immunity", {
		default: "cgl_waiver_governmental_immunity",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglNonOwnedAircraftLiabilityAirportOperatorEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_non_owned_aircraft_liability_airport_operator", {
		default: "cgl_non_owned_aircraft_liability_airport_operator",
	}),
	...PremiumConsiderationSchema.properties,
	scheduledLocations: Type.String({ default: "" }),
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	passengerEachPersonLimit: Type.Number({ default: 0 }),
	maxSeats: Type.Number({ default: 0 }),
	maxSeatsUnder12500Pounds: Type.Number({ default: 0 }),
});

export const CglNonOwnedAircraftLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_non_owned_aircraft_liability", {
		default: "cgl_non_owned_aircraft_liability",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	passengerEachPersonLimit: Type.Number({ default: 0 }),
	aircraftWingType: Type.Union(
		[
			Type.Literal("  "),
			Type.Literal("fixed  wing"),
			Type.Literal("rotor  wing"),
			Type.Literal("  rotor  wing"),
		],
		{ default: "  " },
	),
	airworthinessCategory: Type.Union(
		[Type.Literal("a “Standard” category"), Type.Literal("an Airworthiness Certificate")],
		{ default: "a “Standard” category" },
	),
	maxSeats: Type.Number({ default: 0 }),
	aircraftWingTypeUnder12500Pounds: Type.Union(
		[
			Type.Literal("  "),
			Type.Literal("fixed  wing"),
			Type.Literal("rotor  wing"),
			Type.Literal("  rotor  wing"),
		],
		{ default: "  " },
	),
	airworthinessCategoryUnder12500Pounds: Type.Union(
		[Type.Literal("a “Standard” category"), Type.Literal("an Airworthiness Certificate")],
		{ default: "a “Standard” category" },
	),
	maxSeatsUnder12500Pounds: Type.Number({ default: 0 }),
});

export const CglExcessAutoLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_excess_auto_liability", {
		default: "cgl_excess_auto_liability",
	}),
	...PremiumConsiderationSchema.properties,
	excessLimit: Type.Number({ default: 0 }),
	primaryInsurer: Type.String({ default: "" }),
	primaryPolicyNumber: Type.String({ default: "" }),
	primaryLimit: Type.Number({ default: 0 }),
});

export const CglExcessEmployersLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_excess_employers_liability", {
		default: "cgl_excess_employers_liability",
	}),
	...PremiumConsiderationSchema.properties,
	excessLimit: Type.Number({ default: 0 }),
	primaryInsurer: Type.String({ default: "" }),
	primaryPolicyNumber: Type.String({ default: "" }),
	primaryLimit: Type.Number({ default: 0 }),
});

export const CglGaragekeepersLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_garagekeepers_liability", {
		default: "cgl_garagekeepers_liability",
	}),
	...PremiumConsiderationSchema.properties,
	scheduledLocations: Type.String({ default: "" }),
	anyOneAutoLimit: Type.Number({ default: 0 }),
	anyOneLossLimit: Type.Number({ default: 0 }),
	eachAutoDeductible: Type.Number({ default: 0 }),
});

export const CglAirportOpsAreaAutoEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_airport_ops_area_auto", {
		default: "cgl_airport_ops_area_auto",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceLimit: Type.Number({ default: 0 }),
});

export const CglExtendedMobileEquipmentOffAirportEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_extended_mobile_equipment_off_airport", {
		default: "cgl_extended_mobile_equipment_off_airport",
	}),
	...PremiumConsiderationSchema.properties,
	scheduledLocations: Type.String({ default: "" }),
});

export const CglFinancedPremiumEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_financed_premium", {
		default: "cgl_financed_premium",
	}),
	...PremiumConsiderationSchema.properties,
	lenderName: Type.String({ default: "" }),
	lenderAddressLine1: Type.String({ default: "" }),
	lenderAddressLine2: Type.String({ default: "" }),
	lenderPhone: Type.String({ default: "" }),
});

export const CglBroadCoverageEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_broad_coverage", {
		default: "cgl_broad_coverage",
	}),
	...PremiumConsiderationSchema.properties,
	incidentalMedicalMalpracticeLimit: Type.Number({ default: 0 }),
});

export const CglBroadCoverageChangeEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_broad_coverage_change", {
		default: "cgl_broad_coverage_change",
	}),
	...PremiumConsiderationSchema.properties,
	incidentalMedicalMalpracticeLimit: Type.Number({ default: 0 }),
});

export const CglWarHijackingWriteBackEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_war_hijacking_write_back", {
		default: "cgl_war_hijacking_write_back",
	}),
	...PremiumConsiderationSchema.properties,
	warWriteBackSubLimit: Type.Number({ default: 0 }),
});

export const CglGroundingLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_grounding_liability", {
		default: "cgl_grounding_liability",
	}),
	...PremiumConsiderationSchema.properties,
	groundingLimit: Type.Number({ default: 0 }),
});

export const CglHangarkeepersWorkInProgressEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_hangarkeepers_work_in_progress", {
		default: "cgl_hangarkeepers_work_in_progress",
	}),
	...PremiumConsiderationSchema.properties,
	workInProgressLimit: Type.Number({ default: 0 }),
	workInProgressDeductible: Type.Number({ default: 0 }),
});

export const CglExtendedHangarkeepersInFlightEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_extended_hangarkeepers_in_flight", {
		default: "cgl_extended_hangarkeepers_in_flight",
	}),
	...PremiumConsiderationSchema.properties,
	inFlightEachOccurrenceLimit: Type.Number({ default: 0 }),
	inFlightDeductible: Type.Number({ default: 0 }),
});

export const CglGeographicAreasExclusionEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_geographic_areas_exclusion", {
		default: "cgl_geographic_areas_exclusion",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAmendmentLimitsDesignatedEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amendment_limits_designated", {
		default: "cgl_amendment_limits_designated",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	eachPersonMaximumExcludingProductsCompletedOperations: Type.Number({ default: 0 }),
	damageToPremisesRentedToYou: Type.Number({ default: 0 }),
	productsCompletedOperationsAggregate: Type.Number({ default: 0 }),
	productsCompletedOperationsEachPersonMaximum: Type.Number({ default: 0 }),
	personalAndAdvertisingInjuryAggregate: Type.Number({ default: 0 }),
	personalAndAdvertisingInjuryEachPersonMaximum: Type.Number({ default: 0 }),
	medicalExpenseEachPerson: Type.Number({ default: 0 }),
	hangarkeepersEachLoss: Type.Number({ default: 0 }),
	hangarkeepersEachAircraft: Type.Number({ default: 0 }),
	deductibleTurbineAircraft: Type.Number({ default: 0 }),
	deductibleOtherAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleTurbineAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleOtherAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleNonAircraftClaim: Type.Number({ default: 0 }),
});

export const CglAmendmentLimitsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amendment_limits", {
		default: "cgl_amendment_limits",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceAllHazards: Type.Number({ default: 0 }),
	eachOccurrenceExcludingProductsCompletedOperations: Type.Number({ default: 0 }),
	eachOccurrenceProductsCompletedOperations: Type.Number({ default: 0 }),
	productsCompletedOperationsAggregate: Type.Number({ default: 0 }),
	damageToPremisesRentedToYou: Type.Number({ default: 0 }),
	personalAndAdvertisingInjuryAggregate: Type.Number({ default: 0 }),
	medicalExpenseEachPerson: Type.Number({ default: 0 }),
	hangarkeepersEachLoss: Type.Number({ default: 0 }),
	hangarkeepersEachAircraft: Type.Number({ default: 0 }),
	deductibleTurbineAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleTurbineAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleOtherAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleNonAircraftClaim: Type.Number({ default: 0 }),
});

export const CglIncreasedLimitsDesignatedPersonsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_increased_limits_designated_persons", {
		default: "cgl_increased_limits_designated_persons",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceAllHazards: Type.Number({ default: 0 }),
	eachOccurrenceExcludingProductsCompletedOperations: Type.Number({ default: 0 }),
	eachOccurrenceProductsCompletedOperations: Type.Number({ default: 0 }),
	productsCompletedOperationsAggregate: Type.Number({ default: 0 }),
	damageToPremisesRentedToYou: Type.Number({ default: 0 }),
	personalAndAdvertisingInjuryAggregate: Type.Number({ default: 0 }),
	medicalExpenseEachPerson: Type.Number({ default: 0 }),
	hangarkeepersEachLoss: Type.Number({ default: 0 }),
	hangarkeepersEachAircraft: Type.Number({ default: 0 }),
	deductibleTurbineAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleTurbineAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleOtherAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleNonAircraftClaim: Type.Number({ default: 0 }),
});

export const CglDeductibleEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_deductible", {
		default: "cgl_deductible",
	}),
	...PremiumConsiderationSchema.properties,
	bodilyInjuryPerClaim: Type.Number({ default: 0 }),
	bodilyInjuryPerOccurrence: Type.Number({ default: 0 }),
	propertyDamagePerClaimReciprocatingAircraft: Type.Number({ default: 0 }),
	propertyDamagePerClaimTurbineAircraft: Type.Number({ default: 0 }),
	propertyDamagePerClaimOther: Type.Number({ default: 0 }),
	propertyDamagePerOccurrence: Type.Number({ default: 0 }),
	personalAndAdvertisingInjuryPerClaim: Type.Number({ default: 0 }),
	personalAndAdvertisingInjuryPerOffense: Type.Number({ default: 0 }),
	hangarkeepersPerReciprocatingEngineAircraft: Type.Number({ default: 0 }),
	hangarkeepersPerTurbineEngineAircraft: Type.Number({ default: 0 }),
	hangarkeepersPerLoss: Type.Number({ default: 0 }),
	annualAggregateDeductible: Type.Number({ default: 0 }),
});

export const CglPremiumAmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_premium_amendment", {
		default: "cgl_premium_amendment",
	}),
	...PremiumConsiderationSchema.properties,
	premiumSubjectToAudit: Type.Number({ default: 0 }),
	stateTaxOrOther: Type.Number({ default: 0 }),
	totalPremium: Type.Number({ default: 0 }),
	amendedEndorsementNumber: Type.String({ default: "" }),
	amendedAdditionalPremium: Type.Number({ default: 0 }),
});

export const CglSparePartsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_spare_parts", {
		default: "cgl_spare_parts",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	deductibleEachLoss: Type.Number({ default: 0 }),
	item1Description: Type.String({ default: "" }),
	item1InsuredValue: Type.Number({ default: 0 }),
	item2Description: Type.String({ default: "" }),
	item2InsuredValue: Type.Number({ default: 0 }),
	item3Description: Type.String({ default: "" }),
	item3InsuredValue: Type.Number({ default: 0 }),
	item4Description: Type.String({ default: "" }),
	item4InsuredValue: Type.Number({ default: 0 }),
	item5Description: Type.String({ default: "" }),
	item5InsuredValue: Type.Number({ default: 0 }),
	item6Description: Type.String({ default: "" }),
	item6InsuredValue: Type.Number({ default: 0 }),
	item7Description: Type.String({ default: "" }),
	item7InsuredValue: Type.Number({ default: 0 }),
	item8Description: Type.String({ default: "" }),
	item8InsuredValue: Type.Number({ default: 0 }),
	lossPayee: Type.String({ default: "" }),
	lossPayeeMailingAddress: Type.String({ default: "" }),
});

export const CglCargoLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_cargo_liability", {
		default: "cgl_cargo_liability",
	}),
	...PremiumConsiderationSchema.properties,
	locations: Type.String({ default: "" }),
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	deductibleEachClaim: Type.Number({ default: 0 }),
	deductibleMaximumEachOccurrence: Type.Number({ default: 0 }),
});

export const CglNonOwnedAircraftPhysicalDamageEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_non_owned_aircraft_physical_damage", {
		default: "cgl_non_owned_aircraft_physical_damage",
	}),
	...PremiumConsiderationSchema.properties,
	categoryAAircraftType: Type.String({ default: "" }),
	categoryAAirworthinessCertificate: Type.String({ default: "" }),
	categoryAMaximumSeats: Type.Number({ default: 0 }),
	categoryBAircraftType: Type.String({ default: "" }),
	categoryBAirworthinessCertificate: Type.String({ default: "" }),
	categoryBMaximumSeats: Type.Number({ default: 0 }),
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	deductibleNotInMotion: Type.Number({ default: 0 }),
	deductibleInMotionIngestionOrMooring: Type.Number({ default: 0 }),
});

export const CglBaggageLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_baggage_liability", {
		default: "cgl_baggage_liability",
	}),
	...PremiumConsiderationSchema.properties,
	locations: Type.String({ default: "" }),
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	deductibleEachLoss: Type.Number({ default: 0 }),
});

export const CglMechanicsToolsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_mechanics_tools", {
		default: "cgl_mechanics_tools",
	}),
	...PremiumConsiderationSchema.properties,
	eachEmployeeLimit: Type.Number({ default: 0 }),
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	deductibleEachEmployee: Type.Number({ default: 0 }),
});

export const CglAdditionalInsuredDesignatedOptionalClausesEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_designated_optional_clauses", {
		default: "cgl_additional_insured_designated_optional_clauses",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAirshowLiabilityCoveragePartEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_airshow_liability_coverage_part", {
		default: "cgl_airshow_liability_coverage_part",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAmendedDefinitionPropertyDamageEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amended_definition_property_damage", {
		default: "cgl_amended_definition_property_damage",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAmendmentWhoIsInsuredPublicEntitiesEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amendment_who_is_insured_public_entities", {
		default: "cgl_amendment_who_is_insured_public_entities",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglDefenseCostsWithinLimitEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_defense_costs_within_limit", {
		default: "cgl_defense_costs_within_limit",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglExcludedProductsCompletedOperationsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_excluded_products_completed_operations", {
		default: "cgl_excluded_products_completed_operations",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglExclusionCompletedOperationsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_completed_operations", {
		default: "cgl_exclusion_completed_operations",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglExclusionIntercompanyProductsSuitEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_intercompany_products_suit", {
		default: "cgl_exclusion_intercompany_products_suit",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglForeignMilitaryHullEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_foreign_military_hull", {
		default: "cgl_foreign_military_hull",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglHoneywellAvionicsDealershipEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_honeywell_avionics_dealership", {
		default: "cgl_honeywell_avionics_dealership",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglHoneywellHoldHarmlessEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_honeywell_hold_harmless", {
		default: "cgl_honeywell_hold_harmless",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglMisdeliveryLiquidProductsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_misdelivery_liquid_products", {
		default: "cgl_misdelivery_liquid_products",
	}),
	...PremiumConsiderationSchema.properties,
});

export const CglAdditionalInsuredControllingInterestEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_controlling_interest", {
		default: "cgl_additional_insured_controlling_interest",
	}),
	...PremiumConsiderationSchema.properties,
	designatedPremises: Type.String({ default: "" }),
	additionalInsuredName: Type.String({ default: "" }),
});

export const CglAdditionalInsuredManagersLessorsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_managers_lessors", {
		default: "cgl_additional_insured_managers_lessors",
	}),
	...PremiumConsiderationSchema.properties,
	designatedPremises: Type.String({ default: "" }),
	additionalInsuredName: Type.String({ default: "" }),
});

export const CglAdditionalInsuredManagersLessorsBroadFormEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_managers_lessors_broad_form", {
		default: "cgl_additional_insured_managers_lessors_broad_form",
	}),
	...PremiumConsiderationSchema.properties,
	designatedPremises: Type.String({ default: "" }),
	additionalInsuredName: Type.String({ default: "" }),
});

export const CglAdditionalInsuredLessorLeasedEquipmentEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_lessor_leased_equipment", {
		default: "cgl_additional_insured_lessor_leased_equipment",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
	additionalInsuredName: Type.String({ default: "" }),
});

export const CglAdditionalInsuredOwnersLesseesContractorsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_owners_lessees_contractors", {
		default: "cgl_additional_insured_owners_lessees_contractors",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
	additionalInsuredName: Type.String({ default: "" }),
});

export const CglAdditionalInsuredDesignatedExcludingPaiEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_designated_excluding_pai", {
		default: "cgl_additional_insured_designated_excluding_pai",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
	designatedPersonsOrganizations: Type.String({ default: "" }),
});

export const CglAdditionalInsuredDesignatedExtendedEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_designated_extended", {
		default: "cgl_additional_insured_designated_extended",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
	designatedPersonsOrganizations: Type.String({ default: "" }),
	designatedPersonsOrganizationsContinued: Type.String({ default: "" }),
});

export const CglAdditionalInsuredVendorsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_vendors", {
		default: "cgl_additional_insured_vendors",
	}),
	...PremiumConsiderationSchema.properties,
	vendorName: Type.String({ default: "" }),
	vendorProduct: Type.String({ default: "" }),
});

export const CglAdditionalInsuredFuelLubricantSuppliersEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_additional_insured_fuel_lubricant_suppliers", {
		default: "cgl_additional_insured_fuel_lubricant_suppliers",
	}),
	...PremiumConsiderationSchema.properties,
	additionalInsuredNameOrOrganization: Type.String({ default: "" }),
	generalAggregateLimit: Type.Number({ default: 0 }),
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	productsCompletedOperationsAggregate: Type.Number({ default: 0 }),
});

export const CglDeletionExclusionSaleOfAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_deletion_exclusion_sale_of_aircraft", {
		default: "cgl_deletion_exclusion_sale_of_aircraft",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
});

export const CglExclusionHealthCosmeticServicesEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_health_cosmetic_services", {
		default: "cgl_exclusion_health_cosmetic_services",
	}),
	...PremiumConsiderationSchema.properties,
	designatedLocations: Type.String({ default: "" }),
});

export const CglExclusionNewEntitiesEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_exclusion_new_entities", {
		default: "cgl_exclusion_new_entities",
	}),
	...PremiumConsiderationSchema.properties,
	excludedNewEntities: Type.String({ default: "" }),
});

export const CglNegligentInstructionSupervisionEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_negligent_instruction_supervision", {
		default: "cgl_negligent_instruction_supervision",
	}),
	...PremiumConsiderationSchema.properties,
	singleLimitAggregate: Type.Number({ default: 0 }),
	bodilyInjuryEachPerson: Type.Number({ default: 0 }),
});

export const CglNegligentInstructionSupervisionIndependentInstructorsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_negligent_instruction_supervision_independent_instructors", {
		default: "cgl_negligent_instruction_supervision_independent_instructors",
	}),
	...PremiumConsiderationSchema.properties,
	singleLimitAggregate: Type.Number({ default: 0 }),
	bodilyInjuryEachPerson: Type.Number({ default: 0 }),
});

export const CglAirshowLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_airshow_liability", {
		default: "cgl_airshow_liability",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceLimit: Type.Number({ default: 0 }),
});

export const CglBoeingEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_boeing", {
		default: "cgl_boeing",
	}),
	...PremiumConsiderationSchema.properties,
	boeingAgreement: Type.Union([
		Type.Literal("Hardware Material Services General Terms Agreement"),
		Type.Literal("Patent and Technology License"),
	], { default: "Hardware Material Services General Terms Agreement" }),
});

export const CglInspectionLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_inspection_liability", {
		default: "cgl_inspection_liability",
	}),
	...PremiumConsiderationSchema.properties,
	inspectionCoverageStartDate: Type.String({ default: "" }),
});

export const CglExtendedNonOwnedAircraftPilotWarrantyEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_extended_non_owned_aircraft_pilot_warranty", {
		default: "cgl_extended_non_owned_aircraft_pilot_warranty",
	}),
	...PremiumConsiderationSchema.properties,
	pilotRequirement: Type.Union([
		Type.Literal("a pilot"),
		Type.Literal("an employed pilot"),
	], { default: "a pilot" }),
});

export const CglBaeSystemsTssEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_bae_systems_tss", {
		default: "cgl_bae_systems_tss",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	productsCompletedOperationsAggregate: Type.Number({ default: 0 }),
	propertyDamageDeductibleTurbineAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleOtherAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleNonAircraftClaim: Type.Number({ default: 0 }),
});

export const CglRockwellCollinsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_rockwell_collins", {
		default: "cgl_rockwell_collins",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	productsCompletedOperationsAggregate: Type.Number({ default: 0 }),
	hangarkeepersEachLoss: Type.Number({ default: 0 }),
	hangarkeepersEachAircraft: Type.Number({ default: 0 }),
	deductibleTurbineAircraft: Type.Number({ default: 0 }),
	deductibleOtherAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleTurbineAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleOtherAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleNonAircraftClaim: Type.Number({ default: 0 }),
});

export const CglStecCorporationEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_stec_corporation", {
		default: "cgl_stec_corporation",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	productsLiabilityAggregate: Type.Number({ default: 0 }),
	completedOperationsLiabilityAggregate: Type.Number({ default: 0 }),
	propertyDamageDeductibleTurbineAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleOtherAircraft: Type.Number({ default: 0 }),
	propertyDamageDeductibleNonAircraftClaim: Type.Number({ default: 0 }),
});

export const CglContingentControlTowerLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_contingent_control_tower_liability", {
		default: "cgl_contingent_control_tower_liability",
	}),
	...PremiumConsiderationSchema.properties,
	coveredLocations: Type.String({ default: "" }),
	excludedLocations: Type.String({ default: "" }),
	perOccurrenceLimit: Type.Number({ default: 0 }),
	annualAggregateLimit: Type.Number({ default: 0 }),
});

export const CglHostLiquorLiabilityLimitationEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_host_liquor_liability_limitation", {
		default: "cgl_host_liquor_liability_limitation",
	}),
	...PremiumConsiderationSchema.properties,
	locations: Type.String({ default: "" }),
	perOccurrenceLimit: Type.Number({ default: 0 }),
	annualAggregateLimit: Type.Number({ default: 0 }),
});

export const CglHangarkeepersLeasedManagedAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_hangarkeepers_leased_managed_aircraft", {
		default: "cgl_hangarkeepers_leased_managed_aircraft",
	}),
	...PremiumConsiderationSchema.properties,
	eachLossLimit: Type.Number({ default: 0 }),
	eachAircraftLimit: Type.Number({ default: 0 }),
	deductibleEachLoss: Type.Number({ default: 0 }),
});

export const CglSpacecraftProductsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_spacecraft_products", {
		default: "cgl_spacecraft_products",
	}),
	...PremiumConsiderationSchema.properties,
	singleSatelliteLimit: Type.Number({ default: 0 }),
	multipleSatellitesLimit: Type.Number({ default: 0 }),
});

export const CglSelfInsuredRetentionEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_self_insured_retention", {
		default: "cgl_self_insured_retention",
	}),
	...PremiumConsiderationSchema.properties,
	retentionAmount: Type.Number({ default: 0 }),
	aggregateRetentionAmount: Type.Number({ default: 0 }),
	lossSummaryFrequency: Type.Union([
		Type.Literal("monthly"),
		Type.Literal("quarterly"),
		Type.Literal("annual"),
	], { default: "monthly" }),
});

export const CglEmployeeBenefitsLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_employee_benefits_liability", {
		default: "cgl_employee_benefits_liability",
	}),
	...PremiumConsiderationSchema.properties,
	eachEmployeeLimit: Type.Number({ default: 0 }),
	eachEmployeeDeductible: Type.Number({ default: 0 }),
	coveragePremium: Type.Number({ default: 0 }),
	aggregateLimit: Type.Number({ default: 0 }),
	retroactiveDate: Type.String({ default: "" }),
});

export const CglPremisesAmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_premises_amendment", {
		default: "cgl_premises_amendment",
	}),
	...PremiumConsiderationSchema.properties,
	location1Number: Type.String({ default: "" }),
	location1Address: Type.String({ default: "" }),
	location2Number: Type.String({ default: "" }),
	location2Address: Type.String({ default: "" }),
	location3Number: Type.String({ default: "" }),
	location3Address: Type.String({ default: "" }),
	location4Number: Type.String({ default: "" }),
	location4Address: Type.String({ default: "" }),
});

export const CglProductsCompletedOpsLeasedManagedAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_products_completed_ops_leased_managed_aircraft", {
		default: "cgl_products_completed_ops_leased_managed_aircraft",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceAndAggregateLimit: Type.Number({ default: 0 }),
});

export const CglProductsCompletedOpsMaintenanceLeasedAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_products_completed_ops_maintenance_leased_aircraft", {
		default: "cgl_products_completed_ops_maintenance_leased_aircraft",
	}),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceLimit: Type.Number({ default: 0 }),
	annualAggregateLimit: Type.Number({ default: 0 }),
});

export const CglUaaMembersBroadFormEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_uaa_members_broad_form", {
		default: "cgl_uaa_members_broad_form",
	}),
	...PremiumConsiderationSchema.properties,
	airportOpsAutoEachOccurrenceLimit: Type.Number({ default: 0 }),
	airportOpsAutoPropertyDamageDeductible: Type.String({ default: "Not Applicable" }),
	cargoEachOccurrenceLimit: Type.Number({ default: 0 }),
	cargoDeductibleEachClaim: Type.Number({ default: 0 }),
	cargoDeductibleEachOccurrence: Type.Number({ default: 0 }),
	garagekeepersAnyOneAutoLimit: Type.Number({ default: 0 }),
	garagekeepersAnyOneLossLimit: Type.Number({ default: 0 }),
	garagekeepersEachAutoDeductible: Type.Number({ default: 0 }),
	hostLiquorPerOccurrenceLimit: Type.Number({ default: 0 }),
	hostLiquorAnnualAggregateLimit: Type.Number({ default: 0 }),
	incidentalMedicalMalpracticeLimit: Type.Number({ default: 0 }),
	mechanicsToolsEachEmployeeLimit: Type.Number({ default: 0 }),
	mechanicsToolsEachOccurrenceLimit: Type.Number({ default: 0 }),
	mechanicsToolsEachEmployeeDeductible: Type.Number({ default: 0 }),
	studentRenterPilotCombinedSingleLimit: Type.Number({ default: 0 }),
	studentRenterPilotLimitsBasis: Type.Union([
		Type.Literal("part of and not in addition to"),
		Type.Literal("in addition to"),
	], { default: "part of and not in addition to" }),
});

export const CglPremiumCalculationEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_premium_calculation", {
		default: "cgl_premium_calculation",
	}),
	...PremiumConsiderationSchema.properties,
	location1Number: Type.String({ default: "" }),
	location1Classification: Type.String({ default: "" }),
	location1Code: Type.String({ default: "" }),
	location1PremiumBase: Type.Number({ default: 0 }),
	location1RatePremisesOperations: Type.Number({ default: 0 }),
	location1RateProductsCompletedOperations: Type.Number({ default: 0 }),
	location1AdvancePremiumPremisesOperations: Type.Number({ default: 0 }),
	location1AdvancePremiumProductsCompletedOperations: Type.Number({ default: 0 }),
	location2Number: Type.String({ default: "" }),
	location2Classification: Type.String({ default: "" }),
	location2Code: Type.String({ default: "" }),
	location2PremiumBase: Type.Number({ default: 0 }),
	location2RatePremisesOperations: Type.Number({ default: 0 }),
	location2RateProductsCompletedOperations: Type.Number({ default: 0 }),
	location2AdvancePremiumPremisesOperations: Type.Number({ default: 0 }),
	location2AdvancePremiumProductsCompletedOperations: Type.Number({ default: 0 }),
	location3Number: Type.String({ default: "" }),
	location3Classification: Type.String({ default: "" }),
	location3Code: Type.String({ default: "" }),
	location3PremiumBase: Type.Number({ default: 0 }),
	location3RatePremisesOperations: Type.Number({ default: 0 }),
	location3RateProductsCompletedOperations: Type.Number({ default: 0 }),
	location3AdvancePremiumPremisesOperations: Type.Number({ default: 0 }),
	location3AdvancePremiumProductsCompletedOperations: Type.Number({ default: 0 }),
});

export const CglPremiumCalculationGrossReceiptsEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_premium_calculation_gross_receipts", {
		default: "cgl_premium_calculation_gross_receipts",
	}),
	...PremiumConsiderationSchema.properties,
	companySharePercent: Type.String({ default: "" }),
	premisesFlatAnnualPremium: Type.Number({ default: 0 }),
	productsCompletedOperationsAdvancePremium: Type.Number({ default: 0 }),
	hangarkeepersFlatAnnualPremium: Type.Number({ default: 0 }),
	endorsementPremium: Type.Number({ default: 0 }),
	totalPremiumDescription: Type.String({ default: "" }),
	totalRatingBasis: Type.String({ default: "" }),
	totalRate: Type.String({ default: "" }),
	totalPremiumAmount: Type.Number({ default: 0 }),
	totalFlatRatedAndAdvancePremium: Type.Number({ default: 0 }),
	minimumProductsCompletedOperationsPremium: Type.Number({ default: 0 }),
	maximumProductsCompletedOperationsPremium: Type.Number({ default: 0 }),
});

export const CglAmendmentLimitsProductsCompletedOpsHazardEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amendment_limits_products_completed_ops_hazard", {
		default: "cgl_amendment_limits_products_completed_ops_hazard",
	}),
	...PremiumConsiderationSchema.properties,
	turbineEachOccurrenceProductsCompletedOperations: Type.Number({ default: 0 }),
	turbineProductsCompletedOperationsAggregate: Type.Number({ default: 0 }),
	pistonEachOccurrenceProductsCompletedOperations: Type.Number({ default: 0 }),
	pistonProductsCompletedOperationsAggregate: Type.Number({ default: 0 }),
});

export const CglAmendmentDeductiblesPerOccurrenceEndorsementSchema = Type.Object({
	slug: Type.Literal("cgl_amendment_deductibles_per_occurrence", {
		default: "cgl_amendment_deductibles_per_occurrence",
	}),
	...PremiumConsiderationSchema.properties,
	hangarkeepersDeductibleEachOccurrence: Type.Number({ default: 0 }),
	propertyDamageDeductibleEachOccurrence: Type.Number({ default: 0 }),
});

export const DeletionOfExclusionPdYourProductWorkEndorsementSchema = Type.Object({
	slug: Type.Literal("deletion_of_exclusion_pd_your_product_work", {
		default: "deletion_of_exclusion_pd_your_product_work",
	}),
	...PremiumConsiderationSchema.properties,
});

export const AmendmentLimitsProductsCompletedOpsPistonEndorsementSchema = Type.Object({
	slug: Type.Literal("amendment_limits_products_completed_ops_piston", {
		default: "amendment_limits_products_completed_ops_piston",
	}),
	...PremiumConsiderationSchema.properties,
	aggregateLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const RightOfFirstRefusalEndorsementSchema = Type.Object({
	slug: Type.Literal("right_of_first_refusal", { default: "right_of_first_refusal" }),
	...PremiumConsiderationSchema.properties,
});

export const MinimumEarnedPremiumEndorsementSchema = Type.Object({
	slug: Type.Literal("minimum_earned_premium", { default: "minimum_earned_premium" }),
	...PremiumConsiderationSchema.properties,
	percentageOfTotalPremium: Type.Number({
		default: 0, minimum: 0, maximum: 100,
	}),
});

export const AmendatoryEndorsementSchema = Type.Object({
	slug: Type.Literal("amendatory", { default: "amendatory" }),
	...PremiumConsiderationSchema.properties,
	description: Type.String({ default: "" }),
});

export const AirAmbulanceUseEndorsementSchema = Type.Object({
	slug: Type.Literal("air_ambulance_use", { default: "air_ambulance_use" }),
	...PremiumConsiderationSchema.properties,
});

export const PurposeOfUseEndorsementSchema = Type.Object({
	slug: Type.Literal("purpose_of_use", { default: "purpose_of_use" }),
	...PremiumConsiderationSchema.properties,
	purposeOfUseDescription: Type.String({ default: "" }),
});

export const SalesDemonstrationUseEndorsementSchema = Type.Object({
	slug: Type.Literal("sales_demonstration_use", { default: "sales_demonstration_use" }),
	...PremiumConsiderationSchema.properties,
});

export const LeasedAircraftContingentEndorsementSchema = Type.Object({
	slug: Type.Literal("leased_aircraft_contingent", { default: "leased_aircraft_contingent" }),
	...PremiumConsiderationSchema.properties,
	combinedSingleLimitEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	physicalDamageAgreedValue: Type.Union([Type.Null(), Type.Number()], { default: null }),
	scheduleAsHeldOnFile: Type.Boolean({ default: false }),
	aircraftSchedule: Type.Array(
		Type.Object({
			tailNumber: Type.String(),
			makeModel: Type.String(),
			aircraftYear: Type.String(),
			aircraftInsuredValue: Type.Union([Type.Null(), Type.Number()], { default: null }),
			aircraftDeductibles: Type.String({ default: "" }),
		}),
		{ default: [] },
	),
});

export const PhysicalDamageAircraftRowSchema = Type.Object({
	tailNumber: Type.String({ default: "" }),
	makeModel: Type.String({ default: "" }),
	year: Type.String({ default: "" }),
	seats: Type.String({ default: "" }),
	insuredValue: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageLetter: Type.String({ default: "" }),
	deductibleNotInMotion: Type.Union([Type.Null(), Type.Number()], { default: null }),
	deductibleInMotion: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const AmendedAircraftScheduleEndorsementSchema = Type.Object({
	slug: Type.Literal("amended_aircraft_schedule", { default: "amended_aircraft_schedule" }),
	...PremiumConsiderationSchema.properties,
	aircraftDescription: Type.String({ default: "" }),
	bodilyInjuryEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	bodilyInjuryEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	propertyDamageEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	passengerLiabilityEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	passengerLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	singleLimitEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	singleLimitEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	medicalExpenseEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	medicalExpenseEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	aircraftSchedule: Type.Array(PhysicalDamageAircraftRowSchema, { default: [] }),
});

export const ManagedAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("managed_aircraft", { default: "managed_aircraft" }),
	...PremiumConsiderationSchema.properties,
	bodilyInjuryEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	bodilyInjuryEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	propertyDamageEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	passengerLiabilityEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	passengerLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	singleLimitEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	singleLimitEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	medicalExpenseEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	medicalExpenseEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	aircraftSchedule: Type.Array(PhysicalDamageAircraftRowSchema, { default: [] }),
	insuredOwner: Type.String({ default: "" }),
	nonOwnedBodilyInjuryEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedBodilyInjuryEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedPropertyDamageEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedPassengerLiabilityEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedPassengerLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedSingleLimitEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedSingleLimitEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedMedicalExpenseEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedMedicalExpenseEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	premisesLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	productsLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	hostLiquorLiabilityAggregate: Type.Union([Type.Null(), Type.Number()], { default: null }),
	mobileEquipmentLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	personalInjuryLiabilityEachOffense: Type.Union([Type.Null(), Type.Number()], { default: null }),
	lienholders: Type.Array(
		Type.Object({
			registration: Type.String({ default: "" }),
			lienholder: Type.String({ default: "" }),
		}),
		{ default: [] },
	),
});

export const ManagedAircraftBroadEndorsementSchema = Type.Object({
	slug: Type.Literal("managed_aircraft_broad", { default: "managed_aircraft_broad" }),
	...PremiumConsiderationSchema.properties,
	bodilyInjuryEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	bodilyInjuryEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	propertyDamageEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	passengerLiabilityEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	passengerLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	singleLimitEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	singleLimitEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	medicalExpenseEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	medicalExpenseEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	aircraftSchedule: Type.Array(PhysicalDamageAircraftRowSchema, { default: [] }),
	insuredOwner: Type.String({ default: "" }),
	nonOwnedBodilyInjuryEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedBodilyInjuryEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedPropertyDamageEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedPassengerLiabilityEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedPassengerLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedSingleLimitEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedSingleLimitEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedMedicalExpenseEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nonOwnedMedicalExpenseEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	premisesLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	productsLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	hostLiquorLiabilityAggregate: Type.Union([Type.Null(), Type.Number()], { default: null }),
	mobileEquipmentLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	personalInjuryLiabilityEachOffense: Type.Union([Type.Null(), Type.Number()], { default: null }),
	cargoEachOccurrenceLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
	cargoDeductibleEachClaim: Type.Union([Type.Null(), Type.Number()], { default: null }),
	cargoDeductibleEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	spareEnginesPartsEachLossLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
	spareEnginesPartsInMotionDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	spareEnginesPartsNotInMotionDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	garagekeepersAnyOneAutoLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
	garagekeepersAnyOneLossLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
	garagekeepersEachAutoDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	lienholders: Type.Array(
		Type.Object({
			registration: Type.String({ default: "" }),
			lienholder: Type.String({ default: "" }),
		}),
		{ default: [] },
	),
});

export const LeasedEngineEndorsementSchema = Type.Object({
	slug: Type.Literal("leased_engine", { default: "leased_engine" }),
	...PremiumConsiderationSchema.properties,
	maxEngineLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
	deductibleAttached: Type.Number({ default: 0 }),
	deductibleNotAttached: Type.Number({ default: 0 }),
});

export const LeasedEngineScheduledEndorsementSchema = Type.Object({
	slug: Type.Literal("leased_engine_scheduled", { default: "leased_engine_scheduled" }),
	...PremiumConsiderationSchema.properties,
	deductibleEachLoss: Type.Number({ default: 0 }),
	engineSchedule: Type.Array(
		Type.Object({
			manufacturer: Type.String({ default: "" }),
			model: Type.String({ default: "" }),
			serialNumber: Type.String({ default: "" }),
			tailNumber: Type.String({ default: "" }),
			ownerLessor: Type.String({ default: "" }),
			insuredValue: Type.Union([Type.Null(), Type.Number()], { default: null }),
		}),
		{ default: [] },
	),
});

export type LeasedEngineScheduleRow = typeof LeasedEngineScheduledEndorsementSchema.static["engineSchedule"][number];

export const PhysicalDamageEndorsementSchema = Type.Object({
	slug: Type.Literal("physical_damage", { default: "physical_damage" }),
	...PremiumConsiderationSchema.properties,
	aircraftSchedule: Type.Array(PhysicalDamageAircraftRowSchema, { default: [] }),
});

/** Currency fields on the Floats & Skis endorsement DOCX (0014). */
export const FLOATS_AND_SKIS_CURRENCY_FIELDS = [
	"withWheelsInsuredValue",
	"withFloatsInsuredValue",
	"withSkisInsuredValue",
	"detachedWheelsInsuredValue",
	"detachedFloatsInsuredValue",
	"detachedSkisInsuredValue",
	"wheelsDeductible",
	"floatsDeductible",
	"skisDeductible",
	"flightWheelsDeductible",
	"flightFloatsDeductible",
	"flightSkisDeductible",
	"taxiWheelsDeductible",
	"taxiFloatsDeductible",
	"taxiSkisDeductible",
	"nimWheelsDeductible",
	"nimFloatsDeductible",
	"nimSkisDeductible",
] as const;

export const FloatsAndSkisEndorsementSchema = Type.Object({
	slug: Type.Literal("floats_and_skis", { default: "floats_and_skis" }),
	...PremiumConsiderationSchema.properties,
	aircraftTail: Type.String({ default: "" }),
	withWheelsInsuredValue: Type.Number({ default: 0 }),
	withFloatsInsuredValue: Type.Number({ default: 0 }),
	withSkisInsuredValue: Type.Number({ default: 0 }),
	detachedWheelsInsuredValue: Type.Number({ default: 0 }),
	detachedFloatsInsuredValue: Type.Number({ default: 0 }),
	detachedSkisInsuredValue: Type.Number({ default: 0 }),
	wheelsDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	floatsDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	skisDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	flightWheelsDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	flightFloatsDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	flightSkisDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	taxiWheelsDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	taxiFloatsDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	taxiSkisDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nimWheelsDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nimFloatsDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	nimSkisDeductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const WarHijackingPerilsEndorsementSchema = Type.Object({
	slug: Type.Literal("war_hijacking_perils", {
		default: "war_hijacking_perils",
	}),
	...PremiumConsiderationSchema.properties,
	...OptionalAutoExpirationProperty,
	allScheduledAircraft: Type.Boolean({ default: true }),
	allReportedAircraft: Type.Boolean({ default: false }),
	selectedTailNumbers: Type.Optional(Type.Array(Type.String(), { default: [] })),
	limit: Type.Boolean({ default: true }),
	limitAmount: Type.Union([Type.Null(), Type.Number()], { default: null }),
	sameAsSingleLimit: Type.Boolean({ default: false }),
	singleLimitMax: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const WarHijackingPhysicalDamageEndorsementSchema = Type.Object({
	slug: Type.Literal("war_hijacking_physical_damage", {
		default: "war_hijacking_physical_damage",
	}),
	...PremiumConsiderationSchema.properties,
	limit: Type.Number({ default: 0 }),
	selectedTailNumbers: Type.Optional(Type.Array(Type.String(), { default: [] })),
});

export const TriaLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("tria_liability", { default: "tria_liability" }),
	...PremiumConsiderationSchema.properties,
});

export const TriaHullWriteBackEndorsementSchema = Type.Object({
	slug: Type.Literal("tria_hull_write_back", {
		default: "tria_hull_write_back",
	}),
	...PremiumConsiderationSchema.properties,
	excludedAircraftSchedule: Type.Array(
		Type.Object({
			tailNumber: Type.String(),
			makeModel: Type.String(),
			year: Type.Number(),
			insuredValue: Type.Number(),
		}),
		{ default: [] },
	),
});

export const CargoLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("cargo_liability", { default: "cargo_liability" }),
	...PremiumConsiderationSchema.properties,
	liabilityLimitEachOccurrence: Type.Number({ default: 0 }),
	deductible: Type.Number({ default: 1_000 }),
	maxDeductible: Type.Number({ default: 5_000 }),
});

export const AutomaticInsuranceForIncreasedValueEndorsementSchema = Type.Object(
	{
		slug: Type.Literal("automatic_insurance_for_increased_value", {
			default: "automatic_insurance_for_increased_value",
		}),
		...PremiumConsiderationSchema.properties,
		maxInsuredValue: Type.Number({ default: 0 }),
		maxPercentageOfInsuredAmount: Type.Number({ default: 25 }),
	},
);

export const AirportOpsAreaAutoEndorsementSchema = Type.Object({
	slug: Type.Literal("airport_ops_area_auto", {
		default: "airport_ops_area_auto",
	}),
	...PremiumConsiderationSchema.properties,
	liabilityLimit: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
	deductible: Type.Union([Type.Null(), Type.Number()], { default: 500 }),
});

export const ExcessEmployersLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("excess_employers_liability", {
		default: "excess_employers_liability",
	}),
	...PremiumConsiderationSchema.properties,
	excessLimit: Type.Number({ default: 0 }),
	primaryInsurer: Type.String({ default: "" }),
	primaryPolicyNumber: Type.String({ default: "" }),
	primaryLimit: Type.Number({ default: 1_000_000 }),
});

export const ExcessAutoLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("excess_auto_liability", { default: "excess_auto_liability" }),
	...PremiumConsiderationSchema.properties,
	excessLimit: Type.Number({ default: 0 }),
	primaryLimit: Type.Number({ default: 0 }),
	primaryPolicies: Type.Array(
		Type.Object({
			insurer: Type.String({ default: "" }),
			policyNumber: Type.String({ default: "" }),
			periodFrom: Type.String({ default: "" }),
			periodTo: Type.String({ default: "" }),
		}),
		{ default: [] },
	),
});

export const DamageToPremisesEndorsementSchema = Type.Object({
	slug: Type.Literal("damage_to_premises", { default: "damage_to_premises" }),
	...PremiumConsiderationSchema.properties,
	liabilityLimit: Type.Number({ default: 50_000 }),
});

export const GaragekeepersEndorsementSchema = Type.Object({
	slug: Type.Literal("garagekeepers", { default: "garagekeepers" }),
	...PremiumConsiderationSchema.properties,
	limitOneAuto: Type.Number({ default: 50_000 }),
	limitOneLoss: Type.Number({ default: 100_000 }),
	deductibleEachAuto: Type.Union([Type.Null(), Type.Number()], { default: 1000 }),
});

export const AutomaticIncreaseSparePartsEndorsementSchema = Type.Object({
	slug: Type.Literal("automatic_increase_spare_parts", {
		default: "automatic_increase_spare_parts",
	}),
	...PremiumConsiderationSchema.properties,
	appliesTo: Type.String({ default: "" }),
	maxAutomaticLimit: Type.Number({ default: 62_500 }),
});

export const BroadFormContractualLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("broad_form_contractual_liability", {
		default: "broad_form_contractual_liability",
	}),
	...PremiumConsiderationSchema.properties,
	limitFollowsAircraftLimit: Type.Boolean({ default: true }),
	limitHasCustomOccurrenceLimit: Type.Boolean({ default: false }),
	limitOfLiabilityEachOccurrence: Type.Union([Type.Null(), Type.Number()], {
		default: null,
	}),
});

export const ContractualLiabilitySpecificContractEndorsementSchema = Type.Object({
	slug: Type.Literal("contractual_liability_specific_contract", {
		default: "contractual_liability_specific_contract",
	}),
	...PremiumConsiderationSchema.properties,
	contractParty: Type.String({ default: "" }),
	contractDate: Type.Union([Type.Null(), Type.String()], { default: null }),
	contractWording: Type.String({ default: "" }),
});

export const PersonalInjuryExtensionSchema = Type.Object({
	slug: Type.Literal("personal_injury_extension", {
		default: "personal_injury_extension",
	}),
	...PremiumConsiderationSchema.properties,
	limitOfLiabilityEachOffense: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
	limitOfLiabilityAnnualAggregate: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
});

export const AdvertisingInjuryExtensionSchema = Type.Object({
	slug: Type.Literal("advertising_injury_extension", {
		default: "advertising_injury_extension",
	}),
	...PremiumConsiderationSchema.properties,
	limitOfLiabilityEachOffense: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
	limitOfLiabilityAnnualAggregate: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
});

export const FamilyAssistanceExpenseEndorsementSchema = Type.Object({
	slug: Type.Literal("family_assistance_expense", {
		default: "family_assistance_expense",
	}),
	...PremiumConsiderationSchema.properties,
	limitPerPerson: Type.Number({ default: 25_000 }),
	limitPerOccurrence: Type.Number({ default: 25_000 }),
	limitAnnualAggregate: Type.Number({ default: 25_000 }),
});

export const CostReimbursementEndorsementSchema = Type.Object({
	slug: Type.Literal("cost_reimbursement", { default: "cost_reimbursement" }),
	...PremiumConsiderationSchema.properties,
	reimbursementSchedule: Type.Array(
		Type.Object({
			pilotName: Type.String({ default: "" }),
			reimbursedBy: Type.String({ default: "" }),
			ratePerFlightHour: Type.Union([Type.Null(), Type.Number()], { default: null }),
		}),
		{ default: [] },
	),
});

export const PassengerVoluntarySettlementSchema = Type.Object({
	slug: Type.Literal("passenger_voluntary_settlement", {
		default: "passenger_voluntary_settlement",
	}),
	...PremiumConsiderationSchema.properties,
	limitEachOccurrenceCrew: Type.Number({ default: 100_000 }),
	limitEachOccurrencePassenger: Type.Number({ default: 100_000 }),
	hasNonOwnedCoverage: Type.Boolean({ default: false }),
	nonOwnedLimitEachOccurrenceCrew: Type.Union([Type.Null(), Type.Number()], { default: 100_000 }),
	nonOwnedLimitEachOccurrencePassenger: Type.Union([Type.Null(), Type.Number()], { default: 100_000 }),
	totalCombinedLimit: Type.Number({ default: 0 }),
	maxWeeklyLimit: Type.Number({ default: 1250 }),
	maxNumberOfWeeks: Type.Number({ default: 52 }),
});

export const ManagedAircraftExtendedPdEndorsementSchema = Type.Object({
	slug: Type.Literal("managed_aircraft_extended_pd", { default: "managed_aircraft_extended_pd" }),
	...PremiumConsiderationSchema.properties,
	exclusion13c: Type.Boolean({ default: false }),
	exclusion13d: Type.Boolean({ default: false }),
	limitAmount: Type.Number({ default: 0 }),
	limitPercent: Type.Number({ default: 0 }),
	deductible: Type.Number({ default: 0 }),
});

export const NonOwnedAircraftPhysicalDamageEndorsementSchema = Type.Object({
	slug: Type.Literal("non_owned_aircraft_physical_damage", {
		default: "non_owned_aircraft_physical_damage",
	}),
	...PremiumConsiderationSchema.properties,
	coverageOption: Type.Union(
		[Type.Literal("a"), Type.Literal("b"), Type.Literal("c")],
		{ default: "a" },
	),
	limitEachOccurrence: Type.Number({ default: 0 }),
	deductibleInMotion: Type.Number({ default: 0 }),
	deductibleNotInMotion: Type.Number({ default: 0 }),
	optionAMaxSeats: Type.Union([Type.Null(), Type.Number()], { default: 15 }),
	optionBMaxSeats: Type.Union([Type.Null(), Type.Number()], { default: 15 }),
	optionAFixedOrRotorWing: Type.Union(
		[Type.Null(), Type.Literal("fixed-wing"), Type.Literal("rotor-wing")],
		{ default: "fixed-wing" },
	),
	optionBFixedOrRotorWing: Type.Union(
		[Type.Null(), Type.Literal("fixed-wing"), Type.Literal("rotor-wing")],
		{ default: "fixed-wing" },
	),
	optionAAirworthinessCert: Type.Union([Type.Null(), Type.String()], {
		default: "Standard",
	}),
	optionBAirworthinessCert: Type.Union([Type.Null(), Type.String()], {
		default: "Standard",
	}),
	optionCText: Type.Union([Type.Null(), Type.String()], { default: null }),
	pilotWarrantySupplement: Type.Union([Type.Null(), Type.String()], {
		default: null,
	}),
});

export const ManagedAircraftBroadExtendedPdEndorsementSchema = Type.Object({
	slug: Type.Literal("managed_aircraft_broad_extended_pd", { default: "managed_aircraft_broad_extended_pd" }),
	...PremiumConsiderationSchema.properties,
	exclusion13c: Type.Boolean({ default: false }),
	exclusion13d: Type.Boolean({ default: false }),
	limitAmount: Type.Number({ default: 0 }),
	deductible: Type.Number({ default: 0 }),
});

export const NonOwnedAircraftLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("non_owned_aircraft_liability", {
		default: "non_owned_aircraft_liability",
	}),
	...PremiumConsiderationSchema.properties,
	coverageOption: Type.Union(
		[Type.Literal("a"), Type.Literal("b"), Type.Literal("c")],
		{ default: "a" },
	),
	limitEachOccurrence: Type.Number({ default: 0 }),
	deductibleInMotion: Type.Number({ default: 0 }),
	deductibleNotInMotion: Type.Number({ default: 0 }),
	optionAMaxSeats: Type.Union([Type.Null(), Type.Number()], { default: 15 }),
	optionBMaxSeats: Type.Union([Type.Null(), Type.Number()], { default: 15 }),
	optionAFixedOrRotorWing: Type.Union(
		[Type.Null(), Type.Literal("fixed-wing"), Type.Literal("rotor-wing")],
		{ default: "fixed-wing" },
	),
	optionBFixedOrRotorWing: Type.Union(
		[Type.Null(), Type.Literal("fixed-wing"), Type.Literal("rotor-wing")],
		{ default: "fixed-wing" },
	),
	optionAAirworthinessCert: Type.Union([Type.Null(), Type.String()], {
		default: "Standard",
	}),
	optionBAirworthinessCert: Type.Union([Type.Null(), Type.String()], {
		default: "Standard",
	}),
	optionCText: Type.Union([Type.Null(), Type.String()], { default: null }),
	coverageDLimitEachOccurrence: Type.Union([Type.Null(), Type.Number()], {
		default: null,
	}),
	coverageDLimitPerPassenger: Type.Union([Type.Null(), Type.Number()], {
		default: null,
	}),
	coverageELimitPerPerson: Type.Union([Type.Null(), Type.Number()], {
		default: null,
	}),
	coverageELimitEachOccurrence: Type.Union([Type.Null(), Type.Number()], {
		default: null,
	}),
});

export const StudentAndRenterPilotLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("student_and_renter_pilot_liability", {
		default: "student_and_renter_pilot_liability",
	}),
	...PremiumConsiderationSchema.properties,
	limitsOfLiability: Type.Number({ default: 0 }),
});

export const IncidentalMedicalMalpracticeEndorsementSchema = Type.Object({
	slug: Type.Literal("incidental_medical_malpractice", {
		default: "incidental_medical_malpractice",
	}),
	...PremiumConsiderationSchema.properties,
	liabilityLimitEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
});

export const IncidentalMedicalMalpracticeAvEndorsementSchema = Type.Object({
	slug: Type.Literal("incidental_medical_malpractice_av", {
		default: "incidental_medical_malpractice_av",
	}),
	...PremiumConsiderationSchema.properties,
	limitEachOccurrence: Type.Number({ default: 0 }),
	limitAnnualAggregate: Type.Number({ default: 0 }),
});

export const MechanicsToolsEndorsementSchema = Type.Object({
	slug: Type.Literal("mechanics_tools", { default: "mechanics_tools" }),
	...PremiumConsiderationSchema.properties,
	limitEachEmployee: Type.Number({ default: 10_000 }),
	limitPerOccurrence: Type.Number({ default: 10_000 }),
	deductible: Type.Union([Type.Null(), Type.Number()], { default: 1000 }),
});

export const PortableEquipmentEndorsementSchema = Type.Object({
	slug: Type.Literal("portable_equipment", { default: "portable_equipment" }),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	portableAvionicsLimit: Type.Number({ default: 0 }),
	safetyEquipmentLimit: Type.Number({ default: 0 }),
});

export const KnowledgeOfOccurrenceSchema = Type.Object({
	slug: Type.Literal("knowledge_of_occurrence", {
		default: "knowledge_of_occurrence",
	}),
	...PremiumConsiderationSchema.properties,
});

export const LayupEndorsementSchema = Type.Object({
	slug: Type.Literal("layup", { default: "layup" }),
	...PremiumConsiderationSchema.properties,
	returnPercentage: Type.Number({ default: 50 }),
	minConsecutiveDays: Type.Number({ default: 45 }),
});

export const LayupReturnEndorsementSchema = Type.Object({
	slug: Type.Literal("layup_return", { default: "layup_return" }),
	...PremiumConsiderationSchema.properties,
	aircraftSchedule: Type.Array(
		Type.Object({
			tailNumber: Type.String({ default: "" }),
			year: Type.String({ default: "" }),
			makeModel: Type.String({ default: "" }),
			beginningDate: Type.String({ default: "" }),
			endingDate: Type.String({ default: "" }),
		}),
		{ default: [] },
	),
});

export const LayupReturnSingleEndorsementSchema = Type.Object({
	slug: Type.Literal("layup_return_single", { default: "layup_return_single" }),
	...PremiumConsiderationSchema.properties,
	allAircraft: Type.Boolean({ default: true }),
	aircraftSchedule: Type.Array(
		Type.Object({
			tailNumber: Type.String({ default: "" }),
			year: Type.String({ default: "" }),
			makeModel: Type.String({ default: "" }),
		}),
		{ default: [] },
	),
	beginningDate: Type.String({ default: "" }),
	endingDate: Type.String({ default: "" }),
});

export const LienholdersInterestEndorsementSchema = Type.Object({
	slug: Type.Literal("lienholders_interest", {
		default: "lienholders_interest",
	}),
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	lienholder: Type.String({ default: "" }),
});

export const LienholdersInterestMultipleEndorsementSchema = Type.Object({
	slug: Type.Literal("lienholders_interest_multiple", {
		default: "lienholders_interest_multiple",
	}),
	...PremiumConsiderationSchema.properties,
	lienholder: Type.String({ default: "" }),
	lienSchedule: Type.Array(
		Type.Object({
			tailNumber: Type.String({ default: "" }),
			amountOfLien: Type.Union([Type.Null(), Type.Number()], { default: null }),
		}),
		{ default: [] },
	),
});

export const LienholdersInterestBlanketFleetEndorsementSchema = Type.Object({
	slug: Type.Literal("lienholders_interest_blanket_fleet", {
		default: "lienholders_interest_blanket_fleet",
	}),
	...PremiumConsiderationSchema.properties,
});

export const LossPayableEndorsementSchema = Type.Object({
	slug: Type.Literal("loss_payable", { default: "loss_payable" }),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	lossPayee: Type.String({ default: "" }),
});

export const ConstructiveTotalLossEndorsementSchema = Type.Object({
	slug: Type.Literal("constructive_total_loss", { default: "constructive_total_loss" }),
	...PremiumConsiderationSchema.properties,
	allScheduledAircraft: Type.Boolean({ default: true }),
	selectedTailNumbers: Type.Optional(Type.Array(Type.String(), { default: [] })),
	costToRepairPercent: Type.String({ default: "" }),
});

/** Text for `{aircraftTail}` in the 0005 DOCX and UI labels (legacy `aircraftTail` JSON still supported). */
export function constructiveTotalLossScheduleText (data: {
	allScheduledAircraft?: boolean;
	selectedTailNumbers?: string[];
	aircraftTail?: string;
}): string {
	if (data.selectedTailNumbers?.length) {
		return data.selectedTailNumbers.join(", ");
	}
	if (data.allScheduledAircraft !== false) {
		return "all scheduled aircraft";
	}
	const legacy = data.aircraftTail?.trim();
	return legacy ?? "";
}

export const TotalLossOnlyEndorsementSchema = Type.Object({
	slug: Type.Literal("total_loss_only", { default: "total_loss_only" }),
	...PremiumConsiderationSchema.properties,
});

export const DisappearanceAmendmentOfDefinitionEndorsementSchema = Type.Object({
	slug: Type.Literal("disappearance_amendment_of_definition", {
		default: "disappearance_amendment_of_definition",
	}),
	...PremiumConsiderationSchema.properties,
	disappearanceTimeframeDays: Type.Number({ default: 0 }),
});

/** DOCX `{disappearanceTimeframe}`; supports legacy string field on stored JSON. */
export function disappearanceTimeframeText (data: {
	disappearanceTimeframeDays?: number;
	disappearanceTimeframe?: string;
}): string {
	if (typeof data.disappearanceTimeframeDays === "number" && data.disappearanceTimeframeDays > 0) {
		return formatWrittenDaysParenthetical(data.disappearanceTimeframeDays);
	}
	return data.disappearanceTimeframe?.trim() ?? "";
}

export const OverheadSupervisionEndorsementSchema = Type.Object({
	slug: Type.Literal("overhead_supervision", {
		default: "overhead_supervision",
	}),
	...PremiumConsiderationSchema.properties,
});

export const OverheadSupervisionSpecifiedRateEndorsementSchema = Type.Object({
	slug: Type.Literal("overhead_supervision_specified_rate", {
		default: "overhead_supervision_specified_rate",
	}),
	...PremiumConsiderationSchema.properties,
	hourlyRate: Type.Number({ default: 0 }),
	materialSurplusPercent: Type.Number({ default: 0 }),
});

export const OwnerLessorEndorsementSchema = Type.Object({
	slug: Type.Literal("owner_lessor", { default: "owner_lessor" }),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	aircraftOwner: Type.String({ default: "" }),
});

export const OwnerLessorEndorsementFormSchema = Type.Object({
	slug: Type.Literal("owner_lessor_endorsement", { default: "owner_lessor_endorsement" }),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	aircraftOwner: Type.String({ default: "" }),
});

export const BroadOwnerLessorEndorsementSchema = Type.Object({
	slug: Type.Literal("broad_owner_lessor", { default: "broad_owner_lessor" }),
	...PremiumConsiderationSchema.properties,
});

export const AdditionalInsuredOwnersLessorsEndorsementSchema = Type.Object({
	slug: Type.Literal("additional_insured_owners_lessors", {
		default: "additional_insured_owners_lessors",
	}),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	aircraftOwner: Type.String({ default: "" }),
	liabilityLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
	liabilitySubLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const AdditionalInsuredOwnersLessorsBroadEndorsementSchema = Type.Object({
	slug: Type.Literal("additional_insured_owners_lessors_broad", {
		default: "additional_insured_owners_lessors_broad",
	}),
	...PremiumConsiderationSchema.properties,
	liabilityLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
	liabilitySubLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const COMPONENT_PARTS_SCHEDULE2_KEYS = [
	"schedule2A",
	"schedule2B",
	"schedule2C",
	"schedule2D",
	"schedule2E",
	"schedule2F",
	"schedule2G",
	"schedule2H",
	"schedule2I",
	"schedule2J",
	"schedule2K",
	"schedule2L",
] as const;

export type ComponentPartsSchedule2Key = typeof COMPONENT_PARTS_SCHEDULE2_KEYS[number];

export const COMPONENT_PARTS_SCHEDULE_ROWS: ReadonlyArray<{
	key: ComponentPartsSchedule2Key;
	label: string;
	schedule1Percent: number;
}> = [
	{
		key: "schedule2A", label: "Ailerons/spoilers", schedule1Percent: 5, 
	},
	{
		key: "schedule2B", label: "Elevator(s)", schedule1Percent: 5, 
	},
	{
		key: "schedule2C", label: "Engine(s)", schedule1Percent: 25, 
	},
	{
		key: "schedule2D", label: "Flaps/Slats", schedule1Percent: 5, 
	},
	{
		key: "schedule2E", label: "Fuselage", schedule1Percent: 20, 
	},
	{
		key: "schedule2F", label: "Horizontal Stabilizer(s)/Canard(s)", schedule1Percent: 5, 
	},
	{
		key: "schedule2G", label: "Instruments/Avionics/Radios", schedule1Percent: 25, 
	},
	{
		key: "schedule2H", label: "Landing Gear", schedule1Percent: 20, 
	},
	{
		key: "schedule2I", label: "Rudder(s)", schedule1Percent: 5, 
	},
	{
		key: "schedule2J", label: "Propeller(s)", schedule1Percent: 10, 
	},
	{
		key: "schedule2K", label: "Vertical Stabilizer(s)", schedule1Percent: 5, 
	},
	{
		key: "schedule2L", label: "All else", schedule1Percent: 10, 
	},
];

export const ComponentPartsScheduleEndorsementSchema = Type.Object({
	slug: Type.Literal("component_parts_schedule", { default: "component_parts_schedule" }),
	...PremiumConsiderationSchema.properties,
	useSchedule2: Type.Boolean({ default: false }),
	schedule2A: Type.Number({ default: 5 }),
	schedule2B: Type.Number({ default: 5 }),
	schedule2C: Type.Number({ default: 25 }),
	schedule2D: Type.Number({ default: 5 }),
	schedule2E: Type.Number({ default: 20 }),
	schedule2F: Type.Number({ default: 5 }),
	schedule2G: Type.Number({ default: 25 }),
	schedule2H: Type.Number({ default: 20 }),
	schedule2I: Type.Number({ default: 5 }),
	schedule2J: Type.Number({ default: 10 }),
	schedule2K: Type.Number({ default: 5 }),
	schedule2L: Type.Number({ default: 10 }),
});

export const FinancedPremiumEndorsementSchema = Type.Object({
	slug: Type.Literal("financed_premium", { default: "financed_premium" }),
	lender: Type.String({ default: "" }),
});

export const PremiumInstallmentEndorsementSchema = Type.Object({
	slug: Type.Literal("premium_installment", { default: "premium_installment" }),
	...PremiumConsiderationSchema.properties,
	appliesTo: Type.String({ default: "" }),
	installments: Type.Array(
		Type.Object({
			dueDate: Type.String({ default: "" }),
			amount: Type.Union([Type.Null(), Type.Number()], { default: null }),
		}),
		{ default: [] },
	),
});

export const AdditionalInsuredPilotServiceProviderEndorsementSchema = Type.Object({
	slug: Type.Literal("additional_insured_pilot_service_provider", {
		default: "additional_insured_pilot_service_provider",
	}),
	...PremiumConsiderationSchema.properties,
	serviceProvider: Type.String({ default: "" }),
});

export const MexicanLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("mexican_liability", {
		default: "mexican_liability",
	}),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	policyNumbers: Type.String({ default: "" }),
});

export const AircraftRelocationExpenseEndorsementSchema = Type.Object({
	slug: Type.Literal("aircraft_relocation_expense", {
		default: "aircraft_relocation_expense",
	}),
	...PremiumConsiderationSchema.properties,
	perAircraftLimit: Type.Number({ default: 5_000 }),
});

export const UnearnedPremiumEndorsementSchema = Type.Object({
	slug: Type.Literal("unearned_premium", { default: "unearned_premium" }),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
});

export const PolicyTerritoryEndorsementSchema = Type.Object({
	slug: Type.Literal("policy_territory", { default: "policy_territory" }),
	...PremiumConsiderationSchema.properties,
	...OptionalAutoExpirationProperty,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
});

export const PolicyPeriodRevisionEndorsementSchema = Type.Object({
	slug: Type.Literal("policy_period_revision", { default: "policy_period_revision" }),
	...PremiumConsiderationSchema.properties,
	fromDate: Type.String({ default: "" }),
	toDate: Type.String({ default: "" }),
	/** Stored on application so removing the revision can restore the prior term. */
	previousFromDate: Type.Optional(Type.String()),
	previousToDate: Type.Optional(Type.String()),
});

export const RenewalEndorsementSchema = Type.Object({
	slug: Type.Literal("renewal_endorsement", { default: "renewal_endorsement" }),
	...PremiumConsiderationSchema.properties,
	fromDate: Type.String({ default: "" }),
	toDate: Type.String({ default: "" }),
	renewalPolicyNumber: Type.String({ default: "" }),
	renewalPremium: Type.Union([Type.Number(), Type.Null()], { default: null }),
	taxesSurcharges: Type.Union([Type.Number(), Type.Null()], { default: null }),
	mexicanInsurancePremium: Type.Union([Type.Number(), Type.Null()], { default: null }),
});

export const AirworthinessCertificateEndorsementSchema = Type.Object({
	slug: Type.Literal("airworthiness_certificate", { default: "airworthiness_certificate" }),
	...PremiumConsiderationSchema.properties,
	replaces: Type.Boolean({ default: true }),
	deletes: Type.Boolean({ default: false }),
});


export const WaiverOfSubrogationAvEndorsementSchema = Type.Object({
	slug: Type.Literal("waiver_of_subrogation_av", { default: "waiver_of_subrogation_av" }),
	...PremiumConsiderationSchema.properties,
	partyName: Type.String({ default: "" }),
	attention: Type.Optional(Type.Union([Type.String(), Type.Null()])),
	partyAddressLineOne: Type.String({ default: "" }),
	partyAddressLineTwo: Type.String({ default: "" }),
	excessLiability: Type.Boolean({ default: false }),
	nonOperational: Type.Boolean({ default: false }),
	flightInstruction: Type.Boolean({ default: false }),
	flightInstructionPilots: Type.String({ default: "" }),
	hangarkeepers: Type.Boolean({ default: false }),
	workmanshipExclusion: Type.Boolean({ default: false }),
});

export const AdditionalInsuredEndorsementSchema = Type.Object({
	slug: Type.Literal("additional_insured", { default: "additional_insured" }),
	...PremiumConsiderationSchema.properties,
	additionalInsuredName: Type.String({ default: "" }),
	additionalInsuredAddressLineOne: Type.String({ default: "" }),
	additionalInsuredAddressLineTwo: Type.String({ default: "" }),
	attention: Type.Optional(Type.Union([Type.String(), Type.Null()])),
	maxLiability: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
	maxWarLiability: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
	asAdditionalInsured: Type.Boolean({ default: false }),
	dryLease: Type.Boolean({ default: false }),
	liabilityCoverages: Type.Boolean({ default: false }),
	liabilityCoveragesOpsOnly: Type.Boolean({ default: false }),
	opsOnly: Type.Boolean({ default: false }),
	useOfPremises: Type.Boolean({ default: false }),
	workPerformed: Type.Boolean({ default: false }),
	noLiabilityPremium: Type.Boolean({ default: false }),
	isPrimary: Type.Boolean({ default: false }),
	noLiabilityIncrease: Type.Boolean({ default: false }),
	thirtyDayNotice: Type.Boolean({ default: false }),
	waivePhysicalDamage: Type.Boolean({ default: false }),
	exclusionForDesignManufacture: Type.Boolean({ default: false }),
});

export const IncreasedLimitsDesignatedPersonsEndorsementSchema = Type.Object({
	slug: Type.Literal("increased_limits_designated_persons", { default: "increased_limits_designated_persons" }),
	...PremiumConsiderationSchema.properties,
	designatedPersonName: Type.String({ default: "" }),
	designatedPersonAddressLineOne: Type.String({ default: "" }),
	designatedPersonAddressLineTwo: Type.String({ default: "" }),
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	coverageAEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageAEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageBEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageCEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageCEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageDEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageDEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageEEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageEEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const WaiverOfSubrogationEndorsementSchema = Type.Object({
	slug: Type.Literal("waiver_of_subrogation", { default: "waiver_of_subrogation" }),
	...PremiumConsiderationSchema.properties,
	waiverPartyName: Type.String({ default: "" }),
	waiverPartyAddressLineOne: Type.String({ default: "" }),
	waiverPartyAddressLineTwo: Type.String({ default: "" }),
	tailNumbers: Type.Array(Type.String(), { default: [] }),
});

export const AutomaticAttachmentForNewlyAcquiredAircraftSchema = Type.Object({
	slug: Type.Literal("automatic_attachment_for_newly_acquired_aircraft", {
		default:
    "automatic_attachment_for_newly_acquired_aircraft", 
	}),
	...PremiumConsiderationSchema.properties,
	coverageOption: Type.Union(
		[Type.Literal("a"), Type.Literal("b"), Type.Literal("c")],
		{ default: "a" },
	),
	maxTotalSeats: Type.Number({ default: 10 }),
	maxAutoAttachHullLimit: Type.Number({ default: 10_000_000 }),
	deductibleNotInMotion: Type.Number({ default: 10_000 }),
	deductibleInMotion: Type.Number({ default: 10_000 }),
	cText: Type.Union([Type.Null(), Type.String()], { default: null }),
	excludedAircraft: Type.Array(Type.String(), { default: [] }),
});

export const PremisesMedicalCoverageEndorsementSchema = Type.Object({
	slug: Type.Literal("premises_medical_coverage", { default: "premises_medical_coverage" }),
	...PremiumConsiderationSchema.properties,
	limitPerPerson: Type.Number({ default: 0 }),
});

export const PremisesAmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("premises_amendment", { default: "premises_amendment" }),
	...PremiumConsiderationSchema.properties,
	/** Null applies the same limit as Coverages A, B and D on the Declarations. */
	limitPerOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const HostLiquorLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("host_liquor_liability", { default: "host_liquor_liability" }),
	...PremiumConsiderationSchema.properties,
	limitAggregate: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const BaggageAndPersonalEffectsEndorsementSchema = Type.Object({
	slug: Type.Literal("baggage_and_personal_effects", { default: "baggage_and_personal_effects" }),
	...PremiumConsiderationSchema.properties,
	limitAnyOnePassenger: Type.Number({ default: 0 }),
	deductibleEachLossEachPax: Type.Number({ default: 0 }),
});

export const HangarAndContentsEndorsementSchema = Type.Object({
	slug: Type.Literal("hangar_and_contents", { default: "hangar_and_contents" }),
	...PremiumConsiderationSchema.properties,
	appliesTo: Type.String({ default: "" }),
	limitEachOccurrence: Type.Number({ default: 0 }),
});

export const TripInterruptionExpenseEndorsementSchema = Type.Object({
	slug: Type.Literal("trip_interruption_expense", { default: "trip_interruption_expense" }),
	...PremiumConsiderationSchema.properties,
	limitEachPassengerEachOccurrence: Type.Number({ default: 0 }),
});

export const ExtraExpenseForSubAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("extra_expense_for_sub_aircraft", { default: "extra_expense_for_sub_aircraft" }),
	...PremiumConsiderationSchema.properties,
	liabilityLimitEachDayEachAircraft: Type.Number({ default: 0 }),
	liabilityLimitEachOccurrenceEachAircraft: Type.Number({ default: 0 }),
	waitPeriod: Type.Number({
		default: 5, minimum: 0, maximum: 5, 
	}),
	daysFromOccurrence: Type.Number({ default: 65, minimum: 65 }),
});

export const ConsequentialLossEndorsementSchema = Type.Object({
	slug: Type.Literal("consequential_loss", { default: "consequential_loss" }),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	limitAggregate: Type.Number({ default: 0 }),
});

export const ConsequentialLossCoverageEndorsementSchema = Type.Object({
	slug: Type.Literal("consequential_loss_coverage", { default: "consequential_loss_coverage" }),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	limitAggregate: Type.Number({ default: 0 }),
});

export const ExtraExpenseForTempPartsEndorsementSchema = Type.Object({
	slug: Type.Literal("extra_expense_for_temp_parts", { default: "extra_expense_for_temp_parts" }),
	...PremiumConsiderationSchema.properties,
	limitEachLoss: Type.Number({ default: 50_000 }),
	repairDays: Type.Optional(Type.Number({ default: 5, minimum: 1 })),
	excludedAircraft: Type.Array(Type.String(), { default: [] }),
});

export const MobileEquipmentLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("mobile_equipment_liability", { default: "mobile_equipment_liability" }),
	...PremiumConsiderationSchema.properties,
	limitEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
});

export const HangarkeepersLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("hangarkeepers_liability", { default: "hangarkeepers_liability" }),
	...PremiumConsiderationSchema.properties,
	limitEachAircraft: Type.Number({ default: 0 }),
	limitEachOccurrence: Type.Number({ default: 0 }),
	deductible: Type.Union([Type.Null(), Type.Number()], { default: 1_0000 }),
});

export const ProductsLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("products_liability", { default: "products_liability" }),
	...PremiumConsiderationSchema.properties,
	liabilityLimit: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
});

export const SparePartsEndorsementSchema = Type.Object({
	slug: Type.Literal("spare_parts", { default: "spare_parts" }),
	...PremiumConsiderationSchema.properties,
	liabilityLimitAnyOneOccurrence: Type.Number({ default: 0 }),
	deductible: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
});

export const SparePartsScheduledEndorsementSchema = Type.Object({
	slug: Type.Literal("spare_parts_scheduled", { default: "spare_parts_scheduled" }),
	...PremiumConsiderationSchema.properties,
	liabilityLimitAnyOneOccurrence: Type.Number({ default: 0 }),
	deductible: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
	sparePartsSchedule: Type.Array(
		Type.Object({
			description: Type.String({ default: "" }),
			insuredValue: Type.Number({ default: 0 }),
		}),
		{ default: [] },
	),
	lossPayee: Type.String({ default: "" }),
	lossPayeeMailingAddress: Type.String({ default: "" }),
});

export const EmergencyLandingEndorsementSchema = Type.Object({
	slug: Type.Literal("emergency_landing", { default: "emergency_landing" }),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	percentageOfHullValue: Type.Number({ default: 0 }),
});

export const SearchAndRescueEndorsementSchema = Type.Object({
	slug: Type.Literal("search_and_rescue", { default: "search_and_rescue" }),
	...PremiumConsiderationSchema.properties,
	limitAnyOneOccurrence: Type.Number({ default: 0 }),
	tailNumbers: Type.Array(Type.String(), { default: [] }),
});

export const RunwayFoamingEndorsementSchema = Type.Object({
	slug: Type.Literal("runway_foaming", { default: "runway_foaming" }),
	...PremiumConsiderationSchema.properties,
	liabilityLimit: Type.Number({ default: 0 }),
	tailNumbers: Type.Array(Type.String(), { default: [] }),
});

export const SupplementaryPaymentsBailBondEndorsementSchema = Type.Object({
	slug: Type.Literal("supplementary_payments_bail_bond", { default: "supplementary_payments_bail_bond" }),
	...PremiumConsiderationSchema.properties,
	bailBondLimit: Type.Number({ default: 0 }),
	lossOfEarningsLimitPerDay: Type.Number({ default: 0 }),
});

export const NoClaimsBonusEndorsementSchema = Type.Object({
	slug: Type.Literal("no_claims_bonus", { default: "no_claims_bonus" }),
	...PremiumConsiderationSchema.properties,
	percentage: Type.Number({
		default: 5, exclusiveMinimum: 0,
	}),
});

export const FullPremiumInEventOfClaimEndorsementSchema = Type.Object({
	slug: Type.Literal("full_premium_in_event_of_claim", { default: "full_premium_in_event_of_claim" }),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	fullAnnualPremium: Type.Number({ default: 0 }),
});

export const ProportionalShareEndorsementSchema = Type.Object({
	slug: Type.Literal("proportional_share", { default: "proportional_share" }),
	lineSize: Type.Number({
		default: 100, minimum: 0, maximum: 100,
	}),
});

export const remainingProportionalShare = (lineSize: number) => Number((100 - lineSize).toFixed(2));

export const MINIMAL_LOSS_CREDIT_COVERAGE_BASES = [
	{
		value: "physical_damage_excluding_war",
		label: "Physical Damage excluding War and Terrorism (TRIA)",
		variable: "pdExcludingWarSelected",
	},
	{
		value: "physical_damage_including_war",
		label: "Physical Damage including War and Terrorism (TRIA)",
		variable: "pdIncludingWarSelected",
	},
	{
		value: "physical_damage_liability_excluding_war",
		label: "Physical Damage and Liability excluding War and Terrorism (TRIA)",
		variable: "pdLiabilityExcludingWarSelected",
	},
	{
		value: "physical_damage_liability_including_war",
		label: "Physical Damage and Liability including War and Terrorism (TRIA)",
		variable: "pdLiabilityIncludingWarSelected",
	},
	{
		value: "total_policy_premium_excluding_war",
		label: "Total Policy Premium excluding War and Terrorism (TRIA)",
		variable: "totalPremiumExcludingWarSelected",
	},
	{
		value: "total_policy_premium_including_war",
		label: "Total Policy Premium including War and Terrorism (TRIA)",
		variable: "totalPremiumIncludingWarSelected",
	},
] as const;

export type MinimalLossCreditCoverageBasis =
	typeof MINIMAL_LOSS_CREDIT_COVERAGE_BASES[number]["value"];

/** Endorsements written against form 0102 05 25 predate the selection and always meant this basis. */
export const MINIMAL_LOSS_CREDIT_DEFAULT_COVERAGE_BASIS: MinimalLossCreditCoverageBasis =
	"physical_damage_excluding_war";

export const MinimalLossCreditEndorsementSchema = Type.Object({
	slug: Type.Literal("minimal_loss_credit", { default: "minimal_loss_credit" }),
	...PremiumConsiderationSchema.properties,
	creditPercentage: Type.Number({
		default: 15, minimum: 0, maximum: 100,
	}),
	earnedPremiumPercentage: Type.Number({
		default: 70, minimum: 0, maximum: 100,
	}),
	coverageBasis: Type.Optional(Type.Union(
		MINIMAL_LOSS_CREDIT_COVERAGE_BASES.map(({ value }) => Type.Literal(value)),
		{ default: MINIMAL_LOSS_CREDIT_DEFAULT_COVERAGE_BASIS },
	)),
});

export const DepreciatedValueCoverageEndorsementSchema = Type.Object({
	slug: Type.Literal("depreciated_value_coverage", {
		default: "depreciated_value_coverage",
	}),
	...PremiumConsiderationSchema.properties,
	aircraftSchedule: Type.Array(
		Type.Object({
			tailNumber: Type.String(),
			makeModel: Type.String(),
			maxIndemnityPercent: Type.Number({
				default: 5, minimum: 0, maximum: 100, 
			}),
			retentionPercent: Type.Number({
				default: 5, minimum: 0, maximum: 100, 
			}),
		}),
		{ default: [] },
	),
});

export const CrewTrainingEndorsementSchema = Type.Object({
	slug: Type.Literal("crew_training", { default: "crew_training" }),
	...PremiumConsiderationSchema.properties,
	schedule: Type.String({ default: "" }),
});

export const LossControlSafetyEndorsementSchema = Type.Object({
	slug: Type.Literal("loss_control_safety", { default: "loss_control_safety" }),
	...PremiumConsiderationSchema.properties,
	bursaryAmount: Type.Number({ default: 0 }),
	description: Type.Optional(Type.String()),
});

export const InvalidationClauseEndorsementSchema = Type.Object({
	slug: Type.Literal("invalidation_clause", { default: "invalidation_clause" }),
	...PremiumConsiderationSchema.properties,
	agreementName: Type.String({ default: "" }),
	schedule: Type.String({ default: "" }),
});

export const CancellationClauseAmmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("cancellation_clause_ammendment", { default: "cancellation_clause_ammendment" }),
	...PremiumConsiderationSchema.properties,
});

export const LiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("liability", { default: "liability" }),
	...PremiumConsiderationSchema.properties,
	...OptionalAutoExpirationProperty,
	conditions: Type.String({ default: "" }),
	coverageAEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageAEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageBEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageCEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageCEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageDEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageDEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageEEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageEEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	euCertificateSchedule: Type.Optional(Type.Array(
		Type.Object({
			tailNumber: Type.String(),
			makeModel: Type.String({ default: "" }),
			mtowKg: Type.Union([Type.Null(), Type.Number()], { default: null }),
			passengerSeats: Type.Union([Type.Null(), Type.Number()], { default: null }),
			cargoWeightKg: Type.Union([Type.Null(), Type.Number()], { default: null }),
		}),
		{ default: [] },
	)),
});

export const LiabilityPerPersonLimitEndorsementSchema = Type.Object({
	slug: Type.Literal("liability_per_person_limit", { default: "liability_per_person_limit" }),
	...PremiumConsiderationSchema.properties,
	coverageDEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageDEachPerson: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const RestrictedPassengerOccurrenceLimitEndorsementSchema = Type.Object({
	slug: Type.Literal("restricted_passenger_occurrence_limit", { default: "restricted_passenger_occurrence_limit" }),
	...PremiumConsiderationSchema.properties,
	coverageDEachOccurrence: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageDEachPassenger: Type.Union([Type.Null(), Type.Number()], { default: null }),
	coverageDEachOccurrenceAllPassengers: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const ReferralLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("referral_liability", { default: "referral_liability" }),
	...PremiumConsiderationSchema.properties,
});


export const MultiYearPolicyEndorsementSchema = Type.Object({
	slug: Type.Literal("multi_year_policy", { default: "multi_year_policy" }),
	...PremiumConsiderationSchema.properties,
	schedule: Type.Array(Type.Object({
		year: Type.Number(),
		premium: Type.Number(),
	}), { default: [] }),
});

export const MultiYearPolicyAnnualEndorsementSchema = Type.Object({
	slug: Type.Literal("multi_year_policy_annual", { default: "multi_year_policy_annual" }),
	...PremiumConsiderationSchema.properties,
	schedule: MultiYearPolicyEndorsementSchema.properties.schedule,
});

export const ExcludedUsesEndorsementSchema = Type.Object({
	slug: Type.Literal("excluded_uses", { default: "excluded_uses" }),
	...PremiumConsiderationSchema.properties,
	excludesApplicationOfChemicals: Type.Boolean({ default: false, description: "Application of Chemicals" }),
	excludesAerialMapping: Type.Boolean({ default: false, description: "Aerial Mapping" }),
	excludesAerialSurvey: Type.Boolean({ default: false, description: "Aerial Survey" }),
	excludesAirAmbulance: Type.Boolean({ default: false, description: "Air Ambulance" }),
	excludesAnimalHerding: Type.Boolean({ default: false, description: "Animal Herding" }),
	excludesBannerTowing: Type.Boolean({ default: false, description: "Banner Towing" }),
	excludesCloudSeedingWeatherMod: Type.Boolean({ default: false, description: "Cloud Seeding/Weather Modification" }),
	excludesControlledBurning: Type.Boolean({ default: false, description: "Controlled Burning"  }),
	excludesFireSuppression: Type.Boolean({ default: false, description: "Fire Suppression" }),
	excludesFishSpotting: Type.Boolean({ default: false, description: "Fish Spotting" }),
	excludesFogDispersal: Type.Boolean({ default: false, description: "Fog Dispersal" }),
	excludesFlightInstructionToOthers: Type.Boolean({ default: false, description: "Flight Instruction to Others" }),
	excludesLawEnforcement: Type.Boolean({ default: false, description: "Law Enforcement" }),
	excludesVideo: Type.Boolean({ default: false, description: "Movie/Video/Film Flying" }),
	excludesParachuting: Type.Boolean({ default: false, description: "Parachuting/Skydiving" }),
	excludesPhotography: Type.Boolean({ default: false, description: "Photography" }),
	excludesHeliskiing: Type.Boolean({ default: false, description: "Heli-skiing" }),
	excludesPipelinePatrol: Type.Boolean({ default: false, description: "Pipeline Patrol" }),
	excludesPowerlinePatrol: Type.Boolean({ default: false, description: "Powerline Patrol" }),
	excludesPowerlineMaintenance: Type.Boolean({ default: false, description: "Powerline Stringing/Maintenance" }),
	excludesPredatorControl: Type.Boolean({ default: false, description: "Predator Control" }),
	excludesSearchAndRescue: Type.Boolean({ default: false, description: "Search and Rescue" }),
	excludesSlungLoad: Type.Boolean({ default: false, description: "Slung Load/Long Line Use" }),
	excludesTrafficWatch: Type.Boolean({ default: false, description: "Traffic watch" }),
	excludesTVNews: Type.Boolean({ default: false, description: "TV/News/Electronic or Capturing" }),
	excludesWildlifeTracking: Type.Boolean({ default: false, description: "Wildlife Tracking or Capturing" }),
	excludesOther: Type.Boolean({ default: false, description: "Other" }),
	otherExclusion: Type.Union([Type.Null(), Type.String()], { default: null }),
});

export const RentalToNamedPilotsEndorsementSchema = Type.Object({
	slug: Type.Literal("rental_to_named_pilots", { default: "rental_to_named_pilots" }),
	...PremiumConsiderationSchema.properties,
	waiveRecoveryAgainstNamedPilots: Type.Boolean({ default: false }),
	insuredParagraph3NotApplicable: Type.Boolean({ default: false }),
});

export const IndependentPilotEndorsementSchema = Type.Object({
	slug: Type.Literal("independent_pilot", { default: "independent_pilot" }),
	...PremiumConsiderationSchema.properties,
});

export const ExcludedPilotsEndorsementSchema = Type.Object({
	slug: Type.Literal("excluded_pilots", { default: "excluded_pilots" }),
	...PremiumConsiderationSchema.properties,
	excludedPilots: Type.String({ default: "" }),
});

export const ExcludedAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("excluded_aircraft", { default: "excluded_aircraft" }),
	...PremiumConsiderationSchema.properties,
	excludedAircraftSchedule: Type.Array(
		Type.Object({
			tailNumber: Type.String({ default: "" }),
			year: Type.String({ default: "" }),
			makeModel: Type.String({ default: "" }),
		}),
		{ default: [] },
	),
});

export const CustomEndorsementSchema = Type.Object({
	slug: Type.Literal("custom_endorsement", { default: "custom_endorsement" }),
	...PremiumConsiderationSchema.properties,
	title: Type.String({ default: "Custom endorsement title here" }),
	order: Type.Number({ default: 0 }),
	limits: Type.Array(Type.Object({
		qualifier: Type.String(),
		limit: Type.Union([Type.Null(), Type.Number()], { default: null }),
	}), { default: [] }),
});

/** Manual mid-term billing; invoice/credit memo in QuickBooks. Optional snapshot when linked to another endorsement. */
export const MidTermInvoiceEndorsementSchema = Type.Object({
	slug: Type.Literal("mid_term_invoice", { default: "mid_term_invoice" }),
	billingType: Type.Union([Type.Literal("invoice"), Type.Literal("refund")]),
	description: Type.String({ minLength: 1 }),
	/** Premium and fees as positive dollar amounts; broker commission as positive dollars to the broker. */
	premium: Type.Number(),
	/** Line-size-scaled premium invoiced for this endorsement (same as `premium` for manual mid-term invoices). */
	invoicedPremium: Type.Optional(Type.Number()),
	brokerCommission: Type.Number(),
	mexicanFee: Type.Number(),
	stateSurcharge: Type.Number(),
	policyFee: Type.Number(),
	/** Snapshot of the linked endorsement when provided; null when none was selected. */
	sourceEndorsement: Type.Union([
		Type.Null(),
		Type.Object({
			id: Type.String({ format: "uuid" }),
			slug: Type.String(),
			effectiveDate: Type.Union([Type.Null(), Type.String()]),
			documentEndorsementNumber: Type.Union([Type.Null(), Type.Integer()]),
			data: Type.Unknown(),
		}),
	]),
});

export const PolicyCancellationEndorsementSchema = Type.Object({
	slug: Type.Literal("policy_cancellation", { default: "policy_cancellation" }),
	refundedPremium: Type.Number(),
	refundedBrokerCommission: Type.Number(),
	stateSurcharge: Type.Optional(Type.Number()),
	mexicanCertificate: Type.Optional(Type.Number()),
	policyFee: Type.Optional(Type.Number()),
	reason: Type.Union([
		Type.Literal("non_payment"),
		Type.Literal("material_misrepresentation"),
		Type.Literal("underwriting_decision"),
		Type.Literal("re_marketed"),
		Type.Literal("sale_of_insured_asset"),
		Type.Literal("other"),
	]),
	description: Type.String(),
	cancelledByUserId: Type.String(),
	userEmail: Type.String(),
});

/** Pilot warranty text; inception row is created at bind, mid-term changes use deletion + new row. */
export const PilotWarrantyEndorsementSchema = Type.Object({
	slug: Type.Literal("pilot_warranty", { default: "pilot_warranty" }),
	...PremiumConsiderationSchema.properties,
	pilotWarrantyMarkdown: Type.String({ default: "" }),
});

/** UAS schedules store the serial number in `tailNumber` so tail-based lookups keep working. */
export const UasLiabilityRowSchema = Type.Object({
	tailNumber: Type.String({ default: "" }),
	liabilityLimit: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

const uasDeductibleType = () => Type.Optional(Type.Union([Type.Literal("fixed_amount"), Type.Literal("percentage")]));

export const UasPhysicalDamageRowSchema = Type.Object({
	tailNumber: Type.String({ default: "" }),
	makeModel: Type.String({ default: "" }),
	year: Type.String({ default: "" }),
	insuredValue: Type.Union([Type.Null(), Type.Number()], { default: null }),
	deductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
	deductibleType: uasDeductibleType(),
});

export const UasAircraftRowSchema = Type.Object({
	...UasPhysicalDamageRowSchema.properties,
	...UasLiabilityRowSchema.properties,
});

export const UasAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_aircraft", { default: "uas_aircraft" }),
	...PremiumConsiderationSchema.properties,
	aircraftSchedule: Type.Array(UasAircraftRowSchema, { default: [] }),
});

export const UasLiabilityAmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_liability_amendment", { default: "uas_liability_amendment" }),
	...PremiumConsiderationSchema.properties,
	aircraftSchedule: Type.Array(UasLiabilityRowSchema, { default: [] }),
});

const nullableAmount = () => Type.Union([Type.Null(), Type.Number()], { default: null });

export const UasLimitsAmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_limits_amendment", { default: "uas_limits_amendment" }),
	...PremiumConsiderationSchema.properties,
	liabilitySchedule: Type.Array(UasLiabilityRowSchema, { default: [] }),
	nonOwnedLiabilityLimit: nullableAmount(),
	newlyAcquiredLiabilityLimit: nullableAmount(),
	premisesLiabilityLimit: nullableAmount(),
	fireLegalLiabilityLimit: nullableAmount(),
	saleOfAircraftProductsLimit: nullableAmount(),
	contractualLiabilityLimit: nullableAmount(),
	personalInjuryLimit: nullableAmount(),
	aircraftSchedule: Type.Array(UasPhysicalDamageRowSchema, { default: [] }),
	unexpectedLandingLimit: nullableAmount(),
	unexpectedLandingAggregate: nullableAmount(),
	medicalPaymentsEachPerson: nullableAmount(),
	medicalPaymentsEachAccident: nullableAmount(),
});

export const UasPhysicalDamageEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_physical_damage", { default: "uas_physical_damage" }),
	...PremiumConsiderationSchema.properties,
	aircraftSchedule: Type.Array(UasPhysicalDamageRowSchema, { default: [] }),
});

export const UasNotInMotionPhysicalDamageEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_not_in_motion_physical_damage", { default: "uas_not_in_motion_physical_damage" }),
	...PremiumConsiderationSchema.properties,
	physicalDamageChange: Type.Union([Type.Literal("added"), Type.Literal("amended")], { default: "amended" }),
	aircraftSchedule: Type.Array(UasPhysicalDamageRowSchema, { default: [] }),
});

export const UasSpecialEquipmentEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_special_equipment", { default: "uas_special_equipment" }),
	...PremiumConsiderationSchema.properties,
	equipmentSchedule: Type.Array(
		Type.Object({
			description: Type.String({ default: "" }),
			insuredValue: Type.Union([Type.Null(), Type.Number()], { default: null }),
			deductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
		}),
		{ default: [] },
	),
});

export const SpecialEquipmentEndorsementSchema = Type.Object({
	slug: Type.Literal("special_equipment", { default: "special_equipment" }),
	...PremiumConsiderationSchema.properties,
	tailNumbers: Type.Array(Type.String(), { default: [] }),
	equipmentSchedule: Type.Array(
		Type.Object({
			description: Type.String({ default: "" }),
			insuredValue: Type.Union([Type.Null(), Type.Number()], { default: null }),
		}),
		{ default: [] },
	),
	deductible: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

export const PremiumAmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("premium_amendment", { default: "premium_amendment" }),
	...PremiumConsiderationSchema.properties,
	amendDeclarationsPremium: Type.Boolean({ default: true }),
	policyPremium: nullableAmount(),
	totalPremium: nullableAmount(),
	amendEndorsementPremium: Type.Boolean({ default: false }),
	amendedEndorsementNumber: Type.String({ default: "" }),
	/** Signed: negative is a return premium. */
	amendedEndorsementPremium: nullableAmount(),
});

export const UasPremiumAmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_premium_amendment", { default: "uas_premium_amendment" }),
	...PremiumConsiderationSchema.properties,
	amendDeclarationsPremium: Type.Boolean({ default: true }),
	policyPremium: nullableAmount(),
	taxes: nullableAmount(),
	feesAndSurcharges: nullableAmount(),
	amendEndorsementPremium: Type.Boolean({ default: false }),
	amendedEndorsementNumber: Type.String({ default: "" }),
	/** Signed: negative is a return premium. */
	amendedEndorsementPremium: nullableAmount(),
});

export const UasProductsLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_products_liability", { default: "uas_products_liability" }),
	...PremiumConsiderationSchema.properties,
	liabilityLimit: Type.Union([Type.Null(), Type.Number()], { default: 0 }),
});

export const UasTriaPhysicalDamageWriteBackEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_tria_physical_damage_write_back", { default: "uas_tria_physical_damage_write_back" }),
	...PremiumConsiderationSchema.properties,
	scheduleElection: Type.Union([Type.Literal("not_purchased"), Type.Literal("purchased")], { default: "not_purchased" }),
	excludedAircraftSchedule: Type.Array(
		Type.Object({
			tailNumber: Type.String({ default: "" }),
			makeModel: Type.String({ default: "" }),
			year: Type.String({ default: "" }),
			insuredValue: Type.Union([Type.Null(), Type.Number()], { default: null }),
		}),
		{ default: [] },
	),
});

export const UasWarLiabilityWriteBackEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_war_liability_write_back", { default: "uas_war_liability_write_back" }),
	...PremiumConsiderationSchema.properties,
	allScheduledAircraft: Type.Boolean({ default: true }),
	allReportedAircraft: Type.Boolean({ default: false }),
	selectedTailNumbers: Type.Optional(Type.Array(Type.String(), { default: [] })),
	limit: Type.Boolean({ default: true }),
	limitAmount: Type.Union([Type.Null(), Type.Number()], { default: null }),
	sameAsSingleLimit: Type.Boolean({ default: false }),
	singleLimitMax: Type.Union([Type.Null(), Type.Number()], { default: null }),
});

/** Schedule fields exist only so the war-liability toggle can schedule it alongside 0011. */
export const UasWarAutoTerminationEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_war_auto_termination", { default: "uas_war_auto_termination" }),
	...PremiumConsiderationSchema.properties,
	allScheduledAircraft: Type.Boolean({ default: true }),
	allReportedAircraft: Type.Boolean({ default: false }),
	selectedTailNumbers: Type.Optional(Type.Array(Type.String(), { default: [] })),
});

export const UasTriaLiabilityWriteBackEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_tria_liability_write_back", { default: "uas_tria_liability_write_back" }),
	...PremiumConsiderationSchema.properties,
});

export const UasDispensableLoadEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_dispensable_load", { default: "uas_dispensable_load" }),
	...PremiumConsiderationSchema.properties,
});

export const UasFellowEmployeeEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_fellow_employee", { default: "uas_fellow_employee" }),
	...PremiumConsiderationSchema.properties,
});

export const UasCancellationClauseAmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_cancellation_clause_amendment", { default: "uas_cancellation_clause_amendment" }),
	...PremiumConsiderationSchema.properties,
});

const UasReportingPeriodSchema = Type.Union([
	Type.Literal("monthly"),
	Type.Literal("quarterly"),
	Type.Literal("annually"),
], { default: "monthly" });

export const UasReportingFormEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_reporting_form", { default: "uas_reporting_form" }),
	...PremiumConsiderationSchema.properties,
	reportingFrequency: UasReportingPeriodSchema,
	premiumBasis: Type.Union([Type.Literal("adjusted"), Type.Literal("invoiced")], { default: "adjusted" }),
	premiumFrequency: UasReportingPeriodSchema,
	maximumAutomaticInsuredValue: nullableAmount(),
	notInMotionDeductible: nullableAmount(),
	inMotionDeductible: nullableAmount(),
	liabilityLimit: nullableAmount(),
	medicalPaymentsEachPerson: nullableAmount(),
	medicalPaymentsEachAccident: nullableAmount(),
	attachAnyUav: Type.Boolean({ default: false }),
	attachOwnedOrLeased: Type.Boolean({ default: false }),
	minimumDepositPremium: nullableAmount(),
	premiumRates: Type.String({ default: "" }),
});

export const UasAerialApplicationEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_aerial_application", { default: "uas_aerial_application" }),
	...PremiumConsiderationSchema.properties,
	liabilitySchedule: Type.Array(Type.Object({
		tailNumber: Type.String({ default: "" }),
		nonChemicalLimit: nullableAmount(),
		chemicalLimit: nullableAmount(),
	}), { default: [] }),
	nonOwnedNonChemicalLimit: nullableAmount(),
	nonOwnedChemicalLimit: nullableAmount(),
	newlyAcquiredNonChemicalLimit: nullableAmount(),
	newlyAcquiredChemicalLimit: nullableAmount(),
	contractualNonChemicalLimit: nullableAmount(),
	contractualChemicalLimit: nullableAmount(),
	groundSprayingUnits: Type.Array(Type.Object({
		vin: Type.String({ default: "" }),
		makeModel: Type.String({ default: "" }),
		year: Type.String({ default: "" }),
		chemicalCategory: Type.Union([Type.Literal("CC"), Type.Literal("RC"), Type.Literal("XC")], { default: "CC" }),
	}), { default: [] }),
	groundSprayingUnitProRatedClause: Type.Boolean({ default: false }),
	groundSprayingUnitMaximumPremium: nullableAmount(),
	aircraftSchedule: Type.Array(UasPhysicalDamageRowSchema, { default: [] }),
	aircraftProRatedClause: Type.Boolean({ default: false }),
	aircraftMaximumPremium: nullableAmount(),
	excludedEquipment: Type.String({ default: "" }),
	professionalAerialApplicators: Type.Boolean({ default: false }),
	extraExpenseEachDayLimit: nullableAmount(),
	extraExpenseEachOccurrenceLimit: nullableAmount(),
	extraExpenseDays: nullableAmount(),
	extraExpenseExcludedAircraft: Type.String({ default: "" }),
	sparePartsLimit: nullableAmount(),
	sparePartsDeductible: nullableAmount(),
	residentialAreaCoverage: Type.Boolean({ default: false }),
	residentialAreaOption: Type.Union([Type.Literal("deleted"), Type.Literal("replaced")], { default: "deleted" }),
	residentialAreaExceptions: Type.String({ default: "" }),
	careCustodyControlCoverage: Type.Boolean({ default: false }),
	careCustodyControlOption: Type.Union([
		Type.Literal("deleted"),
		Type.Literal("owned_or_leased"),
		Type.Literal("scheduled"),
	], { default: "deleted" }),
	careCustodyControlExceptions: Type.String({ default: "" }),
});

export const UasInterferenceExclusionEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_interference_exclusion", { default: "uas_interference_exclusion" }),
	...PremiumConsiderationSchema.properties,
});

/** Holder, attention and cap fields match GA `additional_insured` so the UAS AI certificate reuses its renderer. */
export const UasAdditionalInsuredEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_additional_insured", { default: "uas_additional_insured" }),
	...PremiumConsiderationSchema.properties,
	additionalInsuredName: Type.String({ default: "" }),
	additionalInsuredAddressLineOne: Type.String({ default: "" }),
	additionalInsuredAddressLineTwo: Type.String({ default: "" }),
	attention: Type.Optional(Type.Union([Type.String(), Type.Null()])),
	maxLiability: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
	maxWarLiability: Type.Optional(Type.Union([Type.Number(), Type.Null()])),
	faaCertNumbers: Type.Array(Type.String(), { default: [] }),
	asAdditionalInsured: Type.Boolean({ default: false }),
	premisesOwnerOrLessor: Type.Boolean({ default: false }),
	liabilityCoveragesOpsOnly: Type.Boolean({ default: false }),
	liabilityAndPhysicalDamageOpsOnly: Type.Boolean({ default: false }),
	exclusionForDesignManufacture: Type.Boolean({ default: false }),
});

export const UasBlanketLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_blanket_liability", { default: "uas_blanket_liability" }),
	...PremiumConsiderationSchema.properties,
	description: Type.String({ default: "" }),
	liabilityLimit: nullableAmount(),
});

export const UasBlanketPhysicalDamageEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_blanket_physical_damage", { default: "uas_blanket_physical_damage" }),
	...PremiumConsiderationSchema.properties,
	insuredValue: nullableAmount(),
	deductible: nullableAmount(),
	deductibleType: uasDeductibleType(),
});

export const UasMunitionsEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_munitions", { default: "uas_munitions" }),
	...PremiumConsiderationSchema.properties,
});

export const UasBroadContractualLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_broad_contractual_liability", { default: "uas_broad_contractual_liability" }),
	...PremiumConsiderationSchema.properties,
	contractParty: Type.String({ default: "" }),
	contractDate: Type.Union([Type.Null(), Type.String()], { default: null }),
	contractWording: Type.String({ default: "" }),
});

/** Billed from lobData.uas.chemical; included/additionalPremium stay empty so it isn't counted twice. */
export const UasAgSprayChemicalLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("uas_ag_spray_chemical_liability", { default: "uas_ag_spray_chemical_liability" }),
	...PremiumConsiderationSchema.properties,
	selectedTailNumbers: Type.Array(Type.String(), { default: [] }),
	chemicalPremium: Type.Number({ default: 0 }),
	nonChemicalEachPersonLimit: nullableAmount(),
	nonChemicalEachOccurrenceLimit: nullableAmount(),
	chemicalEachPersonLimit: nullableAmount(),
	chemicalEachOccurrenceLimit: nullableAmount(),
	chemicalAggregateLimit: nullableAmount(),
	medicalExpenseEachPersonLimit: nullableAmount(),
	medicalExpenseEachOccurrenceLimit: nullableAmount(),
	aircraftSchedule: Type.Array(Type.Object({
		tailNumber: Type.String({ default: "" }),
		makeModel: Type.String({ default: "" }),
		year: Type.String({ default: "" }),
		insuredValue: nullableAmount(),
		physicalDamageCoverageLetters: Type.String({ default: "" }),
		chemicalCoverageLetters: Type.String({ default: "" }),
		deductibleNotInMotion: nullableAmount(),
		deductibleInMotion: nullableAmount(),
		deductibleType: uasDeductibleType(),
	}), { default: [] }),
	fullAnnualPremiumClause: Type.Boolean({ default: false }),
	fullAnnualPremium: nullableAmount(),
});

const plPremiumOnlySchema = <S extends string>(slug: S) => Type.Object({
	slug: Type.Literal(slug, { default: slug }),
	...PremiumConsiderationSchema.properties,
});

export const PlExtendedPolicyTermEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_extended_policy_term", { default: "pl_extended_policy_term" }),
	...PremiumConsiderationSchema.properties,
	schedule: MultiYearPolicyEndorsementSchema.properties.schedule,
});

export const PlMultiYearPolicyEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_multi_year_policy", { default: "pl_multi_year_policy" }),
	...PremiumConsiderationSchema.properties,
	schedule: MultiYearPolicyEndorsementSchema.properties.schedule,
});

export const PlLimitsAmendmentEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_limits_amendment", { default: "pl_limits_amendment" }),
	...PremiumConsiderationSchema.properties,
	coverageALimit: nullableAmount(),
	coverageBLimit: nullableAmount(),
	combinedAggregateLimit: nullableAmount(),
});

export const PlDeductibleEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_deductible", { default: "pl_deductible" }),
	...PremiumConsiderationSchema.properties,
	perClaimDeductible: nullableAmount(),
	perOccurrenceDeductible: nullableAmount(),
});

export const PlBroadCoverageEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_broad_coverage", { default: "pl_broad_coverage" }),
	...PremiumConsiderationSchema.properties,
	hasProductTestingExpense: Type.Boolean({ default: false }),
	hasInAircraftTesting: Type.Boolean({ default: false }),
	hasAirportPremises: Type.Boolean({ default: false }),
});

export const PlPremisesLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_premises_liability", { default: "pl_premises_liability" }),
	...PremiumConsiderationSchema.properties,
	scheduledLocations: Type.String({ default: "" }),
	fireLegalLimit: nullableAmount(),
	premisesLimit: nullableAmount(),
});

export const PlPremisesLiabilityMedPayEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_premises_liability_med_pay", { default: "pl_premises_liability_med_pay" }),
	...PremiumConsiderationSchema.properties,
	scheduledLocations: Type.String({ default: "" }),
	fireLegalLimit: nullableAmount(),
	premisesLimit: nullableAmount(),
	medicalEachPerson: nullableAmount(),
	medicalEachOccurrence: nullableAmount(),
});

export const PlAirportPremisesLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_airport_premises_liability", { default: "pl_airport_premises_liability" }),
	...PremiumConsiderationSchema.properties,
	airportPremisesLimit: nullableAmount(),
});

export const PlAmendedDefinitionGroundingEndorsementSchema = plPremiumOnlySchema("pl_amended_definition_grounding");
export const PlExtendedDefinitionGroundingEndorsementSchema = plPremiumOnlySchema("pl_extended_definition_grounding");
export const PlInvestigationExpensesEndorsementSchema = plPremiumOnlySchema("pl_investigation_expenses");
export const PlInAircraftTestingEndorsementSchema = plPremiumOnlySchema("pl_in_aircraft_testing");

export const PlHangarkeepersLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_hangarkeepers_liability", { default: "pl_hangarkeepers_liability" }),
	...PremiumConsiderationSchema.properties,
	notInFlightEachAircraft: nullableAmount(),
	notInFlightEachOccurrence: nullableAmount(),
	inFlightEachAircraft: nullableAmount(),
	inFlightEachOccurrence: nullableAmount(),
	notInFlightDeductible: nullableAmount(),
	inFlightDeductiblePercent: nullableAmount(),
	inFlightDeductibleMinimum: nullableAmount(),
});

export const PlForeignMilitaryHullEndorsementSchema = plPremiumOnlySchema("pl_foreign_military_hull");

export const PlWarHijackingWriteBackEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_war_hijacking_write_back", { default: "pl_war_hijacking_write_back" }),
	...PremiumConsiderationSchema.properties,
	warWriteBackSubLimit: nullableAmount(),
});

export const PlWarAutoTerminationAmendmentEndorsementSchema = plPremiumOnlySchema("pl_war_auto_termination_amendment");

export const PlSpacecraftProductsEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_spacecraft_products", { default: "pl_spacecraft_products" }),
	...PremiumConsiderationSchema.properties,
	singleSatelliteLimit: nullableAmount(),
	multipleSatellitesLimit: nullableAmount(),
});

export const PlPersonalInjuryExtensionEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_personal_injury_extension", { default: "pl_personal_injury_extension" }),
	...PremiumConsiderationSchema.properties,
	limitOfLiabilityEachOffense: nullableAmount(),
	limitOfLiabilityAnnualAggregate: nullableAmount(),
});

export const PlAdvertisingInjuryExtensionEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_advertising_injury_extension", { default: "pl_advertising_injury_extension" }),
	...PremiumConsiderationSchema.properties,
	limitOfLiabilityEachOffense: nullableAmount(),
	limitOfLiabilityAnnualAggregate: nullableAmount(),
});

export const PlDeletionExclusionPdYourProductEndorsementSchema = plPremiumOnlySchema("pl_deletion_exclusion_pd_your_product");

export const PlAirportOpsAreaExcessAutoEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_airport_ops_area_excess_auto", { default: "pl_airport_ops_area_excess_auto" }),
	...PremiumConsiderationSchema.properties,
	eachOccurrenceLimit: nullableAmount(),
});

export const PlFireLegalLiabilityEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_fire_legal_liability", { default: "pl_fire_legal_liability" }),
	...PremiumConsiderationSchema.properties,
	fireLegalLimit: nullableAmount(),
});

export const PlDateChangeRecognitionEndorsementSchema = plPremiumOnlySchema("pl_date_change_recognition");
export const PlAmendedContractReportingEndorsementSchema = plPremiumOnlySchema("pl_amended_contract_reporting");

export const PlDiscontinuedProductsEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_discontinued_products", { default: "pl_discontinued_products" }),
	...PremiumConsiderationSchema.properties,
	discontinuedAfterDate: Type.Union([Type.Null(), Type.String()], { default: null }),
});

export const PlToxicSubstanceExclusionEndorsementSchema = plPremiumOnlySchema("pl_toxic_substance_exclusion");

export const PlDesignatedProductsEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_designated_products", { default: "pl_designated_products" }),
	...PremiumConsiderationSchema.properties,
	designatedProducts: Type.String({ default: "" }),
});

export const PlAdditionalInsuredEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_additional_insured", { default: "pl_additional_insured" }),
	...PremiumConsiderationSchema.properties,
	additionalInsuredName: Type.String({ default: "" }),
});

export const PlAdditionalInsuredVendorsEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_additional_insured_vendors", { default: "pl_additional_insured_vendors" }),
	...PremiumConsiderationSchema.properties,
	vendors: Type.Array(Type.Object({
		vendorName: Type.String({ default: "" }),
		vendorProduct: Type.String({ default: "" }),
	}), { default: [] }),
});

export const PlBoeingEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_boeing", { default: "pl_boeing" }),
	...PremiumConsiderationSchema.properties,
	gtaNumber: Type.String({ default: "" }),
});

export const PlAutomaticRenewalEndorsementSchema = Type.Object({
	slug: Type.Literal("pl_automatic_renewal", { default: "pl_automatic_renewal" }),
	...PremiumConsiderationSchema.properties,
	renewalPolicyNumber: Type.String({ default: "" }),
});

export const PlAmendatoryNyEndorsementSchema = plPremiumOnlySchema("pl_amendatory_ny");
export const PlOtherInsuranceIlEndorsementSchema = plPremiumOnlySchema("pl_other_insurance_il");

/** Mid-term record: removes a clause and carries a snapshot of the deleted endorsement for the deletion form. */
export const DeletionEndorsementSchema = Type.Object({
	slug: Type.Literal("deletion", { default: "deletion" }),
	/** Policy endorsement row voided by this deletion; original row is retained for full policy PDFs. */
	supersedesPolicyEndorsementId: Type.String(),
	deletedEndorsementSlug: Type.String(),
	/** Legacy, no longer written: the number is resolved at render time, and stored values went stale. */
	deletedEndorsementDocumentNumber: Type.Optional(Type.Integer()),
	deletionPremiumIncluded: Type.Boolean({ default: false }),
	deletionAdditionalPremium: Type.Union([Type.Null(), Type.Number()], { default: null }),
	/** Line-size-scaled premium invoiced for this deletion (credit when negative program amount). */
	deletionInvoicedPremium: Type.Optional(Type.Union([Type.Null(), Type.Number()])),
	originalEndorsement: Type.Unknown(),
});

const MidTermAircraftSnapshot = Type.Object({
	aircraftId: Type.String(),
	tailNumber: Type.String(),
	make: Type.String(),
	model: Type.String(),
	/** Model year for mid-term aircraft forms; optional for rows created before this field existed. */
	year: Type.Optional(Type.Union([Type.Null(), Type.String(), Type.Number()])),
	insuredValue: Type.Union([Type.Null(), Type.Number()]),
	hullRate: Type.Union([Type.Null(), Type.Number()]),
	liabilityLimit: Type.Union([Type.Null(), Type.Number()]),
	perPassengerLiabilitySublimit: Type.Union([Type.Null(), Type.Number()]),
	liabilityPremium: Type.Union([Type.Null(), Type.Number()]),
	deductibleType: Type.Union([Type.Null(), Type.Literal("fixed_amount"), Type.Literal("percentage")]),
	imDeductible: Type.Union([Type.Null(), Type.Number()]),
	nimDeductible: Type.Union([Type.Null(), Type.Number()]),
	hullWarRate: Type.Union([Type.Null(), Type.Number()]),
	warLiabilityPremium: Type.Union([Type.Null(), Type.Number()]),
	totalPremium: Type.Union([Type.Null(), Type.Number()]),
	/** Optional for endorsements created before this field was stored. */
	useType: Type.Optional(Type.Union([Type.Null(), Type.String()])),
	subUse: Type.Optional(Type.Union([Type.Null(), Type.String()])),
	medicalPayments: Type.Optional(Type.Union([Type.Null(), Type.Number()])),
	coverageLetter: Type.Optional(Type.Union([
		Type.Null(),
		Type.Literal("F"),
		Type.Literal("G"),
		Type.Literal("H"),
	])),
	seats: Type.Optional(Type.Union([Type.Null(), Type.String()])),
});

export const AdditionalAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("additional_aircraft", { default: "additional_aircraft" }),
	after: MidTermAircraftSnapshot,
	premiumAdjustment: Type.Number(),
	/** Line-size-scaled premium invoiced for this endorsement. */
	invoicedPremium: Type.Optional(Type.Number()),
	note: Type.Optional(Type.String()),
});

export const AdjustedAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("adjusted_aircraft", { default: "adjusted_aircraft" }),
	before: MidTermAircraftSnapshot,
	after: MidTermAircraftSnapshot,
	premiumAdjustment: Type.Number(),
	/** Line-size-scaled premium invoiced for this endorsement. */
	invoicedPremium: Type.Optional(Type.Number()),
	note: Type.Optional(Type.String()),
});

export const RemovedAircraftEndorsementSchema = Type.Object({
	slug: Type.Literal("removed_aircraft", { default: "removed_aircraft" }),
	before: MidTermAircraftSnapshot,
	premiumAdjustment: Type.Number(),
	/** Line-size-scaled premium invoiced for this endorsement. */
	invoicedPremium: Type.Optional(Type.Number()),
	note: Type.Optional(Type.String()),
});

const MidTermInsuredSnapshot = Type.Object({
	namedInsured: Type.Union([Type.Null(), Type.String()]),
	attention: Type.Union([Type.Null(), Type.String()]),
	streetAddress: Type.Union([Type.Null(), Type.String()]),
	city: Type.Union([Type.Null(), Type.String()]),
	state: Type.Union([Type.Null(), Type.String()]),
	postalCode: Type.Union([Type.Null(), Type.String()]),
	country: Type.Union([Type.Null(), Type.String()]),
});

export const ChangedNamedInsuredEndorsementSchema = Type.Object({
	slug: Type.Literal("changed_named_insured", { default: "changed_named_insured" }),
	before: MidTermInsuredSnapshot,
	after: MidTermInsuredSnapshot,
	note: Type.Optional(Type.String()),
});

const MidTermProducerSnapshot = Type.Object({
	brokerEmail: Type.Union([Type.Null(), Type.String()]),
	producerName: Type.Union([Type.Null(), Type.String()]),
	producerAddressLine1: Type.Union([Type.Null(), Type.String()]),
	producerAddressLine2: Type.Union([Type.Null(), Type.String()]),
});

export const ChangedProducerEndorsementSchema = Type.Object({
	slug: Type.Literal("changed_producer", { default: "changed_producer" }),
	before: MidTermProducerSnapshot,
	after: MidTermProducerSnapshot,
	note: Type.Optional(Type.String()),
});

export const RegistrationNumberRevisionEndorsementSchema = Type.Object({
	slug: Type.Literal("registration_number_revision", { default: "registration_number_revision" }),
	aircraftId: Type.String(),
	previousRegistrationNumber: Type.String(),
	newRegistrationNumber: Type.String(),
	/** Pro-rata premium delta for this mid-term edit (same basis as adjusted_aircraft). */
	premiumAdjustment: Type.Number(),
	/** Line-size-scaled premium invoiced for this endorsement. */
	invoicedPremium: Type.Optional(Type.Number()),
	note: Type.Optional(Type.String()),
});

export const PolicyNumberRevisionEndorsementSchema = Type.Object({
	slug: Type.Literal("policy_number_revision", { default: "policy_number_revision" }),
	...PremiumConsiderationSchema.properties,
	previousPolicyNumber: Type.String({ default: "" }),
	newPolicyNumber: Type.String({ default: "" }),
});

export const MidTermEndorsementSchema = Type.Union([
	AdditionalAircraftEndorsementSchema,
	AdjustedAircraftEndorsementSchema,
	RemovedAircraftEndorsementSchema,
	ChangedNamedInsuredEndorsementSchema,
	ChangedProducerEndorsementSchema,
	RegistrationNumberRevisionEndorsementSchema,
]);

export type MidTermEndorsement = typeof MidTermEndorsementSchema.static;

export const MIDTERM_ENDORSEMENT_SLUGS = new Set([
	"additional_aircraft",
	"adjusted_aircraft",
	"removed_aircraft",
	"changed_named_insured",
	"changed_producer",
	"registration_number_revision",
]);

/** Omitted from combined policy/quote endorsement PDFs (no clause template). */
export const DOCUMENT_EXCLUDED_POLICY_ENDORSEMENT_SLUGS = new Set<string>([
	"mid_term_invoice",
]);

export const GenericEndorsementSchema = Type.Union([
	BroadFormEndorsementSchema,
	BroadNamedInsuredEndorsementSchema,
	DateChangeRecognitionEndorsementSchema,
	DeletionOfCoverageCoEmployeesEndorsementSchema,
	MentalAnguishLiabilityEndorsementSchema,
	CglCommunicableDiseaseExclusionEndorsementSchema,
	CglDataEventClauseEndorsementSchema,
	CglDateChangeRecognitionEndorsementSchema,
	CglPfasExclusionEndorsementSchema,
	CglAmendatoryMoEndorsementSchema,
	CglAmendatoryNhEndorsementSchema,
	CglAmendatoryNyEndorsementSchema,
	CglAmendatorySdEndorsementSchema,
	CglAmendatoryVaEndorsementSchema,
	CglCommunicableDiseaseExclusionSdEndorsementSchema,
	CglCommunicableDiseaseExclusionVtEndorsementSchema,
	CglSupplementaryPaymentsEndorsementSchema,
	CglHostLiquorExtensionEndorsementSchema,
	CglCoverageTerritoryWorldwideEndorsementSchema,
	CglReferralLiabilityEndorsementSchema,
	CglPersonalAdvertisingInjuryExtensionEndorsementSchema,
	CglDeletionExclusionPdProductWorkEndorsementSchema,
	CglDeletionExclusionCoEmployeesEndorsementSchema,
	CglIncidentalMedicalMalpracticeEndorsementSchema,
	CglDeletionExclusionsRestaurantsCateringEndorsementSchema,
	CglAdditionalInsuredDesignatedEndorsementSchema,
	CglWaiverTransferRightsRecoveryEndorsementSchema,
	CglExclusionProductsCompletedOpsEndorsementSchema,
	CglExclusionMedicalPaymentsEndorsementSchema,
	CglExclusionDamageToPremisesRentedEndorsementSchema,
	CglExclusionPersonalAdvertisingInjuryEndorsementSchema,
	CglExclusionHangarkeepersLiabilityEndorsementSchema,
	CglExclusionMobileEquipmentEndorsementSchema,
	CglLimitedCoverageDesignatedProductsEndorsementSchema,
	CglExclusionHostLiquorEndorsementSchema,
	CglPollutionProductSoldEndorsementSchema,
	CglCancellationClauseAmendmentEndorsementSchema,
	CglSupplementaryPaymentsLimitationEndorsementSchema,
	CglOverheadSupervisionEndorsementSchema,
	CglOverheadSupervisionSpecifiedRateEndorsementSchema,
	CglContractReportingProvisionEndorsementSchema,
	CglScheduleOfContractsEndorsementSchema,
	CglWaiverGovernmentalImmunityEndorsementSchema,
	CglNonOwnedAircraftLiabilityAirportOperatorEndorsementSchema,
	CglNonOwnedAircraftLiabilityEndorsementSchema,
	CglExcessAutoLiabilityEndorsementSchema,
	CglExcessEmployersLiabilityEndorsementSchema,
	CglGaragekeepersLiabilityEndorsementSchema,
	CglAirportOpsAreaAutoEndorsementSchema,
	CglExtendedMobileEquipmentOffAirportEndorsementSchema,
	CglFinancedPremiumEndorsementSchema,
	CglBroadCoverageEndorsementSchema,
	CglBroadCoverageChangeEndorsementSchema,
	CglWarHijackingWriteBackEndorsementSchema,
	CglGroundingLiabilityEndorsementSchema,
	CglHangarkeepersWorkInProgressEndorsementSchema,
	CglExtendedHangarkeepersInFlightEndorsementSchema,
	CglGeographicAreasExclusionEndorsementSchema,
	CglAmendmentLimitsDesignatedEndorsementSchema,
	CglAmendmentLimitsEndorsementSchema,
	CglIncreasedLimitsDesignatedPersonsEndorsementSchema,
	CglDeductibleEndorsementSchema,
	CglPremiumAmendmentEndorsementSchema,
	CglSparePartsEndorsementSchema,
	CglCargoLiabilityEndorsementSchema,
	CglNonOwnedAircraftPhysicalDamageEndorsementSchema,
	CglBaggageLiabilityEndorsementSchema,
	CglMechanicsToolsEndorsementSchema,
	CglAdditionalInsuredDesignatedOptionalClausesEndorsementSchema,
	CglAirshowLiabilityCoveragePartEndorsementSchema,
	CglAmendedDefinitionPropertyDamageEndorsementSchema,
	CglAmendmentWhoIsInsuredPublicEntitiesEndorsementSchema,
	CglDefenseCostsWithinLimitEndorsementSchema,
	CglExcludedProductsCompletedOperationsEndorsementSchema,
	CglExclusionCompletedOperationsEndorsementSchema,
	CglExclusionIntercompanyProductsSuitEndorsementSchema,
	CglForeignMilitaryHullEndorsementSchema,
	CglHoneywellAvionicsDealershipEndorsementSchema,
	CglHoneywellHoldHarmlessEndorsementSchema,
	CglMisdeliveryLiquidProductsEndorsementSchema,
	CglAdditionalInsuredControllingInterestEndorsementSchema,
	CglAdditionalInsuredManagersLessorsEndorsementSchema,
	CglAdditionalInsuredManagersLessorsBroadFormEndorsementSchema,
	CglAdditionalInsuredLessorLeasedEquipmentEndorsementSchema,
	CglAdditionalInsuredOwnersLesseesContractorsEndorsementSchema,
	CglAdditionalInsuredDesignatedExcludingPaiEndorsementSchema,
	CglAdditionalInsuredDesignatedExtendedEndorsementSchema,
	CglAdditionalInsuredVendorsEndorsementSchema,
	CglAdditionalInsuredFuelLubricantSuppliersEndorsementSchema,
	CglDeletionExclusionSaleOfAircraftEndorsementSchema,
	CglExclusionHealthCosmeticServicesEndorsementSchema,
	CglExclusionNewEntitiesEndorsementSchema,
	CglNegligentInstructionSupervisionEndorsementSchema,
	CglNegligentInstructionSupervisionIndependentInstructorsEndorsementSchema,
	CglAirshowLiabilityEndorsementSchema,
	CglBoeingEndorsementSchema,
	CglInspectionLiabilityEndorsementSchema,
	CglExtendedNonOwnedAircraftPilotWarrantyEndorsementSchema,
	CglBaeSystemsTssEndorsementSchema,
	CglRockwellCollinsEndorsementSchema,
	CglStecCorporationEndorsementSchema,
	CglContingentControlTowerLiabilityEndorsementSchema,
	CglHostLiquorLiabilityLimitationEndorsementSchema,
	CglHangarkeepersLeasedManagedAircraftEndorsementSchema,
	CglSpacecraftProductsEndorsementSchema,
	CglSelfInsuredRetentionEndorsementSchema,
	CglEmployeeBenefitsLiabilityEndorsementSchema,
	CglPremisesAmendmentEndorsementSchema,
	CglProductsCompletedOpsLeasedManagedAircraftEndorsementSchema,
	CglProductsCompletedOpsMaintenanceLeasedAircraftEndorsementSchema,
	CglUaaMembersBroadFormEndorsementSchema,
	CglPremiumCalculationEndorsementSchema,
	CglPremiumCalculationGrossReceiptsEndorsementSchema,
	CglAmendmentLimitsProductsCompletedOpsHazardEndorsementSchema,
	CglAmendmentDeductiblesPerOccurrenceEndorsementSchema,
	DeletionOfExclusionPdYourProductWorkEndorsementSchema,
	AmendmentLimitsProductsCompletedOpsPistonEndorsementSchema,
	DepreciatedValueCoverageEndorsementSchema,
	RightOfFirstRefusalEndorsementSchema,
	MinimumEarnedPremiumEndorsementSchema,
	AmendatoryEndorsementSchema,
	AirAmbulanceUseEndorsementSchema,
	PurposeOfUseEndorsementSchema,
	SalesDemonstrationUseEndorsementSchema,
	LeasedAircraftContingentEndorsementSchema,
	AmendedAircraftScheduleEndorsementSchema,
	ManagedAircraftEndorsementSchema,
	ManagedAircraftBroadEndorsementSchema,
	LeasedEngineEndorsementSchema,
	LeasedEngineScheduledEndorsementSchema,
	PhysicalDamageEndorsementSchema,
	FloatsAndSkisEndorsementSchema,
	WarHijackingPerilsEndorsementSchema,
	WarHijackingPhysicalDamageEndorsementSchema,
	TriaLiabilityEndorsementSchema,
	TriaHullWriteBackEndorsementSchema,
	CargoLiabilityEndorsementSchema,
	AutomaticInsuranceForIncreasedValueEndorsementSchema,
	AirportOpsAreaAutoEndorsementSchema,
	ExcessEmployersLiabilityEndorsementSchema,
	ExcessAutoLiabilityEndorsementSchema,
	DamageToPremisesEndorsementSchema,
	GaragekeepersEndorsementSchema,
	AutomaticIncreaseSparePartsEndorsementSchema,
	BroadFormContractualLiabilityEndorsementSchema,
	ContractualLiabilitySpecificContractEndorsementSchema,
	PersonalInjuryExtensionSchema,
	AdvertisingInjuryExtensionSchema,
	FamilyAssistanceExpenseEndorsementSchema,
	CostReimbursementEndorsementSchema,
	PassengerVoluntarySettlementSchema,
	ManagedAircraftExtendedPdEndorsementSchema,
	NonOwnedAircraftPhysicalDamageEndorsementSchema,
	ManagedAircraftBroadExtendedPdEndorsementSchema,
	NonOwnedAircraftLiabilityEndorsementSchema,
	StudentAndRenterPilotLiabilityEndorsementSchema,
	IncidentalMedicalMalpracticeEndorsementSchema,
	IncidentalMedicalMalpracticeAvEndorsementSchema,
	MechanicsToolsEndorsementSchema,
	PortableEquipmentEndorsementSchema,
	KnowledgeOfOccurrenceSchema,
	LayupEndorsementSchema,
	LayupReturnEndorsementSchema,
	LayupReturnSingleEndorsementSchema,
	LienholdersInterestEndorsementSchema,
	LienholdersInterestMultipleEndorsementSchema,
	LienholdersInterestBlanketFleetEndorsementSchema,
	LossPayableEndorsementSchema,
	ConstructiveTotalLossEndorsementSchema,
	TotalLossOnlyEndorsementSchema,
	DisappearanceAmendmentOfDefinitionEndorsementSchema,
	OverheadSupervisionEndorsementSchema,
	OverheadSupervisionSpecifiedRateEndorsementSchema,
	OwnerLessorEndorsementSchema,
	OwnerLessorEndorsementFormSchema,
	BroadOwnerLessorEndorsementSchema,
	AdditionalInsuredOwnersLessorsEndorsementSchema,
	AdditionalInsuredOwnersLessorsBroadEndorsementSchema,
	ComponentPartsScheduleEndorsementSchema,
	FinancedPremiumEndorsementSchema,
	PremiumInstallmentEndorsementSchema,
	AdditionalInsuredPilotServiceProviderEndorsementSchema,
	MexicanLiabilityEndorsementSchema,
	AircraftRelocationExpenseEndorsementSchema,
	AutomaticAttachmentForNewlyAcquiredAircraftSchema,
	UnearnedPremiumEndorsementSchema,
	PolicyTerritoryEndorsementSchema,
	PolicyPeriodRevisionEndorsementSchema,
	RenewalEndorsementSchema,
	PolicyNumberRevisionEndorsementSchema,
	AirworthinessCertificateEndorsementSchema,
	PremisesMedicalCoverageEndorsementSchema,
	PremisesAmendmentEndorsementSchema,
	HostLiquorLiabilityEndorsementSchema,
	BaggageAndPersonalEffectsEndorsementSchema,
	HangarAndContentsEndorsementSchema,
	TripInterruptionExpenseEndorsementSchema,
	ExtraExpenseForSubAircraftEndorsementSchema,
	ConsequentialLossEndorsementSchema,
	ConsequentialLossCoverageEndorsementSchema,
	ExtraExpenseForTempPartsEndorsementSchema,
	MobileEquipmentLiabilityEndorsementSchema,
	HangarkeepersLiabilityEndorsementSchema,
	ProductsLiabilityEndorsementSchema,
	SparePartsEndorsementSchema,
	SparePartsScheduledEndorsementSchema,
	EmergencyLandingEndorsementSchema,
	SearchAndRescueEndorsementSchema,
	RunwayFoamingEndorsementSchema,
	SupplementaryPaymentsBailBondEndorsementSchema,
	NoClaimsBonusEndorsementSchema,
	FullPremiumInEventOfClaimEndorsementSchema,
	ProportionalShareEndorsementSchema,
	MinimalLossCreditEndorsementSchema,
	WaiverOfSubrogationAvEndorsementSchema,
	AdditionalInsuredEndorsementSchema,
	IncreasedLimitsDesignatedPersonsEndorsementSchema,
	WaiverOfSubrogationEndorsementSchema,
	CrewTrainingEndorsementSchema,
	LossControlSafetyEndorsementSchema,
	InvalidationClauseEndorsementSchema,
	CancellationClauseAmmendmentEndorsementSchema,
	LiabilityEndorsementSchema,
	LiabilityPerPersonLimitEndorsementSchema,
	RestrictedPassengerOccurrenceLimitEndorsementSchema,
	ReferralLiabilityEndorsementSchema,
	MultiYearPolicyEndorsementSchema,
	MultiYearPolicyAnnualEndorsementSchema,
	ExcludedUsesEndorsementSchema,
	IndependentPilotEndorsementSchema,
	RentalToNamedPilotsEndorsementSchema,
	ExcludedPilotsEndorsementSchema,
	ExcludedAircraftEndorsementSchema,
	PilotWarrantyEndorsementSchema,
	CustomEndorsementSchema,
	PolicyCancellationEndorsementSchema,
	MidTermInvoiceEndorsementSchema,
	DeletionEndorsementSchema,
	UasAircraftEndorsementSchema,
	UasLiabilityAmendmentEndorsementSchema,
	UasLimitsAmendmentEndorsementSchema,
	UasPhysicalDamageEndorsementSchema,
	UasNotInMotionPhysicalDamageEndorsementSchema,
	UasSpecialEquipmentEndorsementSchema,
	SpecialEquipmentEndorsementSchema,
	PremiumAmendmentEndorsementSchema,
	UasPremiumAmendmentEndorsementSchema,
	UasProductsLiabilityEndorsementSchema,
	UasTriaPhysicalDamageWriteBackEndorsementSchema,
	UasWarLiabilityWriteBackEndorsementSchema,
	UasWarAutoTerminationEndorsementSchema,
	UasTriaLiabilityWriteBackEndorsementSchema,
	UasDispensableLoadEndorsementSchema,
	UasFellowEmployeeEndorsementSchema,
	UasCancellationClauseAmendmentEndorsementSchema,
	UasReportingFormEndorsementSchema,
	UasAerialApplicationEndorsementSchema,
	UasInterferenceExclusionEndorsementSchema,
	UasAdditionalInsuredEndorsementSchema,
	UasBlanketLiabilityEndorsementSchema,
	UasBlanketPhysicalDamageEndorsementSchema,
	UasMunitionsEndorsementSchema,
	UasBroadContractualLiabilityEndorsementSchema,
	UasAgSprayChemicalLiabilityEndorsementSchema,
	PlExtendedPolicyTermEndorsementSchema,
	PlMultiYearPolicyEndorsementSchema,
	PlLimitsAmendmentEndorsementSchema,
	PlDeductibleEndorsementSchema,
	PlBroadCoverageEndorsementSchema,
	PlPremisesLiabilityEndorsementSchema,
	PlPremisesLiabilityMedPayEndorsementSchema,
	PlAirportPremisesLiabilityEndorsementSchema,
	PlAmendedDefinitionGroundingEndorsementSchema,
	PlExtendedDefinitionGroundingEndorsementSchema,
	PlInvestigationExpensesEndorsementSchema,
	PlInAircraftTestingEndorsementSchema,
	PlHangarkeepersLiabilityEndorsementSchema,
	PlForeignMilitaryHullEndorsementSchema,
	PlWarHijackingWriteBackEndorsementSchema,
	PlWarAutoTerminationAmendmentEndorsementSchema,
	PlSpacecraftProductsEndorsementSchema,
	PlPersonalInjuryExtensionEndorsementSchema,
	PlAdvertisingInjuryExtensionEndorsementSchema,
	PlDeletionExclusionPdYourProductEndorsementSchema,
	PlAirportOpsAreaExcessAutoEndorsementSchema,
	PlFireLegalLiabilityEndorsementSchema,
	PlDateChangeRecognitionEndorsementSchema,
	PlAmendedContractReportingEndorsementSchema,
	PlDiscontinuedProductsEndorsementSchema,
	PlToxicSubstanceExclusionEndorsementSchema,
	PlDesignatedProductsEndorsementSchema,
	PlAdditionalInsuredEndorsementSchema,
	PlAdditionalInsuredVendorsEndorsementSchema,
	PlBoeingEndorsementSchema,
	PlAutomaticRenewalEndorsementSchema,
	PlAmendatoryNyEndorsementSchema,
	PlOtherInsuranceIlEndorsementSchema,
]);

const qualifierLabels = {
	each_occurrence: "Each occurrence",
	each_day: "Each day",
	each_aircraft: "Each aircraft",
	each_loss: "Each loss",
	each_person: "Each person",
	aggregate: "Aggregate",
	any_one_offense: "Any one offense",
	any_one_passenger: "Any one passenger",
	any_one_occurrence: "Any one occurrence",
	annual_aggregate: "Annual aggregate",
	deductible: "Deductible",
};

export type GenericEndorsement = typeof GenericEndorsementSchema.static;


function conditionallyFormatCurrency ({
	value, hasCoverage,
}: {
	value: number | null;
	hasCoverage: boolean;
}) {
	if (hasCoverage) {
		return formatCurrency(value);
	}
	return "N/A";
}

export function leasedEngineDescription (row: Pick<LeasedEngineScheduleRow, "manufacturer" | "model" | "serialNumber">): string {
	const description = `${row.manufacturer} ${row.model}`.trim();
	const serial = row.serialNumber.trim();
	return serial ? `${description} S/N ${serial}`.trim() : description;
}

function physicalDamageAircraftRowVariables (row: typeof PhysicalDamageAircraftRowSchema.static) {
	return {
		tailNumber: row.tailNumber,
		makeModel: row.makeModel,
		year: row.year,
		seats: row.seats,
		insuredValue: nullishCurrencyOrBlank(row.insuredValue),
		coverageLetter: row.coverageLetter,
		deductibleNotInMotion: nullishCurrencyOrBlank(row.deductibleNotInMotion),
		deductibleInMotion: nullishCurrencyOrBlank(row.deductibleInMotion),
	};
}

function nullishCurrencyOrBlank (value: number | null) {
	if (value === null) {
		return "";
	}
	return formatCurrency(value);
}

/** Formats a liability limit for documents: "Follows Aircraft Limit" when null, otherwise formatted currency. */
function formatLimitOrFollowsAircraft (value: number | null): string {
	return value === null ? "Follows Aircraft Limit" : formatCurrency(value);
}

/** Returns qualifier for print: "Follows Aircraft Limit" when amount is null, otherwise the default qualifier. */
function limitQualifierForPrint (amount: number | null, defaultQualifier: string): string {
	return amount === null ? "Follows Aircraft Limit" : defaultQualifier;
}

function conditionallyFormatPercentage ({
	value, hasCoverage,
}: {
	value: number | null;
	hasCoverage: boolean;
}) {
	if (hasCoverage) {
		if (typeof value === "number" && value > 1) {
			const decimalPlaces = value.toString().split(".")[1]?.length ?? 0;
			if (decimalPlaces > 0) {
				return formatPercent(value / 100, decimalPlaces);
			}
			return formatPercentWhole(value / 100);
		}
		return formatPercentWhole(value);
	}
	return "N/A";
}

function formatSchedule2Percent (value: number): string {
	return conditionallyFormatPercentage({ value, hasCoverage: true });
}

function formatFloatsAndSkisDeductible (value: number | null | undefined): string {
	if (value === null || value === undefined) {
		return "NIL";
	}
	return formatCurrency(value);
}

function floatsAndSkisDocVariables (
	endorsement: typeof FloatsAndSkisEndorsementSchema.static,
): Record<string, string> {
	const insuredKeys = [
		"withWheelsInsuredValue",
		"withFloatsInsuredValue",
		"withSkisInsuredValue",
		"detachedWheelsInsuredValue",
		"detachedFloatsInsuredValue",
		"detachedSkisInsuredValue",
	] as const;
	const deductibleKeys = [
		"wheelsDeductible",
		"floatsDeductible",
		"skisDeductible",
		"flightWheelsDeductible",
		"flightFloatsDeductible",
		"flightSkisDeductible",
		"taxiWheelsDeductible",
		"taxiFloatsDeductible",
		"taxiSkisDeductible",
		"nimWheelsDeductible",
		"nimFloatsDeductible",
		"nimSkisDeductible",
	] as const;
	const vars: Record<string, string> = {
		premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
		aircraftTail: endorsement.aircraftTail,
	};
	for (const key of insuredKeys) {
		vars[key] = formatCurrency(endorsement[key]);
	}
	for (const key of deductibleKeys) {
		vars[key] = formatFloatsAndSkisDeductible(endorsement[key]);
	}
	return vars;
}

function getPremiumText (endorsement: { included?: boolean, additionalPremium?: number | null }): string {
	if (endorsement.included) {
		return "Included";
	}
	return formatCurrency(endorsement.additionalPremium ?? 0);
}

function getPremiumTextPricedAbove (endorsement: { included?: boolean, additionalPremium?: number | null }): string {
	const additional = endorsement.additionalPremium ?? 0;
	if (endorsement.included || Math.abs(additional) < 0.005) {
		return "See Above";
	}
	return formatCurrency(additional);
}

function mapWingType (wingType: "fixed-wing" | "rotor-wing" | null) {
	const vals = {
		"fixed-wing": "Fixed-Wing",
		"rotor-wing": "Rotor-Wing",
	};
	return vals[wingType as keyof typeof vals] ?? "N/A";
};

function premiumConsiderationText (
	included: boolean,
	premiumAmount: number | null | undefined,
) {
	const premium = premiumAmount ?? 0;
	if (included || premium === 0) {
		return "the included premium";
	} else if (premium < 0) {
		return `a return premium of ${formatCurrency(premium)}`;
	}
	return `an additional premium of ${formatCurrency(premium)}`;
}

function tailString (tailNumbers: string[] | string | null): string {
	if (Array.isArray(tailNumbers)) {
		return tailNumbers.join(", ");
	}
	return tailNumbers ?? "";
}

function currencyWithCentsIfAny (value: number | null): string {
	return value === null ? "" : formatCurrency(value, !Number.isInteger(value));
}

function singleLine (value: string): string {
	return value
		.split(/\r?\n/)
		.map(line => line.trim())
		.filter(Boolean)
		.join(", ");
}

function formatOptionalAutoExpiration (value: string | null | undefined): string {
	const trimmed = value?.trim();
	if (!trimmed) {
		return "";
	}
	return formatDateFullMonth(trimmed.slice(0, 10), "UTC");
}

function layupReturnDate (value: string): string {
	return value ? formatDateFullMonth(value, "UTC") : "";
}

function autoExpirationPrintVariables (endorsement: { autoExpiration?: string | null | undefined }) {
	return { autoExpiration: formatOptionalAutoExpiration(endorsement.autoExpiration) };
}

function appendAutoExpirationPrintLimit (
	limits: Endorsement["limits"],
	autoExpiration: string | null | undefined,
) {
	const formatted = formatOptionalAutoExpiration(autoExpiration);
	if (formatted) {
		limits.push({
			qualifier: `Auto expiration: ${formatted}`,
			amount: null,
			premiumIncluded: "",
		});
	}
}
interface PrintConfig {
	endorsement: typeof GenericEndorsementSchema.static;
	insuredValue?: number;
	liabilityLimit?: number;
	/** Unscaled 100% program base premium (aircraft + endorsement) for percentage-derived schedule rows. */
	totalPremium?: number;
	lob?: LOBEnumType | null;
}
export interface InitializeParams {
	hullValue: number;
	liabilityLimit: number;
	namedInsured: string;
	aircraftCount?: number;
}

export interface RenderContext {
	policyNumber: string;
	effectiveDate: Date;
	/** Declarations-aligned DOCX variables (policy PDF) for mid-term aircraft endorsement forms. */
	declarationsAligned?: Record<string, unknown>;
	/** Bound-quote line size — scales mid-term `premiumAdjustment` in document wording (matches invoicing). */
	lineSize?: number | null;
	/** Unscaled 100% program base premium (aircraft + endorsement) for render-time dollar calcs. */
	totalBasePremium?: number | null;
	/** Live printed # per policy endorsement row id; deletion forms resolve the clause they void. */
	documentNumbersByEndorsementId?: ReadonlyMap<string, number>;
}
export interface StateFormVariant {
	documentName: string;
	formNumber: string;
}

export interface EndorsementConfig<T = unknown> {
	label: string;
	schema: TAnySchema;
	documentName: string;
	formNumber: string;
	hasStateOverride?: boolean;
	/** Lines whose quote pickers offer the slug. */
	lob: readonly LOBEnumType[];
	/** Filed state replacements for `documentName`, keyed by two-letter risk state. */
	stateDocuments?: Readonly<Record<string, StateFormVariant>>;
	/** States with no filed form; the slug is not offerable on risks located there. */
	excludedStates?: readonly string[];
	documentsByLob?: Partial<Record<LOBEnumType, StateFormVariant>>;
	labelsByLob?: Partial<Record<LOBEnumType, string>>;
	includeInQuoteTemplate: boolean;
	renewal: "carry" | "drop";
	toPrintEndorsement?: (config: PrintConfig) => Array<Endorsement>;
	generateDynamicVariables?: (endorsement: GenericEndorsement, ctx?: RenderContext) => Record<string, unknown>;
	initialize?: (params: InitializeParams) => T;
}

function generateAircraftMidTermEndorsementDynamicVariables (
	endorsement: { slug: string; premiumAdjustment?: number; invoicedPremium?: number },
	ctx?: RenderContext,
): Record<string, unknown> {
	const base = { ...(ctx?.declarationsAligned ?? {}) };
	const rawAdj = endorsement.premiumAdjustment ?? 0;
	const consideration = typeof endorsement.invoicedPremium === "number"
		? midTermPremiumConsiderationFromInvoiced(endorsement.invoicedPremium)
		: midTermPremiumConsiderationForDoc(rawAdj, ctx?.lineSize);
	return {
		...base,
		premiumConsideration: consideration,
		"premium consideration text": consideration,
	};
}

function generateRegistrationNumberRevisionEndorsementDynamicVariables (
	endorsement: GenericEndorsement,
	ctx?: RenderContext,
): Record<string, unknown> {
	if (!Value.Check(RegistrationNumberRevisionEndorsementSchema, endorsement)) {
		return {};
	}
	const e = endorsement as typeof RegistrationNumberRevisionEndorsementSchema.static;
	const consideration = typeof e.invoicedPremium === "number"
		? midTermPremiumConsiderationFromInvoiced(e.invoicedPremium)
		: midTermPremiumConsiderationForDoc(e.premiumAdjustment, ctx?.lineSize);
	const base = { ...(ctx?.declarationsAligned ?? {}) };
	return {
		...base,
		premiumConsideration: consideration,
		"premium consideration text": consideration,
		previousRegistrationNumber: e.previousRegistrationNumber,
		newRegistrationNumber: e.newRegistrationNumber,
	};
}

function generateChangedNamedInsuredEndorsementDynamicVariables (
	endorsement: GenericEndorsement,
	ctx?: RenderContext,
): Record<string, unknown> {
	if (!Value.Check(ChangedNamedInsuredEndorsementSchema, endorsement)) {
		return {};
	}
	const e = endorsement as typeof ChangedNamedInsuredEndorsementSchema.static;
	const { before: b, after: a } = e;
	const fb = ctx?.declarationsAligned ?? {};
	const named = a.namedInsured !== null
		? a.namedInsured
		: (b.namedInsured ?? (typeof fb.insuredName === "string" ? fb.insuredName : ""));
	const attentionRaw = a.attention !== null ? a.attention : b.attention;
	const insuredAttentionLine = formatAttentionLine(attentionRaw ?? undefined);
	const street = a.streetAddress !== null
		? a.streetAddress
		: (b.streetAddress ?? (typeof fb.insuredAddressLine1 === "string" ? fb.insuredAddressLine1 : ""));
	const insuredAddressLine1 = street ?? "";
	const city = a.city !== null ? a.city : b.city;
	const state = a.state !== null ? a.state : b.state;
	const postalCode = a.postalCode !== null ? a.postalCode : b.postalCode;
	let insuredAddressLine2 = formatAddressLineTwo({
		city,
		state,
		postalCode,
	});
	if (!insuredAddressLine2) {
		insuredAddressLine2 = typeof fb.insuredAddressLine2 === "string" ? fb.insuredAddressLine2 : "";
	}
	const consideration = midTermPremiumConsiderationForDoc(0, ctx?.lineSize);
	return {
		premiumConsideration: consideration,
		"premium consideration text": consideration,
		insuredName: named,
		insuredAttentionLine,
		insuredAddressLine1,
		insuredAddressLine2,
	};
}

function generateChangedProducerEndorsementDynamicVariables (
	endorsement: GenericEndorsement,
	ctx?: RenderContext,
): Record<string, unknown> {
	if (!Value.Check(ChangedProducerEndorsementSchema, endorsement)) {
		return {};
	}
	const e = endorsement as typeof ChangedProducerEndorsementSchema.static;
	const { before: b, after: a } = e;
	const consideration = midTermPremiumConsiderationForDoc(0, ctx?.lineSize);
	const removedLine1 = b.producerAddressLine1 ?? "";
	const removedLine2 = b.producerAddressLine2 ?? "";
	const newLine1 = a.producerAddressLine1 ?? "";
	const newLine2 = a.producerAddressLine2 ?? "";
	return {
		premiumConsideration: consideration,
		"premium consideration text": consideration,
		removedProducerName: b.producerName ?? "",
		removedProducerAddressLine1: removedLine1,
		removedProducerAddressLine2: removedLine2,
		removedBrokerAddressLine1: removedLine1,
		removedBrokerAddressLine2: removedLine2,
		newProducerName: a.producerName ?? "",
		newProducerAddressLine1: newLine1,
		newProducerAddressLine2: newLine2,
	};
}

function generateRemovedAircraftEndorsementDynamicVariables (
	endorsement: GenericEndorsement,
	ctx?: RenderContext,
): Record<string, unknown> {
	if (!Value.Check(RemovedAircraftEndorsementSchema, endorsement)) {
		return {};
	}
	const e = endorsement as typeof RemovedAircraftEndorsementSchema.static;
	const base = { ...(ctx?.declarationsAligned ?? {}) };
	const rawAdj = e.premiumAdjustment ?? 0;
	const consideration = midTermPremiumConsiderationForDoc(rawAdj, ctx?.lineSize);
	const yr = e.before.year;
	const aircraftYear = yr === null || yr === undefined ? "" : String(yr);
	return {
		...base,
		premiumConsideration: consideration,
		"premium consideration text": consideration,
		aircraftYear,
		aircraftMakeModel: `${e.before.make} ${e.before.model}`.trim(),
		aircraftTailNumber: e.before.tailNumber,
	};
}

const UAS_AIRCRAFT_DOCUMENT: StateFormVariant = {
	documentName: "N UAS E CLASSA 0001 05 25 Aircraft Endorsement.docx",
	formNumber: "N UAS E CLASSA 0001 05 25",
};

function uasCurrencyOrNotCovered (value: number | null): string {
	return value === null ? "N/C" : formatCurrency(value);
}

export function uasLiabilityRowVariables (row: typeof UasLiabilityRowSchema.static) {
	return {
		tailNumber: row.tailNumber,
		liabilityLimit: uasCurrencyOrNotCovered(row.liabilityLimit),
	};
}

export function uasPhysicalDamageRowVariables (row: typeof UasPhysicalDamageRowSchema.static) {
	let deductible = "N/C";
	if (row.deductible !== null) {
		deductible = formatUasDeductible(row.deductible, row.deductibleType);
	} else if (row.insuredValue !== null) {
		deductible = "NIL";
	}
	return {
		tailNumber: row.tailNumber,
		makeModel: row.makeModel,
		year: row.year,
		insuredValue: uasCurrencyOrNotCovered(row.insuredValue),
		deductible,
	};
}

export function uasAircraftScheduleVariables (rows: ReadonlyArray<typeof UasAircraftRowSchema.static>) {
	return {
		liabilityAircraft: rows.map(uasLiabilityRowVariables),
		aircraft: rows.map(uasPhysicalDamageRowVariables),
	};
}

function uasLimitQualifierForPrint (amount: number | null, defaultQualifier: string): string {
	return amount === null ? "Follows UAS Limit" : defaultQualifier;
}

function uasTerrorismPremium (endorsement: { included: boolean; additionalPremium: number | null }): string {
	return formatCurrency(endorsement.included ? 0 : endorsement.additionalPremium ?? 0);
}

function uasPhysicalDamagePrintLimits (
	endorsement: { included: boolean; additionalPremium: number | null },
	rows: ReadonlyArray<typeof UasPhysicalDamageRowSchema.static>,
): Endorsement["limits"] {
	const limits: Endorsement["limits"] = rows.map((row, index) => ({
		qualifier: `${row.tailNumber.trim()} ${row.makeModel.trim()}`.trim() || "As scheduled",
		amount: row.insuredValue,
		premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
	}));
	if (limits.length === 0) {
		limits.push({
			qualifier: "As scheduled",
			amount: null,
			premiumIncluded: getPremiumText(endorsement),
		});
	}
	return limits;
}

function uasLiabilityPrintLimits (
	endorsement: { included: boolean; additionalPremium: number | null },
	rows: ReadonlyArray<typeof UasLiabilityRowSchema.static>,
): Endorsement["limits"] {
	const limits: Endorsement["limits"] = rows.map((row, index) => ({
		qualifier: `${row.tailNumber.trim()} each occurrence`.trim(),
		amount: row.liabilityLimit,
		premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
	}));
	if (limits.length === 0) {
		limits.push({
			qualifier: "As scheduled",
			amount: null,
			premiumIncluded: getPremiumText(endorsement),
		});
	}
	return limits;
}

const UAS_LIMITS_AMENDMENT_PRINT_ROWS = [
	["nonOwnedLiabilityLimit", "Use of non-owned UAS liability"],
	["newlyAcquiredLiabilityLimit", "Newly acquired UAS liability"],
	["premisesLiabilityLimit", "Premises liability"],
	["fireLegalLiabilityLimit", "Fire legal liability"],
	["saleOfAircraftProductsLimit", "Sale of UAS and UAS products and services"],
	["contractualLiabilityLimit", "Contractual agreements"],
	["personalInjuryLimit", "Personal injury"],
	["unexpectedLandingLimit", "Unexpected landing event"],
	["unexpectedLandingAggregate", "Unexpected landing policy aggregate"],
	["medicalPaymentsEachPerson", "Medical payments each person"],
	["medicalPaymentsEachAccident", "Medical payments each accident"],
] as const;

function uasPrintLimits (
	endorsement: { included?: boolean; additionalPremium?: number | null },
	rows: ReadonlyArray<readonly [string, number | null]>,
	premiumText: (endorsement: { included?: boolean; additionalPremium?: number | null }) => string = getPremiumText,
): Endorsement["limits"] {
	const printed = rows.length > 0 ? rows : [["", null] as const];
	return printed.map(([qualifier, amount], index) => ({
		qualifier,
		amount,
		premiumIncluded: index === 0 ? premiumText(endorsement) : "",
	}));
}

function uasPremiumOnlyPrint (slug: string, name: string) {
	return ({ endorsement }: PrintConfig): Endorsement[] => {
		if (endorsement.slug !== slug || !("included" in endorsement)) {
			return [];
		}
		return [{
			name,
			included: true,
			limits: uasPrintLimits(endorsement, []),
		}];
	};
}

function uasPremiumOnlyVariables (slug: string) {
	return (endorsement: GenericEndorsement): Record<string, unknown> => {
		if (endorsement.slug !== slug || !("included" in endorsement)) {
			return {};
		}
		return { premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium) };
	};
}

function uasDeductibleOrNil (value: number | null): string {
	return value === null ? "NIL" : formatCurrency(value);
}

function uasRowDeductible (
	insuredValue: number | null,
	deductible: number | null,
	deductibleType: "fixed_amount" | "percentage" | undefined,
): string {
	if (deductible !== null) {
		return formatUasDeductible(deductible, deductibleType);
	}
	return insuredValue === null ? "N/C" : "NIL";
}

const UAS_AERIAL_APPLICATION_PRINT_ROWS = [
	["nonOwnedNonChemicalLimit", "Use of non-owned UAS, non-chemical"],
	["nonOwnedChemicalLimit", "Use of non-owned UAS, chemical"],
	["newlyAcquiredNonChemicalLimit", "Newly acquired UAS, non-chemical"],
	["newlyAcquiredChemicalLimit", "Newly acquired UAS, chemical"],
	["contractualNonChemicalLimit", "Contractual agreements, non-chemical"],
	["contractualChemicalLimit", "Contractual agreements, chemical"],
] as const;

const UAS_AG_SPRAY_PRINT_ROWS = [
	["nonChemicalEachPersonLimit", "Non-chemical each person"],
	["nonChemicalEachOccurrenceLimit", "Non-chemical each occurrence"],
	["chemicalEachPersonLimit", "Chemical each person"],
	["chemicalEachOccurrenceLimit", "Chemical each occurrence"],
	["chemicalAggregateLimit", "Chemical aggregate"],
	["medicalExpenseEachPersonLimit", "Medical expense each person"],
	["medicalExpenseEachOccurrenceLimit", "Medical expense each occurrence"],
] as const;

function plMultiYearScheduleVariables (
	schedule: ReadonlyArray<{ year: number; premium: number }>,
	ctx: RenderContext,
) {
	const policyIncrementerStart = Number.parseInt(ctx.policyNumber.slice(-2)) - 1;
	return schedule.map((year) => {
		const policyYearNumber = policyIncrementerStart + year.year;
		const policyYearNumberString = String(policyYearNumber).padStart(2, "0");
		const pNumber = `${ctx.policyNumber.slice(0, -2)}${policyYearNumberString}`;
		const baseDate = new Date(ctx.effectiveDate);
		const effectiveDate = baseDate.setFullYear(baseDate.getFullYear() + year.year - 1);
		const thirtyDays = 1000 * 60 * 60 * 24 * 30;
		const effectiveDatePlusThirtyDays = new Date(effectiveDate + thirtyDays);
		const dueDate = formatDateFullMonth(effectiveDatePlusThirtyDays, "UTC");
		return {
			policyYear: year.year,
			premium: formatCurrency(year.premium, true),
			policyNumber: pNumber,
			dueDate,
		};
	});
}

type PlScheduleEndorsement = typeof PlExtendedPolicyTermEndorsementSchema.static | typeof PlMultiYearPolicyEndorsementSchema.static;

function plScheduleVariables (endorsement: PlScheduleEndorsement, ctx?: RenderContext): Record<string, unknown> {
	if (!ctx?.policyNumber) {
		throw new Error("Policy number is required for multi-year policy endorsement");
	}
	return {
		premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
		schedule: plMultiYearScheduleVariables(endorsement.schedule, ctx).map(row => ({
			...row,
			policyYearLabel: `Year ${numberToEnglishTitleCase(row.policyYear)}`,
		})),
	};
}

function plSchedulePrint (name: string, endorsement: PlScheduleEndorsement): Endorsement[] {
	return [{
		name,
		included: true,
		limits: uasPrintLimits(
			endorsement,
			endorsement.schedule.map(year => [`Year ${year.year} premium: ${formatCurrency(year.premium, true)}`, null] as const),
		),
	}];
}

function plLimitsPrint (
	name: string,
	endorsement: { included: boolean; additionalPremium: number | null },
	rows: ReadonlyArray<readonly [string, number | null]>,
): Endorsement[] {
	return [{
		name,
		included: true,
		limits: uasPrintLimits(endorsement, rows),
	}];
}

function plRefundPremiumConsiderationText (included: boolean, premiumAmount: number | null): string {
	const premium = premiumAmount ?? 0;
	if (!included && premium < 0) {
		return `a premium refund of ${formatCurrency(Math.abs(premium))}`;
	}
	return premiumConsiderationText(included, premium);
}

function plPercentOrBlank (value: number | null): string {
	return value === null ? "" : String(value);
}

export const endorsementConfig: Record<string, EndorsementConfig> = {
	broad_form: {
		label: "Broad Form Endorsement",
		schema: BroadFormEndorsementSchema,
		documentName: "N AC E CLASSA 0025 05 25 Broad Coverage Endorsement.docx",
		formNumber: "N AC E CLASSA 0025 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "broad_form") {
				if (endorsement.hasAirworthiness) {
					endorsements.push({
						name: "Standard Airworthiness Certificate",
						included: endorsement.hasAirworthiness,
						limits: [
							{
								qualifier: "Not Required",
								amount: null,
								premiumIncluded: "Included",
							},
						],
					});
				}
				if (endorsement.hasAutomaticInsuranceForIncreasedValue) {
					endorsements.push({
						name: "Automatic Insurance for Increased Value of Scheduled Aircraft",
						included: endorsement.hasAutomaticInsuranceForIncreasedValue,
						limits: [
							{
								qualifier: `25% or ${formatCurrency(endorsement.automaticInsuredValueLimit)} whichever is lesser`,
								amount: null,
								premiumIncluded: "Included",
							},
						],
					});
				}
				if (endorsement.hasBaggageAndHangar) {
					endorsements.push({
						name: "Baggage Liability",
						included: endorsement.hasBaggageAndHangar,
						limits: [
							{
								qualifier: "Each passenger, any one occurrence",
								amount: endorsement.passengerBaggageLimit,
								premiumIncluded: "Included",
							},
						],
					});
					endorsements.push({
						name: "Property Damage to Non-Owned Hangars & Contents",
						included: endorsement.hasBaggageAndHangar,
						limits: [
							{
								qualifier: qualifierLabels.each_occurrence,
								amount: endorsement.baggagePropertyDamageLimit,
								premiumIncluded: "Included",
							},
						],
					});
				}
				if (endorsement.hasDefinitionPremises) {
					const qualifier = endorsement.definitionPremisesPerOccurrence ? qualifierLabels.each_occurrence : "Same limit as the Coverages A, B and D set forth on the Declarations";
					const amount = endorsement.definitionPremisesPerOccurrence ? endorsement.definitionPremisesPerOccurrenceLimit : null;
					endorsements.push({
						name: "Premises Liability",
						included: endorsement.hasDefinitionPremises,
						limits: [
							{
								qualifier,
								amount,
								premiumIncluded: "Included",
							},
						],
					});
					
				}
				if (endorsement.hasEmergencyOrUnexpectedLanding) {
					endorsements.push({
						name: "Emergency or Unexpected Landing",
						included: endorsement.hasEmergencyOrUnexpectedLanding,
						limits: [
							{
								qualifier: `For any expenses not to exceed ${endorsement.emergencyLandingLimitPercent}% of the insured value of the aircraft involved`,
								amount: null,
								premiumIncluded: "Included",
							},
						],
					});
				}
				if (endorsement.hasSubstituteAircraft) {
					const waitPeriod = endorsement.substituteAircraftWaitPeriod > 0 ? `${endorsement.substituteAircraftWaitPeriod} day wait` : "No wait";
					const daysFromOccurrence = `${endorsement.substituteAircraftDaysFromOccurrence} days max`;
					endorsements.push({
						name: "Extra Expense for Renting Substitute Aircraft",
						included: endorsement.hasSubstituteAircraft,
						limits: [
							{
								qualifier: qualifierLabels.each_occurrence,
								amount: endorsement.substituteAircraftOccurrenceLimit,
								premiumIncluded: "Included",
							},
							{
								qualifier: "Each day",
								amount: endorsement.substituteAircraftDailyLimit,
								premiumIncluded: "",
							},
							{
								qualifier: `${daysFromOccurrence} / ${waitPeriod}`,
								amount: null,
								premiumIncluded: "",
							},
						],
					});
				}
				if (endorsement.hasTempReplacementParts) {
					const repairDays = endorsement.replacementPartsRepairDays ?? 5;
					endorsements.push({
						name: "Extra Expense for Temporary Replacement Aircraft Parts",
						included: endorsement.hasTempReplacementParts,
						limits: [
							{
								qualifier: qualifierLabels.each_loss,
								amount: endorsement.replacementPartsLimit,
								premiumIncluded: "Included",
							},
							{
								qualifier: `${repairDays} ${repairDays === 1 ? "day" : "days"} repair time`,
								amount: null,
								premiumIncluded: "",
							},
						],
					});
				}
				if (endorsement.hasHangarkeepers) {
					endorsements.push({
						name: "Hangarkeepers' Liability",
						included: endorsement.hasHangarkeepers,
						limits: [
							{
								qualifier: qualifierLabels.each_aircraft,
								amount: endorsement.hangarkeepersAircraftLimit,
								premiumIncluded: "Included",
							},
							{
								qualifier: qualifierLabels.each_occurrence,
								amount: endorsement.hangarkeepersOccurrenceLimit,
								premiumIncluded: "",
							},
							{
								qualifier: qualifierLabels.deductible,
								amount: endorsement.hangarkeepersDeductible === null ? "NIL" : endorsement.hangarkeepersDeductible,
								premiumIncluded: "",
							},
						],
					});
				}
				if (endorsement.hasHostLiquor) {
					endorsements.push({
						name: "Host Liquor Liability",
						included: endorsement.hasHostLiquor,
						limits: [
							{
								qualifier: limitQualifierForPrint(endorsement.hostLiquorLimit, qualifierLabels.aggregate),
								amount: endorsement.hostLiquorLimit,
								premiumIncluded: "Included",
							},
						],
					});
				}
				if (endorsement.hasMobileEquipment) {
					endorsements.push({
						name: "Mobile Equipment Liability",
						included: endorsement.hasMobileEquipment,
						limits: [
							{
								qualifier: limitQualifierForPrint(endorsement.mobileEquipmentLimit, qualifierLabels.each_occurrence),
								amount: endorsement.mobileEquipmentLimit,
								premiumIncluded: "Included",
							},
						],
					});
				}
				if (endorsement.hasPhysicalDamage) {
					endorsements.push({
						name: "Physical Damage to Spare Engines, Parts, Avionics, and Accessories",
						included: endorsement.hasPhysicalDamage,
						limits: [
							{
								qualifier: qualifierLabels.each_occurrence,
								amount: endorsement.physicalDamageLimit,
								premiumIncluded: "Included",
							},
							{
								qualifier: qualifierLabels.deductible,
								amount: endorsement.physicalDamageDeductible === null ? "NIL" : endorsement.physicalDamageDeductible,
								premiumIncluded: "",
							},
						],
					});
				}
				if (endorsement.hasPolicyTerritory) {
					endorsements.push({
						name: "Policy Territory",
						included: endorsement.hasPolicyTerritory,
						limits: [
							{
								qualifier: "Anywhere in the world",
								amount: null,
								premiumIncluded: "Included",
							},
						],
					});
				}
				if (endorsement.hasPremisesMedical) {
					endorsements.push({
						name: "Premises Medical Coverage",
						included: endorsement.hasPremisesMedical,
						limits: [
							{
								qualifier: qualifierLabels.each_person,
								amount: endorsement.premisesMedicalLimit,
								premiumIncluded: "Included",
							},
						],
					});
				}
				if (endorsement.hasProductsLiabilitySale) {
					endorsements.push({
						name: "Products Liability - Sale of Aircraft, Aircraft Parts, Food and Beverage",
						included: endorsement.hasProductsLiabilitySale,
						limits: [
							{
								qualifier: limitQualifierForPrint(endorsement.productsLiabilitySaleLimit, qualifierLabels.each_occurrence),
								amount: endorsement.productsLiabilitySaleLimit,
								premiumIncluded: "Included",
							},
						],
					});
				}
				if (endorsement.hasSearchAndRescue) {
					endorsements.push({
						name: "Runway Foaming & Crash Control Expenses",
						included: endorsement.hasSearchAndRescue,
						limits: [
							{
								qualifier: "Each occurrence for each foaming",
								amount: endorsement.runwayFoamingLimit,
								premiumIncluded: "Included",
							},
						],
					});
					endorsements.push({
						name: "Search and Rescue Expenses",
						included: endorsement.hasSearchAndRescue,
						limits: [
							{
								qualifier: "Any one occurrence",
								amount: endorsement.searchAndRescueOccurrenceLimit,
								premiumIncluded: "",
							},
						],
					});
				}
				if (endorsement.hasTripInterruption) {
					endorsements.push({
						name: "Trip Interruption Expense",
						included: endorsement.hasTripInterruption,
						limits: [
							{
								qualifier: "Each passenger, each occurrence",
								amount: endorsement.tripInterruptionLimit,
								premiumIncluded: "Included",
							},
						],
					});
				}
				if (endorsement.hasUnearnedPremium) {
					endorsements.push({
						name: "Unearned Premium",
						included: endorsement.hasUnearnedPremium,
						limits: [
							{
								qualifier: "Included",
								amount: null,
								premiumIncluded: "Included",
							},
						],
					});
				}
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "broad_form") {
				return {
					premiumConsideration: premiumConsiderationText(true, null),
					hasAirworthiness: renderCheckbox(endorsement.hasAirworthiness),
					hasAutomaticInsuranceForIncreasedValue: renderCheckbox(endorsement.hasAutomaticInsuranceForIncreasedValue),
					automaticInsuredValueLimit: conditionallyFormatCurrency({
						value: endorsement.automaticInsuredValueLimit,
						hasCoverage: endorsement.hasAutomaticInsuranceForIncreasedValue,
					}),
					hasBaggageAndHangar: renderCheckbox(endorsement.hasBaggageAndHangar),
					passengerBaggageLimit: conditionallyFormatCurrency({
						value: endorsement.passengerBaggageLimit,
						hasCoverage: endorsement.hasBaggageAndHangar,
					}),
					baggagePropertyDamageLimit: conditionallyFormatCurrency({
						value: endorsement.baggagePropertyDamageLimit,
						hasCoverage: endorsement.hasBaggageAndHangar,
					}),
					hasDefinitionPremises: renderCheckbox(endorsement.hasDefinitionPremises),
					definitionPremisesSameLimit: renderCheckbox(endorsement.definitionPremisesSameLimit),
					definitionPremisesPerOccurrence: renderCheckbox(endorsement.definitionPremisesPerOccurrence),
					definitionPremisesPerOccurrenceLimit: conditionallyFormatCurrency({
						value: endorsement.definitionPremisesPerOccurrenceLimit,
						hasCoverage: endorsement.definitionPremisesPerOccurrence,
					}),
					hasEmergencyOrUnexpectedLanding: renderCheckbox(endorsement.hasEmergencyOrUnexpectedLanding),
					emergencyLandingLimitPercent: conditionallyFormatPercentage({
						value: endorsement.emergencyLandingLimitPercent,
						hasCoverage: endorsement.hasEmergencyOrUnexpectedLanding,
					}),
					hasSubstituteAircraft: renderCheckbox(endorsement.hasSubstituteAircraft),
					substituteAircraftDailyLimit: conditionallyFormatCurrency({
						value: endorsement.substituteAircraftDailyLimit,
						hasCoverage: endorsement.hasSubstituteAircraft,
					}),
					substituteAircraftOccurrenceLimit: conditionallyFormatCurrency({
						value: endorsement.substituteAircraftOccurrenceLimit,
						hasCoverage: endorsement.hasSubstituteAircraft,
					}),
					substituteAircraftWaitPeriod: endorsement.substituteAircraftWaitPeriod,
					substituteAircraftDaysFromOccurrence: endorsement.substituteAircraftDaysFromOccurrence,
					hasTempReplacementParts: renderCheckbox(endorsement.hasTempReplacementParts),
					replacementPartsLimit: conditionallyFormatCurrency({
						value: endorsement.replacementPartsLimit,
						hasCoverage: endorsement.hasTempReplacementParts,
					}),
					replacementPartsRepairDays: endorsement.replacementPartsRepairDays ?? 5,
					hasHangarkeepers: renderCheckbox(endorsement.hasHangarkeepers),
					hangarkeepersAircraftLimit: conditionallyFormatCurrency({
						value: endorsement.hangarkeepersAircraftLimit,
						hasCoverage: endorsement.hasHangarkeepers,
					}),
					hangarkeepersOccurrenceLimit: conditionallyFormatCurrency({
						value: endorsement.hangarkeepersOccurrenceLimit,
						hasCoverage: endorsement.hasHangarkeepers,
					}),
					hangarkeepersDeductible: conditionallyFormatCurrency({
						value: endorsement.hangarkeepersDeductible,
						hasCoverage: endorsement.hasHangarkeepers,
					}),
					hasHostLiquor: renderCheckbox(endorsement.hasHostLiquor),
					hostLiquorLimit: endorsement.hasHostLiquor
						? formatLimitOrFollowsAircraft(endorsement.hostLiquorLimit)
						: "N/A",
					hasMobileEquipment: renderCheckbox(endorsement.hasMobileEquipment),
					mobileEquipmentLimit: endorsement.hasMobileEquipment
						? formatLimitOrFollowsAircraft(endorsement.mobileEquipmentLimit)
						: "N/A",
					hasPhysicalDamage: renderCheckbox(endorsement.hasPhysicalDamage),
					physicalDamageLimit: conditionallyFormatCurrency({
						value: endorsement.physicalDamageLimit,
						hasCoverage: endorsement.hasPhysicalDamage,
					}),
					physicalDamageDeductible: conditionallyFormatCurrency({
						value: endorsement.physicalDamageDeductible,
						hasCoverage: endorsement.hasPhysicalDamage,
					}),
					hasPolicyTerritory: renderCheckbox(endorsement.hasPolicyTerritory),
					hasPremisesMedical: renderCheckbox(endorsement.hasPremisesMedical),
					premisesMedicalLimit: conditionallyFormatCurrency({
						value: endorsement.premisesMedicalLimit,
						hasCoverage: endorsement.hasPremisesMedical,
					}),
					hasProductsLiabilitySale: renderCheckbox(endorsement.hasProductsLiabilitySale),
					productsLiabilitySaleLimit: endorsement.hasProductsLiabilitySale
						? formatLimitOrFollowsAircraft(endorsement.productsLiabilitySaleLimit)
						: "N/A",
					hasSearchAndRescue: renderCheckbox(endorsement.hasSearchAndRescue),
					runwayFoamingLimit: conditionallyFormatCurrency({
						value: endorsement.runwayFoamingLimit,
						hasCoverage: endorsement.hasSearchAndRescue,
					}),
					searchAndRescueOccurrenceLimit: conditionallyFormatCurrency({
						value: endorsement.searchAndRescueOccurrenceLimit,
						hasCoverage: endorsement.hasSearchAndRescue,
					}),
					hasTripInterruption: renderCheckbox(endorsement.hasTripInterruption),
					tripInterruptionLimit: conditionallyFormatCurrency({
						value: endorsement.tripInterruptionLimit,
						hasCoverage: endorsement.hasTripInterruption,
					}),
					hasUnearnedPremium: renderCheckbox(endorsement.hasUnearnedPremium),
				};
			}
			return {};
		},
		initialize: ({
			hullValue, liabilityLimit, 
		}): typeof BroadFormEndorsementSchema.static => {
			const broadForm = Value.Default(BroadFormEndorsementSchema, {});
			if (Value.Check(BroadFormEndorsementSchema, broadForm)) {
				broadForm.hostLiquorLimit = liabilityLimit;
				broadForm.mobileEquipmentLimit = liabilityLimit;
				broadForm.definitionPremisesPerOccurrenceLimit = liabilityLimit;
				broadForm.productsLiabilitySaleLimit = liabilityLimit;
				broadForm.automaticInsuredValueLimit = hullValue + (hullValue * 0.25);
				return broadForm;
			}
			throw new Error("Failed to initialize Broad Form Endorsement");
		},
	} satisfies EndorsementConfig<typeof BroadFormEndorsementSchema.static>,
	broad_named_insured: {
		label: "Broad Named Insured Endorsement",
		schema: BroadNamedInsuredEndorsementSchema,
		documentName:
            "N AV E CLASSA 0003 05 25 Broad Named Insured Endorsement.docx",
		includeInQuoteTemplate: true,
		renewal: "carry",
		formNumber: "N AV E CLASSA 0003 05 25",
		lob: ["general_aviation", "uas"],
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "broad_named_insured") {
				endorsements.push({
					name: "Broad Named Insured",
					included: true,
					limits: [
						{
							qualifier: "50% ownership clause",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "broad_named_insured") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					broadNamedInsured: endorsement.broadNamedInsured,
				};
			}
			return {};
		},
		initialize: ({ namedInsured }): typeof BroadNamedInsuredEndorsementSchema.static => {
			const broadNamedInsured = Value.Default(BroadNamedInsuredEndorsementSchema, {});
			if (Value.Check(BroadNamedInsuredEndorsementSchema, broadNamedInsured)) {
				broadNamedInsured.broadNamedInsured = namedInsured;
				return broadNamedInsured;
			}
			throw new Error("Failed to initialize Broad Named Insured Endorsement");
		},
	},
	date_change_recognition: {
		label: "Date Change Recognition Endorsement",
		schema: DateChangeRecognitionEndorsementSchema,
		documentName:
      "N AC E CLASSA 0080 05 25 Date Change Recognition Exclusion Endorsement Limited Write-Back Provision.docx",
		formNumber: "N AC E CLASSA 0080 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "date_change_recognition") {
				endorsements.push({
					name: "Date Change Recognition Limited Write-Back",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "date_change_recognition") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	deletion_of_coverage_co_employees: {
		label: "Deletion of Exclusion Endorsement - Co-Employees",
		schema: DeletionOfCoverageCoEmployeesEndorsementSchema,
		documentName: "N AC E CLASSA 0063 05 25 Deletion of Coverage Endorsement Co-Employees.docx",
		formNumber: "N AC E CLASSA 0063 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "deletion_of_coverage_co_employees") {
				endorsements.push({
					name: "Deletion of Exclusion - Co-Employees",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "deletion_of_coverage_co_employees") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
				};
			}
			return {};
		},
	},
	mental_anguish_liability: {
		label: "Mental Anguish Liability Endorsement",
		schema: MentalAnguishLiabilityEndorsementSchema,
		documentName: "N CGL E CLASSA 0001 TBD Mental Anguish Liability Endorsement.docx",
		formNumber: "N CGL E CLASSA 0001 TBD",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "mental_anguish_liability") {
				endorsements.push({
					name: "Mental Anguish Liability",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "mental_anguish_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	deletion_of_exclusion_pd_your_product_work: {
		label: "Deletion of Exclusion Endorsement Property Damage to Your Product and Your Work",
		schema: DeletionOfExclusionPdYourProductWorkEndorsementSchema,
		documentName: "N CGL E CLASSA 0002 TBD Deletion of Exclusion Property Damage to Your Product and Your Work.docx",
		formNumber: "N CGL E CLASSA 0002 TBD",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "deletion_of_exclusion_pd_your_product_work") {
				endorsements.push({
					name: "Deletion of Exclusion - Property Damage to Your Product and Your Work",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "deletion_of_exclusion_pd_your_product_work") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	amendment_limits_products_completed_ops_piston: {
		label: "Amendment of Limits Products-Completed Operations Piston Engine Endorsement",
		schema: AmendmentLimitsProductsCompletedOpsPistonEndorsementSchema,
		documentName: "N CGL E CLASSA 0003 TBD Amendment of Limits Products-Completed Operations Piston Engine.docx",
		formNumber: "N CGL E CLASSA 0003 TBD",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "amendment_limits_products_completed_ops_piston") {
				endorsements.push({
					name: "Amendment of Limits - Products-Completed Operations Piston Engine",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.aggregate,
							amount: endorsement.aggregateLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "amendment_limits_products_completed_ops_piston") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					aggregateLimit: nullishCurrencyOrBlank(endorsement.aggregateLimit),
				};
			}
			return {};
		},
	},
	cgl_communicable_disease_exclusion: {
		label: "Communicable Disease Exclusion",
		schema: CglCommunicableDiseaseExclusionEndorsementSchema,
		documentName: "N AO E CLASSA 0052 05 25 Communicable Disease Exclusion.docx",
		formNumber: "N AO E CLASSA 0052 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_communicable_disease_exclusion") {
				endorsements.push({
					name: "Communicable Disease Exclusion",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_communicable_disease_exclusion") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_data_event_clause: {
		label: "Data Event Clause",
		schema: CglDataEventClauseEndorsementSchema,
		documentName: "N AO E CLASSA 0100 05 25 Data Event Clause.docx",
		formNumber: "N AO E CLASSA 0100 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_data_event_clause") {
				endorsements.push({
					name: "Data Event Clause",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_data_event_clause") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_date_change_recognition: {
		label: "Date Change Recognition Exclusion Limited Write-Back Provision",
		schema: CglDateChangeRecognitionEndorsementSchema,
		documentName:
      "N AO E CLASSA 0062 05 25 Date Change Recognition Exc Ltd Write Back Provision End.docx",
		formNumber: "N AO E CLASSA 0062 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_date_change_recognition") {
				endorsements.push({
					name: "Date Change Recognition Limited Write-Back",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_date_change_recognition") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_pfas_exclusion: {
		label: "Perfluoroalkyl and Polyfluoroalkyl Substances (PFAS) Exclusion",
		schema: CglPfasExclusionEndorsementSchema,
		documentName: "N AO E CLASSA 0103 05 25 PFAS Exclusion.docx",
		formNumber: "N AO E CLASSA 0103 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_pfas_exclusion") {
				endorsements.push({
					name: "PFAS Exclusion",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_pfas_exclusion") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_amendatory_mo: {
		label: "Missouri Amendatory Endorsement",
		schema: CglAmendatoryMoEndorsementSchema,
		documentName: "N AO E CLASSA MO01 05 25 Missouri Amendatory Endorsement.docx",
		formNumber: "N AO E CLASSA MO01 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amendatory_mo") {
				endorsements.push({
					name: "Missouri Amendatory Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amendatory_mo") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_amendatory_nh: {
		label: "New Hampshire Amendatory Endorsement",
		schema: CglAmendatoryNhEndorsementSchema,
		documentName: "N AO E CLASSA NH01 05 25 New Hampshire Amendatory Endorsement.docx",
		formNumber: "N AO E CLASSA NH01 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amendatory_nh") {
				endorsements.push({
					name: "New Hampshire Amendatory Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amendatory_nh") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_amendatory_ny: {
		label: "New York Amendatory Endorsement",
		schema: CglAmendatoryNyEndorsementSchema,
		documentName: "N AO E CLASSA NY01 05 25 New York Amendatory Endorsement.docx",
		formNumber: "N AO E CLASSA NY01 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amendatory_ny") {
				endorsements.push({
					name: "New York Amendatory Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amendatory_ny") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_amendatory_sd: {
		label: "South Dakota Amendatory Endorsement",
		schema: CglAmendatorySdEndorsementSchema,
		documentName: "N AO E CLASSA SD01 05 25 South Dakota Amendatory Endorsement.docx",
		formNumber: "N AO E CLASSA SD01 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amendatory_sd") {
				endorsements.push({
					name: "South Dakota Amendatory Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amendatory_sd") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_amendatory_va: {
		label: "Virginia Amendatory Endorsement",
		schema: CglAmendatoryVaEndorsementSchema,
		documentName: "N AO E CLASSA VA01 05 25 Virginia Amendatory Endorsement.docx",
		formNumber: "N AO E CLASSA VA01 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amendatory_va") {
				endorsements.push({
					name: "Virginia Amendatory Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amendatory_va") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_communicable_disease_exclusion_sd: {
		label: "Communicable Disease Exclusion - South Dakota",
		schema: CglCommunicableDiseaseExclusionSdEndorsementSchema,
		documentName: "N AO E CLASSA 0052SD 05 25 Communicable Disease Exclusion - South Dakota.docx",
		formNumber: "N AO E CLASSA 0052SD 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_communicable_disease_exclusion_sd") {
				endorsements.push({
					name: "Communicable Disease Exclusion - South Dakota",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_communicable_disease_exclusion_sd") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_communicable_disease_exclusion_vt: {
		label: "Communicable Disease Exclusion - Vermont",
		schema: CglCommunicableDiseaseExclusionVtEndorsementSchema,
		documentName: "N AO E CLASSA 0052VT 05 25 Communicable Disease Exclusion - Vermont.docx",
		formNumber: "N AO E CLASSA 0052VT 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_communicable_disease_exclusion_vt") {
				endorsements.push({
					name: "Communicable Disease Exclusion - Vermont",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_communicable_disease_exclusion_vt") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_supplementary_payments: {
		label: "Supplementary Payments Endorsement",
		schema: CglSupplementaryPaymentsEndorsementSchema,
		documentName: "N AO E CLASSA 0065 05 25 Supplementary Payments Endorsement.docx",
		formNumber: "N AO E CLASSA 0065 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_supplementary_payments") {
				endorsements.push({
					name: "Supplementary Payments",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_supplementary_payments") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_host_liquor_extension: {
		label: "Host Liquor Coverage Extension Endorsement",
		schema: CglHostLiquorExtensionEndorsementSchema,
		documentName: "N AO E CLASSA 0021 05 25 Host Liquor Cove Ext End.docx",
		formNumber: "N AO E CLASSA 0021 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_host_liquor_extension") {
				endorsements.push({
					name: "Host Liquor Coverage Extension",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_host_liquor_extension") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_coverage_territory_worldwide: {
		label: "Amendment of Coverage Territory - Worldwide Coverage",
		schema: CglCoverageTerritoryWorldwideEndorsementSchema,
		documentName: "N AO E CLASSA 0023 05 25 Amend Cov Territory Worldwide.docx",
		formNumber: "N AO E CLASSA 0023 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_coverage_territory_worldwide") {
				endorsements.push({
					name: "Amendment of Coverage Territory - Worldwide Coverage",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_coverage_territory_worldwide") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_referral_liability: {
		label: "Referral Liability Endorsement",
		schema: CglReferralLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0041 05 25 Referral Liability End.docx",
		formNumber: "N AO E CLASSA 0041 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_referral_liability") {
				endorsements.push({
					name: "Referral Liability",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_referral_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_personal_advertising_injury_extension: {
		label: "Personal and Advertising Injury Extension Endorsement",
		schema: CglPersonalAdvertisingInjuryExtensionEndorsementSchema,
		documentName: "N AO E CLASSA 0042 05 25 Personal and Advertising Injury Ext End.docx",
		formNumber: "N AO E CLASSA 0042 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			OK: {
				documentName: "N AO E CLASSA 0042OK 05 25 Personal and Advertising Injury Ext End - Oklahoma.docx",
				formNumber: "N AO E CLASSA 0042OK 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_personal_advertising_injury_extension") {
				endorsements.push({
					name: "Personal and Advertising Injury Extension",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_personal_advertising_injury_extension") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_deletion_exclusion_pd_product_work: {
		label: "Deletion of Exclusion Endorsement - Property Damage to Your Product and Your Work",
		schema: CglDeletionExclusionPdProductWorkEndorsementSchema,
		documentName: "N AO E CLASSA 0044 05 25 Deletion of Exc End Property Damage Your Product Your Work.docx",
		formNumber: "N AO E CLASSA 0044 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_deletion_exclusion_pd_product_work") {
				endorsements.push({
					name: "Deletion of Exclusion - Property Damage to Your Product and Your Work",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_deletion_exclusion_pd_product_work") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_deletion_exclusion_co_employees: {
		label: "Deletion of Exclusion Endorsement - Co-Employees",
		schema: CglDeletionExclusionCoEmployeesEndorsementSchema,
		documentName: "N AO E CLASSA 0045 05 25 Deletion of Exc End CoEmployees.docx",
		formNumber: "N AO E CLASSA 0045 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_deletion_exclusion_co_employees") {
				endorsements.push({
					name: "Deletion of Exclusion - Co-Employees",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_deletion_exclusion_co_employees") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_incidental_medical_malpractice: {
		label: "Incidental Medical Malpractice Liability Endorsement",
		schema: CglIncidentalMedicalMalpracticeEndorsementSchema,
		documentName: "N AO E CLASSA 0049 05 25 Incidental Medical Malpractice Liability.docx",
		formNumber: "N AO E CLASSA 0049 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_incidental_medical_malpractice") {
				endorsements.push({
					name: "Incidental Medical Malpractice Liability",
					included: true,
					limits: [
						{
							qualifier: "Per occurrence and aggregate",
							amount: endorsement.incidentalMalpracticeLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_incidental_medical_malpractice") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					incidentalMalpracticeLimit: formatInt(endorsement.incidentalMalpracticeLimit),
				};
			}
			return {};
		},
	},
	cgl_deletion_exclusions_restaurants_catering: {
		label: "Deletion of Exclusions Endorsement - Restaurants and Catering Operations",
		schema: CglDeletionExclusionsRestaurantsCateringEndorsementSchema,
		documentName: "N AO E CLASSA 0055 05 25 Deletion of Exclusions Restaurant and Catering.docx",
		formNumber: "N AO E CLASSA 0055 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_deletion_exclusions_restaurants_catering") {
				endorsements.push({
					name: "Deletion of Exclusions - Restaurants and Catering Operations",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_deletion_exclusions_restaurants_catering") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_additional_insured_designated: {
		label: "Additional Insured Endorsement - Designated Person or Organization",
		schema: CglAdditionalInsuredDesignatedEndorsementSchema,
		documentName: "N AO E CLASSA 0081 05 25 Addtl Insured End Designated Person or Org.docx",
		formNumber: "N AO E CLASSA 0081 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_designated") {
				endorsements.push({
					name: "Additional Insured - Designated Person or Organization",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_designated") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
					designatedPersonsOrganizations: endorsement.designatedPersonsOrganizations,
				};
			}
			return {};
		},
	},
	cgl_waiver_transfer_rights_recovery: {
		label: "Waiver of Transfer of Rights of Recovery Against Others to Us",
		schema: CglWaiverTransferRightsRecoveryEndorsementSchema,
		documentName: "N AO E CLASSA 0097 05 25 Waiver of Tranfer Rights Recovery to Us.docx",
		formNumber: "N AO E CLASSA 0097 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_waiver_transfer_rights_recovery") {
				endorsements.push({
					name: "Waiver of Transfer of Rights of Recovery Against Others to Us",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_waiver_transfer_rights_recovery") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					waiverPersonOrOrganization: endorsement.waiverPersonOrOrganization,
				};
			}
			return {};
		},
	},
	cgl_exclusion_products_completed_ops: {
		label: "Exclusion - Products-Completed Operations Hazard",
		schema: CglExclusionProductsCompletedOpsEndorsementSchema,
		documentName: "N AO E CLASSA 0066 05 25 Exc Product Completed Ops Hazard.docx",
		formNumber: "N AO E CLASSA 0066 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_products_completed_ops") {
				endorsements.push({
					name: "Exclusion - Products-Completed Operations Hazard",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_products_completed_ops") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_exclusion_medical_payments: {
		label: "Exclusion - Medical Payments",
		schema: CglExclusionMedicalPaymentsEndorsementSchema,
		documentName: "N AO E CLASSA 0068 05 25 Exc Medical Payments.docx",
		formNumber: "N AO E CLASSA 0068 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_medical_payments") {
				endorsements.push({
					name: "Exclusion - Medical Payments",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_medical_payments") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
				};
			}
			return {};
		},
	},
	cgl_exclusion_damage_to_premises_rented: {
		label: "Exclusion - Damage To Premises Rented To You",
		schema: CglExclusionDamageToPremisesRentedEndorsementSchema,
		documentName: "N AO E CLASSA 0069 05 25 Exc Damage to Premises Rented to You.docx",
		formNumber: "N AO E CLASSA 0069 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_damage_to_premises_rented") {
				endorsements.push({
					name: "Exclusion - Damage To Premises Rented To You",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_damage_to_premises_rented") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
				};
			}
			return {};
		},
	},
	cgl_exclusion_personal_advertising_injury: {
		label: "Exclusion - Personal and Advertising Injury Liability",
		schema: CglExclusionPersonalAdvertisingInjuryEndorsementSchema,
		documentName: "N AO E CLASSA 0070 05 25 Exc Personal and Advertising Injury Liab.docx",
		formNumber: "N AO E CLASSA 0070 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_personal_advertising_injury") {
				endorsements.push({
					name: "Exclusion - Personal and Advertising Injury Liability",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_personal_advertising_injury") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
				};
			}
			return {};
		},
	},
	cgl_exclusion_hangarkeepers_liability: {
		label: "Exclusion - Hangarkeeper's Liability",
		schema: CglExclusionHangarkeepersLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0071 05 25 Exc Hangarkeepers Liability.docx",
		formNumber: "N AO E CLASSA 0071 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_hangarkeepers_liability") {
				endorsements.push({
					name: "Exclusion - Hangarkeeper's Liability",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_hangarkeepers_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
				};
			}
			return {};
		},
	},
	cgl_exclusion_mobile_equipment: {
		label: "Exclusion - Mobile Equipment",
		schema: CglExclusionMobileEquipmentEndorsementSchema,
		documentName: "N AO E CLASSA 0072 05 25 Exclusion Mobile Equipment.docx",
		formNumber: "N AO E CLASSA 0072 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_mobile_equipment") {
				endorsements.push({
					name: "Exclusion - Mobile Equipment",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_mobile_equipment") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_limited_coverage_designated_products: {
		label: "Limited Coverage for Designated Products or Work",
		schema: CglLimitedCoverageDesignatedProductsEndorsementSchema,
		documentName: "N AO E CLASSA 0073 05 25 Limitation of Cov to Designated Products Work.docx",
		formNumber: "N AO E CLASSA 0073 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_limited_coverage_designated_products") {
				endorsements.push({
					name: "Limited Coverage for Designated Products or Work",
					included: true,
					limits: [
						{
							qualifier: `${qualifierLabels.aggregate} - as respects your product`,
							amount: endorsement.yourProductAggregateLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.aggregate} - as respects your work`,
							amount: endorsement.yourWorkAggregateLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_limited_coverage_designated_products") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedProductsOrWork: endorsement.designatedProductsOrWork,
					yourProductAggregateLimit: formatInt(endorsement.yourProductAggregateLimit),
					yourWorkAggregateLimit: formatInt(endorsement.yourWorkAggregateLimit),
				};
			}
			return {};
		},
	},
	cgl_exclusion_host_liquor: {
		label: "Exclusion - Host Liquor Liability",
		schema: CglExclusionHostLiquorEndorsementSchema,
		documentName: "N AO E CLASSA 0076 05 25 Exclusion Host Liquor Liability.docx",
		formNumber: "N AO E CLASSA 0076 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			VT: {
				documentName: "N AO E CLASSA 0076VT 05 25 Exclusion Host Liquor Liability - Vermont.docx",
				formNumber: "N AO E CLASSA 0076VT 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_host_liquor") {
				endorsements.push({
					name: "Exclusion - Host Liquor Liability",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_host_liquor") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_pollution_product_sold: {
		label: "Pollution or Contamination of the Product Sold or Supplied",
		schema: CglPollutionProductSoldEndorsementSchema,
		documentName: "N AO E CLASSA 0025 05 25 Pollution Contamination of Product Sold Supplied.docx",
		formNumber: "N AO E CLASSA 0025 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_pollution_product_sold") {
				endorsements.push({
					name: "Pollution or Contamination of the Product Sold or Supplied",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_pollution_product_sold") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_cancellation_clause_amendment: {
		label: "Cancellation Clause Amendment Endorsement",
		schema: CglCancellationClauseAmendmentEndorsementSchema,
		documentName: "N AO E CLASSA 0059 05 25 Cancellation Clause Amend End.docx",
		formNumber: "N AO E CLASSA 0059 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			VA: {
				documentName: "N AO E CLASSA 0059VA 05 25 Cancellation Clause Amend End - VA.docx",
				formNumber: "N AO E CLASSA 0059VA 05 25",
			},
			MO: {
				documentName: "N AO E CLASSA 0059MO 05 25 Cancellation Clause Amend End - Missouri.docx",
				formNumber: "N AO E CLASSA 0059MO 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_cancellation_clause_amendment") {
				endorsements.push({
					name: "Cancellation Clause Amendment Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_cancellation_clause_amendment") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					nonpaymentNoticeDays: endorsement.nonpaymentNoticeDays,
					otherReasonNoticeDays: endorsement.otherReasonNoticeDays,
				};
			}
			return {};
		},
	},
	cgl_supplementary_payments_limitation: {
		label: "Supplementary Payments Limitation Endorsement",
		schema: CglSupplementaryPaymentsLimitationEndorsementSchema,
		documentName: "N AO E CLASSA 0064 05 25 Supplementary Payments Limitation End.docx",
		formNumber: "N AO E CLASSA 0064 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			OK: {
				documentName: "N AO E CLASSA 0064OK 05 25 Supplementary Payments Limitation End - Oklahoma.docx",
				formNumber: "N AO E CLASSA 0064OK 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_supplementary_payments_limitation") {
				endorsements.push({
					name: "Supplementary Payments Limitation Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_supplementary_payments_limitation") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_overhead_supervision: {
		label: "Overhead and Supervision Amendment",
		schema: CglOverheadSupervisionEndorsementSchema,
		documentName: "N AO E CLASSA 0057 05 25 Overhead Supervision Amend.docx",
		formNumber: "N AO E CLASSA 0057 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_overhead_supervision") {
				endorsements.push({
					name: "Overhead and Supervision Amendment",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_overhead_supervision") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					locations: endorsement.locations,
				};
			}
			return {};
		},
	},
	cgl_overhead_supervision_specified_rate: {
		label: "Overhead and Supervision Amendment - Specified Rate",
		schema: CglOverheadSupervisionSpecifiedRateEndorsementSchema,
		documentName: "N AO E CLASSA 0058 05 25 Overhead and Supervision Amend Specified Rate.docx",
		formNumber: "N AO E CLASSA 0058 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_overhead_supervision_specified_rate") {
				endorsements.push({
					name: "Overhead and Supervision Amendment - Specified Rate",
					included: true,
					limits: [
						{
							qualifier: "Straight time labor rate per hour",
							amount: endorsement.straightTimeHourlyRate,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_overhead_supervision_specified_rate") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					locations: endorsement.locations,
					straightTimeHourlyRate: formatInt(endorsement.straightTimeHourlyRate),
				};
			}
			return {};
		},
	},
	cgl_contract_reporting_provision: {
		label: "Contract Reporting Provision",
		schema: CglContractReportingProvisionEndorsementSchema,
		documentName: "N AO E CLASSA 0063 05 25 Contract Reporting Provision.docx",
		formNumber: "N AO E CLASSA 0063 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_contract_reporting_provision") {
				endorsements.push({
					name: "Contract Reporting Provision",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_contract_reporting_provision") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_schedule_of_contracts: {
		label: "Schedule of Contracts Endorsement",
		schema: CglScheduleOfContractsEndorsementSchema,
		documentName: "N AO E CLASSA 0048 05 25 Schedule of Contracts End.docx",
		formNumber: "N AO E CLASSA 0048 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_schedule_of_contracts") {
				endorsements.push({
					name: "Schedule of Contracts Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_schedule_of_contracts") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_waiver_governmental_immunity: {
		label: "Waiver of Governmental Immunity Defense Endorsement",
		schema: CglWaiverGovernmentalImmunityEndorsementSchema,
		documentName: "N AO E CLASSA 0080 05 25 Waiver of Gov Immunity Defense End.docx",
		formNumber: "N AO E CLASSA 0080 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_waiver_governmental_immunity") {
				endorsements.push({
					name: "Waiver of Governmental Immunity Defense Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_waiver_governmental_immunity") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_non_owned_aircraft_liability_airport_operator: {
		label: "Non-Owned Aircraft Liability Endorsement - Airport Owner / Operator",
		schema: CglNonOwnedAircraftLiabilityAirportOperatorEndorsementSchema,
		documentName: "N AO E CLASSA 0038 05 25 Non Owned AC Liability End - Airport Owner Operator.docx",
		formNumber: "N AO E CLASSA 0038 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			WA: {
				documentName: "N AO E CLASSA 0038WA 05 25 Non Owned AC Liability End - Airport Owner Operator - Washington.docx",
				formNumber: "N AO E CLASSA 0038WA 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_non_owned_aircraft_liability_airport_operator") {
				endorsements.push({
					name: "Non-Owned Aircraft Liability Endorsement - Airport Owner / Operator",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.each_person} (passenger)`,
							amount: endorsement.passengerEachPersonLimit === 0 ? null : endorsement.passengerEachPersonLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_non_owned_aircraft_liability_airport_operator") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					scheduledLocations: endorsement.scheduledLocations,
					eachOccurrenceLimit: formatInt(endorsement.eachOccurrenceLimit),
					passengerEachPersonLimit: endorsement.passengerEachPersonLimit === 0 ? "" : formatInt(endorsement.passengerEachPersonLimit),
					maxSeats: endorsement.maxSeats === 0 ? "" : formatInt(endorsement.maxSeats),
					maxSeatsUnder12500Pounds: endorsement.maxSeatsUnder12500Pounds === 0 ? "" : formatInt(endorsement.maxSeatsUnder12500Pounds),
				};
			}
			return {};
		},
	},
	cgl_non_owned_aircraft_liability: {
		label: "Non-Owned Aircraft Liability Endorsement",
		schema: CglNonOwnedAircraftLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0039 05 25 Non Owned Aircraft Liability End.docx",
		formNumber: "N AO E CLASSA 0039 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			WA: {
				documentName: "N AO E CLASSA 0039WA 05 25 Non Owned Aircraft Liability End.docx",
				formNumber: "N AO E CLASSA 0039WA 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_non_owned_aircraft_liability") {
				endorsements.push({
					name: "Non-Owned Aircraft Liability Endorsement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.each_person} (passenger)`,
							amount: endorsement.passengerEachPersonLimit === 0 ? null : endorsement.passengerEachPersonLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_non_owned_aircraft_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceLimit: formatInt(endorsement.eachOccurrenceLimit),
					passengerEachPersonLimit: endorsement.passengerEachPersonLimit === 0 ? "" : formatInt(endorsement.passengerEachPersonLimit),
					aircraftWingType: endorsement.aircraftWingType,
					airworthinessCategory: endorsement.airworthinessCategory,
					maxSeats: endorsement.maxSeats === 0 ? "" : formatInt(endorsement.maxSeats),
					aircraftWingTypeUnder12500Pounds: endorsement.aircraftWingTypeUnder12500Pounds,
					airworthinessCategoryUnder12500Pounds: endorsement.airworthinessCategoryUnder12500Pounds,
					maxSeatsUnder12500Pounds: endorsement.maxSeatsUnder12500Pounds === 0 ? "" : formatInt(endorsement.maxSeatsUnder12500Pounds),
				};
			}
			return {};
		},
	},
	cgl_excess_auto_liability: {
		label: "Excess Auto Liability Coverage Endorsement",
		schema: CglExcessAutoLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0050 05 25 Excess Auto Liability Cov End.docx",
		formNumber: "N AO E CLASSA 0050 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_excess_auto_liability") {
				endorsements.push({
					name: "Excess Auto Liability Coverage Endorsement",
					included: true,
					limits: [
						{
							qualifier: `${qualifierLabels.each_occurrence}, excess of $1,000,000`,
							amount: endorsement.excessLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_excess_auto_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					excessLimit: formatCurrency(endorsement.excessLimit),
					primaryInsurer: endorsement.primaryInsurer,
					primaryPolicyNumber: endorsement.primaryPolicyNumber,
					primaryLimit: formatCurrency(endorsement.primaryLimit),
				};
			}
			return {};
		},
	},
	cgl_excess_employers_liability: {
		label: "Excess Employer’s Liability Coverage Endorsement",
		schema: CglExcessEmployersLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0051 05 25 Excess Employers Liab Cov End.docx",
		formNumber: "N AO E CLASSA 0051 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_excess_employers_liability") {
				endorsements.push({
					name: "Excess Employer’s Liability Coverage Endorsement",
					included: true,
					limits: [
						{
							qualifier: `${qualifierLabels.each_occurrence}, excess of $1,000,000 or the statutory minimum`,
							amount: endorsement.excessLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_excess_employers_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					excessLimit: formatCurrency(endorsement.excessLimit),
					primaryInsurer: endorsement.primaryInsurer,
					primaryPolicyNumber: endorsement.primaryPolicyNumber,
					primaryLimit: formatCurrency(endorsement.primaryLimit),
				};
			}
			return {};
		},
	},
	cgl_garagekeepers_liability: {
		label: "Garagekeepers Liability",
		schema: CglGaragekeepersLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0053 05 25 Garagekeepers Liability.docx",
		formNumber: "N AO E CLASSA 0053 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_garagekeepers_liability") {
				endorsements.push({
					name: "Garagekeepers Liability",
					included: true,
					limits: [
						{
							qualifier: "Any one auto",
							amount: endorsement.anyOneAutoLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Any one loss",
							amount: endorsement.anyOneLossLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} each auto`,
							amount: endorsement.eachAutoDeductible,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_garagekeepers_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					scheduledLocations: endorsement.scheduledLocations,
					anyOneAutoLimit: formatInt(endorsement.anyOneAutoLimit),
					anyOneLossLimit: formatInt(endorsement.anyOneLossLimit),
					eachAutoDeductible: formatInt(endorsement.eachAutoDeductible),
				};
			}
			return {};
		},
	},
	cgl_airport_ops_area_auto: {
		label: "Airport Operations Area Auto Coverage Endorsement",
		schema: CglAirportOpsAreaAutoEndorsementSchema,
		documentName: "N AO E CLASSA 0054 05 25 Airport Ops Area Auto Cov End.docx",
		formNumber: "N AO E CLASSA 0054 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_airport_ops_area_auto") {
				endorsements.push({
					name: "Airport Operations Area Auto Coverage Endorsement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_airport_ops_area_auto") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceLimit: formatInt(endorsement.eachOccurrenceLimit),
				};
			}
			return {};
		},
	},
	cgl_extended_mobile_equipment_off_airport: {
		label: "Extended Mobile Equipment Coverage – Off Airport Premises Endorsement",
		schema: CglExtendedMobileEquipmentOffAirportEndorsementSchema,
		documentName: "N AO E CLASSA 0056 05 25 Ext Mobile Eqpt Off Airport Premises End.docx",
		formNumber: "N AO E CLASSA 0056 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_extended_mobile_equipment_off_airport") {
				endorsements.push({
					name: "Extended Mobile Equipment Coverage – Off Airport Premises Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_extended_mobile_equipment_off_airport") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					scheduledLocations: endorsement.scheduledLocations,
				};
			}
			return {};
		},
	},
	cgl_financed_premium: {
		label: "Financed Premium Endorsement",
		schema: CglFinancedPremiumEndorsementSchema,
		documentName: "N AO E CLASSA 0099 05 25 Financed Premium End.docx",
		formNumber: "N AO E CLASSA 0099 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_financed_premium") {
				endorsements.push({
					name: "Financed Premium Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_financed_premium") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					lenderName: endorsement.lenderName,
					lenderAddressLine1: endorsement.lenderAddressLine1,
					lenderAddressLine2: endorsement.lenderAddressLine2,
					lenderPhone: endorsement.lenderPhone,
				};
			}
			return {};
		},
	},
	cgl_broad_coverage: {
		label: "Broad Coverage Endorsement",
		schema: CglBroadCoverageEndorsementSchema,
		documentName: "N AO E CLASSA 0017 05 25 Broad Coverage End.docx",
		formNumber: "N AO E CLASSA 0017 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_broad_coverage") {
				endorsements.push({
					name: "Broad Coverage Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_broad_coverage") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					incidentalMedicalMalpracticeLimit: formatInt(endorsement.incidentalMedicalMalpracticeLimit),
				};
			}
			return {};
		},
	},
	cgl_broad_coverage_change: {
		label: "Broad Coverage Change Endorsement",
		schema: CglBroadCoverageChangeEndorsementSchema,
		documentName: "N AO E CLASSA 0018 05 25 Broad Coverage Change End.docx",
		formNumber: "N AO E CLASSA 0018 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			VA: {
				documentName: "N AO E CLASSA 0018VA 05 25 Broad Coverage Change End - VA.docx",
				formNumber: "N AO E CLASSA 0018VA 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_broad_coverage_change") {
				endorsements.push({
					name: "Broad Coverage Change Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_broad_coverage_change") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					incidentalMedicalMalpracticeLimit: formatInt(endorsement.incidentalMedicalMalpracticeLimit),
				};
			}
			return {};
		},
	},
	cgl_war_hijacking_write_back: {
		label: "War, Hijacking and Other Perils Limited Liability Write-Back Endorsement",
		schema: CglWarHijackingWriteBackEndorsementSchema,
		documentName: "N AO E CLASSA 0033 05 25 War Hijacking Other Perils Ltd Liability Write Back End.docx",
		formNumber: "N AO E CLASSA 0033 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			DC: {
				documentName: "N AO E CLASSA 0033DC 05 25 War Hijacking Other Perils Ltd Liability Write Back End - District of Columbia.docx",
				formNumber: "N AO E CLASSA 0033DC 05 25",
			},
			VA: {
				documentName: "N AO E CLASSA 0033VA 05 25 War Hijacking Other Perils Ltd Liability Write Back End - VA.docx",
				formNumber: "N AO E CLASSA 0033VA 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_war_hijacking_write_back") {
				endorsements.push({
					name: "War, Hijacking and Other Perils Limited Liability Write-Back Endorsement",
					included: true,
					limits: [
						{
							qualifier: `${qualifierLabels.any_one_occurrence} and annual aggregate`,
							amount: endorsement.warWriteBackSubLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_war_hijacking_write_back") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					warWriteBackSubLimit: formatInt(endorsement.warWriteBackSubLimit),
				};
			}
			return {};
		},
	},
	cgl_grounding_liability: {
		label: "Grounding Liability Endorsement",
		schema: CglGroundingLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0028 05 25 Grounding Liability.docx",
		formNumber: "N AO E CLASSA 0028 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_grounding_liability") {
				endorsements.push({
					name: "Grounding Liability Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Each grounding and grounding annual aggregate",
							amount: endorsement.groundingLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_grounding_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					groundingLimit: formatInt(endorsement.groundingLimit),
				};
			}
			return {};
		},
	},
	cgl_hangarkeepers_work_in_progress: {
		label: "Hangarkeeper's Liability - Extension of Coverage for Work in Progress",
		schema: CglHangarkeepersWorkInProgressEndorsementSchema,
		documentName: "N AO E CLASSA 0030 05 25 Hangarkeepers Liability Ext Cov for Work in Progress.docx",
		formNumber: "N AO E CLASSA 0030 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_hangarkeepers_work_in_progress") {
				endorsements.push({
					name: "Hangarkeeper's Liability - Extension of Coverage for Work in Progress",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_loss,
							amount: endorsement.workInProgressLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} — each loss`,
							amount: endorsement.workInProgressDeductible,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_hangarkeepers_work_in_progress") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					workInProgressLimit: formatInt(endorsement.workInProgressLimit),
					workInProgressDeductible: formatInt(endorsement.workInProgressDeductible),
				};
			}
			return {};
		},
	},
	cgl_extended_hangarkeepers_in_flight: {
		label: "Extended Hangarkeeper's Liability - In Flight Physical Damage to Non-Owned Aircraft Coverage Endorsement",
		schema: CglExtendedHangarkeepersInFlightEndorsementSchema,
		documentName: "N AO E CLASSA 0040 05 25 Extended Hangarkeepers Liability In Flight Physical Damage Non Owned.docx",
		formNumber: "N AO E CLASSA 0040 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_extended_hangarkeepers_in_flight") {
				endorsements.push({
					name: "Extended Hangarkeeper's Liability - In Flight Physical Damage to Non-Owned Aircraft Coverage Endorsement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.inFlightEachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} — each In Flight loss`,
							amount: endorsement.inFlightDeductible,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_extended_hangarkeepers_in_flight") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					inFlightEachOccurrenceLimit: formatInt(endorsement.inFlightEachOccurrenceLimit),
					inFlightDeductible: formatInt(endorsement.inFlightDeductible),
				};
			}
			return {};
		},
	},
	cgl_geographic_areas_exclusion: {
		label: "Geographic Areas Exclusion Clause",
		schema: CglGeographicAreasExclusionEndorsementSchema,
		documentName: "N AO E CLASSA 0034 05 25 Geographic Exc Clause.docx",
		formNumber: "N AO E CLASSA 0034 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_geographic_areas_exclusion") {
				endorsements.push({
					name: "Geographic Areas Exclusion Clause",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_geographic_areas_exclusion") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_amendment_limits_designated: {
		label: "Amendment of Limits of Insurance (Per Person Sublimits)",
		schema: CglAmendmentLimitsDesignatedEndorsementSchema,
		documentName: "N AO E CLASSA 0003 05 25 Amend Limits of Insurance.docx",
		formNumber: "N AO E CLASSA 0003 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amendment_limits_designated") {
				const limits: Endorsement["limits"] = [];
				if (endorsement.eachOccurrenceLimit) {
					limits.push({
						qualifier: qualifierLabels.each_occurrence,
						amount: endorsement.eachOccurrenceLimit,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.eachPersonMaximumExcludingProductsCompletedOperations) {
					limits.push({
						qualifier: "Maximum each person (excluding products-completed operations)",
						amount: endorsement.eachPersonMaximumExcludingProductsCompletedOperations,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.productsCompletedOperationsAggregate) {
					limits.push({
						qualifier: "Products/completed operations aggregate",
						amount: endorsement.productsCompletedOperationsAggregate,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.damageToPremisesRentedToYou) {
					limits.push({
						qualifier: "Damage to premises rented to you — each premises",
						amount: endorsement.damageToPremisesRentedToYou,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.personalAndAdvertisingInjuryAggregate) {
					limits.push({
						qualifier: "Personal & advertising injury aggregate",
						amount: endorsement.personalAndAdvertisingInjuryAggregate,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.medicalExpenseEachPerson) {
					limits.push({
						qualifier: "Medical expense — each person",
						amount: endorsement.medicalExpenseEachPerson,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.hangarkeepersEachLoss) {
					limits.push({
						qualifier: "Hangarkeeper's liability — each loss",
						amount: endorsement.hangarkeepersEachLoss,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.hangarkeepersEachAircraft) {
					limits.push({
						qualifier: "Hangarkeeper's liability — each aircraft",
						amount: endorsement.hangarkeepersEachAircraft,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (limits.length === 0) {
					limits.push({
						qualifier: "Included",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				endorsements.push({
					name: "Amendment of Limits of Insurance (Per Person Sublimits)",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amendment_limits_designated") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceLimit: formatCurrency(endorsement.eachOccurrenceLimit),
					eachPersonMaximumExcludingProductsCompletedOperations: formatCurrency(endorsement.eachPersonMaximumExcludingProductsCompletedOperations),
					damageToPremisesRentedToYou: formatCurrency(endorsement.damageToPremisesRentedToYou),
					productsCompletedOperationsAggregate: formatCurrency(endorsement.productsCompletedOperationsAggregate),
					productsCompletedOperationsEachPersonMaximum: formatCurrency(endorsement.productsCompletedOperationsEachPersonMaximum),
					personalAndAdvertisingInjuryAggregate: formatCurrency(endorsement.personalAndAdvertisingInjuryAggregate),
					personalAndAdvertisingInjuryEachPersonMaximum: formatCurrency(endorsement.personalAndAdvertisingInjuryEachPersonMaximum),
					medicalExpenseEachPerson: formatCurrency(endorsement.medicalExpenseEachPerson),
					hangarkeepersEachLoss: formatCurrency(endorsement.hangarkeepersEachLoss),
					hangarkeepersEachAircraft: formatCurrency(endorsement.hangarkeepersEachAircraft),
					deductibleTurbineAircraft: formatCurrency(endorsement.deductibleTurbineAircraft),
					deductibleOtherAircraft: formatCurrency(endorsement.deductibleOtherAircraft),
					propertyDamageDeductibleTurbineAircraft: formatCurrency(endorsement.propertyDamageDeductibleTurbineAircraft),
					propertyDamageDeductibleOtherAircraft: formatCurrency(endorsement.propertyDamageDeductibleOtherAircraft),
					propertyDamageDeductibleNonAircraftClaim: formatCurrency(endorsement.propertyDamageDeductibleNonAircraftClaim),
				};
			}
			return {};
		},
	},
	cgl_amendment_limits: {
		label: "Amendment of Limits of Insurance",
		schema: CglAmendmentLimitsEndorsementSchema,
		documentName: "N AO E CLASSA 0004 05 25 Amend Limits of Insurance.docx",
		formNumber: "N AO E CLASSA 0004 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amendment_limits") {
				const limits: Endorsement["limits"] = [];
				if (endorsement.eachOccurrenceAllHazards) {
					limits.push({
						qualifier: "Each occurrence (all hazards combined)",
						amount: endorsement.eachOccurrenceAllHazards,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.eachOccurrenceExcludingProductsCompletedOperations) {
					limits.push({
						qualifier: "Each occurrence (excluding products-completed operations)",
						amount: endorsement.eachOccurrenceExcludingProductsCompletedOperations,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.eachOccurrenceProductsCompletedOperations) {
					limits.push({
						qualifier: "Each occurrence – products/completed operations",
						amount: endorsement.eachOccurrenceProductsCompletedOperations,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.productsCompletedOperationsAggregate) {
					limits.push({
						qualifier: "Products/completed operations aggregate",
						amount: endorsement.productsCompletedOperationsAggregate,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.damageToPremisesRentedToYou) {
					limits.push({
						qualifier: "Damage to premises rented to you — each premises",
						amount: endorsement.damageToPremisesRentedToYou,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.personalAndAdvertisingInjuryAggregate) {
					limits.push({
						qualifier: "Personal & advertising injury aggregate",
						amount: endorsement.personalAndAdvertisingInjuryAggregate,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.medicalExpenseEachPerson) {
					limits.push({
						qualifier: "Medical expense — each person",
						amount: endorsement.medicalExpenseEachPerson,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.hangarkeepersEachLoss) {
					limits.push({
						qualifier: "Hangarkeeper's liability — each loss",
						amount: endorsement.hangarkeepersEachLoss,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.hangarkeepersEachAircraft) {
					limits.push({
						qualifier: "Hangarkeeper's liability — each aircraft",
						amount: endorsement.hangarkeepersEachAircraft,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (limits.length === 0) {
					limits.push({
						qualifier: "Included",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				endorsements.push({
					name: "Amendment of Limits of Insurance",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amendment_limits") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceAllHazards: formatCurrency(endorsement.eachOccurrenceAllHazards),
					eachOccurrenceExcludingProductsCompletedOperations: formatCurrency(endorsement.eachOccurrenceExcludingProductsCompletedOperations),
					eachOccurrenceProductsCompletedOperations: formatCurrency(endorsement.eachOccurrenceProductsCompletedOperations),
					productsCompletedOperationsAggregate: formatCurrency(endorsement.productsCompletedOperationsAggregate),
					damageToPremisesRentedToYou: formatCurrency(endorsement.damageToPremisesRentedToYou),
					personalAndAdvertisingInjuryAggregate: formatCurrency(endorsement.personalAndAdvertisingInjuryAggregate),
					medicalExpenseEachPerson: formatCurrency(endorsement.medicalExpenseEachPerson),
					hangarkeepersEachLoss: formatCurrency(endorsement.hangarkeepersEachLoss),
					hangarkeepersEachAircraft: formatCurrency(endorsement.hangarkeepersEachAircraft),
					deductibleTurbineAircraft: formatInt(endorsement.deductibleTurbineAircraft),
					propertyDamageDeductibleTurbineAircraft: formatCurrency(endorsement.propertyDamageDeductibleTurbineAircraft),
					propertyDamageDeductibleOtherAircraft: formatCurrency(endorsement.propertyDamageDeductibleOtherAircraft),
					propertyDamageDeductibleNonAircraftClaim: formatCurrency(endorsement.propertyDamageDeductibleNonAircraftClaim),
				};
			}
			return {};
		},
	},
	cgl_increased_limits_designated_persons: {
		label: "Increased Limits for Designated Person(s) or Organization(s)",
		schema: CglIncreasedLimitsDesignatedPersonsEndorsementSchema,
		documentName: "N AO E CLASSA 0005 05 25 Increased Limits DP or Org.docx",
		formNumber: "N AO E CLASSA 0005 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_increased_limits_designated_persons") {
				const limits: Endorsement["limits"] = [];
				if (endorsement.eachOccurrenceAllHazards) {
					limits.push({
						qualifier: "Each occurrence (all hazards combined)",
						amount: endorsement.eachOccurrenceAllHazards,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.eachOccurrenceExcludingProductsCompletedOperations) {
					limits.push({
						qualifier: "Each occurrence (excluding products-completed operations)",
						amount: endorsement.eachOccurrenceExcludingProductsCompletedOperations,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.eachOccurrenceProductsCompletedOperations) {
					limits.push({
						qualifier: "Each occurrence – products/completed operations",
						amount: endorsement.eachOccurrenceProductsCompletedOperations,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.productsCompletedOperationsAggregate) {
					limits.push({
						qualifier: "Products/completed operations aggregate",
						amount: endorsement.productsCompletedOperationsAggregate,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.damageToPremisesRentedToYou) {
					limits.push({
						qualifier: "Damage to premises rented to you — each premises",
						amount: endorsement.damageToPremisesRentedToYou,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.personalAndAdvertisingInjuryAggregate) {
					limits.push({
						qualifier: "Personal & advertising injury aggregate",
						amount: endorsement.personalAndAdvertisingInjuryAggregate,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.medicalExpenseEachPerson) {
					limits.push({
						qualifier: "Medical expense — each person",
						amount: endorsement.medicalExpenseEachPerson,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.hangarkeepersEachLoss) {
					limits.push({
						qualifier: "Hangarkeeper's liability — each loss",
						amount: endorsement.hangarkeepersEachLoss,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.hangarkeepersEachAircraft) {
					limits.push({
						qualifier: "Hangarkeeper's liability — each aircraft",
						amount: endorsement.hangarkeepersEachAircraft,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (limits.length === 0) {
					limits.push({
						qualifier: "Included",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				endorsements.push({
					name: "Increased Limits for Designated Person(s) or Organization(s)",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_increased_limits_designated_persons") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceAllHazards: formatCurrency(endorsement.eachOccurrenceAllHazards),
					eachOccurrenceExcludingProductsCompletedOperations: formatCurrency(endorsement.eachOccurrenceExcludingProductsCompletedOperations),
					eachOccurrenceProductsCompletedOperations: formatCurrency(endorsement.eachOccurrenceProductsCompletedOperations),
					productsCompletedOperationsAggregate: formatCurrency(endorsement.productsCompletedOperationsAggregate),
					damageToPremisesRentedToYou: formatCurrency(endorsement.damageToPremisesRentedToYou),
					personalAndAdvertisingInjuryAggregate: formatCurrency(endorsement.personalAndAdvertisingInjuryAggregate),
					medicalExpenseEachPerson: formatCurrency(endorsement.medicalExpenseEachPerson),
					hangarkeepersEachLoss: formatCurrency(endorsement.hangarkeepersEachLoss),
					hangarkeepersEachAircraft: formatCurrency(endorsement.hangarkeepersEachAircraft),
					deductibleTurbineAircraft: formatInt(endorsement.deductibleTurbineAircraft),
					propertyDamageDeductibleTurbineAircraft: formatCurrency(endorsement.propertyDamageDeductibleTurbineAircraft),
					propertyDamageDeductibleOtherAircraft: formatCurrency(endorsement.propertyDamageDeductibleOtherAircraft),
					propertyDamageDeductibleNonAircraftClaim: formatCurrency(endorsement.propertyDamageDeductibleNonAircraftClaim),
				};
			}
			return {};
		},
	},
	cgl_deductible: {
		label: "Deductible Endorsement",
		schema: CglDeductibleEndorsementSchema,
		documentName: "N AO E CLASSA 0009 05 25 Deductible End.docx",
		formNumber: "N AO E CLASSA 0009 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_deductible") {
				const limits: Endorsement["limits"] = [];
				if (endorsement.bodilyInjuryPerClaim) {
					limits.push({
						qualifier: "Bodily injury — per claim",
						amount: endorsement.bodilyInjuryPerClaim,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.bodilyInjuryPerOccurrence) {
					limits.push({
						qualifier: "Bodily injury — per occurrence",
						amount: endorsement.bodilyInjuryPerOccurrence,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.propertyDamagePerClaimReciprocatingAircraft) {
					limits.push({
						qualifier: "Property damage — per claim reciprocating aircraft",
						amount: endorsement.propertyDamagePerClaimReciprocatingAircraft,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.propertyDamagePerClaimTurbineAircraft) {
					limits.push({
						qualifier: "Property damage — per claim turbine aircraft",
						amount: endorsement.propertyDamagePerClaimTurbineAircraft,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.propertyDamagePerClaimOther) {
					limits.push({
						qualifier: "Property damage — per claim other",
						amount: endorsement.propertyDamagePerClaimOther,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.propertyDamagePerOccurrence) {
					limits.push({
						qualifier: "Property damage — per occurrence",
						amount: endorsement.propertyDamagePerOccurrence,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.personalAndAdvertisingInjuryPerClaim) {
					limits.push({
						qualifier: "Personal and advertising injury — per claim",
						amount: endorsement.personalAndAdvertisingInjuryPerClaim,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.personalAndAdvertisingInjuryPerOffense) {
					limits.push({
						qualifier: "Personal and advertising injury — per offense",
						amount: endorsement.personalAndAdvertisingInjuryPerOffense,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.hangarkeepersPerReciprocatingEngineAircraft) {
					limits.push({
						qualifier: "Hangarkeeper's — per reciprocating engine aircraft",
						amount: endorsement.hangarkeepersPerReciprocatingEngineAircraft,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.hangarkeepersPerTurbineEngineAircraft) {
					limits.push({
						qualifier: "Hangarkeeper's — per turbine engine aircraft",
						amount: endorsement.hangarkeepersPerTurbineEngineAircraft,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.hangarkeepersPerLoss) {
					limits.push({
						qualifier: "Hangarkeeper's — per loss",
						amount: endorsement.hangarkeepersPerLoss,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.annualAggregateDeductible) {
					limits.push({
						qualifier: "Coverages A, B and D — annual aggregate",
						amount: endorsement.annualAggregateDeductible,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (limits.length === 0) {
					limits.push({
						qualifier: "Included",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				endorsements.push({
					name: "Deductible Endorsement",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_deductible") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					bodilyInjuryPerClaim: formatCurrency(endorsement.bodilyInjuryPerClaim),
					bodilyInjuryPerOccurrence: formatCurrency(endorsement.bodilyInjuryPerOccurrence),
					propertyDamagePerClaimReciprocatingAircraft: formatCurrency(endorsement.propertyDamagePerClaimReciprocatingAircraft),
					propertyDamagePerClaimTurbineAircraft: formatCurrency(endorsement.propertyDamagePerClaimTurbineAircraft),
					propertyDamagePerClaimOther: formatCurrency(endorsement.propertyDamagePerClaimOther),
					propertyDamagePerOccurrence: formatCurrency(endorsement.propertyDamagePerOccurrence),
					personalAndAdvertisingInjuryPerClaim: formatCurrency(endorsement.personalAndAdvertisingInjuryPerClaim),
					personalAndAdvertisingInjuryPerOffense: formatCurrency(endorsement.personalAndAdvertisingInjuryPerOffense),
					hangarkeepersPerReciprocatingEngineAircraft: formatCurrency(endorsement.hangarkeepersPerReciprocatingEngineAircraft),
					hangarkeepersPerTurbineEngineAircraft: formatCurrency(endorsement.hangarkeepersPerTurbineEngineAircraft),
					hangarkeepersPerLoss: formatCurrency(endorsement.hangarkeepersPerLoss),
					annualAggregateDeductible: formatCurrency(endorsement.annualAggregateDeductible),
				};
			}
			return {};
		},
	},
	cgl_premium_amendment: {
		label: "Premium Amendment",
		schema: CglPremiumAmendmentEndorsementSchema,
		documentName: "N AO E CLASSA 0016 05 25 Premium Amend.docx",
		formNumber: "N AO E CLASSA 0016 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_premium_amendment") {
				endorsements.push({
					name: "Premium Amendment",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_premium_amendment") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					premiumSubjectToAudit: formatCurrency(endorsement.premiumSubjectToAudit),
					stateTaxOrOther: formatCurrency(endorsement.stateTaxOrOther),
					totalPremium: formatCurrency(endorsement.totalPremium),
					amendedEndorsementNumber: endorsement.amendedEndorsementNumber,
					amendedAdditionalPremium: formatCurrency(endorsement.amendedAdditionalPremium),
				};
			}
			return {};
		},
	},
	cgl_spare_parts: {
		label: "Spare Parts Endorsement",
		schema: CglSparePartsEndorsementSchema,
		documentName: "N AO E CLASSA 0022 05 25 Spare Parts End.docx",
		formNumber: "N AO E CLASSA 0022 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_spare_parts") {
				endorsements.push({
					name: "Spare Parts Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Any one occurrence, any one conveyance, building or location",
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} — each and every loss`,
							amount: endorsement.deductibleEachLoss,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_spare_parts") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceLimit: formatCurrency(endorsement.eachOccurrenceLimit),
					deductibleEachLoss: formatCurrency(endorsement.deductibleEachLoss),
					item1Description: endorsement.item1Description,
					item1InsuredValue: formatCurrency(endorsement.item1InsuredValue),
					item2Description: endorsement.item2Description,
					item2InsuredValue: formatCurrency(endorsement.item2InsuredValue),
					item3Description: endorsement.item3Description,
					item3InsuredValue: formatCurrency(endorsement.item3InsuredValue),
					item4Description: endorsement.item4Description,
					item4InsuredValue: formatCurrency(endorsement.item4InsuredValue),
					item5Description: endorsement.item5Description,
					item5InsuredValue: formatCurrency(endorsement.item5InsuredValue),
					item6Description: endorsement.item6Description,
					item6InsuredValue: formatCurrency(endorsement.item6InsuredValue),
					item7Description: endorsement.item7Description,
					item7InsuredValue: formatCurrency(endorsement.item7InsuredValue),
					item8Description: endorsement.item8Description,
					item8InsuredValue: formatCurrency(endorsement.item8InsuredValue),
					lossPayee: endorsement.lossPayee,
					lossPayeeMailingAddress: endorsement.lossPayeeMailingAddress,
				};
			}
			return {};
		},
	},
	cgl_cargo_liability: {
		label: "Cargo Liability Endorsement",
		schema: CglCargoLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0035 05 25 Cargo Liability End.docx",
		formNumber: "N AO E CLASSA 0035 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_cargo_liability") {
				endorsements.push({
					name: "Cargo Liability Endorsement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} — each claim`,
							amount: endorsement.deductibleEachClaim,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} — maximum each occurrence`,
							amount: endorsement.deductibleMaximumEachOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_cargo_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					locations: endorsement.locations,
					eachOccurrenceLimit: formatCurrency(endorsement.eachOccurrenceLimit),
					deductibleEachClaim: formatInt(endorsement.deductibleEachClaim),
					deductibleMaximumEachOccurrence: formatInt(endorsement.deductibleMaximumEachOccurrence),
				};
			}
			return {};
		},
	},
	cgl_non_owned_aircraft_physical_damage: {
		label: "Non-Owned Aircraft: Physical Damage Endorsement",
		schema: CglNonOwnedAircraftPhysicalDamageEndorsementSchema,
		documentName: "N AO E CLASSA 0037 05 25 Non-owned Aircraft Phys Dam End.docx",
		formNumber: "N AO E CLASSA 0037 05 25",
		lob: ["commercial_general_liability"],
		excludedStates: ["NY"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_non_owned_aircraft_physical_damage") {
				endorsements.push({
					name: "Non-Owned Aircraft: Physical Damage Endorsement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} — not in motion`,
							amount: endorsement.deductibleNotInMotion,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} — in motion, ingestion, or mooring`,
							amount: endorsement.deductibleInMotionIngestionOrMooring,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_non_owned_aircraft_physical_damage") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					categoryAAircraftType: endorsement.categoryAAircraftType,
					categoryAAirworthinessCertificate: endorsement.categoryAAirworthinessCertificate,
					categoryAMaximumSeats: formatInt(endorsement.categoryAMaximumSeats),
					categoryBAircraftType: endorsement.categoryBAircraftType,
					categoryBAirworthinessCertificate: endorsement.categoryBAirworthinessCertificate,
					categoryBMaximumSeats: formatInt(endorsement.categoryBMaximumSeats),
					eachOccurrenceLimit: formatCurrency(endorsement.eachOccurrenceLimit),
					deductibleNotInMotion: formatCurrency(endorsement.deductibleNotInMotion),
					deductibleInMotionIngestionOrMooring: formatCurrency(endorsement.deductibleInMotionIngestionOrMooring),
				};
			}
			return {};
		},
	},
	cgl_baggage_liability: {
		label: "Baggage Liability Endorsement",
		schema: CglBaggageLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0020 05 25 Baggage Liability End.docx",
		formNumber: "N AO E CLASSA 0020 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_baggage_liability") {
				endorsements.push({
					name: "Baggage Liability Endorsement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} — each loss, each occurrence`,
							amount: endorsement.deductibleEachLoss,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_baggage_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					locations: endorsement.locations,
					eachOccurrenceLimit: formatCurrency(endorsement.eachOccurrenceLimit),
					deductibleEachLoss: formatCurrency(endorsement.deductibleEachLoss),
				};
			}
			return {};
		},
	},
	cgl_mechanics_tools: {
		label: "Mechanics Tools Endorsement",
		schema: CglMechanicsToolsEndorsementSchema,
		documentName: "N AO E CLASSA 0043 05 25 Mechanics Tools End.docx",
		formNumber: "N AO E CLASSA 0043 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_mechanics_tools") {
				endorsements.push({
					name: "Mechanics Tools Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Each employee",
							amount: endorsement.eachEmployeeLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} — each employee, any one occurrence`,
							amount: endorsement.deductibleEachEmployee,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_mechanics_tools") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachEmployeeLimit: formatCurrency(endorsement.eachEmployeeLimit),
					eachOccurrenceLimit: formatCurrency(endorsement.eachOccurrenceLimit),
					deductibleEachEmployee: formatCurrency(endorsement.deductibleEachEmployee),
				};
			}
			return {};
		},
	},
	cgl_additional_insured_designated_optional_clauses: {
		label: "Additional Insured - Designated Person or Organization (Optional Clauses)",
		schema: CglAdditionalInsuredDesignatedOptionalClausesEndorsementSchema,
		documentName: "N AO E CLASSA 0084 05 25 Additional Insured DP or Org with options.docx",
		formNumber: "N AO E CLASSA 0084 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_designated_optional_clauses") {
				endorsements.push({
					name: "Additional Insured - Designated Person or Organization (Optional Clauses)",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_designated_optional_clauses") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_airshow_liability_coverage_part: {
		label: "Airshow Liability Coverage Part",
		schema: CglAirshowLiabilityCoveragePartEndorsementSchema,
		documentName: "N AO E CLASSA 0014 05 25 Airshow Liability Coverage Part.docx",
		formNumber: "N AO E CLASSA 0014 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			OR: {
				documentName: "N AO E CLASSA 0014OR 05 25 Airshow Liability Coverage Part - OR.docx",
				formNumber: "N AO E CLASSA 0014OR 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_airshow_liability_coverage_part") {
				endorsements.push({
					name: "Airshow Liability Coverage Part",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_airshow_liability_coverage_part") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_amended_definition_property_damage: {
		label: "Amended Definition of Property Damage",
		schema: CglAmendedDefinitionPropertyDamageEndorsementSchema,
		documentName: "N AO E CLASSA 0002 05 25 Amend Property Damage Definition.docx",
		formNumber: "N AO E CLASSA 0002 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amended_definition_property_damage") {
				endorsements.push({
					name: "Amended Definition of Property Damage",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amended_definition_property_damage") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_amendment_who_is_insured_public_entities: {
		label: "Amendment of Who is an Insured for Public Entities",
		schema: CglAmendmentWhoIsInsuredPublicEntitiesEndorsementSchema,
		documentName: "N AO E CLASSA 0001 05 25 Amend Who Insured Public Entities.docx",
		formNumber: "N AO E CLASSA 0001 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amendment_who_is_insured_public_entities") {
				endorsements.push({
					name: "Amendment of Who is an Insured for Public Entities",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amendment_who_is_insured_public_entities") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_defense_costs_within_limit: {
		label: "Defense Costs Contained Within Liability Limit - Coverages A, B and D",
		schema: CglDefenseCostsWithinLimitEndorsementSchema,
		documentName: "N AO E CLASSA 0012 05 25 Defense Costs within Liability Limit.docx",
		formNumber: "N AO E CLASSA 0012 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			VA: {
				documentName: "N AO E CLASSA 0012VA 05 25 Defense Costs within Liability Limit - VA.docx",
				formNumber: "N AO E CLASSA 0012VA 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_defense_costs_within_limit") {
				endorsements.push({
					name: "Defense Costs Contained Within Liability Limit - Coverages A, B and D",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_defense_costs_within_limit") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_excluded_products_completed_operations: {
		label: "Excluded Products-Completed Operations Endorsement",
		schema: CglExcludedProductsCompletedOperationsEndorsementSchema,
		documentName: "N AO E CLASSA 0074 05 25 Excluded Products Completed Operation End.docx",
		formNumber: "N AO E CLASSA 0074 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_excluded_products_completed_operations") {
				endorsements.push({
					name: "Excluded Products-Completed Operations Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_excluded_products_completed_operations") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_exclusion_completed_operations: {
		label: "Exclusion - Completed Operations Endorsement",
		schema: CglExclusionCompletedOperationsEndorsementSchema,
		documentName: "N AO E CLASSA 0077 05 25 Exclusion Completed Operation End.docx",
		formNumber: "N AO E CLASSA 0077 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_completed_operations") {
				endorsements.push({
					name: "Exclusion - Completed Operations Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_completed_operations") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_exclusion_intercompany_products_suit: {
		label: "Exclusion - Intercompany Products Suit",
		schema: CglExclusionIntercompanyProductsSuitEndorsementSchema,
		documentName: "N AO E CLASSA 0079 05 25 Exc Intercompany Product Suits.docx",
		formNumber: "N AO E CLASSA 0079 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_intercompany_products_suit") {
				endorsements.push({
					name: "Exclusion - Intercompany Products Suit",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_intercompany_products_suit") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_foreign_military_hull: {
		label: "Foreign Military Hull Endorsement",
		schema: CglForeignMilitaryHullEndorsementSchema,
		documentName: "N AO E CLASSA 0029 05 25 Foreign Military Hull End.docx",
		formNumber: "N AO E CLASSA 0029 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_foreign_military_hull") {
				endorsements.push({
					name: "Foreign Military Hull Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_foreign_military_hull") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_honeywell_avionics_dealership: {
		label: "Honeywell Avionics Dealership Endorsement",
		schema: CglHoneywellAvionicsDealershipEndorsementSchema,
		documentName: "N AO E CLASSA 0094 05 25 Honeywell Avionics Dealership End.docx",
		formNumber: "N AO E CLASSA 0094 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_honeywell_avionics_dealership") {
				endorsements.push({
					name: "Honeywell Avionics Dealership Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_honeywell_avionics_dealership") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_honeywell_hold_harmless: {
		label: "Honeywell Hold Harmless Endorsement",
		schema: CglHoneywellHoldHarmlessEndorsementSchema,
		documentName: "N AO E CLASSA 0098 05 25 Honeywell Endorsement.docx",
		formNumber: "N AO E CLASSA 0098 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_honeywell_hold_harmless") {
				endorsements.push({
					name: "Honeywell Hold Harmless Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_honeywell_hold_harmless") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_misdelivery_liquid_products: {
		label: "Misdelivery of Liquid Products Endorsement",
		schema: CglMisdeliveryLiquidProductsEndorsementSchema,
		documentName: "N AO E CLASSA 0024 05 25 Misdelivery of Liquid Products.docx",
		formNumber: "N AO E CLASSA 0024 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_misdelivery_liquid_products") {
				endorsements.push({
					name: "Misdelivery of Liquid Products Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_misdelivery_liquid_products") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	cgl_additional_insured_controlling_interest: {
		label: "Additional Insured - Controlling Interest",
		schema: CglAdditionalInsuredControllingInterestEndorsementSchema,
		documentName: "N AO E CLASSA 0091 05 25 Addtl Insured Controlling Interest.docx",
		formNumber: "N AO E CLASSA 0091 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_controlling_interest") {
				endorsements.push({
					name: "Additional Insured - Controlling Interest",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_controlling_interest") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedPremises: endorsement.designatedPremises,
					additionalInsuredName: endorsement.additionalInsuredName,
				};
			}
			return {};
		},
	},
	cgl_additional_insured_managers_lessors: {
		label: "Additional Insured - Managers or Lessors of Premises",
		schema: CglAdditionalInsuredManagersLessorsEndorsementSchema,
		documentName: "N AO E CLASSA 0085 05 25 Addtl Insured Managers or Lessors of Premises.docx",
		formNumber: "N AO E CLASSA 0085 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_managers_lessors") {
				endorsements.push({
					name: "Additional Insured - Managers or Lessors of Premises",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_managers_lessors") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedPremises: endorsement.designatedPremises,
					additionalInsuredName: endorsement.additionalInsuredName,
				};
			}
			return {};
		},
	},
	cgl_additional_insured_managers_lessors_broad_form: {
		label: "Additional Insured - Managers or Lessors of Premises (Broad Form)",
		schema: CglAdditionalInsuredManagersLessorsBroadFormEndorsementSchema,
		documentName: "N AO E CLASSA 0086 05 25 Additional Insured Managers Lessors Broad Form.docx",
		formNumber: "N AO E CLASSA 0086 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_managers_lessors_broad_form") {
				endorsements.push({
					name: "Additional Insured - Managers or Lessors of Premises (Broad Form)",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_managers_lessors_broad_form") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedPremises: endorsement.designatedPremises,
					additionalInsuredName: endorsement.additionalInsuredName,
				};
			}
			return {};
		},
	},
	cgl_additional_insured_lessor_leased_equipment: {
		label: "Additional Insured - Lessor of Leased Equipment",
		schema: CglAdditionalInsuredLessorLeasedEquipmentEndorsementSchema,
		documentName: "N AO E CLASSA 0089 05 25 Addtl Insured Lessor of Leased Eqpt.docx",
		formNumber: "N AO E CLASSA 0089 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_lessor_leased_equipment") {
				endorsements.push({
					name: "Additional Insured - Lessor of Leased Equipment",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_lessor_leased_equipment") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
					additionalInsuredName: endorsement.additionalInsuredName,
				};
			}
			return {};
		},
	},
	cgl_additional_insured_owners_lessees_contractors: {
		label: "Additional Insured - Owners, Lessees or Contractors",
		schema: CglAdditionalInsuredOwnersLesseesContractorsEndorsementSchema,
		documentName: "N AO E CLASSA 0092 05 25 Addtl Insured Owners Lessees Contractors.docx",
		formNumber: "N AO E CLASSA 0092 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_owners_lessees_contractors") {
				endorsements.push({
					name: "Additional Insured - Owners, Lessees or Contractors",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_owners_lessees_contractors") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
					additionalInsuredName: endorsement.additionalInsuredName,
				};
			}
			return {};
		},
	},
	cgl_additional_insured_designated_excluding_pai: {
		label: "Additional Insured - Designated Person or Organization (Excluding Personal and Advertising Injury)",
		schema: CglAdditionalInsuredDesignatedExcludingPaiEndorsementSchema,
		documentName: "N AO E CLASSA 0083 05 25 Additional Insured DP or Org Excluding Pers Adv Injury.docx",
		formNumber: "N AO E CLASSA 0083 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_designated_excluding_pai") {
				endorsements.push({
					name: "Additional Insured - Designated Person or Organization (Excluding Personal and Advertising Injury)",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_designated_excluding_pai") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
					designatedPersonsOrganizations: endorsement.designatedPersonsOrganizations,
				};
			}
			return {};
		},
	},
	cgl_additional_insured_designated_extended: {
		label: "Additional Insured - Designated Person or Organization (Extended Schedule)",
		schema: CglAdditionalInsuredDesignatedExtendedEndorsementSchema,
		documentName: "N AO E CLASSA 0082 05 25 Additional Insured DP or Org 2.docx",
		formNumber: "N AO E CLASSA 0082 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_designated_extended") {
				endorsements.push({
					name: "Additional Insured - Designated Person or Organization (Extended Schedule)",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_designated_extended") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
					designatedPersonsOrganizations: endorsement.designatedPersonsOrganizations,
					designatedPersonsOrganizationsContinued: endorsement.designatedPersonsOrganizationsContinued,
				};
			}
			return {};
		},
	},
	cgl_additional_insured_vendors: {
		label: "Additional Insured - Vendors",
		schema: CglAdditionalInsuredVendorsEndorsementSchema,
		documentName: "N AO E CLASSA 0087 05 25 Additional Insured Vendors.docx",
		formNumber: "N AO E CLASSA 0087 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_vendors") {
				endorsements.push({
					name: "Additional Insured - Vendors",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_vendors") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					vendorName: endorsement.vendorName,
					vendorProduct: endorsement.vendorProduct,
				};
			}
			return {};
		},
	},
	cgl_additional_insured_fuel_lubricant_suppliers: {
		label: "Additional Insured - Non-Operational Suppliers of Aviation Fuel and Lubricants",
		schema: CglAdditionalInsuredFuelLubricantSuppliersEndorsementSchema,
		documentName: "N AO E CLASSA 0088 05 25 Additional Insured Non Operational Suppliers Aviation Fuel Lubricants.docx",
		formNumber: "N AO E CLASSA 0088 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_additional_insured_fuel_lubricant_suppliers") {
				endorsements.push({
					name: "Additional Insured - Non-Operational Suppliers of Aviation Fuel and Lubricants",
					included: true,
					limits: [
						{
							qualifier: "General aggregate (other than products/completed operations)",
							amount: endorsement.generalAggregateLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Products/completed operations aggregate",
							amount: endorsement.productsCompletedOperationsAggregate,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_additional_insured_fuel_lubricant_suppliers") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					additionalInsuredNameOrOrganization: endorsement.additionalInsuredNameOrOrganization,
					generalAggregateLimit: formatInt(endorsement.generalAggregateLimit),
					eachOccurrenceLimit: formatInt(endorsement.eachOccurrenceLimit),
					productsCompletedOperationsAggregate: formatInt(endorsement.productsCompletedOperationsAggregate),
				};
			}
			return {};
		},
	},
	cgl_deletion_exclusion_sale_of_aircraft: {
		label: "Deletion of Exclusion Endorsement - Sale of Aircraft",
		schema: CglDeletionExclusionSaleOfAircraftEndorsementSchema,
		documentName: "N AO E CLASSA 0047 05 25 Deletion of Exc End Sale of Aircraft.docx",
		formNumber: "N AO E CLASSA 0047 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_deletion_exclusion_sale_of_aircraft") {
				endorsements.push({
					name: "Deletion of Exclusion Endorsement - Sale of Aircraft",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_deletion_exclusion_sale_of_aircraft") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
				};
			}
			return {};
		},
	},
	cgl_exclusion_health_cosmetic_services: {
		label: "Exclusion - Health or Cosmetic Services",
		schema: CglExclusionHealthCosmeticServicesEndorsementSchema,
		documentName: "N AO E CLASSA 0078 05 25 Exc Health or Cosmetic Services.docx",
		formNumber: "N AO E CLASSA 0078 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_health_cosmetic_services") {
				endorsements.push({
					name: "Exclusion - Health or Cosmetic Services",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_health_cosmetic_services") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedLocations: endorsement.designatedLocations,
				};
			}
			return {};
		},
	},
	cgl_exclusion_new_entities: {
		label: "Exclusion - New Entities",
		schema: CglExclusionNewEntitiesEndorsementSchema,
		documentName: "N AO E CLASSA 0067 05 25 Exclusion New Entities.docx",
		formNumber: "N AO E CLASSA 0067 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_exclusion_new_entities") {
				endorsements.push({
					name: "Exclusion - New Entities",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_exclusion_new_entities") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					excludedNewEntities: endorsement.excludedNewEntities,
				};
			}
			return {};
		},
	},
	cgl_negligent_instruction_supervision: {
		label: "Negligent Instruction/Supervision Endorsement",
		schema: CglNegligentInstructionSupervisionEndorsementSchema,
		documentName: "N AO E CLASSA 0060 05 25 Negligent Instruction.docx",
		formNumber: "N AO E CLASSA 0060 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_negligent_instruction_supervision") {
				endorsements.push({
					name: "Negligent Instruction/Supervision Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Single limit bodily injury and property damage - aggregate",
							amount: endorsement.singleLimitAggregate,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Bodily injury - each person",
							amount: endorsement.bodilyInjuryEachPerson,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_negligent_instruction_supervision") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					singleLimitAggregate: formatInt(endorsement.singleLimitAggregate),
					bodilyInjuryEachPerson: formatInt(endorsement.bodilyInjuryEachPerson),
				};
			}
			return {};
		},
	},
	cgl_negligent_instruction_supervision_independent_instructors: {
		label: "Negligent Instruction/Supervision Endorsement Including Independent Flight Instructors",
		schema: CglNegligentInstructionSupervisionIndependentInstructorsEndorsementSchema,
		documentName: "N AO E CLASSA 0061 05 25 Negligent Instruction Independent Flight Instructors.docx",
		formNumber: "N AO E CLASSA 0061 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_negligent_instruction_supervision_independent_instructors") {
				endorsements.push({
					name: "Negligent Instruction/Supervision Endorsement Including Independent Flight Instructors",
					included: true,
					limits: [
						{
							qualifier: "Single limit bodily injury and property damage - aggregate",
							amount: endorsement.singleLimitAggregate,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Bodily injury - each person",
							amount: endorsement.bodilyInjuryEachPerson,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_negligent_instruction_supervision_independent_instructors") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					singleLimitAggregate: formatInt(endorsement.singleLimitAggregate),
					bodilyInjuryEachPerson: formatInt(endorsement.bodilyInjuryEachPerson),
				};
			}
			return {};
		},
	},
	cgl_airshow_liability: {
		label: "Airshow Liability Endorsement",
		schema: CglAirshowLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0015 05 25 Airshow Liability Coverage End.docx",
		formNumber: "N AO E CLASSA 0015 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_airshow_liability") {
				endorsements.push({
					name: "Airshow Liability Endorsement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_airshow_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceLimit: formatInt(endorsement.eachOccurrenceLimit),
				};
			}
			return {};
		},
	},
	cgl_boeing: {
		label: "Boeing Endorsement",
		schema: CglBoeingEndorsementSchema,
		documentName: "N AO E CLASSA 0090 05 25 Boeing End.docx",
		formNumber: "N AO E CLASSA 0090 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			DC: {
				documentName: "N AO E CLASSA 0090DC 05 25 Boeing End - District of Columbia.docx",
				formNumber: "N AO E CLASSA 0090DC 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_boeing") {
				endorsements.push({
					name: "Boeing Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_boeing") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					boeingAgreement: endorsement.boeingAgreement,
				};
			}
			return {};
		},
	},
	cgl_inspection_liability: {
		label: "Inspection Liability",
		schema: CglInspectionLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0102 05 25 INSPECTION LIABILITY.docx",
		formNumber: "N AO E CLASSA 0102 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			GA: {
				documentName: "N AO E CLASSA 0102GA 05 25 INSPECTION LIABILITY - GEORGIA.docx",
				formNumber: "N AO E CLASSA 0102GA 05 25",
			},
			OR: {
				documentName: "N AO E CLASSA 0102OR 05 25 INSPECTION LIABILITY - OREGON.docx",
				formNumber: "N AO E CLASSA 0102OR 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_inspection_liability") {
				endorsements.push({
					name: "Inspection Liability",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_inspection_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					inspectionCoverageStartDate: endorsement.inspectionCoverageStartDate
						? formatDateFullMonth(endorsement.inspectionCoverageStartDate.slice(0, 10), "UTC")
						: "",
				};
			}
			return {};
		},
	},
	cgl_extended_non_owned_aircraft_pilot_warranty: {
		label: "Extended Non-Owned Aircraft Liability - Pilot Warranty Endorsement",
		schema: CglExtendedNonOwnedAircraftPilotWarrantyEndorsementSchema,
		documentName: "N AO E CLASSA 0101 05 25 Extended Non-Owned AC Liability - Pilot Warranty Endorsement.docx",
		formNumber: "N AO E CLASSA 0101 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			GA: {
				documentName: "N AO E CLASSA 0101GA 05 25 Extended Non-Owned AC Liability - Pilot Warranty Endorsement - Georgia.docx",
				formNumber: "N AO E CLASSA 0101GA 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_extended_non_owned_aircraft_pilot_warranty") {
				endorsements.push({
					name: "Extended Non-Owned Aircraft Liability - Pilot Warranty Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_extended_non_owned_aircraft_pilot_warranty") {
				return {
					pilotRequirement: endorsement.pilotRequirement,
				};
			}
			return {};
		},
	},
	cgl_bae_systems_tss: {
		label: "BAE Systems TSS Endorsement",
		schema: CglBaeSystemsTssEndorsementSchema,
		documentName: "N AO E CLASSA 0093 05 25 BAE Systems TSS End.docx",
		formNumber: "N AO E CLASSA 0093 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_bae_systems_tss") {
				endorsements.push({
					name: "BAE Systems TSS Endorsement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Products/completed operations aggregate",
							amount: endorsement.productsCompletedOperationsAggregate,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_bae_systems_tss") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceLimit: formatCurrency(endorsement.eachOccurrenceLimit),
					productsCompletedOperationsAggregate: formatCurrency(endorsement.productsCompletedOperationsAggregate),
					propertyDamageDeductibleTurbineAircraft: formatCurrency(endorsement.propertyDamageDeductibleTurbineAircraft),
					propertyDamageDeductibleOtherAircraft: formatCurrency(endorsement.propertyDamageDeductibleOtherAircraft),
					propertyDamageDeductibleNonAircraftClaim: formatCurrency(endorsement.propertyDamageDeductibleNonAircraftClaim),
				};
			}
			return {};
		},
	},
	cgl_rockwell_collins: {
		label: "Rockwell Collins Endorsement",
		schema: CglRockwellCollinsEndorsementSchema,
		documentName: "N AO E CLASSA 0095 05 25 Rockwell Collins End.docx",
		formNumber: "N AO E CLASSA 0095 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_rockwell_collins") {
				endorsements.push({
					name: "Rockwell Collins Endorsement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Products/completed operations aggregate",
							amount: endorsement.productsCompletedOperationsAggregate,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Hangarkeeper's liability — each loss",
							amount: endorsement.hangarkeepersEachLoss,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Hangarkeeper's liability — each aircraft",
							amount: endorsement.hangarkeepersEachAircraft,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_rockwell_collins") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceLimit: formatCurrency(endorsement.eachOccurrenceLimit),
					productsCompletedOperationsAggregate: formatCurrency(endorsement.productsCompletedOperationsAggregate),
					hangarkeepersEachLoss: formatCurrency(endorsement.hangarkeepersEachLoss),
					hangarkeepersEachAircraft: formatCurrency(endorsement.hangarkeepersEachAircraft),
					deductibleTurbineAircraft: formatCurrency(endorsement.deductibleTurbineAircraft),
					deductibleOtherAircraft: formatCurrency(endorsement.deductibleOtherAircraft),
					propertyDamageDeductibleTurbineAircraft: formatCurrency(endorsement.propertyDamageDeductibleTurbineAircraft),
					propertyDamageDeductibleOtherAircraft: formatCurrency(endorsement.propertyDamageDeductibleOtherAircraft),
					propertyDamageDeductibleNonAircraftClaim: formatCurrency(endorsement.propertyDamageDeductibleNonAircraftClaim),
				};
			}
			return {};
		},
	},
	cgl_stec_corporation: {
		label: "S-TEC Corporation Endorsement",
		schema: CglStecCorporationEndorsementSchema,
		documentName: "N AO E CLASSA 0096 05 25 S-Tec Corp End.docx",
		formNumber: "N AO E CLASSA 0096 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_stec_corporation") {
				endorsements.push({
					name: "S-TEC Corporation Endorsement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.eachOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Products liability aggregate",
							amount: endorsement.productsLiabilityAggregate,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Completed operations liability aggregate",
							amount: endorsement.completedOperationsLiabilityAggregate,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_stec_corporation") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceLimit: formatCurrency(endorsement.eachOccurrenceLimit),
					productsLiabilityAggregate: formatCurrency(endorsement.productsLiabilityAggregate),
					completedOperationsLiabilityAggregate: formatCurrency(endorsement.completedOperationsLiabilityAggregate),
					propertyDamageDeductibleTurbineAircraft: formatCurrency(endorsement.propertyDamageDeductibleTurbineAircraft),
					propertyDamageDeductibleOtherAircraft: formatCurrency(endorsement.propertyDamageDeductibleOtherAircraft),
					propertyDamageDeductibleNonAircraftClaim: formatCurrency(endorsement.propertyDamageDeductibleNonAircraftClaim),
				};
			}
			return {};
		},
	},
	cgl_contingent_control_tower_liability: {
		label: "Contingent Control Tower Liability",
		schema: CglContingentControlTowerLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0032 05 25 Contingent Control Tower Liability.docx",
		formNumber: "N AO E CLASSA 0032 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_contingent_control_tower_liability") {
				endorsements.push({
					name: "Contingent Control Tower Liability",
					included: true,
					limits: [
						{
							qualifier: "Per occurrence",
							amount: endorsement.perOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.annual_aggregate,
							amount: endorsement.annualAggregateLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_contingent_control_tower_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					coveredLocations: endorsement.coveredLocations,
					excludedLocations: endorsement.excludedLocations,
					perOccurrenceLimit: formatInt(endorsement.perOccurrenceLimit),
					annualAggregateLimit: formatInt(endorsement.annualAggregateLimit),
				};
			}
			return {};
		},
	},
	cgl_host_liquor_liability_limitation: {
		label: "Host Liquor Liability Limitation Endorsement",
		schema: CglHostLiquorLiabilityLimitationEndorsementSchema,
		documentName: "N AO E CLASSA 0075 05 25 Host Liquor Liability Limitation End.docx",
		formNumber: "N AO E CLASSA 0075 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_host_liquor_liability_limitation") {
				endorsements.push({
					name: "Host Liquor Liability Limitation Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Per occurrence",
							amount: endorsement.perOccurrenceLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.annual_aggregate,
							amount: endorsement.annualAggregateLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_host_liquor_liability_limitation") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					locations: endorsement.locations,
					perOccurrenceLimit: formatCurrency(endorsement.perOccurrenceLimit),
					annualAggregateLimit: formatCurrency(endorsement.annualAggregateLimit),
				};
			}
			return {};
		},
	},
	cgl_hangarkeepers_leased_managed_aircraft: {
		label: "Hangarkeepers Liability for Property Damage to Leased or Managed Aircraft",
		schema: CglHangarkeepersLeasedManagedAircraftEndorsementSchema,
		documentName: "N AO E CLASSA 0031 05 25 Hangarkeepers Liability Property Damage Leased Managed.docx",
		formNumber: "N AO E CLASSA 0031 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_hangarkeepers_leased_managed_aircraft") {
				endorsements.push({
					name: "Hangarkeepers Liability for Property Damage to Leased or Managed Aircraft",
					included: true,
					limits: [
						{
							qualifier: "Hangarkeeper's liability - each loss",
							amount: endorsement.eachLossLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Hangarkeeper's liability - each aircraft",
							amount: endorsement.eachAircraftLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${qualifierLabels.deductible} - each loss`,
							amount: endorsement.deductibleEachLoss,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_hangarkeepers_leased_managed_aircraft") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachLossLimit: formatInt(endorsement.eachLossLimit),
					eachAircraftLimit: formatInt(endorsement.eachAircraftLimit),
					deductibleEachLoss: formatInt(endorsement.deductibleEachLoss),
				};
			}
			return {};
		},
	},
	cgl_spacecraft_products: {
		label: "Spacecraft Products Endorsement",
		schema: CglSpacecraftProductsEndorsementSchema,
		documentName: "N AO E CLASSA 0036 05 25 Spacecraft Products End.docx",
		formNumber: "N AO E CLASSA 0036 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_spacecraft_products") {
				endorsements.push({
					name: "Spacecraft Products Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Single satellite - any one occurrence",
							amount: endorsement.singleSatelliteLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Two or more satellites - any one satellite",
							amount: endorsement.multipleSatellitesLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_spacecraft_products") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					singleSatelliteLimit: formatCurrency(endorsement.singleSatelliteLimit),
					multipleSatellitesLimit: formatCurrency(endorsement.multipleSatellitesLimit),
				};
			}
			return {};
		},
	},
	cgl_self_insured_retention: {
		label: "Self-Insured Retention",
		schema: CglSelfInsuredRetentionEndorsementSchema,
		documentName: "N AO E CLASSA 0011 05 25 Self Insured Retention End.docx",
		formNumber: "N AO E CLASSA 0011 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			MO: {
				documentName: "N AO E CLASSA 0011MO 05 25 Self Insured Retention End - MO.docx",
				formNumber: "N AO E CLASSA 0011MO 05 25",
			},
			VA: {
				documentName: "N AO E CLASSA 0011VA 05 25 Self Insured Retention End - VA.docx",
				formNumber: "N AO E CLASSA 0011VA 05 25",
			},
			WY: {
				documentName: "N AO E CLASSA 0011WY 05 25 Self Insured Retention End - WY.docx",
				formNumber: "N AO E CLASSA 0011WY 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_self_insured_retention") {
				endorsements.push({
					name: "Self-Insured Retention",
					included: true,
					limits: [
						{
							qualifier: "Self-insured retention - each occurrence",
							amount: endorsement.retentionAmount,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Self-insured retention - aggregate",
							amount: endorsement.aggregateRetentionAmount,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_self_insured_retention") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					retentionAmount: formatInt(endorsement.retentionAmount),
					aggregateRetentionAmount: formatInt(endorsement.aggregateRetentionAmount),
					lossSummaryFrequency: endorsement.lossSummaryFrequency,
				};
			}
			return {};
		},
	},
	cgl_employee_benefits_liability: {
		label: "Employee Benefits Liability Coverage Endorsement",
		schema: CglEmployeeBenefitsLiabilityEndorsementSchema,
		documentName: "N AO E CLASSA 0046 05 25 Employee Benefits Liability Cov End.docx",
		formNumber: "N AO E CLASSA 0046 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			MO: {
				documentName: "N AO E CLASSA 0046MO 05 25 Employee Benefits Liability Cov End - Missouri.docx",
				formNumber: "N AO E CLASSA 0046MO 05 25",
			},
			OR: {
				documentName: "N AO E CLASSA 0046OR 05 25 Employee Benefits Liability Cov End - Oregon.docx",
				formNumber: "N AO E CLASSA 0046OR 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_employee_benefits_liability") {
				endorsements.push({
					name: "Employee Benefits Liability",
					included: true,
					limits: [
						{
							qualifier: "Each employee",
							amount: endorsement.eachEmployeeLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.aggregate,
							amount: endorsement.aggregateLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_employee_benefits_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachEmployeeLimit: formatInt(endorsement.eachEmployeeLimit),
					eachEmployeeDeductible: formatInt(endorsement.eachEmployeeDeductible),
					coveragePremium: formatInt(endorsement.coveragePremium),
					aggregateLimit: formatInt(endorsement.aggregateLimit),
					retroactiveDate: endorsement.retroactiveDate
						? formatDateFullMonth(endorsement.retroactiveDate.slice(0, 10), "UTC")
						: "",
				};
			}
			return {};
		},
	},
	cgl_premises_amendment: {
		label: "Premises Amendment",
		schema: CglPremisesAmendmentEndorsementSchema,
		documentName: "N AO E CLASSA 0013 05 25 Premises Amend.docx",
		formNumber: "N AO E CLASSA 0013 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_premises_amendment") {
				endorsements.push({
					name: "Premises Amendment",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_premises_amendment") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					location1Number: endorsement.location1Number,
					location1Address: endorsement.location1Address,
					location2Number: endorsement.location2Number,
					location2Address: endorsement.location2Address,
					location3Number: endorsement.location3Number,
					location3Address: endorsement.location3Address,
					location4Number: endorsement.location4Number,
					location4Address: endorsement.location4Address,
				};
			}
			return {};
		},
	},
	cgl_products_completed_ops_leased_managed_aircraft: {
		label: "Products-Completed Operations Liability for Leased or Managed Aircraft",
		schema: CglProductsCompletedOpsLeasedManagedAircraftEndorsementSchema,
		documentName: "N AO E CLASSA 0027 05 25 Products Completed Ops Liability Leased Managed.docx",
		formNumber: "N AO E CLASSA 0027 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_products_completed_ops_leased_managed_aircraft") {
				endorsements.push({
					name: "Products-Completed Operations Liability for Leased or Managed Aircraft",
					included: true,
					limits: [
						{
							qualifier: "Each occurrence and in the annual aggregate",
							amount: endorsement.eachOccurrenceAndAggregateLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_products_completed_ops_leased_managed_aircraft") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceAndAggregateLimit: formatInt(endorsement.eachOccurrenceAndAggregateLimit),
				};
			}
			return {};
		},
	},
	cgl_products_completed_ops_maintenance_leased_aircraft: {
		label: "Products/Completed Operations - Maintenance of Your Aircraft Leased to Others",
		schema: CglProductsCompletedOpsMaintenanceLeasedAircraftEndorsementSchema,
		documentName: "N AO E CLASSA 0026 05 25 Products Completed Ops Mntc Your AC Leased Others.docx",
		formNumber: "N AO E CLASSA 0026 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_products_completed_ops_maintenance_leased_aircraft") {
				const limits: Endorsement["limits"] = [];
				if (endorsement.eachOccurrenceLimit > 0) {
					limits.push({
						qualifier: qualifierLabels.each_occurrence,
						amount: endorsement.eachOccurrenceLimit,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.annualAggregateLimit > 0) {
					limits.push({
						qualifier: qualifierLabels.annual_aggregate,
						amount: endorsement.annualAggregateLimit,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (limits.length === 0) {
					limits.push({
						qualifier: "Included",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				endorsements.push({
					name: "Products/Completed Operations - Maintenance of Your Aircraft Leased to Others",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_products_completed_ops_maintenance_leased_aircraft") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					eachOccurrenceLimit: endorsement.eachOccurrenceLimit > 0 ? formatInt(endorsement.eachOccurrenceLimit) : "",
					annualAggregateLimit: endorsement.annualAggregateLimit > 0 ? formatInt(endorsement.annualAggregateLimit) : "",
				};
			}
			return {};
		},
	},
	cgl_uaa_members_broad_form: {
		label: "University Aviation Association Members Broad Form Coverage Endorsement",
		schema: CglUaaMembersBroadFormEndorsementSchema,
		documentName: "N AO E CLASSA 0019 05 25 University Aviation Association BC End.docx",
		formNumber: "N AO E CLASSA 0019 05 25",
		lob: ["commercial_general_liability"],
		stateDocuments: {
			VT: {
				documentName: "N AO E CLASSA 0019VT 05 25 University Aviation Association BC End - Vermont.docx",
				formNumber: "N AO E CLASSA 0019VT 05 25",
			},
			WA: {
				documentName: "N AO E CLASSA 0019WA 05 25 University Aviation Association BC End - Washington.docx",
				formNumber: "N AO E CLASSA 0019WA 05 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_uaa_members_broad_form") {
				const scheduled: { qualifier: string, amount: number }[] = [
					{ qualifier: "Airport operations area excess auto - each occurrence", amount: endorsement.airportOpsAutoEachOccurrenceLimit },
					{ qualifier: "Cargo liability - each occurrence", amount: endorsement.cargoEachOccurrenceLimit },
					{ qualifier: "Garagekeepers' liability - any one auto", amount: endorsement.garagekeepersAnyOneAutoLimit },
					{ qualifier: "Garagekeepers' liability - any one loss", amount: endorsement.garagekeepersAnyOneLossLimit },
					{ qualifier: "Host liquor liability - per occurrence", amount: endorsement.hostLiquorPerOccurrenceLimit },
					{ qualifier: "Host liquor liability - annual aggregate", amount: endorsement.hostLiquorAnnualAggregateLimit },
					{ qualifier: "Incidental medical malpractice - per occurrence and aggregate", amount: endorsement.incidentalMedicalMalpracticeLimit },
					{ qualifier: "Mechanics tools - each employee", amount: endorsement.mechanicsToolsEachEmployeeLimit },
					{ qualifier: "Mechanics tools - each occurrence", amount: endorsement.mechanicsToolsEachOccurrenceLimit },
					{ qualifier: "Student and renter pilot - combined single limit including passengers", amount: endorsement.studentRenterPilotCombinedSingleLimit },
				];
				const limits: Endorsement["limits"] = scheduled
					.filter(row => row.amount > 0)
					.map(row => ({
						qualifier: row.qualifier,
						amount: row.amount,
						premiumIncluded: getPremiumText(endorsement),
					}));
				if (limits.length === 0) {
					limits.push({
						qualifier: "Included",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				endorsements.push({
					name: "University Aviation Association Members Broad Form Coverage",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_uaa_members_broad_form") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					airportOpsAutoEachOccurrenceLimit: formatInt(endorsement.airportOpsAutoEachOccurrenceLimit),
					airportOpsAutoPropertyDamageDeductible: endorsement.airportOpsAutoPropertyDamageDeductible,
					cargoEachOccurrenceLimit: formatInt(endorsement.cargoEachOccurrenceLimit),
					cargoDeductibleEachClaim: formatInt(endorsement.cargoDeductibleEachClaim),
					cargoDeductibleEachOccurrence: formatInt(endorsement.cargoDeductibleEachOccurrence),
					garagekeepersAnyOneAutoLimit: formatInt(endorsement.garagekeepersAnyOneAutoLimit),
					garagekeepersAnyOneLossLimit: formatInt(endorsement.garagekeepersAnyOneLossLimit),
					garagekeepersEachAutoDeductible: formatInt(endorsement.garagekeepersEachAutoDeductible),
					hostLiquorPerOccurrenceLimit: formatInt(endorsement.hostLiquorPerOccurrenceLimit),
					hostLiquorAnnualAggregateLimit: formatInt(endorsement.hostLiquorAnnualAggregateLimit),
					incidentalMedicalMalpracticeLimit: formatInt(endorsement.incidentalMedicalMalpracticeLimit),
					mechanicsToolsEachEmployeeLimit: formatInt(endorsement.mechanicsToolsEachEmployeeLimit),
					mechanicsToolsEachOccurrenceLimit: formatInt(endorsement.mechanicsToolsEachOccurrenceLimit),
					mechanicsToolsEachEmployeeDeductible: formatInt(endorsement.mechanicsToolsEachEmployeeDeductible),
					studentRenterPilotCombinedSingleLimit: formatInt(endorsement.studentRenterPilotCombinedSingleLimit),
					studentRenterPilotLimitsBasis: endorsement.studentRenterPilotLimitsBasis,
				};
			}
			return {};
		},
	},
	cgl_premium_calculation: {
		label: "Premium Calculation Endorsement",
		schema: CglPremiumCalculationEndorsementSchema,
		documentName: "N AO E CLASSA 0007 05 25 Premium Calculation End.docx",
		formNumber: "N AO E CLASSA 0007 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_premium_calculation") {
				endorsements.push({
					name: "Premium Calculation",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_premium_calculation") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					location1Number: endorsement.location1Number,
					location1Classification: endorsement.location1Classification,
					location1Code: endorsement.location1Code,
					location1PremiumBase: endorsement.location1PremiumBase > 0 ? formatInt(endorsement.location1PremiumBase) : "",
					location1RatePremisesOperations: endorsement.location1RatePremisesOperations > 0 ? formatInt(endorsement.location1RatePremisesOperations) : "",
					location1RateProductsCompletedOperations: endorsement.location1RateProductsCompletedOperations > 0 ? formatInt(endorsement.location1RateProductsCompletedOperations) : "",
					location1AdvancePremiumPremisesOperations: endorsement.location1AdvancePremiumPremisesOperations > 0 ? formatInt(endorsement.location1AdvancePremiumPremisesOperations) : "",
					location1AdvancePremiumProductsCompletedOperations: endorsement.location1AdvancePremiumProductsCompletedOperations > 0 ? formatInt(endorsement.location1AdvancePremiumProductsCompletedOperations) : "",
					location2Number: endorsement.location2Number,
					location2Classification: endorsement.location2Classification,
					location2Code: endorsement.location2Code,
					location2PremiumBase: endorsement.location2PremiumBase > 0 ? formatInt(endorsement.location2PremiumBase) : "",
					location2RatePremisesOperations: endorsement.location2RatePremisesOperations > 0 ? formatInt(endorsement.location2RatePremisesOperations) : "",
					location2RateProductsCompletedOperations: endorsement.location2RateProductsCompletedOperations > 0 ? formatInt(endorsement.location2RateProductsCompletedOperations) : "",
					location2AdvancePremiumPremisesOperations: endorsement.location2AdvancePremiumPremisesOperations > 0 ? formatInt(endorsement.location2AdvancePremiumPremisesOperations) : "",
					location2AdvancePremiumProductsCompletedOperations: endorsement.location2AdvancePremiumProductsCompletedOperations > 0 ? formatInt(endorsement.location2AdvancePremiumProductsCompletedOperations) : "",
					location3Number: endorsement.location3Number,
					location3Classification: endorsement.location3Classification,
					location3Code: endorsement.location3Code,
					location3PremiumBase: endorsement.location3PremiumBase > 0 ? formatInt(endorsement.location3PremiumBase) : "",
					location3RatePremisesOperations: endorsement.location3RatePremisesOperations > 0 ? formatInt(endorsement.location3RatePremisesOperations) : "",
					location3RateProductsCompletedOperations: endorsement.location3RateProductsCompletedOperations > 0 ? formatInt(endorsement.location3RateProductsCompletedOperations) : "",
					location3AdvancePremiumPremisesOperations: endorsement.location3AdvancePremiumPremisesOperations > 0 ? formatInt(endorsement.location3AdvancePremiumPremisesOperations) : "",
					location3AdvancePremiumProductsCompletedOperations: endorsement.location3AdvancePremiumProductsCompletedOperations > 0 ? formatInt(endorsement.location3AdvancePremiumProductsCompletedOperations) : "",
				};
			}
			return {};
		},
	},
	cgl_premium_calculation_gross_receipts: {
		label: "Premium Calculation Endorsement - Gross Receipts",
		schema: CglPremiumCalculationGrossReceiptsEndorsementSchema,
		documentName: "N AO E CLASSA 0008 05 25 Premium Calculation End 2.docx",
		formNumber: "N AO E CLASSA 0008 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_premium_calculation_gross_receipts") {
				endorsements.push({
					name: "Premium Calculation - Gross Receipts",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_premium_calculation_gross_receipts") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					companySharePercent: endorsement.companySharePercent,
					premisesFlatAnnualPremium: endorsement.premisesFlatAnnualPremium > 0 ? formatInt(endorsement.premisesFlatAnnualPremium) : "",
					productsCompletedOperationsAdvancePremium: endorsement.productsCompletedOperationsAdvancePremium > 0 ? formatInt(endorsement.productsCompletedOperationsAdvancePremium) : "",
					hangarkeepersFlatAnnualPremium: endorsement.hangarkeepersFlatAnnualPremium > 0 ? formatInt(endorsement.hangarkeepersFlatAnnualPremium) : "",
					endorsementPremium: endorsement.endorsementPremium > 0 ? formatInt(endorsement.endorsementPremium) : "",
					totalPremiumDescription: endorsement.totalPremiumDescription,
					totalRatingBasis: endorsement.totalRatingBasis,
					totalRate: endorsement.totalRate,
					totalPremiumAmount: endorsement.totalPremiumAmount > 0 ? formatInt(endorsement.totalPremiumAmount) : "",
					totalFlatRatedAndAdvancePremium: endorsement.totalFlatRatedAndAdvancePremium > 0 ? formatInt(endorsement.totalFlatRatedAndAdvancePremium) : "",
					minimumProductsCompletedOperationsPremium: endorsement.minimumProductsCompletedOperationsPremium > 0 ? formatInt(endorsement.minimumProductsCompletedOperationsPremium) : "",
					maximumProductsCompletedOperationsPremium: endorsement.maximumProductsCompletedOperationsPremium > 0 ? formatInt(endorsement.maximumProductsCompletedOperationsPremium) : "",
				};
			}
			return {};
		},
	},
	cgl_amendment_limits_products_completed_ops_hazard: {
		label: "Amendment of Limits of Insurance - Products/Completed Operations Hazard",
		schema: CglAmendmentLimitsProductsCompletedOpsHazardEndorsementSchema,
		documentName: "N AO E CLASSA 0006 05 25 Amend Limits of Insurance Product Completed Ops Hazard.docx",
		formNumber: "N AO E CLASSA 0006 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amendment_limits_products_completed_ops_hazard") {
				const scheduled: { qualifier: string, amount: number }[] = [
					{ qualifier: "Turbine engine aircraft - each occurrence, products/completed operations", amount: endorsement.turbineEachOccurrenceProductsCompletedOperations },
					{ qualifier: "Turbine engine aircraft - products/completed operations aggregate", amount: endorsement.turbineProductsCompletedOperationsAggregate },
					{ qualifier: "Piston engine aircraft - each occurrence, products/completed operations", amount: endorsement.pistonEachOccurrenceProductsCompletedOperations },
					{ qualifier: "Piston engine aircraft - products/completed operations aggregate", amount: endorsement.pistonProductsCompletedOperationsAggregate },
				];
				const limits: Endorsement["limits"] = scheduled
					.filter(row => row.amount > 0)
					.map(row => ({
						qualifier: row.qualifier,
						amount: row.amount,
						premiumIncluded: getPremiumText(endorsement),
					}));
				if (limits.length === 0) {
					limits.push({
						qualifier: "Included",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				endorsements.push({
					name: "Amendment of Limits of Insurance - Products/Completed Operations Hazard",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amendment_limits_products_completed_ops_hazard") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					turbineEachOccurrenceProductsCompletedOperations: endorsement.turbineEachOccurrenceProductsCompletedOperations > 0 ? formatInt(endorsement.turbineEachOccurrenceProductsCompletedOperations) : "",
					turbineProductsCompletedOperationsAggregate: endorsement.turbineProductsCompletedOperationsAggregate > 0 ? formatInt(endorsement.turbineProductsCompletedOperationsAggregate) : "",
					pistonEachOccurrenceProductsCompletedOperations: endorsement.pistonEachOccurrenceProductsCompletedOperations > 0 ? formatInt(endorsement.pistonEachOccurrenceProductsCompletedOperations) : "",
					pistonProductsCompletedOperationsAggregate: endorsement.pistonProductsCompletedOperationsAggregate > 0 ? formatInt(endorsement.pistonProductsCompletedOperationsAggregate) : "",
				};
			}
			return {};
		},
	},
	cgl_amendment_deductibles_per_occurrence: {
		label: "Amendment of Deductibles - Per Occurrence",
		schema: CglAmendmentDeductiblesPerOccurrenceEndorsementSchema,
		documentName: "N AO E CLASSA 0010 05 25 Amend Deductibles Per Occurrence.docx",
		formNumber: "N AO E CLASSA 0010 05 25",
		lob: ["commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cgl_amendment_deductibles_per_occurrence") {
				const scheduled: { qualifier: string, amount: number }[] = [
					{ qualifier: "Hangarkeeper's liability deductible - each occurrence", amount: endorsement.hangarkeepersDeductibleEachOccurrence },
					{ qualifier: "Property damage deductible - each occurrence", amount: endorsement.propertyDamageDeductibleEachOccurrence },
				];
				const limits: Endorsement["limits"] = scheduled
					.filter(row => row.amount > 0)
					.map(row => ({
						qualifier: row.qualifier,
						amount: row.amount,
						premiumIncluded: getPremiumText(endorsement),
					}));
				if (limits.length === 0) {
					limits.push({
						qualifier: "Included",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				endorsements.push({
					name: "Amendment of Deductibles - Per Occurrence",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "cgl_amendment_deductibles_per_occurrence") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					hangarkeepersDeductibleEachOccurrence: endorsement.hangarkeepersDeductibleEachOccurrence > 0 ? formatInt(endorsement.hangarkeepersDeductibleEachOccurrence) : "",
					propertyDamageDeductibleEachOccurrence: endorsement.propertyDamageDeductibleEachOccurrence > 0 ? formatInt(endorsement.propertyDamageDeductibleEachOccurrence) : "",
				};
			}
			return {};
		},
	},
	right_of_first_refusal: {
		label: "Right of First Refusal Endorsement",
		schema: RightOfFirstRefusalEndorsementSchema,
		documentName: "N AC E CLASSA 0073 05 25 Right of First Refusal Endorsement.docx",
		formNumber: "N AC E CLASSA 0073 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "right_of_first_refusal") {
				endorsements.push({
					name: "Right of First Refusal",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "right_of_first_refusal") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
				};
			}
			return {};
		},
	},
	minimum_earned_premium: {
		label: "Minimum Earned Premium Endorsement",
		schema: MinimumEarnedPremiumEndorsementSchema,
		documentName: "N AV E CLASSA 0106 05 25 Minimum Earned Premium Endorsement.docx",
		formNumber: "N AV E CLASSA 0106 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement, totalPremium }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "minimum_earned_premium") {
				const pct = endorsement.percentageOfTotalPremium ?? 0;
				endorsements.push({
					name: "Minimum Earned Premium",
					included: true,
					limits: [
						{
							qualifier: `${pct}% of total premium`,
							amount: (pct / 100) * (totalPremium ?? 0),
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement, ctx?: RenderContext) => {
			if (endorsement.slug !== "minimum_earned_premium") {
				return {};
			}
			const pct = endorsement.percentageOfTotalPremium ?? 0;
			const base = ctx?.totalBasePremium ?? 0;
			return { minimumEarnedPremium: formatCurrency((pct / 100) * base) };
		},
	},
	amendatory: {
		label: "Amendatory Endorsement",
		schema: AmendatoryEndorsementSchema,
		documentName: "N AV E CLASSA 0107 05 25 Amendatory Endorsement.docx",
		formNumber: "N AV E CLASSA 0107 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "amendatory") {
				endorsements.push({
					name: "Amendatory Endorsement",
					included: true,
					limits: [
						{
							qualifier: "",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "amendatory") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				amendatoryEndorsementDescription: endorsement.description,
			};
		},
	},
	air_ambulance_use: {
		label: "Air Ambulance Use Endorsement",
		schema: AirAmbulanceUseEndorsementSchema,
		documentName: "N AV E CLASSA 0076 05 25 Air Ambulance Use Endorsement.docx",
		formNumber: "N AV E CLASSA 0076 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "air_ambulance_use") {
				endorsements.push({
					name: "Air Ambulance Use",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "air_ambulance_use") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
			};
		},
	},
	purpose_of_use: {
		label: "Purpose of Use Endorsement",
		schema: PurposeOfUseEndorsementSchema,
		documentName: "N AV E CLASSA 0021 05 25 Purpose of Use Endorsement.docx",
		formNumber: "N AV E CLASSA 0021 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "purpose_of_use") {
				endorsements.push({
					name: "Purpose of Use Endorsement",
					included: true,
					limits: [
						{
							qualifier: endorsement.purposeOfUseDescription?.trim() ?? "",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "purpose_of_use") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				purposeOfUseDescription: endorsement.purposeOfUseDescription,
			};
		},
	},
	sales_demonstration_use: {
		label: "Sales Demonstration Use Endorsement",
		schema: SalesDemonstrationUseEndorsementSchema,
		documentName: "N AV E CLASSA 0078 05 25 Sales Demonstration Use Endorsement.docx",
		formNumber: "N AV E CLASSA 0078 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "sales_demonstration_use") {
				endorsements.push({
					name: "Sales Demonstration Use Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "sales_demonstration_use") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
			};
		},
	},
	leased_aircraft_contingent: {
		label: "Leased Aircraft Contingent Liability and Physical Damage Endorsement",
		schema: LeasedAircraftContingentEndorsementSchema,
		documentName: "N AV E CLASSA 0108 05 25 Leased Aircraft Contingent Liability and Physical Damage Endorsement.docx",
		formNumber: "N AV E CLASSA 0108 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "leased_aircraft_contingent") {
				endorsements.push({
					name: "Leased Aircraft Contingent Liability and Physical Damage",
					included: true,
					limits: [
						{
							qualifier: "Combined single limit each occurrence",
							amount: endorsement.combinedSingleLimitEachOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Physical damage agreed value",
							amount: endorsement.physicalDamageAgreedValue,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "leased_aircraft_contingent") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				combinedSingleLimitEachOccurrence: nullishCurrencyOrBlank(endorsement.combinedSingleLimitEachOccurrence),
				physicalDamageAgreedValue: nullishCurrencyOrBlank(endorsement.physicalDamageAgreedValue),
				scheduleAsHeldOnFile: renderCheckbox(endorsement.scheduleAsHeldOnFile),
				aircraft: endorsement.aircraftSchedule.map((row) => ({
					tailNumber: row.tailNumber,
					makeModel: row.makeModel,
					aircraftYear: row.aircraftYear,
					aircraftInsuredValue: nullishCurrencyOrBlank(row.aircraftInsuredValue),
					aircraftDeductibles: row.aircraftDeductibles,
				})),
			};
		},
	},
	amended_aircraft_schedule: {
		label: "Amended Aircraft Schedule",
		schema: AmendedAircraftScheduleEndorsementSchema,
		documentName: "N AC E CLASSA 0001 05 25 Amended Aircraft Schedule Endorsement.docx",
		formNumber: "N AC E CLASSA 0001 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "amended_aircraft_schedule") {
				return [];
			}
			const scheduledTails = endorsement.aircraftSchedule
				.map((row) => row.tailNumber.trim())
				.filter((tail) => tail.length > 0);
			const description = endorsement.aircraftDescription.trim() || scheduledTails.join(", ");
			const limits: Endorsement["limits"] = [
				{
					qualifier: description || "As scheduled",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				},
			];
			const limitRows: Array<[string, number | null]> = [
				["Single limit each occurrence", endorsement.singleLimitEachOccurrence],
				["Each passenger", endorsement.singleLimitEachPassenger],
				["Bodily injury each person", endorsement.bodilyInjuryEachPerson],
				["Bodily injury each occurrence", endorsement.bodilyInjuryEachOccurrence],
				["Property damage each occurrence", endorsement.propertyDamageEachOccurrence],
				["Passenger liability each passenger", endorsement.passengerLiabilityEachPassenger],
				["Passenger liability each occurrence", endorsement.passengerLiabilityEachOccurrence],
				["Medical expense each person", endorsement.medicalExpenseEachPerson],
				["Medical expense each occurrence", endorsement.medicalExpenseEachOccurrence],
			];
			for (const [qualifier, amount] of limitRows) {
				if (amount !== null) {
					limits.push({
						qualifier, amount, premiumIncluded: "", 
					});
				}
			}
			return [{
				name: "Amended Aircraft Schedule",
				included: true,
				limits,
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "amended_aircraft_schedule") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				aircraftDescription: endorsement.aircraftDescription,
				coverageAPer: nullishCurrencyOrBlank(endorsement.bodilyInjuryEachPerson),
				coverageAOcc: nullishCurrencyOrBlank(endorsement.bodilyInjuryEachOccurrence),
				coverageBOcc: nullishCurrencyOrBlank(endorsement.propertyDamageEachOccurrence),
				coverageCPax: nullishCurrencyOrBlank(endorsement.passengerLiabilityEachPassenger),
				coverageCOcc: nullishCurrencyOrBlank(endorsement.passengerLiabilityEachOccurrence),
				coverageDOcc: nullishCurrencyOrBlank(endorsement.singleLimitEachOccurrence),
				coverageDPax: nullishCurrencyOrBlank(endorsement.singleLimitEachPassenger),
				coverageEPer: nullishCurrencyOrBlank(endorsement.medicalExpenseEachPerson),
				coverageEOcc: nullishCurrencyOrBlank(endorsement.medicalExpenseEachOccurrence),
				aircraft: endorsement.aircraftSchedule.map(physicalDamageAircraftRowVariables),
			};
		},
		initialize: ({ liabilityLimit }): typeof AmendedAircraftScheduleEndorsementSchema.static => {
			const endorsement = Value.Default(AmendedAircraftScheduleEndorsementSchema, {});
			if (Value.Check(AmendedAircraftScheduleEndorsementSchema, endorsement)) {
				endorsement.singleLimitEachOccurrence = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize Amended Aircraft Schedule Endorsement");
		},
	} satisfies EndorsementConfig<typeof AmendedAircraftScheduleEndorsementSchema.static>,
	managed_aircraft: {
		label: "Managed Aircraft Endorsement",
		schema: ManagedAircraftEndorsementSchema,
		documentName: "N AC E CLASSA 0003 05 25 Managed Aircraft Endorsement.docx",
		formNumber: "N AC E CLASSA 0003 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "managed_aircraft") {
				return [];
			}
			const scheduledTails = endorsement.aircraftSchedule
				.map((row) => row.tailNumber.trim())
				.filter((tail) => tail.length > 0)
				.join(", ");
			const owner = endorsement.insuredOwner.trim();
			const description = owner && scheduledTails ? `${owner}: ${scheduledTails}` : owner || scheduledTails;
			const limits: Endorsement["limits"] = [
				{
					qualifier: description || "As scheduled",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				},
			];
			const limitRows: Array<[string, number | null]> = [
				["Single limit each occurrence", endorsement.singleLimitEachOccurrence],
				["Each passenger", endorsement.singleLimitEachPassenger],
				["Bodily injury each person", endorsement.bodilyInjuryEachPerson],
				["Bodily injury each occurrence", endorsement.bodilyInjuryEachOccurrence],
				["Property damage each occurrence", endorsement.propertyDamageEachOccurrence],
				["Passenger liability each passenger", endorsement.passengerLiabilityEachPassenger],
				["Passenger liability each occurrence", endorsement.passengerLiabilityEachOccurrence],
				["Medical expense each person", endorsement.medicalExpenseEachPerson],
				["Medical expense each occurrence", endorsement.medicalExpenseEachOccurrence],
			];
			for (const [qualifier, amount] of limitRows) {
				if (amount !== null) {
					limits.push({
						qualifier, amount, premiumIncluded: "",
					});
				}
			}
			return [{
				name: "Managed Aircraft",
				included: true,
				limits,
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "managed_aircraft") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				coverageAPer: nullishCurrencyOrBlank(endorsement.bodilyInjuryEachPerson),
				coverageAOcc: nullishCurrencyOrBlank(endorsement.bodilyInjuryEachOccurrence),
				coverageBOcc: nullishCurrencyOrBlank(endorsement.propertyDamageEachOccurrence),
				coverageCPax: nullishCurrencyOrBlank(endorsement.passengerLiabilityEachPassenger),
				coverageCOcc: nullishCurrencyOrBlank(endorsement.passengerLiabilityEachOccurrence),
				coverageDOcc: nullishCurrencyOrBlank(endorsement.singleLimitEachOccurrence),
				coverageDPax: nullishCurrencyOrBlank(endorsement.singleLimitEachPassenger),
				coverageEPer: nullishCurrencyOrBlank(endorsement.medicalExpenseEachPerson),
				coverageEOcc: nullishCurrencyOrBlank(endorsement.medicalExpenseEachOccurrence),
				aircraft: endorsement.aircraftSchedule.map(physicalDamageAircraftRowVariables),
				insuredOwner: endorsement.insuredOwner,
				nonOwnedAPer: nullishCurrencyOrBlank(endorsement.nonOwnedBodilyInjuryEachPerson),
				nonOwnedAOcc: nullishCurrencyOrBlank(endorsement.nonOwnedBodilyInjuryEachOccurrence),
				nonOwnedBOcc: nullishCurrencyOrBlank(endorsement.nonOwnedPropertyDamageEachOccurrence),
				nonOwnedCPax: nullishCurrencyOrBlank(endorsement.nonOwnedPassengerLiabilityEachPassenger),
				nonOwnedCOcc: nullishCurrencyOrBlank(endorsement.nonOwnedPassengerLiabilityEachOccurrence),
				nonOwnedDOcc: nullishCurrencyOrBlank(endorsement.nonOwnedSingleLimitEachOccurrence),
				nonOwnedDPax: nullishCurrencyOrBlank(endorsement.nonOwnedSingleLimitEachPassenger),
				nonOwnedEPer: nullishCurrencyOrBlank(endorsement.nonOwnedMedicalExpenseEachPerson),
				nonOwnedEOcc: nullishCurrencyOrBlank(endorsement.nonOwnedMedicalExpenseEachOccurrence),
				premisesLiability: nullishCurrencyOrBlank(endorsement.premisesLiabilityEachOccurrence),
				productsLiability: nullishCurrencyOrBlank(endorsement.productsLiabilityEachOccurrence),
				hostLiquorLiability: nullishCurrencyOrBlank(endorsement.hostLiquorLiabilityAggregate),
				mobileEquipmentLiability: nullishCurrencyOrBlank(endorsement.mobileEquipmentLiabilityEachOccurrence),
				personalInjuryLiability: nullishCurrencyOrBlank(endorsement.personalInjuryLiabilityEachOffense),
				lienholders: endorsement.lienholders.map((row) => ({
					registration: row.registration,
					lienholder: row.lienholder,
				})),
			};
		},
		initialize: ({ liabilityLimit }): typeof ManagedAircraftEndorsementSchema.static => {
			const endorsement = Value.Default(ManagedAircraftEndorsementSchema, {});
			if (Value.Check(ManagedAircraftEndorsementSchema, endorsement)) {
				endorsement.singleLimitEachOccurrence = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize Managed Aircraft Endorsement");
		},
	} satisfies EndorsementConfig<typeof ManagedAircraftEndorsementSchema.static>,
	managed_aircraft_broad: {
		label: "Broad Form Managed Aircraft Endorsement",
		schema: ManagedAircraftBroadEndorsementSchema,
		documentName: "N AC E CLASSA 0004 05 25 Broad Form Managed Aircraft Endorsement.docx",
		formNumber: "N AC E CLASSA 0004 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "managed_aircraft_broad") {
				return [];
			}
			const scheduledTails = endorsement.aircraftSchedule
				.map((row) => row.tailNumber.trim())
				.filter((tail) => tail.length > 0)
				.join(", ");
			const owner = endorsement.insuredOwner.trim();
			const description = owner && scheduledTails ? `${owner}: ${scheduledTails}` : owner || scheduledTails;
			const limits: Endorsement["limits"] = [
				{
					qualifier: description || "As scheduled",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				},
			];
			const limitRows: Array<[string, number | null]> = [
				["Single limit each occurrence", endorsement.singleLimitEachOccurrence],
				["Each passenger", endorsement.singleLimitEachPassenger],
				["Bodily injury each person", endorsement.bodilyInjuryEachPerson],
				["Bodily injury each occurrence", endorsement.bodilyInjuryEachOccurrence],
				["Property damage each occurrence", endorsement.propertyDamageEachOccurrence],
				["Passenger liability each passenger", endorsement.passengerLiabilityEachPassenger],
				["Passenger liability each occurrence", endorsement.passengerLiabilityEachOccurrence],
				["Medical expense each person", endorsement.medicalExpenseEachPerson],
				["Medical expense each occurrence", endorsement.medicalExpenseEachOccurrence],
			];
			for (const [qualifier, amount] of limitRows) {
				if (amount !== null) {
					limits.push({
						qualifier, amount, premiumIncluded: "",
					});
				}
			}
			return [{
				name: "Broad Form Managed Aircraft",
				included: true,
				limits,
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "managed_aircraft_broad") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				coverageAPer: nullishCurrencyOrBlank(endorsement.bodilyInjuryEachPerson),
				coverageAOcc: nullishCurrencyOrBlank(endorsement.bodilyInjuryEachOccurrence),
				coverageBOcc: nullishCurrencyOrBlank(endorsement.propertyDamageEachOccurrence),
				coverageCPax: nullishCurrencyOrBlank(endorsement.passengerLiabilityEachPassenger),
				coverageCOcc: nullishCurrencyOrBlank(endorsement.passengerLiabilityEachOccurrence),
				coverageDOcc: nullishCurrencyOrBlank(endorsement.singleLimitEachOccurrence),
				coverageDPax: nullishCurrencyOrBlank(endorsement.singleLimitEachPassenger),
				coverageEPer: nullishCurrencyOrBlank(endorsement.medicalExpenseEachPerson),
				coverageEOcc: nullishCurrencyOrBlank(endorsement.medicalExpenseEachOccurrence),
				aircraft: endorsement.aircraftSchedule.map(physicalDamageAircraftRowVariables),
				insuredOwner: endorsement.insuredOwner,
				nonOwnedCoverageAPer: nullishCurrencyOrBlank(endorsement.nonOwnedBodilyInjuryEachPerson),
				nonOwnedCoverageAOcc: nullishCurrencyOrBlank(endorsement.nonOwnedBodilyInjuryEachOccurrence),
				nonOwnedCoverageBOcc: nullishCurrencyOrBlank(endorsement.nonOwnedPropertyDamageEachOccurrence),
				nonOwnedCoverageCPax: nullishCurrencyOrBlank(endorsement.nonOwnedPassengerLiabilityEachPassenger),
				nonOwnedCoverageCOcc: nullishCurrencyOrBlank(endorsement.nonOwnedPassengerLiabilityEachOccurrence),
				nonOwnedCoverageDOcc: nullishCurrencyOrBlank(endorsement.nonOwnedSingleLimitEachOccurrence),
				nonOwnedCoverageDPax: nullishCurrencyOrBlank(endorsement.nonOwnedSingleLimitEachPassenger),
				nonOwnedCoverageEPer: nullishCurrencyOrBlank(endorsement.nonOwnedMedicalExpenseEachPerson),
				nonOwnedCoverageEOcc: nullishCurrencyOrBlank(endorsement.nonOwnedMedicalExpenseEachOccurrence),
				premisesLiabilityEachOccurrence: nullishCurrencyOrBlank(endorsement.premisesLiabilityEachOccurrence),
				productsLiabilityEachOccurrence: nullishCurrencyOrBlank(endorsement.productsLiabilityEachOccurrence),
				hostLiquorLiabilityAggregate: nullishCurrencyOrBlank(endorsement.hostLiquorLiabilityAggregate),
				mobileEquipmentLiabilityEachOccurrence: nullishCurrencyOrBlank(endorsement.mobileEquipmentLiabilityEachOccurrence),
				personalInjuryLiabilityEachOffense: nullishCurrencyOrBlank(endorsement.personalInjuryLiabilityEachOffense),
				cargoEachOccurrenceLimit: nullishCurrencyOrBlank(endorsement.cargoEachOccurrenceLimit),
				cargoDeductibleEachClaim: nullishCurrencyOrBlank(endorsement.cargoDeductibleEachClaim),
				cargoDeductibleEachOccurrence: nullishCurrencyOrBlank(endorsement.cargoDeductibleEachOccurrence),
				spareEnginesPartsEachLossLimit: nullishCurrencyOrBlank(endorsement.spareEnginesPartsEachLossLimit),
				spareEnginesPartsInMotionDeductible: nullishCurrencyOrBlank(endorsement.spareEnginesPartsInMotionDeductible),
				spareEnginesPartsNotInMotionDeductible: nullishCurrencyOrBlank(endorsement.spareEnginesPartsNotInMotionDeductible),
				garagekeepersAnyOneAutoLimit: nullishCurrencyOrBlank(endorsement.garagekeepersAnyOneAutoLimit),
				garagekeepersAnyOneLossLimit: nullishCurrencyOrBlank(endorsement.garagekeepersAnyOneLossLimit),
				garagekeepersEachAutoDeductible: nullishCurrencyOrBlank(endorsement.garagekeepersEachAutoDeductible),
				lienholders: endorsement.lienholders.map((row) => ({
					registration: row.registration,
					lienholder: row.lienholder,
				})),
			};
		},
		initialize: ({ liabilityLimit }): typeof ManagedAircraftBroadEndorsementSchema.static => {
			const endorsement = Value.Default(ManagedAircraftBroadEndorsementSchema, {});
			if (Value.Check(ManagedAircraftBroadEndorsementSchema, endorsement)) {
				endorsement.singleLimitEachOccurrence = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize Broad Form Managed Aircraft Endorsement");
		},
	} satisfies EndorsementConfig<typeof ManagedAircraftBroadEndorsementSchema.static>,
	leased_engine: {
		label: "Leased, Loaned or Rented Engine Coverage",
		schema: LeasedEngineEndorsementSchema,
		documentName: "N AV E CLASSA 0027 05 25 Leased, Loaned or Rented Engine Coverage.docx",
		formNumber: "N AV E CLASSA 0027 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "leased_engine") {
				return [];
			}
			return [{
				name: "Leased, Loaned or Rented Engine Coverage",
				included: true,
				limits: [{
					qualifier: "Maximum limit any one engine",
					amount: endorsement.maxEngineLimit,
					premiumIncluded: getPremiumText(endorsement),
				}, {
					qualifier: "Deductible each loss, engine attached to an insured aircraft",
					amount: endorsement.deductibleAttached,
					premiumIncluded: "",
				}, {
					qualifier: "Deductible each loss, otherwise",
					amount: endorsement.deductibleNotAttached,
					premiumIncluded: "",
				}],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "leased_engine") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				maxEngineLimit: nullishCurrencyOrBlank(endorsement.maxEngineLimit),
				deductibleAttached: formatCurrency(endorsement.deductibleAttached),
				deductibleNotAttached: formatCurrency(endorsement.deductibleNotAttached),
			};
		},
	},
	leased_engine_scheduled: {
		label: "Leased, Loaned or Rented Engine Coverage – Scheduled",
		schema: LeasedEngineScheduledEndorsementSchema,
		documentName: "N AV E CLASSA 0028 05 25 Leased, Loaned or Rented Engine Coverage - Scheduled.docx",
		formNumber: "N AV E CLASSA 0028 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "leased_engine_scheduled") {
				return [];
			}
			const limits: Endorsement["limits"] = endorsement.engineSchedule.map((row, index) => ({
				qualifier: `${leasedEngineDescription(row)} on ${row.tailNumber.trim()}`.trim(),
				amount: row.insuredValue,
				premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
			}));
			if (limits.length === 0) {
				limits.push({
					qualifier: "As scheduled",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				});
			}
			limits.push({
				qualifier: "Deductible each loss",
				amount: endorsement.deductibleEachLoss,
				premiumIncluded: "",
			});
			return [{
				name: "Leased, Loaned or Rented Engine Coverage – Scheduled",
				included: true,
				limits,
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "leased_engine_scheduled") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				deductibleAmountEachLoss: formatCurrency(endorsement.deductibleEachLoss),
				engines: endorsement.engineSchedule.map((row) => ({
					engineDescription: leasedEngineDescription(row),
					tailNumber: row.tailNumber,
					ownerLessor: row.ownerLessor,
					insuredValue: nullishCurrencyOrBlank(row.insuredValue),
				})),
			};
		},
	},
	war_hijacking_perils: {
		label: "War Liability Write-Back",
		schema: WarHijackingPerilsEndorsementSchema,
		documentName: "N AC E CLASSA 0047 05 25 War Hijack Other Perils Lim Liab Endo.docx",
		hasStateOverride: true,
		formNumber: "N AC E CLASSA 0047 05 25",  
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "war_hijacking_perils") {
				let qualifier = "All scheduled aircraft";
				let amount: number | null = null;
				if (endorsement.allReportedAircraft) {
					qualifier = "All reported aircraft";
				}
				if (endorsement.sameAsSingleLimit) {
					qualifier = "Same as single limit";
					amount = endorsement.singleLimitMax;
				}
				if (endorsement.limit) {
					qualifier = "Limit";
					amount = endorsement.limitAmount;
				}
				if (endorsement.singleLimitMax) {
					qualifier = "Single limit max";
				}
				const limits: Endorsement["limits"] = [
					{
						qualifier,
						amount,
						premiumIncluded: getPremiumTextPricedAbove(endorsement),
					},
				];
				appendAutoExpirationPrintLimit(limits, endorsement.autoExpiration);
				endorsements.push({
					name: "War Liability Write-Back",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "war_hijacking_perils") {
				const variables: Record<string, string> = {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					allScheduledAircraft: renderCheckbox(endorsement.allScheduledAircraft),
					allReportedAircraft: renderCheckbox(endorsement.allReportedAircraft),
					limit: renderCheckbox(endorsement.limit),
					limitAmount: conditionallyFormatCurrency({
						value: endorsement.limitAmount,
						hasCoverage: endorsement.limit,
					}),
					sameAsSingleLimit: renderCheckbox(endorsement.sameAsSingleLimit),
					singleLimitMax: conditionallyFormatCurrency({
						value: endorsement.singleLimitMax,
						hasCoverage: endorsement.sameAsSingleLimit,
					}),
					allScheduledLabel: "All scheduled",
					allScheduledBoldLabel: "Aircraft",
				};
				if (endorsement.selectedTailNumbers?.length) { 
					variables.allScheduledAircraft = renderCheckbox(true);
					variables.allReportedAircraft = renderCheckbox(false);
					variables.allScheduledLabel = `Solely as respects to: ${endorsement.selectedTailNumbers.join(", ")}`;
					variables.allScheduledBoldLabel = "";
				}
				return {
					...variables,
					...autoExpirationPrintVariables(endorsement),
				};
			}
			return {};
		},
		initialize: ({ liabilityLimit }): typeof WarHijackingPerilsEndorsementSchema.static => {
			const endorsement = Value.Default(WarHijackingPerilsEndorsementSchema, {});
			if (Value.Check(WarHijackingPerilsEndorsementSchema, endorsement)) {
				endorsement.limitAmount = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize War Hijacking Perils Endorsement");
		},
	} satisfies EndorsementConfig<typeof WarHijackingPerilsEndorsementSchema.static>,
	war_hijacking_physical_damage: {
		label: "War Hijacking Physical Damage Endorsement (Hull War)",
		schema: WarHijackingPhysicalDamageEndorsementSchema,
		documentName: "N AV E CLASSA 0034 05 25 War Hijacking Ext and Other Perils Physical Damage Write Back End.docx",
		formNumber: "N AV E CLASSA 0034 05 25",  
		lob: ["general_aviation", "uas"],
		hasStateOverride: true,
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "war_hijacking_physical_damage") {
				endorsements.push({
					name: "Hull War Physical Damage",
					included: true,
					limits: [
						{
							qualifier: "",
							amount: null,
							premiumIncluded: getPremiumTextPricedAbove(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "war_hijacking_physical_damage") {
				const aircraftSchedule = endorsement.selectedTailNumbers?.length ?
					`Solely as respects to the following aircraft: ${endorsement.selectedTailNumbers.join(", ")}\n\n` :
					"";
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					aircraftSchedule,
				};
			}
			return {};
		},
	} satisfies EndorsementConfig<typeof WarHijackingPhysicalDamageEndorsementSchema.static>,
	tria_liability: {
		label: "Tria Liability Write-Back",
		schema: TriaLiabilityEndorsementSchema,
		documentName: "N AV E CLASSA 0121 05 25 TRIA Liability Write-Back.docx",
		formNumber: "N AV E CLASSA 0121 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "tria_liability") {
				endorsements.push({
					name: "Tria Liability Write-Back",
					included: true,
					limits: [
						{
							qualifier: "",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "tria_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	tria_hull_write_back: {
		label: "Tria Hull Write-Back - Physical Damage",
		schema: TriaHullWriteBackEndorsementSchema,
		documentName: "N AV E CLASSA 0120 05 25 TRIA Hull Write-Back Physical Damage Coverage.docx",
		formNumber: "N AV E CLASSA 0120 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement, insuredValue }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
            
			if (endorsement.slug === "tria_hull_write_back") {
				const limits = [{
					qualifier: qualifierLabels.each_occurrence,
					amount: insuredValue ?? null,
					premiumIncluded: "If Hull War Purchased",
				}];
				if (endorsement.excludedAircraftSchedule.length > 0) {
					const tails = endorsement.excludedAircraftSchedule.map(aircraft => aircraft.tailNumber).join(", ");
					limits.push({
						qualifier: `Excluded: ${tails}`,
						amount: null,
						premiumIncluded: "",
					});
				}
				endorsements.push({
					name: "Tria Hull Write-Back - Physical Damage",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "tria_hull_write_back") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					excludedAircraftSchedule: endorsement.excludedAircraftSchedule.map(aircraft => ({
						tailNumber: aircraft.tailNumber,
						makeModel: aircraft.makeModel,
						year: aircraft.year,
						insuredValue: formatCurrency(aircraft.insuredValue),
					})),
				};
			}
			return {};
		},
	},
	cargo_liability: {
		label: "Cargo Liability Endorsement",
		schema: CargoLiabilityEndorsementSchema,
		documentName: "N AC E CLASSA 0052 05 25 CARGO LIABILITY ENDORSEMENT.docx",
		formNumber: "N AC E CLASSA 0052 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cargo_liability") {
				endorsements.push({
					name: "Cargo Liability",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.liabilityLimitEachOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.deductible,
							amount: endorsement.deductible,
							premiumIncluded: "",
						},
						{
							qualifier: "Max Deductible",
							amount: endorsement.maxDeductible,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables (endorsement: GenericEndorsement) {
			if (endorsement.slug === "cargo_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					liabilityLimitEachOccurence: formatCurrency(endorsement.liabilityLimitEachOccurrence),
					deductible: formatCurrency(endorsement.deductible),
					maxDeductible: formatCurrency(endorsement.maxDeductible),
				};
			}
			return {};
		},
	},
	automatic_insurance_for_increased_value: {
		label: "Automatic Insurance for Increased Value Endorsement",
		schema: AutomaticInsuranceForIncreasedValueEndorsementSchema,
		documentName: "N AC E CLASSA 0031 05 25  Automatic Insurance for Increased Insured Value Endorsement.docx",
		formNumber: "N AC E CLASSA 0031 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "automatic_insurance_for_increased_value") {
				endorsements.push({
					name: "Automatic Increase in Insured Value for Scheduled Aircraft",
					included: true,
					limits: [
						{
							qualifier: `${endorsement.maxPercentageOfInsuredAmount}% or ${formatCurrency(endorsement.maxInsuredValue)} whichever is lesser`,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "automatic_insurance_for_increased_value") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					maxPercentageOfInsuredAmount: conditionallyFormatPercentage({
						value: endorsement.maxPercentageOfInsuredAmount,
						hasCoverage: true,
					}),
					maxInsuredValue: formatCurrency(endorsement.maxInsuredValue),
				};
			}
			return {};
		},
		initialize: ({ hullValue }): typeof AutomaticInsuranceForIncreasedValueEndorsementSchema.static => {
			const endorsement = Value.Default(AutomaticInsuranceForIncreasedValueEndorsementSchema, {});
			if (Value.Check(AutomaticInsuranceForIncreasedValueEndorsementSchema, endorsement)) {
				endorsement.maxInsuredValue = hullValue + (hullValue * 0.25);
				return endorsement;
			}
			throw new Error("Failed to initialize Automatic Insurance for Increased Value Endorsement");
		},
	} as EndorsementConfig<typeof AutomaticInsuranceForIncreasedValueEndorsementSchema.static>,
	airport_ops_area_auto: {
		label: "Airport Operations Area Auto Coverage Endorsement",
		schema: AirportOpsAreaAutoEndorsementSchema,
		documentName:
      "N AC E CLASSA 0071 05 25 Airport Operations Area Auto Coverage Endorsement.docx",
		formNumber: "N AC E CLASSA 0071 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "airport_ops_area_auto") {
				endorsements.push({
					name: "Airport Operations Area Auto Coverage",
					included: true,
					limits: [
						{
							qualifier: limitQualifierForPrint(endorsement.liabilityLimit, qualifierLabels.each_occurrence),
							amount: endorsement.liabilityLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.deductible,
							amount: endorsement.deductible === null ? "NIL" : endorsement.deductible,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "airport_ops_area_auto") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					liabilityLimit: formatLimitOrFollowsAircraft(endorsement.liabilityLimit),
					deductible: formatCurrency(endorsement.deductible),
				};
			}
			return {};
		},
		initialize: ({ liabilityLimit }): typeof AirportOpsAreaAutoEndorsementSchema.static => {
			const endorsement = Value.Default(AirportOpsAreaAutoEndorsementSchema, {});
			if (Value.Check(AirportOpsAreaAutoEndorsementSchema, endorsement)) {
				endorsement.liabilityLimit = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize Airport Operations Area Auto Coverage Endorsement");
		},
	} satisfies EndorsementConfig<typeof AirportOpsAreaAutoEndorsementSchema.static>,
	excess_employers_liability: {
		label: "Excess Employer’s Liability Coverage Endorsement",
		schema: ExcessEmployersLiabilityEndorsementSchema,
		documentName:
      "N AV E CLASSA 0054 05 25 Excess Employers Liability Coverage Endorsement.docx",
		formNumber: "N AV E CLASSA 0054 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "excess_employers_liability") {
				endorsements.push({
					name: "Excess Employer’s Liability Coverage",
					included: true,
					limits: [
						{
							qualifier: `${qualifierLabels.each_occurrence}, excess of $1,000,000 or the statutory minimum`,
							amount: endorsement.excessLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "excess_employers_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					excessLimit: formatCurrency(endorsement.excessLimit),
					primaryInsurer: endorsement.primaryInsurer,
					primaryPolicyNumber: endorsement.primaryPolicyNumber,
					primaryLimit: formatCurrency(endorsement.primaryLimit),
				};
			}
			return {};
		},
	} satisfies EndorsementConfig<typeof ExcessEmployersLiabilityEndorsementSchema.static>,
	excess_auto_liability: {
		label: "Excess Auto Liability Coverage Endorsement",
		schema: ExcessAutoLiabilityEndorsementSchema,
		documentName: "N AC E CLASSA 0067 05 25 Excess Auto Liability Coverage Endorsement.docx",
		formNumber: "N AC E CLASSA 0067 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "excess_auto_liability") {
				endorsements.push({
					name: "Excess Auto Liability Coverage",
					included: true,
					limits: [
						{
							qualifier: `${qualifierLabels.each_occurrence}, excess of ${formatCurrency(endorsement.primaryLimit)}`,
							amount: endorsement.excessLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "excess_auto_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					excessLimit: formatCurrency(endorsement.excessLimit),
					primaryLimit: formatCurrency(endorsement.primaryLimit),
					primaryPolicies: endorsement.primaryPolicies.map((row) => ({
						insurer: row.insurer,
						policyNumber: row.policyNumber,
						policyPeriod: [row.periodFrom, row.periodTo]
							.map((date) => date ? formatDateFullMonth(date.slice(0, 10), "UTC") : "")
							.filter(Boolean)
							.join(" to "),
					})),
				};
			}
			return {};
		},
	} satisfies EndorsementConfig<typeof ExcessAutoLiabilityEndorsementSchema.static>,
	damage_to_premises: {
		label: "Damage to Premises Endorsement (Fire Legal)",
		schema: DamageToPremisesEndorsementSchema,
		documentName:
            "N AC E CLASSA 0072 05 25 Damage to Premises Endorsement (Fire Legal).docx",
		formNumber: "N AC E CLASSA 0072 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "damage_to_premises") {
				endorsements.push({
					name: "Damage to Premises (Fire Legal)",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.liabilityLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "damage_to_premises") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					liabilityLimit: formatCurrency(endorsement.liabilityLimit),
				};
			}
			return {};
		},
	},
	depreciated_value_coverage: {
		label: "Depreciated Value Coverage Endorsement",
		schema: DepreciatedValueCoverageEndorsementSchema,
		documentName: "N AV E CLASSA 0110 05 25 Depreciated Value Coverage Endorsement.docx",
		formNumber: "N AV E CLASSA 0110 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "depreciated_value_coverage") {
				const scheduleLimits = endorsement.aircraftSchedule.map((row, index) => ({
					qualifier: `${row.tailNumber} — ${row.makeModel}: Max indemnity ${row.maxIndemnityPercent}%, Retention ${row.retentionPercent}%`,
					amount: null,
					premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
				}));
				endorsements.push({
					name: "Depreciated Value Coverage",
					included: true,
					limits: scheduleLimits.length > 0
						? scheduleLimits
						: [{
							qualifier: qualifierLabels.each_aircraft,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						}],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "depreciated_value_coverage") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					aircraft: endorsement.aircraftSchedule.map((row) => ({
						tailNumber: row.tailNumber,
						makeModel: row.makeModel,
						maxIndemnityPercent: conditionallyFormatPercentage({
							value: row.maxIndemnityPercent,
							hasCoverage: true,
						}),
						retentionPercent: conditionallyFormatPercentage({
							value: row.retentionPercent,
							hasCoverage: true,
						}),
					})),
				};
			}
			return {};
		},
	},
	garagekeepers: {
		label: "Garagekeepers' Liability Endorsement",
		schema: GaragekeepersEndorsementSchema,
		documentName:
            "N AC E CLASSA 0070 05 25 Garagekeeper's Liability Endorsement.docx",
		formNumber: "N AC E CLASSA 0070 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "garagekeepers") {
				endorsements.push({
					name: "Garagekeepers' Liability",
					included: true,
					limits: [
						{
							qualifier: "Any one auto",
							amount: endorsement.limitOneAuto,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Any one loss",
							amount: endorsement.limitOneLoss,
							premiumIncluded: "",
						},
						{
							qualifier: "Deductible, each auto",
							amount: endorsement.deductibleEachAuto === null ? "NIL" : endorsement.deductibleEachAuto,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "garagekeepers") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitOneAuto: formatCurrency(endorsement.limitOneAuto),
					limitOneLoss: formatCurrency(endorsement.limitOneLoss),
					deductibleEachAuto: formatCurrency(endorsement.deductibleEachAuto),
				};
			}
			return {};
		},
	},
	automatic_increase_spare_parts: {
		label: "Automatic Increased Value of Spare Parts Endorsement",
		schema: AutomaticIncreaseSparePartsEndorsementSchema,
		documentName:
            "N AV E CLASSA 0031 05 25 Automatic Increased Value of Spare Parts Endorsement.docx",
		formNumber: "N AV E CLASSA 0031 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "automatic_increase_spare_parts") {
				endorsements.push({
					name: "Automatic Increased Value of Spare Parts",
					included: true,
					limits: [
						{
							qualifier: `Applies to ${endorsement.appliesTo}`,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Automatic approval limit",
							amount: endorsement.maxAutomaticLimit,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "automatic_increase_spare_parts") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					maxAutomaticLimit: formatCurrency(endorsement.maxAutomaticLimit),
					appliesTo: endorsement.appliesTo,
				};
			}
			return {};
		},
	},
	broad_form_contractual_liability: {
		label: "Broad Form Contractual Liability Endorsement",
		schema: BroadFormContractualLiabilityEndorsementSchema,
		documentName:
            "N AC E CLASSA 0061 05 25 Broad Form Contractual Liability Endorsement.docx",
		formNumber: "N AC E CLASSA 0061 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "broad_form_contractual_liability") {
				const amount = endorsement.limitFollowsAircraftLimit ? null : endorsement.limitOfLiabilityEachOccurrence;
				const qualifier = endorsement.limitFollowsAircraftLimit ? "Follows aircraft limit" : qualifierLabels.each_occurrence;
				endorsements.push({
					name: "Contractual Liability",
					included: true,
					limits: [
						{
							qualifier,
							amount: amount,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "broad_form_contractual_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitFollowsAircraftLimit: renderCheckbox(endorsement.limitFollowsAircraftLimit),
					limitHasCustomOccurrenceLimit: renderCheckbox(endorsement.limitHasCustomOccurrenceLimit),
					limitOfLiabilityEachOccurrence: conditionallyFormatCurrency({
						value: endorsement.limitOfLiabilityEachOccurrence,
						hasCoverage: endorsement.limitHasCustomOccurrenceLimit,
					}),
				};
			}
			return {};
		},
		initialize: ({ liabilityLimit }): typeof BroadFormContractualLiabilityEndorsementSchema.static => {
			const endorsement = Value.Default(BroadFormContractualLiabilityEndorsementSchema, {});
			if (Value.Check(BroadFormContractualLiabilityEndorsementSchema, endorsement)) {
				endorsement.limitOfLiabilityEachOccurrence = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize Broad Form Contractual Liability Endorsement");
		},
	} satisfies EndorsementConfig<typeof BroadFormContractualLiabilityEndorsementSchema.static>,
	contractual_liability_specific_contract: {
		label: "Contractual Liability – Contract Specific Endorsement",
		schema: ContractualLiabilitySpecificContractEndorsementSchema,
		documentName: "N AC E CLASSA 0088 05 25 Contractual Liability - Contract Specific Endorsement.docx",
		formNumber: "N AC E CLASSA 0088 05 25",
		lob: ["general_aviation", "uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "contractual_liability_specific_contract") {
				return [];
			}
			return [{
				name: "Contractual Liability – Specific Contract",
				included: true,
				limits: [
					{
						qualifier: endorsement.contractParty.trim() || "As scheduled",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					},
				],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "contractual_liability_specific_contract") {
				return {};
			}
			const contractDate = endorsement.contractDate?.trim();
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				contractParty: endorsement.contractParty,
				contractDate: contractDate ? formatDateFullMonth(contractDate.slice(0, 10), "UTC") : "",
				contractWording: endorsement.contractWording,
			};
		},
	} satisfies EndorsementConfig<typeof ContractualLiabilitySpecificContractEndorsementSchema.static>,
	personal_injury_extension: {
		label: "Personal Injury Extension Endorsement",
		schema: PersonalInjuryExtensionSchema,
		documentName: "N AC E CLASSA 0054 05 25 PERSONAL INJURY EXTENSION.docx",
		formNumber: "N AC E CLASSA 0054 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "personal_injury_extension") {
				endorsements.push({
					name: "Personal Injury Extension",
					included: true,
					limits: [
						{
							qualifier: limitQualifierForPrint(endorsement.limitOfLiabilityEachOffense, qualifierLabels.any_one_offense),
							amount: endorsement.limitOfLiabilityEachOffense,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: limitQualifierForPrint(endorsement.limitOfLiabilityAnnualAggregate, qualifierLabels.annual_aggregate),
							amount: endorsement.limitOfLiabilityAnnualAggregate,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "personal_injury_extension") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitOfLiabilityEachOffense: formatLimitOrFollowsAircraft(endorsement.limitOfLiabilityEachOffense),
					limitOfLiabilityAnnualAggregate: formatLimitOrFollowsAircraft(endorsement.limitOfLiabilityAnnualAggregate),
				};
			}
			return {};
		},
		initialize: ({ liabilityLimit }): typeof PersonalInjuryExtensionSchema.static => {
			const endorsement = Value.Default(PersonalInjuryExtensionSchema, {});
			if (Value.Check(PersonalInjuryExtensionSchema, endorsement)) {
				endorsement.limitOfLiabilityEachOffense = liabilityLimit;
				endorsement.limitOfLiabilityAnnualAggregate = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize Personal Injury Extension Endorsement");
		},
	} satisfies EndorsementConfig<typeof PersonalInjuryExtensionSchema.static>,
	advertising_injury_extension: {
		label: "Advertising Injury Extension Endorsement",
		schema: AdvertisingInjuryExtensionSchema,
		documentName: "N AC E CLASSA 0055 05 25 Advertising Injury Extension.docx",
		formNumber: "N AC E CLASSA 0055 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "advertising_injury_extension") {
				endorsements.push({
					name: "Advertising Injury Extension",
					included: true,
					limits: [
						{
							qualifier: limitQualifierForPrint(endorsement.limitOfLiabilityEachOffense, qualifierLabels.any_one_offense),
							amount: endorsement.limitOfLiabilityEachOffense,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: limitQualifierForPrint(endorsement.limitOfLiabilityAnnualAggregate, qualifierLabels.annual_aggregate),
							amount: endorsement.limitOfLiabilityAnnualAggregate,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "advertising_injury_extension") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitOfLiabilityEachOffense: formatLimitOrFollowsAircraft(endorsement.limitOfLiabilityEachOffense),
					limitOfLiabilityAnnualAggregate: formatLimitOrFollowsAircraft(endorsement.limitOfLiabilityAnnualAggregate),
				};
			}
			return {};
		},
		initialize: ({ liabilityLimit }): typeof AdvertisingInjuryExtensionSchema.static => {
			const endorsement = Value.Default(AdvertisingInjuryExtensionSchema, {});
			if (Value.Check(AdvertisingInjuryExtensionSchema, endorsement)) {
				endorsement.limitOfLiabilityEachOffense = liabilityLimit;
				endorsement.limitOfLiabilityAnnualAggregate = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize Advertising Injury Extension Endorsement");
		},
	} satisfies EndorsementConfig<typeof AdvertisingInjuryExtensionSchema.static>,
	family_assistance_expense: {
		label: "Family Assistance Expense Reimbursement Endorsement",
		schema: FamilyAssistanceExpenseEndorsementSchema,
		documentName:
            "N AV E CLASSA 0050 05 25 Family Assistance Expense Reimbursement Endorsement.docx",
		formNumber: "N AV E CLASSA 0050 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "family_assistance_expense") {
				endorsements.push({
					name: "Family Assistance Expense Reimbursement",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_person,
							amount: endorsement.limitPerPerson,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.limitPerOccurrence,
							premiumIncluded: "",
						},
						{
							qualifier: qualifierLabels.annual_aggregate,
							amount: endorsement.limitAnnualAggregate,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "family_assistance_expense") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitPerPerson: formatCurrency(endorsement.limitPerPerson),
					limitPerOccurrence: formatCurrency(endorsement.limitPerOccurrence),
					limitAnnualAggregate: formatCurrency(endorsement.limitAnnualAggregate),
				};
			}
			return {};
		},
	} satisfies EndorsementConfig<typeof FamilyAssistanceExpenseEndorsementSchema.static>,
	cost_reimbursement: {
		label: "Cost Reimbursement Endorsement",
		schema: CostReimbursementEndorsementSchema,
		documentName: "N AV E CLASSA 0065 05 25 Cost Reimbursement Endorsement.docx",
		formNumber: "N AV E CLASSA 0065 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "cost_reimbursement") {
				return [];
			}
			const limits: Endorsement["limits"] = endorsement.reimbursementSchedule.map((row, index) => ({
				qualifier: `${row.pilotName}, reimbursed by ${row.reimbursedBy}: ${currencyWithCentsIfAny(row.ratePerFlightHour)} per flight hour`,
				amount: null,
				premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
			}));
			if (limits.length === 0) {
				limits.push({
					qualifier: "As scheduled",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				});
			}
			return [{
				name: "Cost Reimbursement",
				included: true,
				limits,
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "cost_reimbursement") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				reimbursementSchedule: endorsement.reimbursementSchedule.map((row) => ({
					pilotName: row.pilotName,
					reimbursedBy: row.reimbursedBy,
					ratePerFlightHour: currencyWithCentsIfAny(row.ratePerFlightHour),
				})),
			};
		},
	} satisfies EndorsementConfig<typeof CostReimbursementEndorsementSchema.static>,
	passenger_voluntary_settlement: {
		label: "Passenger Voluntary Settlement Endorsement",
		schema: PassengerVoluntarySettlementSchema,
		documentName:
            "N AC E CLASSA 0056 05 25 Passenger Voluntary Settlements.docx",
		formNumber: "N AC E CLASSA 0056 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "passenger_voluntary_settlement") {
				const nonOwnedLimits = endorsement.hasNonOwnedCoverage ? [
					{
						qualifier: "Non-owned: Each non-crew passenger, each occurrence",
						amount: endorsement.nonOwnedLimitEachOccurrencePassenger,
						premiumIncluded: getPremiumText(endorsement),
					},
					{
						qualifier: "Non-owned: Each crew passenger, each occurrence",
						amount: endorsement.nonOwnedLimitEachOccurrenceCrew,
						premiumIncluded: "",
					},
					{
						qualifier: "Total all non-owned aircraft crew and non-crew combined, each occurrence",
						amount: endorsement.totalCombinedLimit,
						premiumIncluded: "",
					},
				] : [];
                
				const limits = [
					{
						qualifier: "Each non-crew passenger, each occurrence",
						amount: endorsement.limitEachOccurrencePassenger,
						premiumIncluded: getPremiumText(endorsement),
					},
					{
						qualifier: "Each crew passenger, each occurrence",
						amount: endorsement.limitEachOccurrenceCrew,
						premiumIncluded: "",
					},
					...nonOwnedLimits,
					
					{
						qualifier: "Max weekly indemnity limit, each passenger",
						amount: endorsement.maxWeeklyLimit,
						premiumIncluded: "",
					},
			
					{
						qualifier: `Max indemnity period: ${endorsement.maxNumberOfWeeks} weeks`,
						amount: null,
						premiumIncluded: "",
					},
					
				];
                
	
				endorsements.push({
					name: "Passenger Voluntary Settlement",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "passenger_voluntary_settlement") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitEachOccurrencePassenger: formatCurrency(endorsement.limitEachOccurrencePassenger),
					limitEachOccurrenceCrew: formatCurrency(endorsement.limitEachOccurrenceCrew),
					nonOwnedLimitEachOccurrencePassenger: conditionallyFormatCurrency({
						value: endorsement.nonOwnedLimitEachOccurrencePassenger,
						hasCoverage: endorsement.hasNonOwnedCoverage,
					}),
					nonOwnedLimitEachOccurrenceCrew: conditionallyFormatCurrency({
						value: endorsement.nonOwnedLimitEachOccurrenceCrew,
						hasCoverage: endorsement.hasNonOwnedCoverage,
					}),
					
					maxWeeklyLimit: formatCurrency(endorsement.maxWeeklyLimit),
					totalCombinedLimit: formatCurrency(endorsement.totalCombinedLimit),
					maxNumberOfWeeks: endorsement.maxNumberOfWeeks,
				};
			}
			return {};
		},
	},
	managed_aircraft_extended_pd: {
		label: "Extended Physical Damage Coverage for Managed Aircraft",
		schema: ManagedAircraftExtendedPdEndorsementSchema,
		documentName: "N AC E CLASSA 0012 05 25 Extended Physical Damage Coverage for Managed Aircraft.docx",
		formNumber: "N AC E CLASSA 0012 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "managed_aircraft_extended_pd") {
				return [];
			}
			return [{
				name: "Extended Physical Damage Coverage for Managed Aircraft",
				included: true,
				limits: [
					{
						qualifier: `Limit of insurance (or ${formatPercentWhole(endorsement.limitPercent / 100)} of insured value, whichever is less)`,
						amount: endorsement.limitAmount,
						premiumIncluded: getPremiumText(endorsement),
					},
					{
						qualifier: `${qualifierLabels.deductible}: each loss`,
						amount: endorsement.deductible,
						premiumIncluded: "",
					},
				],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "managed_aircraft_extended_pd") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				exclusion13c: renderCheckbox(endorsement.exclusion13c),
				exclusion13d: renderCheckbox(endorsement.exclusion13d),
				limitAmount: formatCurrency(endorsement.limitAmount),
				limitPercent: formatPercentWhole(endorsement.limitPercent / 100),
				deductible: formatCurrency(endorsement.deductible),
			};
		},
	},
	non_owned_aircraft_physical_damage: {
		label: "Non-Owned Aircraft Physical Damage Endorsement",
		schema: NonOwnedAircraftPhysicalDamageEndorsementSchema,
		documentName:
            "N AC E CLASSA 0057 05 25 Non-Owned Aircraft Physical Dam Endo.docx",
		formNumber: "N AC E CLASSA 0057 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			const limits: typeof endorsements[number]["limits"] = [];
			if (endorsement.slug === "non_owned_aircraft_physical_damage") {
				if (endorsement.coverageOption === "a") {
					const type = endorsement.optionAFixedOrRotorWing === "fixed-wing" ? "Fixed-Wing" : "Rotor-Wing";
					limits.push({
						qualifier: `${type} land aircraft bearing ${endorsement.optionAAirworthinessCert} Airworthiness Certificate having no more than ${endorsement.optionAMaxSeats} total seats`,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.coverageOption === "b") {
					const type = endorsement.optionBFixedOrRotorWing === "fixed-wing" ? "Fixed-Wing" : "Rotor-Wing";
					limits.push({
						qualifier: `${type} land aircraft bearing ${endorsement.optionBAirworthinessCert} Airworthiness Certificate having no more than ${endorsement.optionBMaxSeats} total seats and a certified gross weight not in excess of 12,500 pounds`,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.coverageOption === "c") {
					limits.push({
						qualifier: endorsement.optionCText,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				limits.push({
					qualifier: qualifierLabels.each_occurrence,
					amount: endorsement.limitEachOccurrence,
					premiumIncluded: "",
				});
				limits.push({
					qualifier: `${qualifierLabels.deductible}: Not In Motion`,
					amount: endorsement.deductibleNotInMotion,
					premiumIncluded: "",
				});
				limits.push({
					qualifier: `${qualifierLabels.deductible}: In Motion, Ingestion, or Mooring`,
					amount: endorsement.deductibleInMotion,
					premiumIncluded: "",
				});
				if (endorsement.pilotWarrantySupplement) {
					limits.push({
						qualifier: endorsement.pilotWarrantySupplement,
						amount: null,
						premiumIncluded: "",
					});
				}
			}
			endorsements.push({
				name: "Non-Owned Aircraft Physical Damage",
				included: true,
				limits: limits,
			});
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "non_owned_aircraft_physical_damage") {
                
				const mapWingType = (wingType: "fixed-wing" | "rotor-wing" | null) => {
					const vals = {
						"fixed-wing": "Fixed-Wing",
						"rotor-wing": "Rotor-Wing",
					};
					return vals[wingType as keyof typeof vals] ?? "N/A";
				};
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					hasOptionA: renderCheckbox(endorsement.coverageOption === "a"),
					hasOptionB: renderCheckbox(endorsement.coverageOption === "b"),
					hasOptionC: renderCheckbox(endorsement.coverageOption === "c"),
					optionAFixedOrRotorWing: mapWingType(endorsement.optionAFixedOrRotorWing),
					optionBFixedOrRotorWing: mapWingType(endorsement.optionBFixedOrRotorWing),
					optionAAirworthinessCert: endorsement.optionAAirworthinessCert ?? "Standard",
					optionBAirworthinessCert: endorsement.optionBAirworthinessCert ?? "Standard",
					optionAMaxSeats: endorsement.optionAMaxSeats,
					optionBMaxSeats: endorsement.optionBMaxSeats,
					optionCText: endorsement.optionCText ?? "",
					limitEachOccurrence: formatCurrency(endorsement.limitEachOccurrence),
					deductibleInMotion: formatCurrency(endorsement.deductibleInMotion),
					deductibleNotInMotion: formatCurrency(endorsement.deductibleNotInMotion),
					pilotWarrantySupplement: endorsement.pilotWarrantySupplement ?? "",
				};
			}
			return {};
		},
	},
	managed_aircraft_broad_extended_pd: {
		label: "Broad Extended Physical Damage Coverage for Managed Aircraft",
		schema: ManagedAircraftBroadExtendedPdEndorsementSchema,
		documentName: "N AC E CLASSA 0011 05 25 Broad Extended Physical Damage Coverage for Managed Aircraft.docx",
		formNumber: "N AC E CLASSA 0011 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "managed_aircraft_broad_extended_pd") {
				return [];
			}
			return [{
				name: "Broad Extended Physical Damage Coverage for Managed Aircraft",
				included: true,
				limits: [
					{
						qualifier: "Limit of insurance (or 20% of insured value, whichever is less)",
						amount: endorsement.limitAmount,
						premiumIncluded: getPremiumText(endorsement),
					},
					{
						qualifier: `${qualifierLabels.deductible}: each loss`,
						amount: endorsement.deductible,
						premiumIncluded: "",
					},
				],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "managed_aircraft_broad_extended_pd") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				exclusion13c: renderCheckbox(endorsement.exclusion13c),
				exclusion13d: renderCheckbox(endorsement.exclusion13d),
				limitAmount: formatCurrency(endorsement.limitAmount),
				deductible: formatCurrency(endorsement.deductible),
			};
		},
	} satisfies EndorsementConfig<typeof ManagedAircraftBroadExtendedPdEndorsementSchema.static>,
	non_owned_aircraft_liability: {
		label: "Non-Owned Aircraft Liability Endorsement",
		schema: NonOwnedAircraftLiabilityEndorsementSchema,
		documentName:
            "N AC E CLASSA 0058 05 25 NON-OWNED AIRCRAFT LIABILITY ENDORSEMENT.docx",
		formNumber: "N AC E CLASSA 0058 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			const limits: typeof endorsements[number]["limits"] = [];
			if (endorsement.slug === "non_owned_aircraft_liability") {
				if (endorsement.coverageOption === "a") {
					const type = endorsement.optionAFixedOrRotorWing === "fixed-wing" ? "Fixed-Wing" : "Rotor-Wing";
					limits.push({
						qualifier: `${type} land aircraft bearing ${endorsement.optionAAirworthinessCert} Airworthiness Certificate having no more than ${endorsement.optionAMaxSeats} total seats`,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.coverageOption === "b") {
					const type = endorsement.optionBFixedOrRotorWing === "fixed-wing" ? "Fixed-Wing" : "Rotor-Wing";
					limits.push({
						qualifier: `${type} land aircraft bearing ${endorsement.optionBAirworthinessCert} Airworthiness Certificate having no more than ${endorsement.optionBMaxSeats} total seats and a certified gross weight not in excess of 12,500 pounds`,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.coverageOption === "c") {
					limits.push({
						qualifier: endorsement.optionCText,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				if (endorsement.coverageDLimitEachOccurrence) {
					limits.push({
						qualifier: "Bodily Injury & Property Damage: Each Occurrence",
						amount: endorsement.coverageDLimitEachOccurrence,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageDLimitPerPassenger) {
					limits.push({
						qualifier: "Bodily Injury & Property Damage: Each Passenger",
						amount: endorsement.coverageDLimitPerPassenger,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageELimitPerPerson) {
					limits.push({
						qualifier: "Medical Expense - Including Crew: Each Person",
						amount: endorsement.coverageELimitPerPerson,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageELimitEachOccurrence) {
					limits.push({
						qualifier: "Medical Expense - Including Crew: Each Occurrence",
						amount: endorsement.coverageELimitEachOccurrence,
						premiumIncluded: "",
					});
				}
			}
			endorsements.push({
				name: "Non-Owned Aircraft Liability",
				included: true,
				limits: limits,
			});
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "non_owned_aircraft_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					hasOptionA: renderCheckbox(endorsement.coverageOption === "a"),
					hasOptionB: renderCheckbox(endorsement.coverageOption === "b"),
					hasOptionC: renderCheckbox(endorsement.coverageOption === "c"),
					optionAFixedOrRotorWing: mapWingType(endorsement.optionAFixedOrRotorWing),
					optionBFixedOrRotorWing: mapWingType(endorsement.optionBFixedOrRotorWing),
					optionAAirworthinessCert: endorsement.optionAAirworthinessCert ?? "Standard",
					optionBAirworthinessCert: endorsement.optionBAirworthinessCert ?? "Standard",
					optionAMaxSeats: endorsement.optionAMaxSeats,
					optionBMaxSeats: endorsement.optionBMaxSeats,
					optionCText: endorsement.optionCText ?? "",
					coverageDLimitEachOccurrence: conditionallyFormatCurrency({
						value: endorsement.coverageDLimitEachOccurrence,
						hasCoverage: endorsement.coverageDLimitEachOccurrence !== null,
					}),
					coverageDLimitPerPassenger: conditionallyFormatCurrency({
						value: endorsement.coverageDLimitPerPassenger,
						hasCoverage: endorsement.coverageDLimitPerPassenger !== null,
					}),
					coverageELimitPerPerson: conditionallyFormatCurrency({
						value: endorsement.coverageELimitPerPerson,
						hasCoverage: endorsement.coverageELimitPerPerson !== null,
					}),
					coverageELimitEachOccurrence: conditionallyFormatCurrency({
						value: endorsement.coverageELimitEachOccurrence,
						hasCoverage: endorsement.coverageELimitEachOccurrence !== null,
					}),
				};
			}
			return {};
		},
		initialize: ({ liabilityLimit }): typeof NonOwnedAircraftLiabilityEndorsementSchema.static => {
			const endorsement = Value.Default(NonOwnedAircraftLiabilityEndorsementSchema, {});
			if (Value.Check(NonOwnedAircraftLiabilityEndorsementSchema, endorsement)) {
				endorsement.limitEachOccurrence = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize Non-Owned Aircraft Liability Endorsement");
		},
	} satisfies EndorsementConfig<typeof NonOwnedAircraftLiabilityEndorsementSchema.static>,

	student_and_renter_pilot_liability: {
		label: "Student and Renter Pilot Liability Endorsement",
		schema: StudentAndRenterPilotLiabilityEndorsementSchema,
		documentName: "N AC E CLASSA 0064 05 25 Student and Renter Pilot Liability Endorsement.docx",
		formNumber: "N AC E CLASSA 0064 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "student_and_renter_pilot_liability") {
				endorsements.push({
					name: "Student and Renter Pilot Liability",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.limitsOfLiability,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "student_and_renter_pilot_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitsOfLiability: formatCurrency(endorsement.limitsOfLiability),
				};
			}
			return {};
		},
	} satisfies EndorsementConfig<typeof StudentAndRenterPilotLiabilityEndorsementSchema.static>,
	incidental_medical_malpractice: {
		label: "Incidental Medical Malpractice Endorsement",
		schema: IncidentalMedicalMalpracticeEndorsementSchema,
		documentName:
            "N AC E CLASSA 0065 05 25 Incidental Medical Malpractice Liability (most common).docx",
		formNumber: "N AC E CLASSA 0065 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "incidental_medical_malpractice") {
				endorsements.push({
					name: "Incidental Medical Malpractice",
					included: true,
					limits: [
						{
							qualifier: limitQualifierForPrint(endorsement.liabilityLimitEachOccurrence, qualifierLabels.each_occurrence),
							amount: endorsement.liabilityLimitEachOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "incidental_medical_malpractice") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					liabilityLimitEachOccurrence: formatLimitOrFollowsAircraft(endorsement.liabilityLimitEachOccurrence),
				};
			}
			return {};
		},
		initialize: ({ liabilityLimit }): typeof IncidentalMedicalMalpracticeEndorsementSchema.static => {
			const endorsement = Value.Default(IncidentalMedicalMalpracticeEndorsementSchema, {});
			if (Value.Check(IncidentalMedicalMalpracticeEndorsementSchema, endorsement)) {
				endorsement.liabilityLimitEachOccurrence = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize Incidental Medical Malpractice Endorsement");
		},
	} satisfies EndorsementConfig<typeof IncidentalMedicalMalpracticeEndorsementSchema.static>,
	incidental_medical_malpractice_av: {
		label: "Incidental Medical Malpractice Liability Endorsement",
		schema: IncidentalMedicalMalpracticeAvEndorsementSchema,
		documentName: "N AV E CLASSA 0048 05 25 Incidental Medical Malpractice Liability Endorsement.docx",
		formNumber: "N AV E CLASSA 0048 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "incidental_medical_malpractice_av") {
				endorsements.push({
					name: "Incidental Medical Malpractice Liability",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.limitEachOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.annual_aggregate,
							amount: endorsement.limitAnnualAggregate,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "incidental_medical_malpractice_av") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitEachOccurrence: formatCurrency(endorsement.limitEachOccurrence),
					limitAnnualAggregate: formatCurrency(endorsement.limitAnnualAggregate),
				};
			}
			return {};
		},
	} satisfies EndorsementConfig<typeof IncidentalMedicalMalpracticeAvEndorsementSchema.static>,
	mechanics_tools: {
		label: "Mechanics Tools Endorsement",
		schema: MechanicsToolsEndorsementSchema,
		documentName: "N AC E CLASSA 0060 05 25 Mechanical Tools Endorsement.docx",
		formNumber: "N AC E CLASSA 0060 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "mechanics_tools") {
				endorsements.push({
					name: "Mechanics Tools",
					included: true,
					limits: [
						{
							qualifier: "Each employee",
							amount: endorsement.limitEachEmployee,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.limitPerOccurrence,
							premiumIncluded: "",
						},
						{
							qualifier: `${qualifierLabels.deductible}: each employee, any one occurrence`,
							amount: endorsement.deductible === null ? "NIL" : endorsement.deductible,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "mechanics_tools") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitEachEmployee: formatCurrency(endorsement.limitEachEmployee),
					limitPerOccurrence: formatCurrency(endorsement.limitPerOccurrence),
					deductible: conditionallyFormatCurrency({
						value: endorsement.deductible,
						hasCoverage: endorsement.deductible !== null,
					}),
				};
			}
			return {};
		},
	},
	portable_equipment: {
		label: "Portable Equipment Endorsement",
		schema: PortableEquipmentEndorsementSchema,
		documentName: "N AV E CLASSA 0064 05 25 Portable Equipment Endorsement.docx",
		formNumber: "N AV E CLASSA 0064 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "portable_equipment") {
				endorsements.push({
					name: "Portable Equipment",
					included: true,
					limits: [
						{
							qualifier: "Portable avionics, headsets: each occurrence",
							amount: endorsement.portableAvionicsLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Safety equipment: each occurrence",
							amount: endorsement.safetyEquipmentLimit,
							premiumIncluded: "",
						},
						{
							qualifier: `Applies to: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "portable_equipment") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					tailNumbers: tailString(endorsement.tailNumbers),
					portableAvionicsLimit: formatCurrency(endorsement.portableAvionicsLimit),
					safetyEquipmentLimit: formatCurrency(endorsement.safetyEquipmentLimit),
				};
			}
			return {};
		},
	},
	multi_year_policy: {
		label: "Multi-Year Policy Endorsement",
		schema: MultiYearPolicyEndorsementSchema,
		documentName: "N AV E CLASSA 0013 05 25 Multiple Year Policy With Prospective Premium Endorsement.docx",
		formNumber: "N AV E CLASSA 0013 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "multi_year_policy") {
				const limits = endorsement.schedule.map((year) => ({
					qualifier: `Year ${year.year} Premium: ${formatCurrency(year.premium, true)}`,
					amount: null,
					premiumIncluded: "",
				}));
				const contingencies = [
					"- Loss ratio < 50%",
					"- No significant changes in operation",
					"- No significant changes with named insured entity",
				];
				limits.push(...contingencies.map(c => ({
					qualifier: c,
					amount: null,
					premiumIncluded: "",
				})));
				limits[0].premiumIncluded = getPremiumText(endorsement);
				endorsements.push({
					name: "Multi-Year Policy",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement, ctx?: RenderContext) => {
			if (!ctx?.policyNumber) {
				throw new Error("Policy number is required for multi-year policy endorsement");
			}
			const lastTwoDigits = ctx.policyNumber.slice(-2);
			const policyIncrementerStart = Number.parseInt(lastTwoDigits) - 1;
			
			if (endorsement.slug === "multi_year_policy") {
				const schedule = endorsement.schedule.map((year) => {
					const policyYearNumber = policyIncrementerStart + year.year;
					const policyYearNumberString = String(policyYearNumber).padStart(2, "0");
					const pNumber = `${ctx.policyNumber.slice(0, -2)}${policyYearNumberString}`;
					const baseDate = new Date(ctx.effectiveDate);
					const effectiveDate = baseDate.setFullYear(baseDate.getFullYear() + year.year - 1);
					const thirtyDays = 1000 * 60 * 60 * 24 * 30;
					const effectiveDatePlusThirtyDays = new Date(effectiveDate + thirtyDays);
					const dueDate = formatDateFullMonth(effectiveDatePlusThirtyDays, "UTC");
					return {
						policyYear: year.year,    
						premium: formatCurrency(year.premium, true),
						policyNumber: pNumber,
						dueDate,
					};
				});
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					schedule,
				};
			}
			return {};
		},
	},
	multi_year_policy_annual: {
		label: "Multiple Year Policy Endorsement (Annual Premium Review)",
		schema: MultiYearPolicyAnnualEndorsementSchema,
		documentName: "N AV E CLASSA 0012 05 25 Multiple Year Policy Endorsement.docx",
		formNumber: "N AV E CLASSA 0012 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "multi_year_policy_annual") {
				return [];
			}
			const qualifiers = endorsement.schedule.length
				? endorsement.schedule.map((year) => `Year ${year.year} Premium: ${formatCurrency(year.premium, true)}`)
				: [""];
			return [{
				name: "Multi-Year Policy",
				included: true,
				limits: qualifiers.map((qualifier, index) => ({
					qualifier,
					amount: null,
					premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
				})),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement, ctx?: RenderContext) => {
			if (endorsement.slug !== "multi_year_policy_annual") {
				return {};
			}
			if (!ctx?.policyNumber) {
				throw new Error("Policy number is required for multi-year policy endorsement");
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				schedule: plMultiYearScheduleVariables(endorsement.schedule, ctx).map(row => ({
					...row,
					policyYearLabel: `Year ${numberToEnglishTitleCase(row.policyYear)}`,
				})),
			};
		},
	} satisfies EndorsementConfig<typeof MultiYearPolicyAnnualEndorsementSchema.static>,
	knowledge_of_occurrence: {
		label: "Knowledge of Occurrence",
		schema: KnowledgeOfOccurrenceSchema,
		documentName: "N AV E CLASSA 0056 05 25 Knowledge of Occurrence.docx",
		formNumber: "N AV E CLASSA 0056 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "knowledge_of_occurrence") {
				endorsements.push({
					name: "Knowledge of Occurrence",
					included: true,
					limits: [
						{
							qualifier: "",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "knowledge_of_occurrence") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	layup: {
		label: "Layup Endorsement",
		schema: LayupEndorsementSchema,
		documentName: "N AC E CLASSA 0062 05 25 Lay-Up Endorsement.docx",
		formNumber: "N AC E CLASSA 0062 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "layup") {
				endorsements.push({
					name: "Lay-Up",
					included: true,
					limits: [
						{
							qualifier: `${endorsement.minConsecutiveDays} day minimum (consecutive)`,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${endorsement.returnPercentage}% of pro-rata premium`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "layup") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					minConsecutiveDays: endorsement.minConsecutiveDays,
					returnPercentage: conditionallyFormatPercentage({
						value: endorsement.returnPercentage,
						hasCoverage: true,
					}),
				};
			}
			return {};
		},
	},
	layup_return: {
		label: "Lay-Up Return Endorsement",
		schema: LayupReturnEndorsementSchema,
		documentName: "N AV E CLASSA 0045 05 25 Lay-Up Return Endorsement - Fleet with Aircraft Detail.docx",
		formNumber: "N AV E CLASSA 0045 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "layup_return") {
				return [];
			}
			const limits: Endorsement["limits"] = endorsement.aircraftSchedule.map((row, index) => ({
				qualifier: row.beginningDate && row.endingDate
					? `${row.tailNumber}: ${layupReturnDate(row.beginningDate)} to ${layupReturnDate(row.endingDate)}`
					: row.tailNumber,
				amount: null,
				premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
			}));
			return [{
				name: "Lay-Up Return",
				included: true,
				limits: limits.length > 0
					? limits
					: [{
						qualifier: "As scheduled",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					}],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "layup_return") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				aircraft: endorsement.aircraftSchedule.map((row) => ({
					tailNumber: row.tailNumber,
					year: row.year,
					makeModel: row.makeModel,
					beginningDate: layupReturnDate(row.beginningDate),
					endingDate: layupReturnDate(row.endingDate),
				})),
			};
		},
	},
	layup_return_single: {
		label: "Lay-Up Return Endorsement",
		schema: LayupReturnSingleEndorsementSchema,
		documentName: "N AV E CLASSA 0044 05 25 Lay-Up Return Endorsement.docx",
		formNumber: "N AV E CLASSA 0044 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "layup_return_single") {
				return [];
			}
			const period = endorsement.beginningDate && endorsement.endingDate
				? `${layupReturnDate(endorsement.beginningDate)} to ${layupReturnDate(endorsement.endingDate)}`
				: "";
			const subjects = endorsement.allAircraft
				? ["All aircraft"]
				: endorsement.aircraftSchedule.map((row) => row.tailNumber);
			const limits: Endorsement["limits"] = subjects.map((subject, index) => ({
				qualifier: period ? `${subject}: ${period}` : subject,
				amount: null,
				premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
			}));
			return [{
				name: "Lay-Up Return",
				included: true,
				limits: limits.length > 0
					? limits
					: [{
						qualifier: "As scheduled",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					}],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "layup_return_single") {
				return {};
			}
			const aircraft = endorsement.allAircraft
				? []
				: endorsement.aircraftSchedule.map((row, index) => ({
					checkbox: index === 0 ? renderCheckbox(true) : "",
					description: [row.year, row.makeModel, row.tailNumber].filter(Boolean).join(", "),
				}));
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				allAircraft: renderCheckbox(endorsement.allAircraft),
				aircraft: aircraft.length > 0
					? aircraft
					: [{ checkbox: renderCheckbox(!endorsement.allAircraft), description: "" }],
				beginningDate: layupReturnDate(endorsement.beginningDate),
				endingDate: layupReturnDate(endorsement.endingDate),
			};
		},
	},
	lienholders_interest: {
		label: "Lienholders Interest Endorsement",
		schema: LienholdersInterestEndorsementSchema,
		documentName: "N AV E CLASSA 0095 05 25 Lienholders Interest Endorsement.docx",
		formNumber: "N AV E CLASSA 0095 05 25",
		lob: ["general_aviation", "uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement, lob }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "lienholders_interest") {
				endorsements.push({
					name: "Lienholders Interest",
					included: true,
					limits: [
						{
							qualifier: endorsement.lienholder,
							amount: null,
							premiumIncluded: "Included",
						},
						{
							qualifier: `${lob === "uas" ? "UAS" : "Aircraft"}: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "lienholders_interest") {
				return {
					lienholder: endorsement.lienholder,
					tailNumbers: tailString(endorsement.tailNumbers),
				};
			}
			return {};
		},
	},
	lienholders_interest_multiple: {
		label: "Lienholder's Interest Endorsement (2 or More Lienholders on 1 Aircraft)",
		schema: LienholdersInterestMultipleEndorsementSchema,
		documentName: "N AV E CLASSA 0096 05 25 Lienholders Interest Endorsement 2 or More Lienholders on 1 Aircraft.docx",
		formNumber: "N AV E CLASSA 0096 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "lienholders_interest_multiple") {
				return [];
			}
			return [{
				name: "Lienholders Interest (2 or More Lienholders)",
				included: true,
				limits: [
					{
						qualifier: endorsement.lienholder,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					},
					...endorsement.lienSchedule.map((row) => ({
						qualifier: row.amountOfLien === null
							? `Aircraft: ${row.tailNumber}`
							: `Aircraft: ${row.tailNumber}, amount of lien: ${currencyWithCentsIfAny(row.amountOfLien)}`,
						amount: null,
						premiumIncluded: "",
					})),
				],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "lienholders_interest_multiple") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				lienholder: endorsement.lienholder,
				lienSchedule: endorsement.lienSchedule.map((row) => ({
					tailNumber: row.tailNumber,
					amountOfLien: currencyWithCentsIfAny(row.amountOfLien),
				})),
			};
		},
	} satisfies EndorsementConfig<typeof LienholdersInterestMultipleEndorsementSchema.static>,
	lienholders_interest_blanket_fleet: {
		label: "Broad Form Lienholder’s Interest Endorsement",
		schema: LienholdersInterestBlanketFleetEndorsementSchema,
		documentName: "N AV E CLASSA 0097 05 25 Broad Form Lienholders Interest Endorsement.docx",
		formNumber: "N AV E CLASSA 0097 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "lienholders_interest_blanket_fleet") {
				endorsements.push({
					name: "Broad Form Lienholder’s Interest",
					included: true,
					limits: [
						{
							qualifier: "Aircraft: all aircraft shown in Item 4. of the Policy Declarations",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "lienholders_interest_blanket_fleet") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	} satisfies EndorsementConfig<typeof LienholdersInterestBlanketFleetEndorsementSchema.static>,
	overhead_supervision: {
		label: "Overhead and Supervision Amendment",
		schema: OverheadSupervisionEndorsementSchema,
		documentName: "N AC E CLASSA 0074 05 25 Overhead and Supervision Amendment.docx",
		formNumber: "N AC E CLASSA 0074 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "overhead_supervision") {
				endorsements.push({
					name: "Overhead and Supervision Amendment",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "overhead_supervision") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
				};
			}
			return {};
		},
	},
	overhead_supervision_specified_rate: {
		label: "Overhead and Supervision Amendment Specified Rate",
		schema: OverheadSupervisionSpecifiedRateEndorsementSchema,
		documentName: "N AC E CLASSA 0075 05 25 Overhead and Supervision Amendment Specified Rate.docx",
		formNumber: "N AC E CLASSA 0075 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "overhead_supervision_specified_rate") {
				endorsements.push({
					name: "Overhead and Supervision Amendment Specified Rate",
					included: true,
					limits: [
						{
							qualifier: `Hourly rate: ${formatCurrency(endorsement.hourlyRate)}`,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Material surplus: ${formatWrittenPercentParenthetical(endorsement.materialSurplusPercent)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "overhead_supervision_specified_rate") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					hourlyRate: formatCurrency(endorsement.hourlyRate),
					materialSurplusPercent: formatWrittenPercentParenthetical(endorsement.materialSurplusPercent),
				};
			}
			return {};
		},
	},
	disappearance_amendment_of_definition: {
		label: "Disappearance of Amendment of Definition",
		schema: DisappearanceAmendmentOfDefinitionEndorsementSchema,
		documentName: "N AV E CLASSA 0057 05 25 Disappearance of Amendment of Definition.docx",
		formNumber: "N AV E CLASSA 0057 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "disappearance_amendment_of_definition") {
				endorsements.push({
					name: "Disappearance of Amendment of Definition",
					included: true,
					limits: [
						{
							qualifier: disappearanceTimeframeText(endorsement),
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "disappearance_amendment_of_definition") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					disappearanceTimeframe: disappearanceTimeframeText(endorsement),
				};
			}
			return {};
		},
	},
	floats_and_skis: {
		label: "Floats & Skis Endorsement",
		schema: FloatsAndSkisEndorsementSchema,
		documentName: "N AC E CLASSA 0014 05 25 Floats & Skis Endorsement.docx",
		formNumber: "N AC E CLASSA 0014 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "floats_and_skis") {
				const insuredTriple = (wheels: number, floats: number, skis: number) =>
					`${formatCurrency(wheels)} / ${formatCurrency(floats)} / ${formatCurrency(skis)}`;
				const deductibleTriple = (
					wheels: number | null,
					floats: number | null,
					skis: number | null,
				) =>
					`${formatFloatsAndSkisDeductible(wheels)} / ${formatFloatsAndSkisDeductible(floats)} / ${formatFloatsAndSkisDeductible(skis)}`;
				endorsements.push({
					name: "Floats & Skis",
					included: true,
					limits: [
						{
							qualifier: `Aircraft: ${endorsement.aircraftTail}`,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Insured Value w/ Wheels / Floats / Skis: ${insuredTriple(endorsement.withWheelsInsuredValue, endorsement.withFloatsInsuredValue, endorsement.withSkisInsuredValue)}`,
							amount: null,
							premiumIncluded: "",
						},
						{
							qualifier: `Insured Value of equipment when Detached Wheels / Floats / Skis: ${insuredTriple(endorsement.detachedWheelsInsuredValue, endorsement.detachedFloatsInsuredValue, endorsement.detachedSkisInsuredValue)}`,
							amount: null,
							premiumIncluded: "",
						},
						{
							qualifier: `Deductibles in flight w/ Wheels / Floats / Skis: ${deductibleTriple(endorsement.flightWheelsDeductible, endorsement.flightFloatsDeductible, endorsement.flightSkisDeductible)}`,
							amount: null,
							premiumIncluded: "",
						},
						{
							qualifier: `Deductibles taxi w/ Wheels / Floats / Skis: ${deductibleTriple(endorsement.taxiWheelsDeductible, endorsement.taxiFloatsDeductible, endorsement.taxiSkisDeductible)}`,
							amount: null,
							premiumIncluded: "",
						},
						{
							qualifier: `Deductibles Not-In-Motion w/ Wheels / Floats / Skis: ${deductibleTriple(endorsement.nimWheelsDeductible, endorsement.nimFloatsDeductible, endorsement.nimSkisDeductible)}`,
							amount: null,
							premiumIncluded: "",
						},
						{
							qualifier: `Deductibles of equipment when detached Wheels / Floats / Skis: ${deductibleTriple(endorsement.wheelsDeductible, endorsement.floatsDeductible, endorsement.skisDeductible)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "floats_and_skis") {
				return floatsAndSkisDocVariables(endorsement);
			}
			return {};
		},
	},
	constructive_total_loss: {
		label: "Constructive Total Loss Endorsement",
		schema: ConstructiveTotalLossEndorsementSchema,
		documentName: "N AV E CLASSA 0005 05 25 Constructive Total Loss Endorsement.docx",
		formNumber: "N AV E CLASSA 0005 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "constructive_total_loss") {
				const schedule = constructiveTotalLossScheduleText(endorsement);
				endorsements.push({
					name: "Constructive Total Loss",
					included: true,
					limits: [
						{
							qualifier: schedule ? `Aircraft: ${schedule}` : "Aircraft",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Cost to repair: ${endorsement.costToRepairPercent}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "constructive_total_loss") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					aircraftTail: constructiveTotalLossScheduleText(endorsement),
					costToRepairPercent: endorsement.costToRepairPercent,
				};
			}
			return {};
		},
	},
	total_loss_only: {
		label: "Total Loss Only Coverage Endorsement",
		schema: TotalLossOnlyEndorsementSchema,
		documentName: "N AV E CLASSA 0075 05 25 Total Loss Only Coverage Endorsement.docx",
		formNumber: "N AV E CLASSA 0075 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "total_loss_only") {
				endorsements.push({
					name: "Total Loss Only Coverage",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "total_loss_only") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	loss_payable: {
		label: "Loss Payable Endorsement",
		schema: LossPayableEndorsementSchema,
		documentName: "N AV E CLASSA 0094 05 25 Loss Payable Endorsement.docx",
		formNumber: "N AV E CLASSA 0094 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "loss_payable") {
				endorsements.push({
					name: "Loss Payable",
					included: true,
					limits: [
						{
							qualifier: endorsement.lossPayee,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Aircraft: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "loss_payable") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					tailNumbers: tailString(endorsement.tailNumbers),
					lossPayee: endorsement.lossPayee,
				};
			}
			return {};
		},
	},
	owner_lessor: {
		label: "Owner Lessor Endorsement with Owners Breach",
		schema: OwnerLessorEndorsementSchema,
		documentName: "N AV E CLASSA 0093 05 25 Owner Lessor Endorsement with Owners Breach.docx",
		formNumber: "N AV E CLASSA 0093 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "owner_lessor") {
				endorsements.push({
					name: "Owner Lessor Endorsement with Owners Breach",
					included: true,
					limits: [
						{
							qualifier: endorsement.aircraftOwner,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Aircraft: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "owner_lessor") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					aircraftTail: tailString(endorsement.tailNumbers),
					aircraftOwner: endorsement.aircraftOwner,
				};
			}
			return {};
		},
	},
	owner_lessor_endorsement: {
		label: "Owner Lessor Endorsement",
		schema: OwnerLessorEndorsementFormSchema,
		documentName: "N AV E CLASSA 0098 05 25 Owner Lessor Endorsement.docx",
		formNumber: "N AV E CLASSA 0098 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "owner_lessor_endorsement") {
				endorsements.push({
					name: "Owner Lessor Endorsement",
					included: true,
					limits: [
						{
							qualifier: endorsement.aircraftOwner,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Aircraft: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "owner_lessor_endorsement") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					aircraftTail: tailString(endorsement.tailNumbers),
					aircraftOwner: endorsement.aircraftOwner,
				};
			}
			return {};
		},
	},
	broad_owner_lessor: {
		label: "Broad Owner/Lessor Endorsement",
		schema: BroadOwnerLessorEndorsementSchema,
		documentName: "N AV E CLASSA 0092 05 25 Broad Owner Lessor Endorsement.docx",
		formNumber: "N AV E CLASSA 0092 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "broad_owner_lessor") {
				endorsements.push({
					name: "Broad Owner/Lessor Endorsement",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "broad_owner_lessor") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
				};
			}
			return {};
		},
	},
	additional_insured_owners_lessors: {
		label: "Additional Insured Owners/Lessors with Op Ints in a Specific Aircraft",
		schema: AdditionalInsuredOwnersLessorsEndorsementSchema,
		documentName: "N AV E CLASSA 0087 05 25 Additional Insured OwnersLessors with Op Ints in a specific aircraft.docx",
		formNumber: "N AV E CLASSA 0087 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "additional_insured_owners_lessors") {
				endorsements.push({
					name: "Additional Insured Owners/Lessors with Op Ints in a Specific Aircraft",
					included: true,
					limits: [
						{
							qualifier: endorsement.aircraftOwner,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: limitQualifierForPrint(endorsement.liabilityLimit, qualifierLabels.each_occurrence),
							amount: endorsement.liabilityLimit,
							premiumIncluded: "",
						},
						{
							qualifier: limitQualifierForPrint(endorsement.liabilitySubLimit, "Liability sublimit"),
							amount: endorsement.liabilitySubLimit,
							premiumIncluded: "",
						},
						{
							qualifier: `Aircraft: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "additional_insured_owners_lessors") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					aircraftTail: tailString(endorsement.tailNumbers),
					aircraftOwner: endorsement.aircraftOwner,
					liabilityLimit: formatLimitOrFollowsAircraft(endorsement.liabilityLimit),
					liabilitySubLimit: formatLimitOrFollowsAircraft(endorsement.liabilitySubLimit),
				};
			}
			return {};
		},
	},
	additional_insured_owners_lessors_broad: {
		label: "Broad Form Additional Insured – Owners/Lessors with Operational Interests",
		schema: AdditionalInsuredOwnersLessorsBroadEndorsementSchema,
		documentName: "N AV E CLASSA 0086 05 25 Broad Form Additional Insured OwnersLessors with Operational Interests.docx",
		formNumber: "N AV E CLASSA 0086 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "additional_insured_owners_lessors_broad") {
				endorsements.push({
					name: "Broad Form Additional Insured – Owners/Lessors with Operational Interests",
					included: true,
					limits: [
						{
							qualifier: limitQualifierForPrint(endorsement.liabilityLimit, qualifierLabels.each_occurrence),
							amount: endorsement.liabilityLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: limitQualifierForPrint(endorsement.liabilitySubLimit, "Each passenger"),
							amount: endorsement.liabilitySubLimit,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "additional_insured_owners_lessors_broad") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					liabilityLimit: formatLimitOrFollowsAircraft(endorsement.liabilityLimit),
					liabilitySubLimit: formatLimitOrFollowsAircraft(endorsement.liabilitySubLimit),
				};
			}
			return {};
		},
	},
	component_parts_schedule: {
		label: "Component Parts Schedule Endorsement",
		schema: ComponentPartsScheduleEndorsementSchema,
		documentName: "N AV E CLASSA 0019 05 25 Component Parts Schedule.docx",
		formNumber: "N AV E CLASSA 0019 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "component_parts_schedule") {
				endorsements.push({
					name: "Component Parts Schedule",
					included: true,
					limits: [
						{
							qualifier: endorsement.useSchedule2
								? "Schedule II (custom percentages)"
								: "Schedule I (default percentages)",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "component_parts_schedule") {
				const useSchedule2 = endorsement.useSchedule2;
				const schedule2Vars = Object.fromEntries(
					COMPONENT_PARTS_SCHEDULE2_KEYS.map((key) => [
						key,
						useSchedule2 ? formatSchedule2Percent(endorsement[key]) : "",
					]),
				);
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					schedule1Selected: renderCheckbox(!useSchedule2),
					schedule2Selected: renderCheckbox(useSchedule2),
					...schedule2Vars,
				};
			}
			return {};
		},
	},
	financed_premium: {
		label: "Financed Premium Endorsement",
		schema: FinancedPremiumEndorsementSchema,
		documentName: "N AV E CLASSA 0101 05 25 Financed Premium Endorsement.docx",
		formNumber: "N AV E CLASSA 0101 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "financed_premium") {
				endorsements.push({
					name: "Financed Premium",
					included: true,
					limits: [
						{
							qualifier: endorsement.lender,
							amount: null,
							premiumIncluded: "Included",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "financed_premium") {
				return {
					lender: endorsement.lender,
				};
			}
			return {};
		},
	},
	premium_installment: {
		label: "Premium Installment Endorsement",
		schema: PremiumInstallmentEndorsementSchema,
		documentName: "N AV E CLASSA 0008 05 25 Premium Installment Endorsement.docx",
		formNumber: "N AV E CLASSA 0008 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "premium_installment") {
				return [];
			}
			return [{
				name: "Premium Installment",
				included: true,
				limits: [{
					qualifier: singleLine(endorsement.appliesTo) || "Included",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				}],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "premium_installment") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				appliesTo: endorsement.appliesTo,
				installments: endorsement.installments.map((row) => ({
					dueDate: row.dueDate ? formatDateFullMonth(row.dueDate.slice(0, 10), "UTC") : "",
					amount: row.amount === null ? "" : formatCurrency(row.amount, true),
				})),
			};
		},
	} satisfies EndorsementConfig<typeof PremiumInstallmentEndorsementSchema.static>,
	additional_insured_pilot_service_provider: {
		label: "Additional Insured Pilot Service Provider",
		schema: AdditionalInsuredPilotServiceProviderEndorsementSchema,
		documentName:
			"N AV E CLASSA 0084 05 25 Additional Insured Pilot Service Providers.docx",
		formNumber: "N AV E CLASSA 0084 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "additional_insured_pilot_service_provider") {
				endorsements.push({
					name: "Additional Insured Pilot Service Provider",
					included: true,
					limits: [
						{
							qualifier: endorsement.serviceProvider,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "additional_insured_pilot_service_provider") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					serviceProvider: singleLine(endorsement.serviceProvider),
				};
			}
			return {};
		},
	},
	mexican_liability: {
		label: "Mexican Liability Endorsement",
		schema: MexicanLiabilityEndorsementSchema,
		documentName: "N AV E CLASSA 0058 05 25 Mexican Liability.docx",
		formNumber: "N AV E CLASSA 0058 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "mexican_liability") {
				endorsements.push({
					name: "Mexican Liability",
					included: true,
					limits: [
						{
							qualifier: `Aircraft: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "If Purchased",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "mexican_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					tailNumbers: tailString(endorsement.tailNumbers),
					policyNumbers: endorsement.policyNumbers,
				};
			}
			return {};
		},
	},
	aircraft_relocation_expense: {
		label: "Aircraft Relocation Expense Endorsement",
		schema: AircraftRelocationExpenseEndorsementSchema,
		documentName: "N AV E CLASSA 0051 05 25 Aircraft Relocation Expense Reimbursement Endorsement.docx",
		formNumber: "N AV E CLASSA 0051 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "aircraft_relocation_expense") {
				endorsements.push({
					name: "Aircraft Relocation Expense",
					included: true,
					limits: [
						{
							qualifier: `${qualifierLabels.each_aircraft}, or 50% of the incurred expense, whichever is less`,
							amount: endorsement.perAircraftLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "aircraft_relocation_expense") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					perAircraftLimit: formatCurrency(endorsement.perAircraftLimit),
				};
			}
			return {};
		},
	},
	automatic_attachment_for_newly_acquired_aircraft: {
		label: "Automatic Attachment for Newly Acquired Aircraft",
		schema: AutomaticAttachmentForNewlyAcquiredAircraftSchema,
		documentName: "N AC E CLASSA 0092 05 25 Automatic Attachment for Newly Aquired Aircraft.docx",
		formNumber: "N AC E CLASSA 0092 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "automatic_attachment_for_newly_acquired_aircraft") {
				endorsements.push({
					name: "Automatic Attachment for Newly Acquired Aircraft",
					included: true,
					limits: [
						{
							qualifier: "Max auto attach hull limit",
							amount: endorsement.maxAutoAttachHullLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Deductible NIM",
							amount: endorsement.deductibleNotInMotion,
							premiumIncluded: "",
						},
						{
							qualifier: "Deductible IM",
							amount: endorsement.deductibleInMotion,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables (endorsement: GenericEndorsement) {
			if (endorsement.slug === "automatic_attachment_for_newly_acquired_aircraft") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					hasA: renderCheckbox(endorsement.coverageOption === "a"),
					hasB: renderCheckbox(endorsement.coverageOption === "b"),
					hasC: renderCheckbox(endorsement.coverageOption === "c"),
					maxTotalSeatsA: endorsement.coverageOption === "a" ? endorsement.maxTotalSeats : "N/A",
					maxTotalSeatsB: endorsement.coverageOption === "b" ? endorsement.maxTotalSeats : "N/A",
					maxAutoAttachHullLimit: formatCurrency(endorsement.maxAutoAttachHullLimit),
					deductibleNotInMotion: formatCurrency(endorsement.deductibleNotInMotion),
					deductibleInMotion: formatCurrency(endorsement.deductibleInMotion),
					cText: endorsement.cText ?? "N/A",
					excludedAircraft: endorsement.excludedAircraft,
				};
			}
			return {};
		},
		initialize: ({ hullValue }): typeof AutomaticAttachmentForNewlyAcquiredAircraftSchema.static => {
			const endorsement = Value.Default(AutomaticAttachmentForNewlyAcquiredAircraftSchema, {});
			if (Value.Check(AutomaticAttachmentForNewlyAcquiredAircraftSchema, endorsement)) {
				endorsement.maxAutoAttachHullLimit = hullValue;
				return endorsement;
			}
			throw new Error("Failed to initialize Automatic Attachment for Newly Acquired Aircraft");
		},
	} satisfies EndorsementConfig<typeof AutomaticAttachmentForNewlyAcquiredAircraftSchema.static>,
	unearned_premium: {
		label: "Unearned Premium Endorsement",
		schema: UnearnedPremiumEndorsementSchema,
		documentName: "N AV E CLASSA 0033 05 25 Unearned Premium Insurance.docx",
		formNumber: "N AV E CLASSA 0033 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "unearned_premium") {
				endorsements.push({
					name: "Unearned Premium Insurance",
					included: true,
					limits: [
						{
							qualifier: `Applies to: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "unearned_premium") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					tailNumbers: endorsement.tailNumbers,
				};
			}
			return {};
		},
	},
	policy_territory: {
		label: "Policy Territory Endorsement",
		schema: PolicyTerritoryEndorsementSchema,
		documentName: "N AC E CLASSA 0040 05 26 Territory Revision Endorsement.docx",
		formNumber: "N AC E CLASSA 0040 05 26",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "policy_territory") {
				const limits = [
					{
						qualifier: "Anywhere in the world",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					},
				];
				if (endorsement.tailNumbers) {
					limits.push({
						qualifier: `Applies to: ${tailString(endorsement.tailNumbers)}`,
						amount: null,
						premiumIncluded: "",
					});
				}
				appendAutoExpirationPrintLimit(limits, endorsement.autoExpiration);
				endorsements.push({
					name: "Policy Territory",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "policy_territory") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					tailNumbers: tailString(endorsement.tailNumbers),
					...autoExpirationPrintVariables(endorsement),
				};
			}
			return {};
		},
	},
	policy_period_revision: {
		label: "Policy Period Revision",
		schema: PolicyPeriodRevisionEndorsementSchema,
		documentName: "N AV E CLASSA 0010 05 25 Policy Period Revision.docx",
		formNumber: "N AV E CLASSA 0010 05 25",
		lob: ["general_aviation", "product_liability", "commercial_general_liability", "uas"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "policy_period_revision"
			? [{
				name: "Policy Period Revision",
				included: true,
				limits: [{
					qualifier: `${endorsement.fromDate} to ${endorsement.toDate}`,
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				}],
			}]
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "policy_period_revision") return {};
			const validDates = policyPeriodRevisionError(endorsement) === null;
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				fromDate: validDates ? formatDateFullMonth(endorsement.fromDate, "UTC") : endorsement.fromDate,
				toDate: validDates ? formatDateFullMonth(endorsement.toDate, "UTC") : endorsement.toDate,
			};
		},
	},
	renewal_endorsement: {
		label: "Renewal Endorsement",
		schema: RenewalEndorsementSchema,
		documentName: "N AV E CLASSA 0023 05 25 Renewal Endorsement.docx",
		formNumber: "N AV E CLASSA 0023 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "renewal_endorsement"
			? [{
				name: "Renewal Endorsement",
				included: true,
				limits: [{
					qualifier: `${endorsement.fromDate} to ${endorsement.toDate}`,
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				}],
			}]
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "renewal_endorsement") return {};
			const validDates = policyPeriodRevisionError(endorsement) === null;
			const taxesSurcharges = endorsement.taxesSurcharges ?? 0;
			const mexicanInsurancePremium = endorsement.mexicanInsurancePremium ?? 0;
			const total = endorsement.renewalPremium === null
				? null
				: endorsement.renewalPremium + taxesSurcharges + mexicanInsurancePremium;
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				fromDate: validDates ? formatDateFullMonth(endorsement.fromDate, "UTC") : endorsement.fromDate,
				toDate: validDates ? formatDateFullMonth(endorsement.toDate, "UTC") : endorsement.toDate,
				renewalPolicyNumber: endorsement.renewalPolicyNumber,
				renewalPremium: currencyWithCentsIfAny(endorsement.renewalPremium),
				taxesSurcharges: currencyWithCentsIfAny(taxesSurcharges),
				mexicanInsurancePremium: currencyWithCentsIfAny(mexicanInsurancePremium),
				totalPremium: currencyWithCentsIfAny(total),
			};
		},
	},
	premises_medical_coverage: {
		label: "Premises Medical Coverage Endorsement",
		schema: PremisesMedicalCoverageEndorsementSchema,
		documentName: "N AC E CLASSA 0041 05 25 Premises Medical Coverage Endorsement.docx",
		formNumber: "N AC E CLASSA 0041 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "premises_medical_coverage") {
				endorsements.push({
					name: "Premises Medical Coverage",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_person,
							amount: endorsement.limitPerPerson,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "premises_medical_coverage") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitPerPerson: formatCurrency(endorsement.limitPerPerson),
				};
			}
			return {};
		},
	},
	premises_amendment: {
		label: "Premises Liability Endorsement",
		schema: PremisesAmendmentEndorsementSchema,
		documentName: "N AC E CLASSA 0034 05 25 Premises Liability Endorsement.docx",
		formNumber: "N AC E CLASSA 0034 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "premises_amendment") {
				endorsements.push({
					name: "Premises Liability",
					included: true,
					limits: [
						{
							qualifier: endorsement.limitPerOccurrence === null ? "Same limit as the Coverages A, B and D set forth on the Declarations" : qualifierLabels.each_occurrence,
							amount: endorsement.limitPerOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "premises_amendment") {
				const hasOwnLimit = endorsement.limitPerOccurrence !== null;
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					sameLimit: renderCheckbox(!hasOwnLimit),
					ownLimit: renderCheckbox(hasOwnLimit),
					limitPerOccurrence: conditionallyFormatCurrency({
						value: endorsement.limitPerOccurrence,
						hasCoverage: hasOwnLimit,
					}),
				};
			}
			return {};
		},
	},
	host_liquor_liability: {
		label: "Host Liquor Liability Endorsement",
		schema: HostLiquorLiabilityEndorsementSchema,
		documentName: "N AC E CLASSA 0038 05 25 HOST LIQUOR LIABILITY.docx",
		formNumber: "N AC E CLASSA 0038 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "host_liquor_liability") {
				endorsements.push({
					name: "Host Liquor Liability",
					included: true,
					limits: [
						{
							qualifier: limitQualifierForPrint(endorsement.limitAggregate, qualifierLabels.aggregate),
							amount: endorsement.limitAggregate,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "host_liquor_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitAggregate: formatLimitOrFollowsAircraft(endorsement.limitAggregate),
				};
			}
			return {};
		},
	},
	baggage_and_personal_effects: {
		label: "Baggage and Personal Effects Endorsement",
		schema: BaggageAndPersonalEffectsEndorsementSchema,
		documentName: "N AC E CLASSA 0032 05 25 Baggage and Personal Effects Endorsement.docx",
		formNumber: "N AC E CLASSA 0032 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "baggage_and_personal_effects") {
				endorsements.push({
					name: "Baggage and Personal Effects",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.any_one_offense,
							amount: endorsement.limitAnyOnePassenger,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Deductible (each loss, each passenger)",
							amount: endorsement.deductibleEachLossEachPax,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "baggage_and_personal_effects") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitAnyOnePassenger: formatCurrency(endorsement.limitAnyOnePassenger),
					deductibleEachLossEachPax: formatCurrency(endorsement.deductibleEachLossEachPax),
				};
			}
			return {};
		},
	}, 
	hangar_and_contents: {
		label: "Hangar and Contents Coverage Endorsement",
		schema: HangarAndContentsEndorsementSchema,
		documentName: "N AC E CLASSA 0033 05 25 Hangar and Contents Coverage Endorsement.docx",
		formNumber: "N AC E CLASSA 0033 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "hangar_and_contents") {
				const appliesTo = singleLine(endorsement.appliesTo);
				endorsements.push({
					name: "Hangar and Contents Coverage",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.limitEachOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
						...(appliesTo ? [{
							qualifier: `Applies to: ${appliesTo}`,
							amount: null,
							premiumIncluded: "",
						}] : []),
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "hangar_and_contents") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					appliesTo: endorsement.appliesTo,
					limitEachOccurrence: formatCurrency(endorsement.limitEachOccurrence),
				};
			}
			return {};
		},
	} satisfies EndorsementConfig<typeof HangarAndContentsEndorsementSchema.static>,
	airworthiness_certificate: {
		label: "Airworthiness Certificate Endorsement",
		schema: AirworthinessCertificateEndorsementSchema,
		documentName: "N AC E CLASSA 0029 05 25 Airworthiness Certificate Endorsement.docx",
		formNumber: "N AC E CLASSA 0029 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "airworthiness_certificate") {
				endorsements.push({
					name: "Airworthiness Certificate",
					included: true,
					limits: [
						{
							qualifier: endorsement.deletes ? "Not required" : "Required",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "airworthiness_certificate") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					deletes: renderCheckbox(endorsement.deletes),
					replaces: renderCheckbox(endorsement.replaces),
				};
			}
			return {};
		},
	},
	trip_interruption_expense: {
		label: "Trip Interruption Expense Endorsement",
		schema: TripInterruptionExpenseEndorsementSchema,
		documentName: "N AC E CLASSA 0091 05 25 Trip Interuption Expense.docx",
		formNumber: "N AC E CLASSA 0091 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "trip_interruption_expense") {
				endorsements.push({
					name: "Trip Interruption Expense",
					included: true,
					limits: [
						{
							qualifier: "Each passenger, each occurrence",
							amount: endorsement.limitEachPassengerEachOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "trip_interruption_expense") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitEachPassengerEachOccurrence: formatCurrency(endorsement.limitEachPassengerEachOccurrence),
				};
			}
			return {};
		},
	},
	extra_expense_for_sub_aircraft: {
		label: "Extra Expense for Sub-Aircraft Endorsement",
		schema: ExtraExpenseForSubAircraftEndorsementSchema,
		documentName: "N AC E CLASSA 0035 05 25 Extra Expense for Renting Substitute Aircraft Endorsement.docx",
		formNumber: "N AC E CLASSA 0035 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
            
			if (endorsement.slug === "extra_expense_for_sub_aircraft") {
				const waitPeriod = endorsement.waitPeriod > 0 ? `${endorsement.waitPeriod} day wait` : "No wait";
				const daysFromOccurrence = `${endorsement.daysFromOccurrence} days max`;
				endorsements.push({
					name: "Extra Expense for Substitute Aircraft",
					included: true,
					limits: [
						{
							qualifier: "Each day, each aircraft",
							amount: endorsement.liabilityLimitEachDayEachAircraft,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Each occurrence, each aircraft",
							amount: endorsement.liabilityLimitEachOccurrenceEachAircraft,
							premiumIncluded: "",
						},
						{
							qualifier: `${daysFromOccurrence} / ${waitPeriod}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "extra_expense_for_sub_aircraft") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					liabilityLimitEachDayEachAircraft: formatCurrency(endorsement.liabilityLimitEachDayEachAircraft),
					liabilityLimitEachOccurrenceEachAircraft: formatCurrency(endorsement.liabilityLimitEachOccurrenceEachAircraft),
					waitPeriod: endorsement.waitPeriod,
					daysFromOccurrence: endorsement.daysFromOccurrence,
				};
			}
			return {};
		},
	},
	consequential_loss: {
		label: "Consequential Loss Coverage Endorsement",
		schema: ConsequentialLossEndorsementSchema,
		documentName: "N AC E CLASSA 0066 05 25 Consequential Loss Coverage Endorsement.docx",
		formNumber: "N AC E CLASSA 0066 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "consequential_loss") {
				endorsements.push({
					name: "Consequential Loss Coverage",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.aggregate,
							amount: endorsement.limitAggregate,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Applies to: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "consequential_loss") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					tailNumbers: tailString(endorsement.tailNumbers),
					limitAggregate: formatCurrency(endorsement.limitAggregate),
				};
			}
			return {};
		},
	},
	consequential_loss_coverage: {
		label: "Consequential Loss Coverage Endorsement",
		schema: ConsequentialLossCoverageEndorsementSchema,
		documentName: "N AV E CLASSA 0052 05 25 Consequential Loss Coverage Endorsement.docx",
		formNumber: "N AV E CLASSA 0052 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "consequential_loss_coverage") {
				endorsements.push({
					name: "Consequential Loss Coverage",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.aggregate,
							amount: endorsement.limitAggregate,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Applies to: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "consequential_loss_coverage") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					tailNumbers: tailString(endorsement.tailNumbers),
					limitAggregate: formatCurrency(endorsement.limitAggregate),
				};
			}
			return {};
		},
	},
	extra_expense_for_temp_parts: {
		label: "Extra Expense for Temporary Replacement Aircraft Parts Endorsement",
		schema: ExtraExpenseForTempPartsEndorsementSchema,
		documentName: "N AC E CLASSA 0036 05 25 Extra Expense of Temporary Replacement Aircraft Parts Endorsement.docx",
		formNumber: "N AC E CLASSA 0036 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "extra_expense_for_temp_parts") {
				const repairDays = endorsement.repairDays ?? 5;
				const limits = [
					{
						qualifier: qualifierLabels.each_loss,
						amount: endorsement.limitEachLoss,
						premiumIncluded: getPremiumText(endorsement),
					},
					{
						qualifier: `${repairDays} ${repairDays === 1 ? "Day" : "Days"} repair time`,
						amount: null,
						premiumIncluded: "",
					},
				];
				if (endorsement.excludedAircraft.length > 0) {
					limits.push({
						qualifier: `Excluding: ${endorsement.excludedAircraft.join(", ")}`,
						amount: null,
						premiumIncluded: "",
					});
				}
				endorsements.push({
					name: "Extra Expense for Temporary Replacement Aircraft Parts",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "extra_expense_for_temp_parts") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitEachLoss: formatCurrency(endorsement.limitEachLoss),
					repairDays: endorsement.repairDays ?? 5,
					excludedAircraft: endorsement.excludedAircraft.join(", "),
				};
			}
			return {};
		},
	},
	mobile_equipment_liability: {
		label: "Mobile Equipment Liability Endorsement",
		schema: MobileEquipmentLiabilityEndorsementSchema,
		documentName: "N AC E CLASSA 0039 05 25 MOBILE EQUIPMENT LIABILITY ENDORSEMENT.docx",
		formNumber: "N AC E CLASSA 0039 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "mobile_equipment_liability") {
				endorsements.push({
					name: "Mobile Equipment Liability",
					included: true,
					limits: [
						{
							qualifier: limitQualifierForPrint(endorsement.limitEachOccurrence, qualifierLabels.each_occurrence),
							amount: endorsement.limitEachOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "mobile_equipment_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitEachOccurrence: formatLimitOrFollowsAircraft(endorsement.limitEachOccurrence),
				};
			}
			return {};
		},
	},
	hangarkeepers_liability: {
		label: "Hangarkeepers Liability Endorsement",
		schema: HangarkeepersLiabilityEndorsementSchema,
		documentName: "N AC E CLASSA 0037 05 25 Hangarkeepers Liability Endorsement.docx",
		formNumber: "N AC E CLASSA 0037 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "hangarkeepers_liability") {
				endorsements.push({
					name: "Hangarkeepers Liability",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_aircraft,
							amount: endorsement.limitEachAircraft,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.limitEachOccurrence,
							premiumIncluded: "",
						},
						{
							qualifier: qualifierLabels.deductible,
							amount: endorsement.deductible === null ? "NIL" : endorsement.deductible,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "hangarkeepers_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitEachAircraft: formatCurrency(endorsement.limitEachAircraft),
					limitEachOccurrence: formatCurrency(endorsement.limitEachOccurrence),
					deductible: conditionallyFormatCurrency({
						value: endorsement.deductible,
						hasCoverage: endorsement.deductible !== null,
					}),
				};
			}
			return {};
		},
	},
	products_liability: {
		label: "Products Liability Endorsement",
		schema: ProductsLiabilityEndorsementSchema,
		documentName: "N AC E CLASSA 0042 05 25 PRODUCTS LIABILITY ENDORSEMENT.docx",
		formNumber: "N AC E CLASSA 0042 05 25",     
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "products_liability") {
				endorsements.push({
					name: "Products Liability",
					included: true,
					limits: [
						{
							qualifier: limitQualifierForPrint(endorsement.liabilityLimit, qualifierLabels.each_occurrence),
							amount: endorsement.liabilityLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "products_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					liabilityLimit: formatLimitOrFollowsAircraft(endorsement.liabilityLimit),
				};
			}
			return {};
		},
	},
	spare_parts: {
		label: "Spare Parts Endorsement",
		schema: SparePartsEndorsementSchema,
		documentName: "N AV E CLASSA 0030 05 25 Spare Parts Endorsement Without Schedule.docx",
		formNumber: "N AV E CLASSA 0030 05 25",     
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "spare_parts") {
				endorsements.push({
					name: "Spare Parts",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.liabilityLimitAnyOneOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.deductible,
							amount: endorsement.deductible === null ? "NIL" : endorsement.deductible,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "spare_parts") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					liabilityLimitAnyOneOccurrence: formatCurrency(endorsement.liabilityLimitAnyOneOccurrence),
					deductible: conditionallyFormatCurrency({
						value: endorsement.deductible,
						hasCoverage: endorsement.deductible !== null,
					}),
				};
			}
			return {};
		},
	},
	spare_parts_scheduled: {
		label: "Spare Parts Endorsement (with schedule)",
		schema: SparePartsScheduledEndorsementSchema,
		documentName: "N AV E CLASSA 0029 05 25 Spare Parts Endorsement with schedule.docx",
		formNumber: "N AV E CLASSA 0029 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "spare_parts_scheduled") {
				endorsements.push({
					name: "Spare Parts",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.each_occurrence,
							amount: endorsement.liabilityLimitAnyOneOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: qualifierLabels.deductible,
							amount: endorsement.deductible === null ? "NIL" : endorsement.deductible,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "spare_parts_scheduled") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					liabilityLimitAnyOneOccurrence: formatCurrency(endorsement.liabilityLimitAnyOneOccurrence),
					deductible: conditionallyFormatCurrency({
						value: endorsement.deductible,
						hasCoverage: endorsement.deductible !== null,
					}),
					sparePartsSchedule: endorsement.sparePartsSchedule.map((row) => ({
						description: row.description,
						insuredValue: formatCurrency(row.insuredValue),
					})),
					lossPayee: endorsement.lossPayee,
					lossPayeeMailingAddress: endorsement.lossPayeeMailingAddress,
				};
			}
			return {};
		},
	},
	emergency_landing: {
		label: "Emergency or Unexpected Landing Endorsement",
		schema: EmergencyLandingEndorsementSchema,
		documentName: "N AV E CLASSA 0024 05 25 Emergency or Unexpected Landing Endorsement.docx",
		formNumber: "N AV E CLASSA 0024 05 25",     
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "emergency_landing") {
				endorsements.push({
					name: "Emergency or Unexpected Landing",
					included: true,
					limits: [
						{
							qualifier: `Up to ${formatPercentWhole(endorsement.percentageOfHullValue / 100)} of hull value`,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
	},
	search_and_rescue: {
		label: "Search and Rescue Expenses Supplemental Liability Payments",
		schema: SearchAndRescueEndorsementSchema,
		documentName: "N AC E CLASSA 0044 05 25 Search and Rescue Expenses Supp Liab Payments End.docx",
		formNumber: "N AC E CLASSA 0044 05 25",  
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "search_and_rescue") {
				endorsements.push({
					name: "Search and Rescue Expenses",
					included: true,
					limits: [
						{
							qualifier: qualifierLabels.any_one_occurrence,
							amount: endorsement.limitAnyOneOccurrence,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Applies to: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables (endorsement: GenericEndorsement) {
			if (endorsement.slug === "search_and_rescue") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					limitAnyOneOccurrence: formatCurrency(endorsement.limitAnyOneOccurrence),
					tailNumbers: tailString(endorsement.tailNumbers),
				};
			}
			return {};
		},
	},
	runway_foaming: {
		label: "Runway or Aircraft Foaming - Supplemental Payments",
		schema: RunwayFoamingEndorsementSchema,
		documentName: "N AC E CLASSA 0043 05 25 Runway or Aircraft Foaming Supplemental Payments Endorsement.docx",
		formNumber: "N AC E CLASSA 0043 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "runway_foaming") {
				endorsements.push({
					name: "Runway or Aircraft Foaming",
					included: true,
					limits: [
						{
							qualifier: "Any one loss or occurrence",
							amount: endorsement.liabilityLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Applies to: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "runway_foaming") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					liabilityLimit: formatCurrency(endorsement.liabilityLimit),
					tailNumbers: tailString(endorsement.tailNumbers),
				};
			}
			return {};
		},
	},
	supplementary_payments_bail_bond: {
		label: "Supplementary Payments Amendment to Bail Bonds",
		schema: SupplementaryPaymentsBailBondEndorsementSchema,
		documentName: "N AC E CLASSA 0024 05 25 Supplementary Payments Amendment to Bail Bonds.docx",
		formNumber: "N AC E CLASSA 0024 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "supplementary_payments_bail_bond") {
				endorsements.push({
					name: "Supplementary Payments Amendment to Bail Bonds",
					included: true,
					limits: [
						{
							qualifier: "Each bail bond",
							amount: endorsement.bailBondLimit,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "Loss of earnings, each day",
							amount: endorsement.lossOfEarningsLimitPerDay,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "supplementary_payments_bail_bond") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					bailBondLimit: formatCurrency(endorsement.bailBondLimit),
					lossOfEarningsLimitPerDay: formatCurrency(endorsement.lossOfEarningsLimitPerDay),
				};
			}
			return {};
		},
	},
	no_claims_bonus: {
		label: "No Claims Bonus Endorsement",
		schema: NoClaimsBonusEndorsementSchema,
		documentName: "N AV E CLASSA 0103 05 25 No Claims Bonus on Renewal Endorsement.docx",
		formNumber: "N AV E CLASSA 0103 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "no_claims_bonus") {
				endorsements.push({
					name: "No Claims Bonus on Renewal",
					included: true,
					limits: [
						{
							qualifier: `${endorsement.percentage}% return of Physical Damage, excluding war, premium for no claims reported 60 post expiry`,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "no_claims_bonus") {
				const prose = [
					"one",
					"two",
					"three",
					"four",
					"five",
					"six",
					"seven",
					"eight",
					"nine",
					"ten",
					"eleven",
					"twelve",
					"thirteen",
					"fourteen",
					"fifteen",
					"sixteen",
					"seventeen",
					"eighteen",
					"nineteen",
					"twenty",
				];
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					percentage: endorsement.percentage,
					percentageProse: Number.isInteger(endorsement.percentage)
						? prose.at(endorsement.percentage - 1) ?? `${endorsement.percentage}`
						: `${endorsement.percentage}`,
				};
			}
			return {};
		},
	},
	full_premium_in_event_of_claim: {
		label: "Full Premium in the Event of a Claim Endorsement",
		schema: FullPremiumInEventOfClaimEndorsementSchema,
		documentName: "N AV E CLASSA 0105 05 25 Full Premium in the Event of a Claim Endorsement.docx",
		formNumber: "N AV E CLASSA 0105 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "full_premium_in_event_of_claim") {
				endorsements.push({
					name: "Full Premium in the Event of a Claim",
					included: true,
					limits: [
						{
							qualifier: "Full annual premium",
							amount: endorsement.fullAnnualPremium,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `Applies to: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "full_premium_in_event_of_claim") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					fullAnnualPremium: formatCurrency(endorsement.fullAnnualPremium),
					tailNumbers: tailString(endorsement.tailNumbers),
				};
			}
			return {};
		},
	},
	proportional_share: {
		label: "Proportional Share Policy Endorsement",
		schema: ProportionalShareEndorsementSchema,
		documentName: "N AV E CLASSA 0006 05 25 Proportional Share Policy Endorsement.docx",
		formNumber: "N AV E CLASSA 0006 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "proportional_share") {
				endorsements.push({
					name: "Proportional Share Policy",
					included: true,
					limits: [
						{
							qualifier: `${endorsement.lineSize}%`,
							amount: null,
							premiumIncluded: "Included",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "proportional_share") {
				return {
					companyShare: endorsement.lineSize,
					remainingShare: remainingProportionalShare(endorsement.lineSize),
				};
			}
			return {};
		},
	},
	minimal_loss_credit: {
		label: "Minimal Loss Credit Endorsement",
		schema: MinimalLossCreditEndorsementSchema,
		documentName: "N AV E CLASSA 0122 08 26 Minimal Loss Credit on Renewal Endorsement.docx",
		formNumber: "N AV E CLASSA 0122 08 26",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "minimal_loss_credit") {
				const selectedBasis = endorsement.coverageBasis ?? MINIMAL_LOSS_CREDIT_DEFAULT_COVERAGE_BASIS;
				const basis = MINIMAL_LOSS_CREDIT_COVERAGE_BASES.find(({ value }) => value === selectedBasis);
				endorsements.push({
					name: "Minimal Loss Credit on Renewal (PCOR)",
					included: true,
					limits: [
						{
							qualifier: `${endorsement.creditPercentage}% of ${endorsement.earnedPremiumPercentage}% of earned ${basis?.label ?? ""} premium`,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "minimal_loss_credit") {
				const selectedBasis = endorsement.coverageBasis ?? MINIMAL_LOSS_CREDIT_DEFAULT_COVERAGE_BASIS;
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					creditPercentage: endorsement.creditPercentage,
					earnedPremiumPercentage: endorsement.earnedPremiumPercentage,
					...Object.fromEntries(
						MINIMAL_LOSS_CREDIT_COVERAGE_BASES.map(({ value, variable }) => [
							variable,
							renderCheckbox(value === selectedBasis),
						]),
					),
				};
			}
			return {};
		},
	},
	waiver_of_subrogation_av: {
		label: "Waiver of Subrogation Endorsement",
		schema: WaiverOfSubrogationAvEndorsementSchema,
		documentName: "N AV E CLASSA 0079 05 25 Waiver of Subrogation Endorsement.docx",
		formNumber: "N AV E CLASSA 0079 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "waiver_of_subrogation_av") {
				endorsements.push({
					name: "Waiver of Subrogation",
					included: true,
					limits: [
						{
							qualifier: endorsement.partyName,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "waiver_of_subrogation_av") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					partyName: endorsement.partyName,
					partyAttention: endorsement.attention ?? "",
					partyAddressLineOne: endorsement.partyAddressLineOne,
					partyAddressLineTwo: endorsement.partyAddressLineTwo,
					excessLiability: renderCheckbox(endorsement.excessLiability),
					nonOperational: renderCheckbox(endorsement.nonOperational),
					flightInstruction: renderCheckbox(endorsement.flightInstruction),
					flightInstructionPilots: endorsement.flightInstructionPilots,
					hangarkeepers: renderCheckbox(endorsement.hangarkeepers),
					workmanshipExclusion: renderCheckbox(endorsement.workmanshipExclusion),
				};
			}
			return {};
		},
	},
	additional_insured: {
		label: "Additional Insured Endorsement",
		schema: AdditionalInsuredEndorsementSchema,
		documentName: "N AV E CLASSA 0082 05 25 Additional Insured Endorsement.docx",
		formNumber: "N AV E CLASSA 0082 05 25",
		lob: ["general_aviation", "product_liability", "uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "additional_insured") {
				endorsements.push({
					name: "Additional Insured",
					included: true,
					limits: [
						{
							qualifier: endorsement.additionalInsuredName,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "additional_insured") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					additionalInsuredName: endorsement.additionalInsuredName,
					additionalInsuredAddressLineOne: endorsement.additionalInsuredAddressLineOne,
					additionalInsuredAddressLineTwo: endorsement.additionalInsuredAddressLineTwo,
					additionalInsuredAttention: endorsement.attention ?? "",
					asAdditionalInsured: renderCheckbox(endorsement.asAdditionalInsured),
					dryLease: renderCheckbox(endorsement.dryLease),
					liabilityCoverages: renderCheckbox(endorsement.liabilityCoverages),
					liabilityCoveragesOpsOnly: renderCheckbox(endorsement.liabilityCoveragesOpsOnly),
					opsOnly: renderCheckbox(endorsement.opsOnly),
					useOfPremises: renderCheckbox(endorsement.useOfPremises),
					workPerformed: renderCheckbox(endorsement.workPerformed),
					noLiabilityPremium: renderCheckbox(endorsement.noLiabilityPremium),
					isPrimary: renderCheckbox(endorsement.isPrimary),
					noLiabilityIncrease: renderCheckbox(endorsement.noLiabilityIncrease),
					thirtyDayNotice: renderCheckbox(endorsement.thirtyDayNotice),
					waivePhysicalDamage: renderCheckbox(endorsement.waivePhysicalDamage),
					exclusionForDesignManufacture: renderCheckbox(endorsement.exclusionForDesignManufacture),
				};
			}
			return {};
		},
	},
	increased_limits_designated_persons: {
		label: "Increased Limits for Designated Person(s) or Organization(s)",
		schema: IncreasedLimitsDesignatedPersonsEndorsementSchema,
		documentName: "N AC E CLASSA 0002 05 25 Increased Limits for Designated Person(s) or Organization(s).docx",
		formNumber: "N AC E CLASSA 0002 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "increased_limits_designated_persons") {
				const limits: Endorsement["limits"] = [
					{
						qualifier: endorsement.designatedPersonName,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					},
				];
				if (endorsement.tailNumbers.length) {
					limits.push({
						qualifier: `Aircraft: ${tailString(endorsement.tailNumbers)}`,
						amount: null,
						premiumIncluded: "",
					});
				}
				const coverages: Array<[string, number | null]> = [
					["Bodily Injury - Each Person", endorsement.coverageAEachPerson],
					["Bodily Injury - Each Occurrence", endorsement.coverageAEachOccurrence],
					["Property Damage - Each Occurrence", endorsement.coverageBEachOccurrence],
					["Passenger Liability - Each Passenger", endorsement.coverageCEachPassenger],
					["Passenger Liability - Each Occurrence", endorsement.coverageCEachOccurrence],
					["Single Limit Bodily Injury & Property Damage - Each Occurrence", endorsement.coverageDEachOccurrence],
					["Single Limit Bodily Injury & Property Damage - Each Passenger", endorsement.coverageDEachPassenger],
					["Medical Expense Including Crew - Each Person", endorsement.coverageEEachPerson],
					["Medical Expense Including Crew - Each Occurrence", endorsement.coverageEEachOccurrence],
				];
				for (const [qualifier, amount] of coverages) {
					if (amount) {
						limits.push({
							qualifier, amount, premiumIncluded: "",
						});
					}
				}
				endorsements.push({
					name: "Increased Limits for Designated Person(s) or Organization(s)",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "increased_limits_designated_persons") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					designatedPersonName: endorsement.designatedPersonName,
					designatedPersonAddressLineOne: endorsement.designatedPersonAddressLineOne,
					designatedPersonAddressLineTwo: endorsement.designatedPersonAddressLineTwo,
					tailNumbers: tailString(endorsement.tailNumbers),
					coverageAEachPerson: nullishCurrencyOrBlank(endorsement.coverageAEachPerson),
					coverageAEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageAEachOccurrence),
					coverageBEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageBEachOccurrence),
					coverageCEachPassenger: nullishCurrencyOrBlank(endorsement.coverageCEachPassenger),
					coverageCEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageCEachOccurrence),
					coverageDEachPassenger: nullishCurrencyOrBlank(endorsement.coverageDEachPassenger),
					coverageDEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageDEachOccurrence),
					coverageEEachPerson: nullishCurrencyOrBlank(endorsement.coverageEEachPerson),
					coverageEEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageEEachOccurrence),
				};
			}
			return {};
		},
	},
	waiver_of_subrogation: {
		label: "Waiver of Subrogation Endorsement",
		schema: WaiverOfSubrogationEndorsementSchema,
		documentName: "N AC E CLASSA 0083 05 25 Waiver of Subrogation Endorsement.docx",
		formNumber: "N AC E CLASSA 0083 05 25",
		lob: ["general_aviation", "uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement, lob }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "waiver_of_subrogation") {
				endorsements.push({
					name: "Waiver of Subrogation",
					included: true,
					limits: [
						{
							qualifier: endorsement.waiverPartyName,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: `${lob === "uas" ? "UAS" : "Aircraft"}: ${tailString(endorsement.tailNumbers)}`,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "waiver_of_subrogation") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					waiverPartyName: endorsement.waiverPartyName,
					waiverPartyAddressLineOne: endorsement.waiverPartyAddressLineOne,
					waiverPartyAddressLineTwo: endorsement.waiverPartyAddressLineTwo,
					tailNumbers: tailString(endorsement.tailNumbers),
				};
			}
			return {};
		},
	},
	crew_training: {
		label: "Crew Training Endorsement",
		schema: CrewTrainingEndorsementSchema,
		documentName: "N AV E CLASSA 0089 05 25 Crew Training Endorsement.docx",
		formNumber: "N AV E CLASSA 0089 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "crew_training") {
				endorsements.push({
					name: "Crew Training",
					included: true,
					limits: [
						{
							qualifier: endorsement.schedule,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "crew_training") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					schedule: endorsement.schedule,
				};
			}
			return {};
		},
	},
	loss_control_safety: {
		label: "Loss Control, Safety Program Expense Reimbursement",
		schema: LossControlSafetyEndorsementSchema,
		documentName: "N AV E CLASSA 0007 05 25 Loss Control Safety Program Expense Reimb.docx",
		formNumber: "N AV E CLASSA 0007 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "loss_control_safety") {

				const qualifier = endorsement.description ? `${formatCurrency(endorsement.bursaryAmount)} - ${endorsement.description}` : `${formatCurrency(endorsement.bursaryAmount)}`;
            
				endorsements.push({
					name: "Loss Control, Safety Program Expense Reimbursement",
					included: true,
					limits: [
						{
							qualifier,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "loss_control_safety") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					bursaryAmount: formatCurrency(endorsement.bursaryAmount),
				};
			}
			return {};
		},
	},
	invalidation_clause: {
		label: "Invalidation Clause Endorsement",
		schema: InvalidationClauseEndorsementSchema,
		documentName: "N AV E CLASSA 0104 05 25 Invalidation Clause Endorsement.docx",
		formNumber: "N AV E CLASSA 0104 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "invalidation_clause") {
				endorsements.push({
					name: "Invalidation Clause",
					included: true,
					limits: [
						{
							qualifier: endorsement.agreementName,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: endorsement.schedule,
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "invalidation_clause") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					agreementName: endorsement.agreementName,
					schedule: endorsement.schedule,
				};
			}
			return {};
		},
	},
	cancellation_clause_ammendment: {
		label: "Cancellation Clause Ammendment Endorsement",
		schema: CancellationClauseAmmendmentEndorsementSchema,
		documentName: "N AC E CLASSA 0077 05 25 Cancellation Clause Amendment Endorsement.docx",
		formNumber: "N AC E CLASSA 0077 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "cancellation_clause_ammendment") {
				endorsements.push({
					name: "Cancellation Clause Amendment",
					included: true,
					limits: [
						{
							qualifier: "10 day notice for non-payment",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
						{
							qualifier: "90 day notice for any other reason",
							amount: null,
							premiumIncluded: "",
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "invalidation_clause") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					agreementName: endorsement.agreementName,
					schedule: endorsement.schedule,
				};
			}
			return {};
		},
	},
	excluded_uses: {
		label: "Excluded Uses Endorsement",
		schema: ExcludedUsesEndorsementSchema,
		documentName: "N AV E CLASSA 0072 05 25 Excluded Uses Endorsement.docx",
		formNumber: "N AV E CLASSA 0072 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "excluded_uses") {
				const excludedUses: Array<string> = [];
            
				const values = Object.entries(endorsement).filter(([key, value]) => key.startsWith("excludes") && value === true);
				for (const [key, value] of values) {
					const schema = ExcludedUsesEndorsementSchema.properties[key as keyof typeof ExcludedUsesEndorsementSchema.properties];
					if (schema.description && value && schema.description !== "Other") {
						excludedUses.push(schema.description);
					}
				}
				if (endorsement.otherExclusion) {
					excludedUses.push(endorsement.otherExclusion);
				}
				endorsements.push({
					name: "Excluded Uses",
					included: true,
					limits: [{
						qualifier: `Excludes the following uses: ${excludedUses.join(", ")}`,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					}],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "excluded_uses") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					excludesApplicationOfChemicals: renderCheckbox(endorsement.excludesApplicationOfChemicals),
					excludesAerialMapping: renderCheckbox(endorsement.excludesAerialMapping),
					excludesAerialSurvey: renderCheckbox(endorsement.excludesAerialSurvey),
					excludesAirAmbulance: renderCheckbox(endorsement.excludesAirAmbulance),
					excludesAnimalHerding: renderCheckbox(endorsement.excludesAnimalHerding),
					excludesBannerTowing: renderCheckbox(endorsement.excludesBannerTowing),
					excludesCloudSeedingWeatherMod: renderCheckbox(endorsement.excludesCloudSeedingWeatherMod),
					excludesControlledBurning: renderCheckbox(endorsement.excludesControlledBurning),
					excludesFireSuppression: renderCheckbox(endorsement.excludesFireSuppression),
					excludesFishSpotting: renderCheckbox(endorsement.excludesFishSpotting),
					excludesFogDispersal: renderCheckbox(endorsement.excludesFogDispersal),
					excludesFlightInstructionToOthers: renderCheckbox(endorsement.excludesFlightInstructionToOthers),
					excludesLawEnforcement: renderCheckbox(endorsement.excludesLawEnforcement),
					excludesVideo: renderCheckbox(endorsement.excludesVideo),
					excludesParachuting: renderCheckbox(endorsement.excludesParachuting),
					excludesPhotography: renderCheckbox(endorsement.excludesPhotography),
					excludesHeliskiing: renderCheckbox(endorsement.excludesHeliskiing),
					excludesPipelinePatrol: renderCheckbox(endorsement.excludesPipelinePatrol),
					excludesPowerlinePatrol: renderCheckbox(endorsement.excludesPowerlinePatrol),
					excludesPowerlineMaintenance: renderCheckbox(endorsement.excludesPowerlineMaintenance),
					excludesPredatorControl: renderCheckbox(endorsement.excludesPredatorControl),
					excludesSearchAndRescue: renderCheckbox(endorsement.excludesSearchAndRescue),
					excludesSlungLoad: renderCheckbox(endorsement.excludesSlungLoad),
					excludesTrafficWatch: renderCheckbox(endorsement.excludesTrafficWatch),
					excludesTVNews: renderCheckbox(endorsement.excludesTVNews),
					excludesWildlifeTracking: renderCheckbox(endorsement.excludesWildlifeTracking),
					excludesOther: renderCheckbox(endorsement.excludesOther),
					otherExclusion: endorsement.otherExclusion ?? "",
				};
			}
			return {};
		},
        
	},
	rental_to_named_pilots: {
		label: "Aircraft Rental to Named Pilots",
		schema: RentalToNamedPilotsEndorsementSchema,
		documentName: "N AV E CLASSA 0066 05 25 Rental to Named Pilots.docx",
		formNumber: "N AV E CLASSA 0066 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "rental_to_named_pilots") {
				endorsements.push({
					name: "Aircraft Rental to Named Pilots",
					included: true,
					limits: [{
						qualifier: "Rental to pilots named on the Pilot Warranty Endorsement",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					}],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "rental_to_named_pilots") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					waiveRecoveryAgainstNamedPilots: renderCheckbox(endorsement.waiveRecoveryAgainstNamedPilots),
					insuredParagraph3NotApplicable: renderCheckbox(endorsement.insuredParagraph3NotApplicable),
				};
			}
			return {};
		},
	},
	independent_pilot: {
		label: "Independent Pilot Endorsement",
		schema: IndependentPilotEndorsementSchema,
		documentName: "N AV E CLASSA 0091 05 25 Independent Pilot Endorsement.docx",
		formNumber: "N AV E CLASSA 0091 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "independent_pilot") {
				endorsements.push({
					name: "Independent Pilot",
					included: true,
					limits: [
						{
							qualifier: "Included",
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "independent_pilot") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	excluded_pilots: {
		label: "Excluded Pilots Endorsement",
		schema: ExcludedPilotsEndorsementSchema,
		documentName: "N AV E CLASSA 0070 05 25 Excluded Pilots.docx",
		formNumber: "N AV E CLASSA 0070 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "excluded_pilots") {
				endorsements.push({
					name: "Excluded Pilots",
					included: true,
					limits: [{
						qualifier: `Excludes the following pilots:\n${endorsement.excludedPilots}`,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					}],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "excluded_pilots") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					excludedPilots: endorsement.excludedPilots,
				};
			}
			return {};
		},
	},
	excluded_aircraft: {
		label: "Excluded Aircraft Endorsement",
		schema: ExcludedAircraftEndorsementSchema,
		documentName: "N AV E CLASSA 0071 05 25 Excluded Aircraft Endorsement.docx",
		formNumber: "N AV E CLASSA 0071 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "excluded_aircraft") {
				const aircraft = endorsement.excludedAircraftSchedule
					.map((row) => `${row.tailNumber} — ${row.year} ${row.makeModel}`.trim())
					.join("\n");
				endorsements.push({
					name: "Excluded Aircraft",
					included: true,
					limits: [{
						qualifier: `Excludes the following aircraft:\n${aircraft}`,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					}],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "excluded_aircraft") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					aircraft: endorsement.excludedAircraftSchedule.map((row) => ({
						year: row.year,
						makeModel: row.makeModel,
						tailNumber: row.tailNumber,
					})),
				};
			}
			return {};
		},
	},
	liability: {
		label: "Liability Endorsement",
		schema: LiabilityEndorsementSchema,
		documentName: "N AC E CLASSA 0006 05 25 Liability Endorsement.docx",
		formNumber: "N AC E CLASSA 0006 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "liability") {
				const limits: Endorsement["limits"] = [
					{
						qualifier: endorsement.conditions,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					},
				];
				if (endorsement.coverageAEachPerson) {
					limits.push({
						qualifier: "Bodily Injury - Each Person",
						amount: endorsement.coverageAEachPerson,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageAEachOccurrence) {
					limits.push({
						qualifier: "Bodily Injury - Each Occurrence",
						amount: endorsement.coverageAEachOccurrence,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageBEachOccurrence) {
					limits.push({
						qualifier: "Property Damage - Each Occurrence",
						amount: endorsement.coverageBEachOccurrence,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageCEachPassenger) {
					limits.push({
						qualifier: "Passenger Liability - Each Passenger",
						amount: endorsement.coverageCEachPassenger,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageCEachOccurrence) {
					limits.push({
						qualifier: "Passenger Liability - Each Occurrence",
						amount: endorsement.coverageCEachOccurrence,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageDEachPassenger) {
					limits.push({
						qualifier: "Single Limit Bodily Injury & Property Damage - Each Passenger",
						amount: endorsement.coverageDEachPassenger,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageDEachOccurrence) {
					limits.push({
						qualifier: "Single Limit Bodily Injury & Property Damage - Each Occurrence",
						amount: endorsement.coverageDEachOccurrence,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageEEachPerson) {
					limits.push({
						qualifier: "Medical Expense Including Crew - Each Person",
						amount: endorsement.coverageEEachPerson,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageEEachOccurrence) {
					limits.push({
						qualifier: "Medical Expense Including Crew - Each Occurrence",
						amount: endorsement.coverageEEachOccurrence,
						premiumIncluded: "",
					});
				}
				appendAutoExpirationPrintLimit(limits, endorsement.autoExpiration);
				endorsements.push({
					name: "Liability Endorsement",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					conditions: endorsement.conditions,
					coverageAEachPerson: nullishCurrencyOrBlank(endorsement.coverageAEachPerson),
					coverageAEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageAEachOccurrence),
					coverageBEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageBEachOccurrence),
					coverageCEachPassenger: nullishCurrencyOrBlank(endorsement.coverageCEachPassenger),
					coverageCEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageCEachOccurrence),
					coverageDEachPassenger: nullishCurrencyOrBlank(endorsement.coverageDEachPassenger),
					coverageDEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageDEachOccurrence),
					coverageEEachPerson: nullishCurrencyOrBlank(endorsement.coverageEEachPerson),
					coverageEEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageEEachOccurrence),
					...autoExpirationPrintVariables(endorsement),
				};
			}
			return {};
		},
	},
	liability_per_person_limit: {
		label: "Amendment of Liability Coverage to Per Person Limit Endorsement",
		schema: LiabilityPerPersonLimitEndorsementSchema,
		documentName: "N AC E CLASSA 0007 05 25 Amendment of Liability Coverage to Per Person Limit Endorsement.docx",
		formNumber: "N AC E CLASSA 0007 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "liability_per_person_limit") {
				const limits: Endorsement["limits"] = [
					{
						qualifier: null,
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					},
				];
				if (endorsement.coverageDEachOccurrence) {
					limits.push({
						qualifier: "Single Limit Bodily Injury & Property Damage - Each Occurrence",
						amount: endorsement.coverageDEachOccurrence,
						premiumIncluded: "",
					});
				}
				if (endorsement.coverageDEachPerson) {
					limits.push({
						qualifier: "Single Limit Bodily Injury & Property Damage - Each Person",
						amount: endorsement.coverageDEachPerson,
						premiumIncluded: "",
					});
				}
				endorsements.push({
					name: "Amendment of Liability Coverage to Per Person Limit",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "liability_per_person_limit") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					coverageDEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageDEachOccurrence),
					coverageDEachPerson: nullishCurrencyOrBlank(endorsement.coverageDEachPerson),
				};
			}
			return {};
		},
	},
	restricted_passenger_occurrence_limit: {
		label: "Restricted Occurrence Limit for Passenger Liability Endorsement",
		schema: RestrictedPassengerOccurrenceLimitEndorsementSchema,
		documentName: "N AC E CLASSA 0008 05 25 Restricted Occurrence Limit for Passenger Liability.docx",
		formNumber: "N AC E CLASSA 0008 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "restricted_passenger_occurrence_limit") {
				const limits: Endorsement["limits"] = [
					{
						qualifier: "Single Limit Bodily Injury & Property Damage - Each Occurrence",
						amount: endorsement.coverageDEachOccurrence,
						premiumIncluded: getPremiumText(endorsement),
					},
				];
				const passengerLimits: Array<[string, number | null]> = [
					["Passenger Liability - Each Passenger", endorsement.coverageDEachPassenger],
					["Passenger Liability - Each Occurrence (All Passengers)", endorsement.coverageDEachOccurrenceAllPassengers],
				];
				for (const [qualifier, amount] of passengerLimits) {
					if (amount) {
						limits.push({
							qualifier, amount, premiumIncluded: "",
						});
					}
				}
				endorsements.push({
					name: "Restricted Occurrence Limit for Passenger Liability Endorsement",
					included: true,
					limits,
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "restricted_passenger_occurrence_limit") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
					coverageDEachOccurrence: nullishCurrencyOrBlank(endorsement.coverageDEachOccurrence),
					coverageDEachPassenger: nullishCurrencyOrBlank(endorsement.coverageDEachPassenger),
					coverageDEachOccurrenceAllPassengers: nullishCurrencyOrBlank(endorsement.coverageDEachOccurrenceAllPassengers),
				};
			}
			return {};
		},
	},
	referral_liability: {
		label: "Referral Liability Endorsement",
		schema: ReferralLiabilityEndorsementSchema,
		documentName: "N AV E CLASSA 0039 05 25 Referral Liability Endorsement.docx",
		formNumber: "N AV E CLASSA 0039 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "referral_liability") {
				endorsements.push({
					name: "Referral Liability",
					included: true,
					limits: [
						{
							qualifier: null,
							amount: null,
							premiumIncluded: getPremiumText(endorsement),
						},
					],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "referral_liability") {
				return {
					premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				};
			}
			return {};
		},
	},
	pilot_warranty: {
		label: "Pilot Warranty Endorsement",
		schema: PilotWarrantyEndorsementSchema,
		documentName: "N AC E CLASSA 0019 05 25 Pilot Warranty Endorsement 4.docx",
		formNumber: "N AC E CLASSA 0019 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: false,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "pilot_warranty") {
				endorsements.push({
					name: "Pilot Warranty",
					included: true,
					limits: [{
						qualifier: "See pilot warranty section",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					}],
				});
			}
			return endorsements;
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "pilot_warranty") {
				return {
					premiumConsideration: premiumConsiderationText(
						endorsement.included,
						endorsement.additionalPremium,
					),
					pilotWarrantyMarkdown: (endorsement as { pilotWarrantyMarkdown?: string }).pilotWarrantyMarkdown ?? "",
				};
			}
			return {};
		},
	},
	policy_cancellation: {
		label: "Policy Cancellation",
		schema: PolicyCancellationEndorsementSchema,
		documentName: "N AV E CLASSA 0109 05 25 Cancellation Endorsement.docx",
		formNumber: "N AV E CLASSA 0109 05 25",
		lob: ["general_aviation", "product_liability", "uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug === "policy_cancellation") {
				return {
					premiumConsideration: premiumConsiderationText(false, endorsement.refundedPremium),
				};
			}
			return {};
		},
	},
	mid_term_invoice: {
		label: "Mid-Term Invoice",
		schema: MidTermInvoiceEndorsementSchema,
		documentName: "N AV E CLASSA 0110 05 25 Mid-Term Invoice.docx",
		formNumber: "N AV E CLASSA 0110 05 25",
		lob: ["general_aviation", "product_liability", "uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
		generateDynamicVariables: () => ({}),
	},
	deletion: {
		label: "Deletion Endorsement",
		schema: DeletionEndorsementSchema,
		documentName: "N AV E CLASSA 0001 05 25 Deletion Endorsement.docx",
		formNumber: "N AV E CLASSA 0001 05 25",
		lob: ["general_aviation", "product_liability", "uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
		generateDynamicVariables: generateDeletionEndorsementDynamicVariables,
	},
	additional_aircraft: {
		label: "Additional Aircraft (Mid-Term)",
		labelsByLob: { uas: "Additional UAS (Mid-Term)" },
		schema: AdditionalAircraftEndorsementSchema,
		documentName: "N AC E CLASSA 0001 05 25 Aircraft Endorsement.docx",
		formNumber: "N AC E CLASSA 0001 05 25",
		documentsByLob: { uas: UAS_AIRCRAFT_DOCUMENT },
		lob: ["general_aviation", "uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
		generateDynamicVariables: (endorsement, ctx) =>
			generateAircraftMidTermEndorsementDynamicVariables(
				endorsement as unknown as { slug: string; premiumAdjustment?: number },
				ctx,
			),
	},
	adjusted_aircraft: {
		label: "Adjusted Aircraft (Mid-Term)",
		labelsByLob: { uas: "Adjusted UAS (Mid-Term)" },
		schema: AdjustedAircraftEndorsementSchema,
		documentName: "N AC E CLASSA 0001 05 25 Aircraft Endorsement.docx",
		formNumber: "N AC E CLASSA 0001 05 25",
		documentsByLob: { uas: UAS_AIRCRAFT_DOCUMENT },
		lob: ["general_aviation", "uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
		generateDynamicVariables: (endorsement, ctx) =>
			generateAircraftMidTermEndorsementDynamicVariables(
				endorsement as unknown as { slug: string; premiumAdjustment?: number },
				ctx,
			),
	},
	removed_aircraft: {
		label: "Removed Aircraft (Mid-Term)",
		labelsByLob: { uas: "Removed UAS (Mid-Term)" },
		schema: RemovedAircraftEndorsementSchema,
		documentName: "N AV E CLASSA 0001 05 25 Aircraft Deletion Endorsement.docx",
		formNumber: "N AV E CLASSA 0001 05 25",
		lob: ["general_aviation", "uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
		generateDynamicVariables: generateRemovedAircraftEndorsementDynamicVariables,
	},
	changed_named_insured: {
		label: "Changed Named Insured (Mid-Term)",
		schema: ChangedNamedInsuredEndorsementSchema,
		documentName: "N AV E CLASSA 0002 05 25 Named Insured and or Address Revision.docx",
		formNumber: "N AV E CLASSA 0002 05 25",
		lob: ["general_aviation", "product_liability", "uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
		generateDynamicVariables: generateChangedNamedInsuredEndorsementDynamicVariables,
	},
	changed_producer: {
		label: "Producer Change (Mid-Term)",
		schema: ChangedProducerEndorsementSchema,
		documentName: "N AV E CLASSA 0009 05 25 Producer Change.docx",
		formNumber: "N AV E CLASSA 0009 05 25",
		lob: ["general_aviation", "product_liability", "uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
		generateDynamicVariables: generateChangedProducerEndorsementDynamicVariables,
	},
	registration_number_revision: {
		label: "Registration Number Revision (Mid-Term)",
		labelsByLob: { uas: "Serial Number Revision (Mid-Term)" },
		schema: RegistrationNumberRevisionEndorsementSchema,
		documentName: "N AV E CLASSA 0018 05 25 Registration Number Revision.docx",
		formNumber: "N AV E CLASSA 0018 05 25",
		lob: ["general_aviation", "uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
		generateDynamicVariables: generateRegistrationNumberRevisionEndorsementDynamicVariables,
	},
	policy_number_revision: {
		label: "Policy Number Revision",
		schema: PolicyNumberRevisionEndorsementSchema,
		documentName: "N AV E CLASSA 0011 05 25 Policy Number Revision.docx",
		formNumber: "N AV E CLASSA 0011 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "policy_number_revision"
			? [{
				name: "Policy Number Revision",
				included: true,
				limits: [{
					qualifier: `${endorsement.previousPolicyNumber} to ${endorsement.newPolicyNumber}`,
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				}],
			}]
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "policy_number_revision") return {};
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				previousPolicyNumber: endorsement.previousPolicyNumber,
				newPolicyNumber: endorsement.newPolicyNumber,
			};
		},
	},
	physical_damage: {
		label: "Physical Damage Endorsement",
		schema: PhysicalDamageEndorsementSchema,
		documentName: "N AC E CLASSA 0009 05 25 Physical Damage Endorsement.docx",
		formNumber: "N AC E CLASSA 0009 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "physical_damage") {
				return [];
			}
			const limits: Endorsement["limits"] = endorsement.aircraftSchedule.map((row, index) => ({
				qualifier: `${row.tailNumber.trim()} ${row.makeModel.trim()}`.trim() || "As scheduled",
				amount: row.insuredValue,
				premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
			}));
			if (limits.length === 0) {
				limits.push({
					qualifier: "As scheduled",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				});
			}
			return [{
				name: "Physical Damage Endorsement",
				included: true,
				limits,
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "physical_damage") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				aircraft: endorsement.aircraftSchedule.map(physicalDamageAircraftRowVariables),
			};
		},
	},
	custom_endorsement: {   
		label: "Custom Endorsement",
		schema: CustomEndorsementSchema,
		documentName: "CLASSA 0000",
		formNumber: "CLASSA 0000",
		lob: ["general_aviation", "product_liability", "commercial_general_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			const endorsements: Endorsement[] = [];
			if (endorsement.slug === "custom_endorsement") {
				const limits = endorsement.limits.map((limit, index) => ({
					qualifier: limit.qualifier,
					amount: limit.limit,
					premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
				}));
                
				if (limits.length  === 0) {
					limits.push({
						qualifier: "",
						amount: null,
						premiumIncluded: getPremiumText(endorsement),
					});
				}
				endorsements.push({
					name: endorsement.title,
					included: true,
					limits,
				});
                
			}
			return endorsements;
		},
		generateDynamicVariables (_: GenericEndorsement) {
			return {};
		},
	},
    
	// Endorsements that are not included in the quote template
	aircraft_policy: {
		label: "Aircraft Policy",
		schema: Type.Object({}),
		documentName: "N AC C CLASSA 0001 05 25 Aircraft Policy.docx",
		formNumber: "N AC C CLASSA 0001 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	data_event_clause: {
		label: "Data Event Clause",
		schema: Type.Object({}),
		documentName: "N AC E CLASSA 0090 05 25 Data Event Clause.docx",
		formNumber: "N AC E CLASSA 0090 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	cover_page: {
		label: "Cover Page",
		schema: Type.Object({}),
		documentName: "N AC P CLASSA 0001 05 25 Aircraft Insurance Policy Cover Page.docx",
		formNumber: "000 - N AC P CLASSA 0001 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	tria_disclosure: {
		label: "TRIA Disclosure",
		schema: Type.Object({}),
		documentName: "N AV E CLASSA 0118 05 25 TRIA Disclosure Endorsement.docx",
		formNumber: "N AV E CLASSA 0118 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	tria_exclusion: {
		label: "TRIA Exclusion",
		schema: Type.Object({}),
		documentName: "N AV E CLASSA 0119 05 25 TRIA Exclusion Endorsement.docx",
		formNumber: "N AV E CLASSA 0119 05 25",
		lob: ["general_aviation", "product_liability"],
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	communicable_disease_exclusion: {
		label: "Communicable Disease Exclusion",
		schema: Type.Object({}),
		documentName: "N AV E CLASSA 0055 05 25 Communicable Disease Exclusion.docx",
		formNumber: "N AV E CLASSA 0055 05 25",
		lob: ["general_aviation"],
		hasStateOverride: true,
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	geographic_areas_exclusion: {
		label: "Geographic Areas Exclusion",
		schema: Type.Object({}),
		documentName: "N AC E CLASSA 0048 05 25 Geographic Areas Exclusion Clause.docx",
		formNumber: "N AC E CLASSA 0048 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	uas_aircraft: {
		label: "UAS Endorsement",
		schema: UasAircraftEndorsementSchema,
		documentName: UAS_AIRCRAFT_DOCUMENT.documentName,
		formNumber: UAS_AIRCRAFT_DOCUMENT.formNumber,
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_aircraft") {
				return [];
			}
			const limits = uasPhysicalDamagePrintLimits(endorsement, endorsement.aircraftSchedule);
			for (const row of endorsement.aircraftSchedule) {
				if (row.liabilityLimit !== null) {
					limits.push({
						qualifier: `${row.tailNumber.trim()} liability each occurrence`.trim(),
						amount: row.liabilityLimit,
						premiumIncluded: "",
					});
				}
			}
			return [{
				name: "UAS Endorsement",
				included: true,
				limits,
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_aircraft") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				...uasAircraftScheduleVariables(endorsement.aircraftSchedule),
			};
		},
	} satisfies EndorsementConfig<typeof UasAircraftEndorsementSchema.static>,
	uas_liability_amendment: {
		label: "Liability Amendment Endorsement",
		schema: UasLiabilityAmendmentEndorsementSchema,
		documentName: "N UAS E CLASSA 0002 05 25 Liability Amendment Endorsement.docx",
		formNumber: "N UAS E CLASSA 0002 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_liability_amendment") {
				return [];
			}
			return [{
				name: "Liability Amendment",
				included: true,
				limits: uasLiabilityPrintLimits(endorsement, endorsement.aircraftSchedule),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_liability_amendment") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				liabilityAircraft: endorsement.aircraftSchedule.map(uasLiabilityRowVariables),
			};
		},
	} satisfies EndorsementConfig<typeof UasLiabilityAmendmentEndorsementSchema.static>,
	uas_limits_amendment: {
		label: "Limits Amendment Endorsement",
		schema: UasLimitsAmendmentEndorsementSchema,
		documentName: "N UAS E CLASSA 0003 05 25 Limits Amendment Endorsement.docx",
		formNumber: "N UAS E CLASSA 0003 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_limits_amendment") {
				return [];
			}
			const rows: Array<[string, number | null]> = [
				...endorsement.liabilitySchedule.map((row): [string, number | null] =>
					[`${row.tailNumber.trim()} liability each occurrence`.trim(), row.liabilityLimit]),
				...UAS_LIMITS_AMENDMENT_PRINT_ROWS
					.filter(([field]) => endorsement[field] !== null)
					.map(([field, qualifier]): [string, number | null] => [qualifier, endorsement[field]]),
				...endorsement.aircraftSchedule.map((row): [string, number | null] =>
					[`${row.tailNumber.trim()} ${row.makeModel.trim()}`.trim(), row.insuredValue]),
			];
			if (rows.length === 0) {
				rows.push(["As scheduled", null]);
			}
			return [{
				name: "Limits Amendment",
				included: true,
				limits: rows.map(([qualifier, amount], index) => ({
					qualifier,
					amount,
					premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
				})),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_limits_amendment") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				liabilityAircraft: endorsement.liabilitySchedule.map(uasLiabilityRowVariables),
				nonOwnedLiabilityLimit: nullishCurrencyOrBlank(endorsement.nonOwnedLiabilityLimit),
				newlyAcquiredLiabilityLimit: nullishCurrencyOrBlank(endorsement.newlyAcquiredLiabilityLimit),
				premisesLiabilityLimit: nullishCurrencyOrBlank(endorsement.premisesLiabilityLimit),
				fireLegalLiabilityLimit: nullishCurrencyOrBlank(endorsement.fireLegalLiabilityLimit),
				saleOfAircraftProductsLimit: nullishCurrencyOrBlank(endorsement.saleOfAircraftProductsLimit),
				contractualLiabilityLimit: nullishCurrencyOrBlank(endorsement.contractualLiabilityLimit),
				personalInjuryLimit: nullishCurrencyOrBlank(endorsement.personalInjuryLimit),
				aircraft: endorsement.aircraftSchedule.map(uasPhysicalDamageRowVariables),
				unexpectedLandingLimit: nullishCurrencyOrBlank(endorsement.unexpectedLandingLimit),
				unexpectedLandingAggregate: nullishCurrencyOrBlank(endorsement.unexpectedLandingAggregate),
				medicalPaymentsEachPerson: nullishCurrencyOrBlank(endorsement.medicalPaymentsEachPerson),
				medicalPaymentsEachAccident: nullishCurrencyOrBlank(endorsement.medicalPaymentsEachAccident),
			};
		},
	} satisfies EndorsementConfig<typeof UasLimitsAmendmentEndorsementSchema.static>,
	uas_physical_damage: {
		label: "Physical Damage Endorsement",
		schema: UasPhysicalDamageEndorsementSchema,
		documentName: "N UAS E CLASSA 0004 05 25 Physical Damage Endorsement.docx",
		formNumber: "N UAS E CLASSA 0004 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_physical_damage") {
				return [];
			}
			return [{
				name: "Physical Damage Endorsement",
				included: true,
				limits: uasPhysicalDamagePrintLimits(endorsement, endorsement.aircraftSchedule),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_physical_damage") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				aircraft: endorsement.aircraftSchedule.map(uasPhysicalDamageRowVariables),
			};
		},
	} satisfies EndorsementConfig<typeof UasPhysicalDamageEndorsementSchema.static>,
	uas_not_in_motion_physical_damage: {
		label: "Not in Motion / Not In-Flight Physical Damage Endorsement",
		schema: UasNotInMotionPhysicalDamageEndorsementSchema,
		documentName: "N UAS E CLASSA 0005 05 25 Not in Motion_Not in Flight Physical Damage Endorsement.docx",
		formNumber: "N UAS E CLASSA 0005 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_not_in_motion_physical_damage") {
				return [];
			}
			return [{
				name: "Not in Motion / Not In-Flight Physical Damage",
				included: true,
				limits: uasPhysicalDamagePrintLimits(endorsement, endorsement.aircraftSchedule),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_not_in_motion_physical_damage") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				physicalDamageChange: endorsement.physicalDamageChange,
				aircraft: endorsement.aircraftSchedule.map(uasPhysicalDamageRowVariables),
			};
		},
	} satisfies EndorsementConfig<typeof UasNotInMotionPhysicalDamageEndorsementSchema.static>,
	uas_special_equipment: {
		label: "Special Equipment Endorsement",
		schema: UasSpecialEquipmentEndorsementSchema,
		documentName: "N UAS E CLASSA 0006 05 25 Special Equipment Endorsement.docx",
		formNumber: "N UAS E CLASSA 0006 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_special_equipment") {
				return [];
			}
			const limits: Endorsement["limits"] = endorsement.equipmentSchedule.map((row, index) => ({
				qualifier: singleLine(row.description) || "As scheduled",
				amount: row.insuredValue,
				premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
			}));
			if (limits.length === 0) {
				limits.push({
					qualifier: "As scheduled",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				});
			}
			return [{
				name: "Special Equipment",
				included: true,
				limits,
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_special_equipment") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				equipment: endorsement.equipmentSchedule.map(row => ({
					description: row.description,
					insuredValue: nullishCurrencyOrBlank(row.insuredValue),
					deductible: row.deductible === null ? "NIL" : formatCurrency(row.deductible),
				})),
			};
		},
	} satisfies EndorsementConfig<typeof UasSpecialEquipmentEndorsementSchema.static>,
	special_equipment: {
		label: "Special Equipment Endorsement",
		schema: SpecialEquipmentEndorsementSchema,
		documentName: "N AC E CLASSA 0013 05 25 Special Equipment Endorsement.docx",
		formNumber: "N AC E CLASSA 0013 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "special_equipment") {
				return [];
			}
			const limits: Endorsement["limits"] = endorsement.equipmentSchedule.map((row, index) => ({
				qualifier: singleLine(row.description) || "As scheduled",
				amount: row.insuredValue,
				premiumIncluded: index === 0 ? getPremiumText(endorsement) : "",
			}));
			if (limits.length === 0) {
				limits.push({
					qualifier: "As scheduled",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				});
			}
			limits.push({
				qualifier: `${qualifierLabels.deductible}: each loss`,
				amount: endorsement.deductible === null ? "NIL" : endorsement.deductible,
				premiumIncluded: "",
			});
			if (endorsement.tailNumbers.length > 0) {
				limits.push({
					qualifier: `Applies to: ${tailString(endorsement.tailNumbers)}`,
					amount: null,
					premiumIncluded: "",
				});
			}
			return [{
				name: "Special Equipment",
				included: true,
				limits,
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "special_equipment") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				tailNumbers: tailString(endorsement.tailNumbers),
				equipment: endorsement.equipmentSchedule.map(row => ({
					description: row.description,
					insuredValue: nullishCurrencyOrBlank(row.insuredValue),
				})),
				deductible: endorsement.deductible === null ? "NIL" : formatCurrency(endorsement.deductible),
			};
		},
	} satisfies EndorsementConfig<typeof SpecialEquipmentEndorsementSchema.static>,
	premium_amendment: {
		label: "Premium Amendment",
		schema: PremiumAmendmentEndorsementSchema,
		documentName: "N AC E CLASSA 0023 05 25 Premium Amendment Endorsement.docx",
		formNumber: "N AC E CLASSA 0023 05 25",
		lob: ["general_aviation"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "premium_amendment") {
				return [];
			}
			return [{
				name: "Premium Amendment",
				included: true,
				limits: [{
					qualifier: "Included",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				}],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "premium_amendment") {
				return {};
			}
			const showDeclarations = endorsement.amendDeclarationsPremium;
			const showEndorsement = endorsement.amendEndorsementPremium;
			const centsOrBlank = (value: number | null) => value === null ? "" : formatCurrency(value, true);
			const amended = showEndorsement ? endorsement.amendedEndorsementPremium : null;
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				amendDeclarationsPremium: renderCheckbox(showDeclarations),
				policyPremium: showDeclarations ? centsOrBlank(endorsement.policyPremium) : "",
				totalPremium: showDeclarations ? centsOrBlank(endorsement.totalPremium) : "",
				amendEndorsementPremium: renderCheckbox(showEndorsement),
				amendedEndorsementNumber: showEndorsement ? endorsement.amendedEndorsementNumber : "",
				amendedPremiumType: amended === null ? "" : amended < 0 ? "a return" : "an additional",
				amendedPremium: amended === null ? "" : formatCurrency(Math.abs(amended)),
			};
		},
	} satisfies EndorsementConfig<typeof PremiumAmendmentEndorsementSchema.static>,
	uas_premium_amendment: {
		label: "Premium Amendment",
		schema: UasPremiumAmendmentEndorsementSchema,
		documentName: "N UAS E CLASSA 0007 05 25 Premium Amendment Endorsement.docx",
		formNumber: "N UAS E CLASSA 0007 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_premium_amendment") {
				return [];
			}
			return [{
				name: "Premium Amendment",
				included: true,
				limits: [{
					qualifier: "Included",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				}],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_premium_amendment") {
				return {};
			}
			const showItem4 = endorsement.amendDeclarationsPremium;
			const centsOrBlank = (value: number | null) => value === null ? "" : formatCurrency(value, true);
			const total = (endorsement.policyPremium ?? 0) + (endorsement.taxes ?? 0) + (endorsement.feesAndSurcharges ?? 0);
			const amended = endorsement.amendedEndorsementPremium;
			let amendedPremiumText = "";
			if (endorsement.amendEndorsementPremium && amended !== null) {
				amendedPremiumText = amended < 0
					? `a return premium of ${formatCurrency(Math.abs(amended))}`
					: `an additional premium of ${formatCurrency(amended)}`;
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				amendDeclarationsPremium: renderCheckbox(showItem4),
				policyPremium: showItem4 ? centsOrBlank(endorsement.policyPremium) : "",
				taxes: showItem4 ? centsOrBlank(endorsement.taxes) : "",
				feesAndSurcharges: showItem4 ? centsOrBlank(endorsement.feesAndSurcharges) : "",
				totalPremium: showItem4 ? formatCurrency(total, true) : "",
				amendEndorsementPremium: renderCheckbox(endorsement.amendEndorsementPremium),
				amendedEndorsementNumber: endorsement.amendEndorsementPremium ? endorsement.amendedEndorsementNumber : "",
				amendedPremiumText,
			};
		},
	} satisfies EndorsementConfig<typeof UasPremiumAmendmentEndorsementSchema.static>,
	uas_tria_exclusion: {
		label: "TRIA Exclusion",
		schema: Type.Object({}),
		documentName: "N UAS E CLASSA 0008 05 25 TRIA Exclusion Endorsement.docx",
		formNumber: "N UAS E CLASSA 0008 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	uas_products_liability: {
		label: "Products Liability Endorsement",
		schema: UasProductsLiabilityEndorsementSchema,
		documentName: "N UAS E CLASSA 0009 05 25 Products Liability Endorsement.docx",
		formNumber: "N UAS E CLASSA 0009 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_products_liability") {
				return [];
			}
			return [{
				name: "Products Liability",
				included: true,
				limits: [{
					qualifier: uasLimitQualifierForPrint(endorsement.liabilityLimit, qualifierLabels.each_occurrence),
					amount: endorsement.liabilityLimit,
					premiumIncluded: getPremiumText(endorsement),
				}],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_products_liability") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				liabilityLimit: formatLimitOrFollowsAircraft(endorsement.liabilityLimit),
			};
		},
	} satisfies EndorsementConfig<typeof UasProductsLiabilityEndorsementSchema.static>,
	uas_tria_physical_damage_write_back: {
		label: "TRIA Physical Damage Write-Back",
		schema: UasTriaPhysicalDamageWriteBackEndorsementSchema,
		documentName: "N UAS E CLASSA 0010 05 25 TRIA Physical Damage Write-Back Endorsement.docx",
		formNumber: "N UAS E CLASSA 0010 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement, insuredValue }: PrintConfig) => {
			if (endorsement.slug !== "uas_tria_physical_damage_write_back") {
				return [];
			}
			const limits: Endorsement["limits"] = [{
				qualifier: qualifierLabels.each_occurrence,
				amount: insuredValue ?? null,
				premiumIncluded: "If Purchased",
			}];
			if (endorsement.excludedAircraftSchedule.length > 0) {
				const tails = endorsement.excludedAircraftSchedule.map(row => row.tailNumber).join(", ");
				const election = endorsement.scheduleElection === "purchased" ? "Purchased" : "Excluded";
				limits.push({
					qualifier: `${election}: ${tails}`,
					amount: null,
					premiumIncluded: "",
				});
			}
			return [{
				name: "TRIA Physical Damage Write-Back",
				included: true,
				limits,
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_tria_physical_damage_write_back") {
				return {};
			}
			const purchased = endorsement.scheduleElection === "purchased";
			return {
				terrorismPremium: uasTerrorismPremium(endorsement),
				coverElection: purchased ? "cover" : "not cover",
				purchaseElection: purchased ? "purchase" : "not purchase",
				excludedAircraftSchedule: endorsement.excludedAircraftSchedule.map(row => ({
					tailNumber: row.tailNumber,
					makeModel: row.makeModel,
					year: row.year,
					insuredValue: nullishCurrencyOrBlank(row.insuredValue),
				})),
			};
		},
	} satisfies EndorsementConfig<typeof UasTriaPhysicalDamageWriteBackEndorsementSchema.static>,
	uas_war_liability_write_back: {
		label: "War, Hijacking and Other Perils Limited Liability Write-Back",
		schema: UasWarLiabilityWriteBackEndorsementSchema,
		documentName: "N UAS E CLASSA 0011 05 25 War, Hijacking and Other Perils Limited Liability Write-Back Endorsement.docx",
		formNumber: "N UAS E CLASSA 0011 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_war_liability_write_back") {
				return [];
			}
			let qualifier = "All scheduled UAS";
			let amount: number | null = null;
			if (endorsement.allReportedAircraft) {
				qualifier = "All reported UAS";
			}
			if (endorsement.sameAsSingleLimit) {
				qualifier = endorsement.singleLimitMax ? "Single limit max" : "Same as single limit";
				amount = endorsement.singleLimitMax;
			} else if (endorsement.limit) {
				qualifier = uasLimitQualifierForPrint(endorsement.limitAmount, "Limit");
				amount = endorsement.limitAmount;
			}
			return [{
				name: "War Liability Write-Back",
				included: true,
				limits: [{
					qualifier,
					amount,
					premiumIncluded: getPremiumTextPricedAbove(endorsement),
				}],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_war_liability_write_back") {
				return {};
			}
			const variables: Record<string, string> = {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				allScheduledAircraft: renderCheckbox(endorsement.allScheduledAircraft),
				allScheduledLabel: "All scheduled aircraft",
				allReportedAircraft: renderCheckbox(endorsement.allReportedAircraft),
				limit: renderCheckbox(endorsement.limit),
				limitAmount: conditionallyFormatCurrency({
					value: endorsement.limitAmount,
					hasCoverage: endorsement.limit,
				}),
				sameAsSingleLimit: renderCheckbox(endorsement.sameAsSingleLimit),
				singleLimitBasis: endorsement.allReportedAircraft ? "reported" : "scheduled",
				singleLimitMax: conditionallyFormatCurrency({
					value: endorsement.singleLimitMax,
					hasCoverage: endorsement.sameAsSingleLimit,
				}),
			};
			if (endorsement.selectedTailNumbers?.length) {
				variables.allScheduledAircraft = renderCheckbox(true);
				variables.allReportedAircraft = renderCheckbox(false);
				variables.allScheduledLabel = `Solely as respects to: ${endorsement.selectedTailNumbers.join(", ")}`;
			}
			return variables;
		},
		initialize: ({ liabilityLimit }): typeof UasWarLiabilityWriteBackEndorsementSchema.static => {
			const endorsement = Value.Default(UasWarLiabilityWriteBackEndorsementSchema, {});
			if (Value.Check(UasWarLiabilityWriteBackEndorsementSchema, endorsement)) {
				endorsement.limitAmount = liabilityLimit;
				return endorsement;
			}
			throw new Error("Failed to initialize UAS War Liability Write-Back Endorsement");
		},
	} satisfies EndorsementConfig<typeof UasWarLiabilityWriteBackEndorsementSchema.static>,
	uas_geographic_areas_exclusion: {
		label: "Geographic Areas Exclusion",
		schema: Type.Object({}),
		documentName: "N UAS E CLASSA 0012 05 25 Geographic Areas Exclusion Endorsement.docx",
		formNumber: "N UAS E CLASSA 0012 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	uas_war_auto_termination: {
		label: "Amendment to Automatic Termination Provisions of the War Liability Write-Back",
		schema: UasWarAutoTerminationEndorsementSchema,
		documentName: "N UAS E CLASSA 0013 05 25 Amend to Auto Term Prov of the War Hijacking Lim Liab WB.docx",
		formNumber: "N UAS E CLASSA 0013 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_war_auto_termination") {
				return [];
			}
			return [{
				name: "War Write-Back Automatic Termination Amendment",
				included: true,
				limits: [{
					qualifier: "",
					amount: null,
					premiumIncluded: getPremiumTextPricedAbove(endorsement),
				}],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_war_auto_termination") {
				return {};
			}
			return { premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium) };
		},
	} satisfies EndorsementConfig<typeof UasWarAutoTerminationEndorsementSchema.static>,
	uas_tria_liability_write_back: {
		label: "TRIA Liability Write-Back",
		schema: UasTriaLiabilityWriteBackEndorsementSchema,
		documentName: "N UAS E CLASSA 0014 05 25 TRIA Liability Write-Back Endorsement.docx",
		formNumber: "N UAS E CLASSA 0014 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_tria_liability_write_back") {
				return [];
			}
			return [{
				name: "TRIA Liability Write-Back",
				included: true,
				limits: [{
					qualifier: "",
					amount: null,
					premiumIncluded: getPremiumText(endorsement),
				}],
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_tria_liability_write_back") {
				return {};
			}
			return { terrorismPremium: uasTerrorismPremium(endorsement) };
		},
	} satisfies EndorsementConfig<typeof UasTriaLiabilityWriteBackEndorsementSchema.static>,
	uas_dispensable_load: {
		label: "Deletion of Exclusion Endorsement - Dispensable Load",
		schema: UasDispensableLoadEndorsementSchema,
		documentName: "N UAS E CLASSA 0015 05 25 Deletion of Exclusion Endorsement - Dispensable Load Endorsement.docx",
		formNumber: "N UAS E CLASSA 0015 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("uas_dispensable_load", "Deletion of Exclusion - Dispensable Load"),
		generateDynamicVariables: uasPremiumOnlyVariables("uas_dispensable_load"),
	} satisfies EndorsementConfig<typeof UasDispensableLoadEndorsementSchema.static>,
	uas_fellow_employee: {
		label: "Fellow Employee Coverage Endorsement",
		schema: UasFellowEmployeeEndorsementSchema,
		documentName: "N UAS E CLASSA 0016 05 25 Fellow Employee Coverage Endorsement.docx",
		formNumber: "N UAS E CLASSA 0016 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("uas_fellow_employee", "Fellow Employee Coverage"),
		generateDynamicVariables: uasPremiumOnlyVariables("uas_fellow_employee"),
	} satisfies EndorsementConfig<typeof UasFellowEmployeeEndorsementSchema.static>,
	uas_cancellation_clause_amendment: {
		label: "Cancellation Clause Amendment Endorsement",
		schema: UasCancellationClauseAmendmentEndorsementSchema,
		documentName: "N UAS E CLASSA 0017 05 25 Cancellation Clause Amendment Endorsement.docx",
		formNumber: "N UAS E CLASSA 0017 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_cancellation_clause_amendment") {
				return [];
			}
			return [{
				name: "Cancellation Clause Amendment",
				included: true,
				limits: uasPrintLimits(endorsement, [
					["10 day notice for non-payment", null],
					["30 day notice for any other reason", null],
				]),
			}];
		},
		generateDynamicVariables: uasPremiumOnlyVariables("uas_cancellation_clause_amendment"),
	} satisfies EndorsementConfig<typeof UasCancellationClauseAmendmentEndorsementSchema.static>,
	uas_reporting_form: {
		label: "Reporting Form Endorsement",
		schema: UasReportingFormEndorsementSchema,
		documentName: "N UAS E CLASSA 0018 05 25 Reporting Form Endorsement.docx",
		formNumber: "N UAS E CLASSA 0018 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_reporting_form") {
				return [];
			}
			const rows: Array<[string, number | null]> = [[`Reported ${endorsement.reportingFrequency}`, null]];
			if (endorsement.maximumAutomaticInsuredValue !== null) {
				rows.push(["Maximum automatic insured value", endorsement.maximumAutomaticInsuredValue]);
			}
			if (endorsement.liabilityLimit !== null) {
				rows.push(["Liability each occurrence", endorsement.liabilityLimit]);
			}
			if (endorsement.minimumDepositPremium !== null) {
				rows.push(["Minimum and deposit premium", endorsement.minimumDepositPremium]);
			}
			return [{
				name: "Reporting Form",
				included: true,
				limits: uasPrintLimits(endorsement, rows),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_reporting_form") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				reportingFrequency: endorsement.reportingFrequency,
				premiumBasis: endorsement.premiumBasis,
				premiumFrequency: endorsement.premiumFrequency,
				maximumAutomaticInsuredValue: nullishCurrencyOrBlank(endorsement.maximumAutomaticInsuredValue),
				notInMotionDeductible: uasDeductibleOrNil(endorsement.notInMotionDeductible),
				inMotionDeductible: uasDeductibleOrNil(endorsement.inMotionDeductible),
				liabilityLimit: nullishCurrencyOrBlank(endorsement.liabilityLimit),
				medicalPaymentsEachPerson: nullishCurrencyOrBlank(endorsement.medicalPaymentsEachPerson),
				medicalPaymentsEachAccident: nullishCurrencyOrBlank(endorsement.medicalPaymentsEachAccident),
				attachAnyUav: renderCheckbox(endorsement.attachAnyUav),
				attachOwnedOrLeased: renderCheckbox(endorsement.attachOwnedOrLeased),
				minimumDepositPremium: nullishCurrencyOrBlank(endorsement.minimumDepositPremium),
				premiumRates: endorsement.premiumRates,
			};
		},
	} satisfies EndorsementConfig<typeof UasReportingFormEndorsementSchema.static>,
	uas_data_event_clause: {
		label: "Data Event Clause",
		schema: Type.Object({}),
		documentName: "N UAS E CLASSA 0019 05 25 Data Event Clause.docx",
		formNumber: "N UAS E CLASSA 0019 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	uas_aerial_application: {
		label: "Aerial Application Endorsement",
		schema: UasAerialApplicationEndorsementSchema,
		documentName: "N UAS E CLASSA 0020 05 25 Aerial Application Endorsement.docx",
		formNumber: "N UAS E CLASSA 0020 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_aerial_application") {
				return [];
			}
			const rows: Array<[string, number | null]> = [
				...endorsement.liabilitySchedule.flatMap((row): Array<[string, number | null]> => [
					[`${row.tailNumber.trim()} non-chemical each occurrence`.trim(), row.nonChemicalLimit],
					[`${row.tailNumber.trim()} chemical each occurrence`.trim(), row.chemicalLimit],
				]),
				...UAS_AERIAL_APPLICATION_PRINT_ROWS
					.filter(([field]) => endorsement[field] !== null)
					.map(([field, qualifier]): [string, number | null] => [qualifier, endorsement[field]]),
				...endorsement.aircraftSchedule.map((row): [string, number | null] =>
					[`${row.tailNumber.trim()} ${row.makeModel.trim()}`.trim(), row.insuredValue]),
			];
			if (endorsement.professionalAerialApplicators) {
				rows.push(["Professional aerial applicators coverage", null]);
			}
			return [{
				name: "Aerial Application",
				included: true,
				limits: uasPrintLimits(endorsement, rows.length > 0 ? rows : [["As scheduled", null]]),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_aerial_application") {
				return {};
			}
			const residentialArea = endorsement.residentialAreaCoverage;
			const careCustodyControl = endorsement.careCustodyControlCoverage;
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				liabilityAircraft: endorsement.liabilitySchedule.map(row => ({
					tailNumber: row.tailNumber,
					nonChemicalLimit: nullishCurrencyOrBlank(row.nonChemicalLimit),
					chemicalLimit: nullishCurrencyOrBlank(row.chemicalLimit),
				})),
				nonOwnedNonChemicalLimit: nullishCurrencyOrBlank(endorsement.nonOwnedNonChemicalLimit),
				nonOwnedChemicalLimit: nullishCurrencyOrBlank(endorsement.nonOwnedChemicalLimit),
				newlyAcquiredNonChemicalLimit: nullishCurrencyOrBlank(endorsement.newlyAcquiredNonChemicalLimit),
				newlyAcquiredChemicalLimit: nullishCurrencyOrBlank(endorsement.newlyAcquiredChemicalLimit),
				contractualNonChemicalLimit: nullishCurrencyOrBlank(endorsement.contractualNonChemicalLimit),
				contractualChemicalLimit: nullishCurrencyOrBlank(endorsement.contractualChemicalLimit),
				groundSprayingUnits: endorsement.groundSprayingUnits.map(row => ({
					vin: row.vin,
					makeModel: row.makeModel,
					year: row.year,
					chemicalCategory: row.chemicalCategory,
				})),
				groundSprayingUnitProRatedClause: renderCheckbox(endorsement.groundSprayingUnitProRatedClause),
				groundSprayingUnitMaximumPremium: nullishCurrencyOrBlank(endorsement.groundSprayingUnitMaximumPremium),
				aircraft: endorsement.aircraftSchedule.map(uasPhysicalDamageRowVariables),
				aircraftProRatedClause: renderCheckbox(endorsement.aircraftProRatedClause),
				aircraftMaximumPremium: nullishCurrencyOrBlank(endorsement.aircraftMaximumPremium),
				excludedEquipment: endorsement.excludedEquipment,
				professionalAerialApplicators: renderCheckbox(endorsement.professionalAerialApplicators),
				extraExpenseEachDayLimit: nullishCurrencyOrBlank(endorsement.extraExpenseEachDayLimit),
				extraExpenseEachOccurrenceLimit: nullishCurrencyOrBlank(endorsement.extraExpenseEachOccurrenceLimit),
				extraExpenseDays: endorsement.extraExpenseDays === null ? "" : String(endorsement.extraExpenseDays),
				extraExpenseExcludedAircraft: endorsement.extraExpenseExcludedAircraft,
				sparePartsLimit: nullishCurrencyOrBlank(endorsement.sparePartsLimit),
				sparePartsDeductible: nullishCurrencyOrBlank(endorsement.sparePartsDeductible),
				residentialAreaCoverage: renderCheckbox(residentialArea),
				residentialAreaDeleted: renderCheckbox(residentialArea && endorsement.residentialAreaOption === "deleted"),
				residentialAreaReplaced: renderCheckbox(residentialArea && endorsement.residentialAreaOption === "replaced"),
				residentialAreaExceptions: residentialArea && endorsement.residentialAreaOption === "replaced"
					? endorsement.residentialAreaExceptions
					: "",
				careCustodyControlCoverage: renderCheckbox(careCustodyControl),
				careCustodyControlDeleted: renderCheckbox(careCustodyControl && endorsement.careCustodyControlOption === "deleted"),
				careCustodyControlOwnedOrLeased: renderCheckbox(careCustodyControl && endorsement.careCustodyControlOption === "owned_or_leased"),
				careCustodyControlScheduled: renderCheckbox(careCustodyControl && endorsement.careCustodyControlOption === "scheduled"),
				careCustodyControlExceptions: careCustodyControl && endorsement.careCustodyControlOption === "scheduled"
					? endorsement.careCustodyControlExceptions
					: "",
			};
		},
	} satisfies EndorsementConfig<typeof UasAerialApplicationEndorsementSchema.static>,
	uas_interference_exclusion: {
		label: "Exclusion of Coverage for Interference with Government or Emergency Operations",
		schema: UasInterferenceExclusionEndorsementSchema,
		documentName: "N UAS E CLASSA 0021 05 25 Exclusion of Coverage for Interference.docx",
		formNumber: "N UAS E CLASSA 0021 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("uas_interference_exclusion", "Interference with Government or Emergency Operations Exclusion"),
		generateDynamicVariables: uasPremiumOnlyVariables("uas_interference_exclusion"),
	} satisfies EndorsementConfig<typeof UasInterferenceExclusionEndorsementSchema.static>,
	uas_additional_insured: {
		label: "Additional Insured Endorsement",
		schema: UasAdditionalInsuredEndorsementSchema,
		documentName: "N UAS E CLASSA 0022 05 25 Additional Insured Endorsement.docx",
		formNumber: "N UAS E CLASSA 0022 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_additional_insured") {
				return [];
			}
			return [{
				name: "Additional Insured",
				included: true,
				limits: uasPrintLimits(endorsement, [[endorsement.additionalInsuredName, null]]),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_additional_insured") {
				return {};
			}
			const faaCertNumbers = endorsement.faaCertNumbers.map(value => value.trim()).filter(Boolean);
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				additionalInsuredName: endorsement.additionalInsuredName,
				additionalInsuredAddressLineOne: endorsement.additionalInsuredAddressLineOne,
				additionalInsuredAddressLineTwo: endorsement.additionalInsuredAddressLineTwo,
				additionalInsuredAttention: endorsement.attention ?? "",
				hasFaaCertNumbers: faaCertNumbers.length > 0,
				faaCertNumbers: faaCertNumbers.join(", "),
				asAdditionalInsured: renderCheckbox(endorsement.asAdditionalInsured),
				premisesOwnerOrLessor: renderCheckbox(endorsement.premisesOwnerOrLessor),
				liabilityCoveragesOpsOnly: renderCheckbox(endorsement.liabilityCoveragesOpsOnly),
				liabilityAndPhysicalDamageOpsOnly: renderCheckbox(endorsement.liabilityAndPhysicalDamageOpsOnly),
				exclusionForDesignManufacture: renderCheckbox(endorsement.exclusionForDesignManufacture),
			};
		},
	} satisfies EndorsementConfig<typeof UasAdditionalInsuredEndorsementSchema.static>,
	uas_blanket_liability: {
		label: "UAS Blanket Liability Endorsement",
		schema: UasBlanketLiabilityEndorsementSchema,
		documentName: "N UAS E CLASSA 0023 05 25 Unmanned Aircraft Blanket Liability Endorsement.docx",
		formNumber: "N UAS E CLASSA 0023 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_blanket_liability") {
				return [];
			}
			return [{
				name: "Blanket Liability",
				included: true,
				limits: uasPrintLimits(
					endorsement,
					[[singleLine(endorsement.description) || "Blanket", endorsement.liabilityLimit]],
					getPremiumTextPricedAbove,
				),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_blanket_liability") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				description: endorsement.description,
				liabilityLimit: nullishCurrencyOrBlank(endorsement.liabilityLimit),
			};
		},
	} satisfies EndorsementConfig<typeof UasBlanketLiabilityEndorsementSchema.static>,
	uas_blanket_physical_damage: {
		label: "UAS Blanket Physical Damage Endorsement",
		schema: UasBlanketPhysicalDamageEndorsementSchema,
		documentName: "N UAS E CLASSA 0024 05 25 Unmanned Aircraft Blanket Physical Damage Endorsement.docx",
		formNumber: "N UAS E CLASSA 0024 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_blanket_physical_damage") {
				return [];
			}
			return [{
				name: "Blanket Physical Damage",
				included: true,
				limits: uasPrintLimits(endorsement, [
					["Insured value", endorsement.insuredValue],
					endorsement.deductibleType === "percentage" && endorsement.deductible !== null
						? [`Deductible ${formatUasDeductible(endorsement.deductible, "percentage")}`, null]
						: ["Deductible", endorsement.deductible],
				], getPremiumTextPricedAbove),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_blanket_physical_damage") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				insuredValue: nullishCurrencyOrBlank(endorsement.insuredValue),
				deductible: formatUasDeductible(endorsement.deductible, endorsement.deductibleType),
			};
		},
	} satisfies EndorsementConfig<typeof UasBlanketPhysicalDamageEndorsementSchema.static>,
	uas_munitions: {
		label: "UAS Munitions Endorsement",
		schema: UasMunitionsEndorsementSchema,
		documentName: "N UAS E CLASSA 0025 05 25 Unmanned Aircraft Munitions Endorsement.docx",
		formNumber: "N UAS E CLASSA 0025 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("uas_munitions", "Munitions"),
		generateDynamicVariables: uasPremiumOnlyVariables("uas_munitions"),
	} satisfies EndorsementConfig<typeof UasMunitionsEndorsementSchema.static>,
	uas_broad_contractual_liability: {
		label: "Broad Contractual Liability Endorsement",
		schema: UasBroadContractualLiabilityEndorsementSchema,
		documentName: "N UAS E CLASSA 0026 05 25 Broad Contractual Liability Endorsement.docx",
		formNumber: "N UAS E CLASSA 0026 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_broad_contractual_liability") {
				return [];
			}
			return [{
				name: "Contractual Liability",
				included: true,
				limits: uasPrintLimits(endorsement, [[endorsement.contractParty.trim() || "As scheduled", null]]),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_broad_contractual_liability") {
				return {};
			}
			const contractDate = endorsement.contractDate?.trim();
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				contractParty: endorsement.contractParty,
				contractDate: contractDate ? formatDateFullMonth(contractDate.slice(0, 10), "UTC") : "",
				contractWording: endorsement.contractWording,
			};
		},
	} satisfies EndorsementConfig<typeof UasBroadContractualLiabilityEndorsementSchema.static>,
	uas_ag_spray_chemical_liability: {
		label: "Agricultural Spray Chemical Liability",
		schema: UasAgSprayChemicalLiabilityEndorsementSchema,
		documentName: "N UAS E CLASSA 0027 05 25 Agricultural Spray Chemical Liability.docx",
		formNumber: "N UAS E CLASSA 0027 05 25",
		lob: ["uas"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "uas_ag_spray_chemical_liability") {
				return [];
			}
			const rows = UAS_AG_SPRAY_PRINT_ROWS
				.filter(([field]) => endorsement[field] !== null)
				.map(([field, qualifier]): [string, number | null] => [qualifier, endorsement[field]]);
			if (endorsement.selectedTailNumbers.length > 0) {
				rows.push([`Applies to: ${endorsement.selectedTailNumbers.join(", ")}`, null]);
			}
			return [{
				name: "Agricultural Spray Chemical Liability",
				included: true,
				limits: uasPrintLimits(endorsement, rows, getPremiumTextPricedAbove),
			}];
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "uas_ag_spray_chemical_liability") {
				return {};
			}
			return {
				chemicalPremium: formatCurrency(endorsement.chemicalPremium),
				nonChemicalEachPersonLimit: nullishCurrencyOrBlank(endorsement.nonChemicalEachPersonLimit),
				nonChemicalEachOccurrenceLimit: nullishCurrencyOrBlank(endorsement.nonChemicalEachOccurrenceLimit),
				chemicalEachPersonLimit: nullishCurrencyOrBlank(endorsement.chemicalEachPersonLimit),
				chemicalEachOccurrenceLimit: nullishCurrencyOrBlank(endorsement.chemicalEachOccurrenceLimit),
				chemicalAggregateLimit: nullishCurrencyOrBlank(endorsement.chemicalAggregateLimit),
				medicalExpenseEachPersonLimit: nullishCurrencyOrBlank(endorsement.medicalExpenseEachPersonLimit),
				medicalExpenseEachOccurrenceLimit: nullishCurrencyOrBlank(endorsement.medicalExpenseEachOccurrenceLimit),
				aircraft: endorsement.aircraftSchedule.map(row => ({
					tailNumber: row.tailNumber,
					makeModel: row.makeModel,
					year: row.year,
					insuredValue: uasCurrencyOrNotCovered(row.insuredValue),
					physicalDamageCoverageLetters: row.physicalDamageCoverageLetters,
					chemicalCoverageLetters: row.chemicalCoverageLetters,
					deductibleNotInMotion: uasRowDeductible(row.insuredValue, row.deductibleNotInMotion, row.deductibleType),
					deductibleInMotion: uasRowDeductible(row.insuredValue, row.deductibleInMotion, row.deductibleType),
				})),
				fullAnnualPremiumClause: renderCheckbox(endorsement.fullAnnualPremiumClause),
				fullAnnualPremium: endorsement.fullAnnualPremiumClause ? nullishCurrencyOrBlank(endorsement.fullAnnualPremium) : "",
			};
		},
	} satisfies EndorsementConfig<typeof UasAgSprayChemicalLiabilityEndorsementSchema.static>,
	pl_extended_policy_term: {
		label: "Extended Policy Term Endorsement",
		schema: PlExtendedPolicyTermEndorsementSchema,
		documentName: "N PL E CLASSA 0001 06 25 - Extended Policy Term.docx",
		formNumber: "N PL E CLASSA 0001 06 25",
		lob: ["product_liability"],
		stateDocuments: {
			NY: {
				documentName: "N PL E CLASSA 0001NY 06 25 - Extended Policy Term Endt - NY.docx",
				formNumber: "N PL E CLASSA 0001NY 06 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_extended_policy_term"
			? plSchedulePrint("Extended Policy Term", endorsement)
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement, ctx?: RenderContext) => endorsement.slug === "pl_extended_policy_term"
			? plScheduleVariables(endorsement, ctx)
			: {},
	} satisfies EndorsementConfig<typeof PlExtendedPolicyTermEndorsementSchema.static>,
	pl_multi_year_policy: {
		label: "Multiple Year Policy With Prospective Premium Endorsement",
		schema: PlMultiYearPolicyEndorsementSchema,
		documentName: "N PL E CLASSA 0002 06 25 - Multiple Year Policy With Prospective Premium Endt.docx",
		formNumber: "N PL E CLASSA 0002 06 25",
		lob: ["product_liability"],
		excludedStates: ["NY"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_multi_year_policy"
			? plSchedulePrint("Multi-Year Policy", endorsement)
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement, ctx?: RenderContext) => endorsement.slug === "pl_multi_year_policy"
			? plScheduleVariables(endorsement, ctx)
			: {},
	} satisfies EndorsementConfig<typeof PlMultiYearPolicyEndorsementSchema.static>,
	pl_limits_amendment: {
		label: "Limits of Insurance Amendment Endorsement",
		schema: PlLimitsAmendmentEndorsementSchema,
		documentName: "N PL E CLASSA 0003 06 25 - Limits of Insurance Amendment Endt.docx",
		formNumber: "N PL E CLASSA 0003 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_limits_amendment"
			? plLimitsPrint("Limits of Insurance Amendment", endorsement, [
				["Coverage A each occurrence and annual aggregate", endorsement.coverageALimit],
				["Coverage B each grounding and annual aggregate", endorsement.coverageBLimit],
				["Coverages A and B combined annual aggregate", endorsement.combinedAggregateLimit],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_limits_amendment") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				coverageALimit: nullishCurrencyOrBlank(endorsement.coverageALimit),
				coverageBLimit: nullishCurrencyOrBlank(endorsement.coverageBLimit),
				combinedAggregateLimit: nullishCurrencyOrBlank(endorsement.combinedAggregateLimit),
			};
		},
	} satisfies EndorsementConfig<typeof PlLimitsAmendmentEndorsementSchema.static>,
	pl_deductible: {
		label: "Deductible Endorsement",
		schema: PlDeductibleEndorsementSchema,
		documentName: "N PL E CLASSA 0004 06 25 - Deductible Endt.docx",
		formNumber: "N PL E CLASSA 0004 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_deductible"
			? plLimitsPrint("Deductible", endorsement, [
				["Coverage A deductible per claim", endorsement.perClaimDeductible],
				["Coverage A deductible per occurrence", endorsement.perOccurrenceDeductible],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_deductible") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				perClaimDeductible: nullishCurrencyOrBlank(endorsement.perClaimDeductible),
				perOccurrenceDeductible: nullishCurrencyOrBlank(endorsement.perOccurrenceDeductible),
			};
		},
	} satisfies EndorsementConfig<typeof PlDeductibleEndorsementSchema.static>,
	pl_broad_coverage: {
		label: "Broad Coverage Endorsement",
		schema: PlBroadCoverageEndorsementSchema,
		documentName: "N PL E CLASSA 0005 06 25 - Broad Coverage Expansion Endt.docx",
		formNumber: "N PL E CLASSA 0005 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "pl_broad_coverage") {
				return [];
			}
			const clauses: Array<readonly [string, number | null]> = [];
			if (endorsement.hasProductTestingExpense) {
				clauses.push(["Extra expense for product testing or investigation, each investigation", 25_000]);
			}
			if (endorsement.hasInAircraftTesting) {
				clauses.push(["In-aircraft testing of your products, each occurrence", 100_000]);
			}
			if (endorsement.hasAirportPremises) {
				clauses.push(["Airport premises liability hazard, each occurrence", 1_000_000]);
			}
			return plLimitsPrint("Broad Coverage", endorsement, clauses);
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_broad_coverage") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				productTestingExpenseBox: renderCheckbox(endorsement.hasProductTestingExpense),
				inAircraftTestingBox: renderCheckbox(endorsement.hasInAircraftTesting),
				airportPremisesBox: renderCheckbox(endorsement.hasAirportPremises),
			};
		},
	} satisfies EndorsementConfig<typeof PlBroadCoverageEndorsementSchema.static>,
	pl_premises_liability: {
		label: "Premises Liability Hazard Insurance Endorsement",
		schema: PlPremisesLiabilityEndorsementSchema,
		documentName: "N PL E CLASSA 0006 06 25 - Premises Liability Hazard Endt.docx",
		formNumber: "N PL E CLASSA 0006 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_premises_liability"
			? plLimitsPrint("Premises Liability Hazard", endorsement, [
				["Fire legal liability, any one occurrence", endorsement.fireLegalLimit],
				["Premises liability hazard, any one occurrence", endorsement.premisesLimit],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_premises_liability") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				scheduledLocations: endorsement.scheduledLocations,
				fireLegalLimit: nullishCurrencyOrBlank(endorsement.fireLegalLimit),
				premisesLimit: nullishCurrencyOrBlank(endorsement.premisesLimit),
			};
		},
	} satisfies EndorsementConfig<typeof PlPremisesLiabilityEndorsementSchema.static>,
	pl_premises_liability_med_pay: {
		label: "Premises Liability Hazard and Medical Payments Insurance Endorsement",
		schema: PlPremisesLiabilityMedPayEndorsementSchema,
		documentName: "N PL E CLASSA 0007 06 25 - Premises Liability Hazard and Med Pay Endt.docx",
		formNumber: "N PL E CLASSA 0007 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_premises_liability_med_pay"
			? plLimitsPrint("Premises Liability Hazard and Medical Payments", endorsement, [
				["Fire legal liability, any one occurrence", endorsement.fireLegalLimit],
				["Premises liability hazard, any one occurrence", endorsement.premisesLimit],
				["Medical payments, each person", endorsement.medicalEachPerson],
				["Medical payments, each occurrence", endorsement.medicalEachOccurrence],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_premises_liability_med_pay") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				scheduledLocations: endorsement.scheduledLocations,
				fireLegalLimit: nullishCurrencyOrBlank(endorsement.fireLegalLimit),
				premisesLimit: nullishCurrencyOrBlank(endorsement.premisesLimit),
				medicalEachPerson: nullishCurrencyOrBlank(endorsement.medicalEachPerson),
				medicalEachOccurrence: nullishCurrencyOrBlank(endorsement.medicalEachOccurrence),
			};
		},
	} satisfies EndorsementConfig<typeof PlPremisesLiabilityMedPayEndorsementSchema.static>,
	pl_airport_premises_liability: {
		label: "Airport Premises Liability Hazard Insurance Endorsement",
		schema: PlAirportPremisesLiabilityEndorsementSchema,
		documentName: "N PL E CLASSA 0008 06 25 - Airport Premises Liability Endt.docx",
		formNumber: "N PL E CLASSA 0008 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_airport_premises_liability"
			? plLimitsPrint("Airport Premises Liability Hazard", endorsement, [
				[qualifierLabels.any_one_occurrence, endorsement.airportPremisesLimit],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_airport_premises_liability") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				airportPremisesLimit: nullishCurrencyOrBlank(endorsement.airportPremisesLimit),
			};
		},
	} satisfies EndorsementConfig<typeof PlAirportPremisesLiabilityEndorsementSchema.static>,
	pl_amended_definition_grounding: {
		label: "Amended Definition of Grounding Endorsement",
		schema: PlAmendedDefinitionGroundingEndorsementSchema,
		documentName: "N PL E CLASSA 0009 06 25 - Amended Definition of Grounding Endt.docx",
		formNumber: "N PL E CLASSA 0009 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("pl_amended_definition_grounding", "Amended Definition of Grounding"),
		generateDynamicVariables: uasPremiumOnlyVariables("pl_amended_definition_grounding"),
	} satisfies EndorsementConfig<typeof PlAmendedDefinitionGroundingEndorsementSchema.static>,
	pl_extended_definition_grounding: {
		label: "Extended Definition of Grounding Endorsement",
		schema: PlExtendedDefinitionGroundingEndorsementSchema,
		documentName: "N PL E CLASSA 0010 06 25 - Extended Definition of Grounding Endt.docx",
		formNumber: "N PL E CLASSA 0010 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("pl_extended_definition_grounding", "Extended Definition of Grounding"),
		generateDynamicVariables: uasPremiumOnlyVariables("pl_extended_definition_grounding"),
	} satisfies EndorsementConfig<typeof PlExtendedDefinitionGroundingEndorsementSchema.static>,
	pl_investigation_expenses: {
		label: "Investigation Expenses Reimbursement Endorsement",
		schema: PlInvestigationExpensesEndorsementSchema,
		documentName: "N PL E CLASSA 0011 06 25 - Investigation Expenses Reimbursement Endt.docx",
		formNumber: "N PL E CLASSA 0011 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_investigation_expenses"
			? plLimitsPrint("Investigation Expenses Reimbursement", endorsement, [["Each investigation", 50_000]])
			: [],
		generateDynamicVariables: uasPremiumOnlyVariables("pl_investigation_expenses"),
	} satisfies EndorsementConfig<typeof PlInvestigationExpensesEndorsementSchema.static>,
	pl_in_aircraft_testing: {
		label: "In-Aircraft Testing of Products Endorsement",
		schema: PlInAircraftTestingEndorsementSchema,
		documentName: "N PL E CLASSA 0012 06 25 - In Aircraft Testing of Products Endt.docx",
		formNumber: "N PL E CLASSA 0012 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_in_aircraft_testing"
			? plLimitsPrint("In-Aircraft Testing of Products", endorsement, [[qualifierLabels.each_occurrence, 100_000]])
			: [],
		generateDynamicVariables: uasPremiumOnlyVariables("pl_in_aircraft_testing"),
	} satisfies EndorsementConfig<typeof PlInAircraftTestingEndorsementSchema.static>,
	pl_hangarkeepers_liability: {
		label: "Hangarkeeper's Liability Insurance Endorsement",
		schema: PlHangarkeepersLiabilityEndorsementSchema,
		documentName: "N PL E CLASSA 0013 06 25 - Hangarkeepers Liability Endt.docx",
		formNumber: "N PL E CLASSA 0013 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_hangarkeepers_liability"
			? plLimitsPrint("Hangarkeeper's Liability", endorsement, [
				["Not in flight, any one aircraft", endorsement.notInFlightEachAircraft],
				["Not in flight, any one occurrence", endorsement.notInFlightEachOccurrence],
				["In flight, any one aircraft", endorsement.inFlightEachAircraft],
				["In flight, any one occurrence", endorsement.inFlightEachOccurrence],
				["Not in flight deductible, each loss", endorsement.notInFlightDeductible],
				[
					endorsement.inFlightDeductiblePercent === null
						? "In flight deductible minimum, each loss"
						: `In flight deductible ${endorsement.inFlightDeductiblePercent}% of value, minimum`,
					endorsement.inFlightDeductibleMinimum,
				],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_hangarkeepers_liability") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				notInFlightEachAircraft: nullishCurrencyOrBlank(endorsement.notInFlightEachAircraft),
				notInFlightEachOccurrence: nullishCurrencyOrBlank(endorsement.notInFlightEachOccurrence),
				inFlightEachAircraft: nullishCurrencyOrBlank(endorsement.inFlightEachAircraft),
				inFlightEachOccurrence: nullishCurrencyOrBlank(endorsement.inFlightEachOccurrence),
				notInFlightDeductible: nullishCurrencyOrBlank(endorsement.notInFlightDeductible),
				inFlightDeductiblePercent: plPercentOrBlank(endorsement.inFlightDeductiblePercent),
				inFlightDeductibleMinimum: nullishCurrencyOrBlank(endorsement.inFlightDeductibleMinimum),
			};
		},
	} satisfies EndorsementConfig<typeof PlHangarkeepersLiabilityEndorsementSchema.static>,
	pl_foreign_military_hull: {
		label: "Foreign Military Hull Endorsement",
		schema: PlForeignMilitaryHullEndorsementSchema,
		documentName: "N PL E CLASSA 0014 06 25 - Foreign Military Hull Endt.docx",
		formNumber: "N PL E CLASSA 0014 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("pl_foreign_military_hull", "Foreign Military Hull"),
		generateDynamicVariables: uasPremiumOnlyVariables("pl_foreign_military_hull"),
	} satisfies EndorsementConfig<typeof PlForeignMilitaryHullEndorsementSchema.static>,
	pl_war_hijacking_write_back: {
		label: "War, Hijacking and Other Perils Limited Liability Write-Back Endorsement",
		schema: PlWarHijackingWriteBackEndorsementSchema,
		documentName: "N PL E CLASSA 0015 06 25 - War, Hijacking and Other Perils Limited Liability Write-Back Endt.docx",
		formNumber: "N PL E CLASSA 0015 06 25",
		lob: ["product_liability"],
		stateDocuments: {
			NY: {
				documentName: "N PL E CLASSA 0015NY 06 25 - War, Hijacking and Other Perils Limited Liability Write-Back Endt.docx",
				formNumber: "N PL E CLASSA 0015NY 06 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_war_hijacking_write_back"
			? plLimitsPrint("War, Hijacking and Other Perils Limited Liability Write-Back", endorsement, [
				[`${qualifierLabels.any_one_occurrence} and annual aggregate`, endorsement.warWriteBackSubLimit],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_war_hijacking_write_back") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				warWriteBackSubLimit: nullishCurrencyOrBlank(endorsement.warWriteBackSubLimit),
			};
		},
	} satisfies EndorsementConfig<typeof PlWarHijackingWriteBackEndorsementSchema.static>,
	pl_war_auto_termination_amendment: {
		label: "Amendment to Automatic Termination Provisions of the War Liability Write-Back",
		schema: PlWarAutoTerminationAmendmentEndorsementSchema,
		documentName: "N PL E CLASSA 0016 06 25 - Amendment to Automatic Termination Provision of War, Hijacking and Other Perils Limited Liability Write-Back End Endt.docx",
		formNumber: "N PL E CLASSA 0016 06 25",
		lob: ["product_liability"],
		stateDocuments: {
			NY: {
				documentName: "N PL E CLASSA 0016NY 06 25 - Amendment to Automatic Termination Provision of War, Hijacking and Other Perils Limited Liability Write-Back End Endt.docx",
				formNumber: "N PL E CLASSA 0016NY 06 25",
			},
		},
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("pl_war_auto_termination_amendment", "War Write-Back Automatic Termination Amendment"),
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_war_auto_termination_amendment") {
				return {};
			}
			return { premiumConsideration: plRefundPremiumConsiderationText(endorsement.included, endorsement.additionalPremium) };
		},
	} satisfies EndorsementConfig<typeof PlWarAutoTerminationAmendmentEndorsementSchema.static>,
	pl_spacecraft_products: {
		label: "Spacecraft Products Endorsement",
		schema: PlSpacecraftProductsEndorsementSchema,
		documentName: "N PL E CLASSA 0017 06 25 - Spacecraft Products Endt.docx",
		formNumber: "N PL E CLASSA 0017 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_spacecraft_products"
			? plLimitsPrint("Spacecraft Products", endorsement, [
				["Single satellite - any one occurrence", endorsement.singleSatelliteLimit],
				["Two or more satellites - any one satellite", endorsement.multipleSatellitesLimit],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_spacecraft_products") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				singleSatelliteLimit: nullishCurrencyOrBlank(endorsement.singleSatelliteLimit),
				multipleSatellitesLimit: nullishCurrencyOrBlank(endorsement.multipleSatellitesLimit),
			};
		},
	} satisfies EndorsementConfig<typeof PlSpacecraftProductsEndorsementSchema.static>,
	pl_personal_injury_extension: {
		label: "Personal Injury Extension",
		schema: PlPersonalInjuryExtensionEndorsementSchema,
		documentName: "N PL E CLASSA 0018 06 25 - Personal Injury Extension.docx",
		formNumber: "N PL E CLASSA 0018 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_personal_injury_extension"
			? plLimitsPrint("Personal Injury Extension", endorsement, [
				[qualifierLabels.any_one_offense, endorsement.limitOfLiabilityEachOffense],
				[qualifierLabels.annual_aggregate, endorsement.limitOfLiabilityAnnualAggregate],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_personal_injury_extension") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				limitOfLiabilityEachOffense: nullishCurrencyOrBlank(endorsement.limitOfLiabilityEachOffense),
				limitOfLiabilityAnnualAggregate: nullishCurrencyOrBlank(endorsement.limitOfLiabilityAnnualAggregate),
			};
		},
	} satisfies EndorsementConfig<typeof PlPersonalInjuryExtensionEndorsementSchema.static>,
	pl_advertising_injury_extension: {
		label: "Advertising Injury Extension",
		schema: PlAdvertisingInjuryExtensionEndorsementSchema,
		documentName: "N PL E CLASSA 0019 06 25 - Advertising Injury Extension.docx",
		formNumber: "N PL E CLASSA 0019 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_advertising_injury_extension"
			? plLimitsPrint("Advertising Injury Extension", endorsement, [
				[qualifierLabels.any_one_offense, endorsement.limitOfLiabilityEachOffense],
				[qualifierLabels.annual_aggregate, endorsement.limitOfLiabilityAnnualAggregate],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_advertising_injury_extension") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				limitOfLiabilityEachOffense: nullishCurrencyOrBlank(endorsement.limitOfLiabilityEachOffense),
				limitOfLiabilityAnnualAggregate: nullishCurrencyOrBlank(endorsement.limitOfLiabilityAnnualAggregate),
			};
		},
	} satisfies EndorsementConfig<typeof PlAdvertisingInjuryExtensionEndorsementSchema.static>,
	pl_deletion_exclusion_pd_your_product: {
		label: "Deletion of Exclusion Endorsement - Property Damage to Your Product",
		schema: PlDeletionExclusionPdYourProductEndorsementSchema,
		documentName: "N PL E CLASSA 0020 06 25 - Deletion of Exclusion Endorsement - Property Damage to Your Product.docx",
		formNumber: "N PL E CLASSA 0020 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("pl_deletion_exclusion_pd_your_product", "Deletion of Exclusion - Property Damage to Your Product"),
		generateDynamicVariables: uasPremiumOnlyVariables("pl_deletion_exclusion_pd_your_product"),
	} satisfies EndorsementConfig<typeof PlDeletionExclusionPdYourProductEndorsementSchema.static>,
	pl_airport_ops_area_excess_auto: {
		label: "Airport Operations Area Excess Auto Coverage Endorsement",
		schema: PlAirportOpsAreaExcessAutoEndorsementSchema,
		documentName: "N PL E CLASSA 0021 06 25 - Excess Auto Liability Coverage Endorsement.docx",
		formNumber: "N PL E CLASSA 0021 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_airport_ops_area_excess_auto"
			? plLimitsPrint("Airport Operations Area Excess Auto Coverage", endorsement, [
				[qualifierLabels.each_occurrence, endorsement.eachOccurrenceLimit],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_airport_ops_area_excess_auto") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				eachOccurrenceLimit: nullishCurrencyOrBlank(endorsement.eachOccurrenceLimit),
			};
		},
	} satisfies EndorsementConfig<typeof PlAirportOpsAreaExcessAutoEndorsementSchema.static>,
	pl_fire_legal_liability: {
		label: "Fire Legal Liability Coverage - Real Property",
		schema: PlFireLegalLiabilityEndorsementSchema,
		documentName: "N PL E CLASSA 0022 06 25 - Fire Legal Liability Coverage - Real Property.docx",
		formNumber: "N PL E CLASSA 0022 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_fire_legal_liability"
			? plLimitsPrint("Fire Legal Liability - Real Property", endorsement, [
				[qualifierLabels.each_occurrence, endorsement.fireLegalLimit],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_fire_legal_liability") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				fireLegalLimit: nullishCurrencyOrBlank(endorsement.fireLegalLimit),
			};
		},
	} satisfies EndorsementConfig<typeof PlFireLegalLiabilityEndorsementSchema.static>,
	pl_date_change_recognition: {
		label: "Date Change Recognition Exclusion Endorsement Limited Write-Back Provision",
		schema: PlDateChangeRecognitionEndorsementSchema,
		documentName: "N PL E CLASSA 0023 06 25 - Date Change Recognition Exclusion  Endorsement Limited Write-Back Provision.docx",
		formNumber: "N PL E CLASSA 0023 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("pl_date_change_recognition", "Date Change Recognition Limited Write-Back"),
		generateDynamicVariables: uasPremiumOnlyVariables("pl_date_change_recognition"),
	} satisfies EndorsementConfig<typeof PlDateChangeRecognitionEndorsementSchema.static>,
	pl_amended_contract_reporting: {
		label: "Amended Contract Reporting Period Endorsement",
		schema: PlAmendedContractReportingEndorsementSchema,
		documentName: "N PL E CLASSA 0024 06 25 - Amended Contract Reporting Period.docx",
		formNumber: "N PL E CLASSA 0024 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("pl_amended_contract_reporting", "Amended Contract Reporting Period"),
		generateDynamicVariables: uasPremiumOnlyVariables("pl_amended_contract_reporting"),
	} satisfies EndorsementConfig<typeof PlAmendedContractReportingEndorsementSchema.static>,
	pl_discontinued_products: {
		label: "Discontinued Products Endorsement",
		schema: PlDiscontinuedProductsEndorsementSchema,
		documentName: "N PL E CLASSA 0025 06 25 - Discontinued Products Endt.docx",
		formNumber: "N PL E CLASSA 0025 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => {
			if (endorsement.slug !== "pl_discontinued_products") {
				return [];
			}
			const date = formatOptionalAutoExpiration(endorsement.discontinuedAfterDate);
			return plLimitsPrint("Discontinued Products", endorsement, [[date ? `Products handled after ${date}` : "", null]]);
		},
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_discontinued_products") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				discontinuedAfterDate: formatOptionalAutoExpiration(endorsement.discontinuedAfterDate),
			};
		},
	} satisfies EndorsementConfig<typeof PlDiscontinuedProductsEndorsementSchema.static>,
	pl_toxic_substance_exclusion: {
		label: "Toxic Substance Exclusion Endorsement",
		schema: PlToxicSubstanceExclusionEndorsementSchema,
		documentName: "N PL E CLASSA 0026 06 25 - Toxic Substance Exclusion.docx",
		formNumber: "N PL E CLASSA 0026 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("pl_toxic_substance_exclusion", "Toxic Substance Exclusion"),
		generateDynamicVariables: uasPremiumOnlyVariables("pl_toxic_substance_exclusion"),
	} satisfies EndorsementConfig<typeof PlToxicSubstanceExclusionEndorsementSchema.static>,
	pl_designated_products: {
		label: "Designated Products Limitation Endorsement",
		schema: PlDesignatedProductsEndorsementSchema,
		documentName: "N PL E CLASSA 0027 06 25 - Designated Products Endt.docx",
		formNumber: "N PL E CLASSA 0027 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_designated_products"
			? plLimitsPrint("Designated Products Limitation", endorsement, [[singleLine(endorsement.designatedProducts), null]])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_designated_products") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				designatedProducts: endorsement.designatedProducts,
			};
		},
	} satisfies EndorsementConfig<typeof PlDesignatedProductsEndorsementSchema.static>,
	pl_additional_insured: {
		label: "Additional Insured Endorsement",
		schema: PlAdditionalInsuredEndorsementSchema,
		documentName: "N PL E CLASSA 0028 06 25 - Additional Insured Endt.docx",
		formNumber: "N PL E CLASSA 0028 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_additional_insured"
			? plLimitsPrint("Additional Insured", endorsement, [[endorsement.additionalInsuredName, null]])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_additional_insured") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				additionalInsuredName: endorsement.additionalInsuredName,
			};
		},
	} satisfies EndorsementConfig<typeof PlAdditionalInsuredEndorsementSchema.static>,
	pl_additional_insured_vendors: {
		label: "Additional Insured - Vendors",
		schema: PlAdditionalInsuredVendorsEndorsementSchema,
		documentName: "N PL E CLASSA 0029 06 25 - Additional Insured Endt - Vendors.docx",
		formNumber: "N PL E CLASSA 0029 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_additional_insured_vendors"
			? plLimitsPrint(
				"Additional Insured - Vendors",
				endorsement,
				endorsement.vendors.map(vendor => [[vendor.vendorName, vendor.vendorProduct].filter(Boolean).join(": "), null] as const),
			)
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_additional_insured_vendors") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				vendors: endorsement.vendors.map(vendor => ({
					vendorName: vendor.vendorName,
					vendorProduct: vendor.vendorProduct,
				})),
			};
		},
	} satisfies EndorsementConfig<typeof PlAdditionalInsuredVendorsEndorsementSchema.static>,
	pl_boeing: {
		label: "Boeing Endorsement",
		schema: PlBoeingEndorsementSchema,
		documentName: "N PL E CLASSA 0030 06 25 - Boeing Endt.docx",
		formNumber: "N PL E CLASSA 0030 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_boeing"
			? plLimitsPrint("Boeing Endorsement", endorsement, [[endorsement.gtaNumber ? `GTA No. ${endorsement.gtaNumber}` : "", null]])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_boeing") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				gtaNumber: endorsement.gtaNumber,
			};
		},
	} satisfies EndorsementConfig<typeof PlBoeingEndorsementSchema.static>,
	pl_data_event_clause: {
		label: "Data Event Clause",
		schema: Type.Object({}),
		documentName: "N PL E CLASSA 0031 06 25 - Data Event Clause.docx",
		formNumber: "N PL E CLASSA 0031 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: false,
		renewal: "drop",
	},
	pl_automatic_renewal: {
		label: "Automatic Renewal Endorsement",
		schema: PlAutomaticRenewalEndorsementSchema,
		documentName: "N PL E CLASSA 0032 06 25 - Automatic Renewal Endorsement.docx",
		formNumber: "N PL E CLASSA 0032 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "drop",
		toPrintEndorsement: ({ endorsement }: PrintConfig) => endorsement.slug === "pl_automatic_renewal"
			? plLimitsPrint("Automatic Renewal", endorsement, [
				[endorsement.renewalPolicyNumber ? `Renewal policy ${endorsement.renewalPolicyNumber}` : "", null],
			])
			: [],
		generateDynamicVariables: (endorsement: GenericEndorsement) => {
			if (endorsement.slug !== "pl_automatic_renewal") {
				return {};
			}
			return {
				premiumConsideration: premiumConsiderationText(endorsement.included, endorsement.additionalPremium),
				renewalPolicyNumber: endorsement.renewalPolicyNumber,
			};
		},
	} satisfies EndorsementConfig<typeof PlAutomaticRenewalEndorsementSchema.static>,
	pl_amendatory_ny: {
		label: "New York Amendatory Endorsement",
		schema: PlAmendatoryNyEndorsementSchema,
		documentName: "N PL E CLASSA NY01 06 25 - New York Amendatory Endt.docx",
		formNumber: "N PL E CLASSA NY01 06 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("pl_amendatory_ny", "New York Amendatory Endorsement"),
		generateDynamicVariables: uasPremiumOnlyVariables("pl_amendatory_ny"),
	} satisfies EndorsementConfig<typeof PlAmendatoryNyEndorsementSchema.static>,
	pl_other_insurance_il: {
		label: "Illinois Other Insurance Endorsement",
		schema: PlOtherInsuranceIlEndorsementSchema,
		documentName: "N PL E CLASSA IL01 05 25 Illinois Other Insurance Endorsement.docx",
		formNumber: "N PL E CLASSA IL01 05 25",
		lob: ["product_liability"],
		includeInQuoteTemplate: true,
		renewal: "carry",
		toPrintEndorsement: uasPremiumOnlyPrint("pl_other_insurance_il", "Illinois Other Insurance Endorsement"),
		generateDynamicVariables: uasPremiumOnlyVariables("pl_other_insurance_il"),
	} satisfies EndorsementConfig<typeof PlOtherInsuranceIlEndorsementSchema.static>,
};

function generateDeletionEndorsementDynamicVariables (
	endorsement: GenericEndorsement,
	ctx?: RenderContext,
): Record<string, unknown> {
	if (endorsement.slug !== "deletion") {
		return {};
	}
	const d = endorsement as typeof DeletionEndorsementSchema.static;
	const orig = d.originalEndorsement;
	let origVars: Record<string, unknown> = {};
	if (Value.Check(GenericEndorsementSchema, orig)) {
		const origConfig = endorsementConfig[orig.slug];
		if (origConfig?.generateDynamicVariables) {
			origVars = origConfig.generateDynamicVariables(orig, ctx);
		}
	}
	const delPrem = d.deletionAdditionalPremium ?? 0;
	const delInvoiced = d.deletionInvoicedPremium;
	const consideration = typeof delInvoiced === "number"
		? midTermPremiumConsiderationFromInvoiced(delInvoiced)
		: premiumConsiderationText(d.deletionPremiumIncluded, delPrem);
	const deletedNumber = ctx?.documentNumbersByEndorsementId?.get(d.supersedesPolicyEndorsementId)
		?? d.deletedEndorsementDocumentNumber;
	return {
		...origVars,
		premiumConsideration: consideration,
		"premium consideration text": consideration,
		deletedEndorsementNumber: deletedNumber == null ? "" : String(deletedNumber).padStart(2, "0"),
		endorsementSlug: d.deletedEndorsementSlug,
		deletionAdditionalPremium: delPrem === 0 ? "" : formatCurrency(delPrem),
		deletionInvoicedPremium: typeof delInvoiced === "number" ? formatCurrency(delInvoiced) : "",
	};
}

const docNumber = (docName: string) => {
	const parts = docName.split("CLASSA");
	return parts.at(1) ?? "0000";
};

/** True when the endorsement carries a positive additional premium (summary strip / policy UI). */
export function endorsementHasBillablePremium (data: unknown): boolean {
	if (!data || typeof data !== "object") {
		return false;
	}
	const d = data as Record<string, unknown>;
	const ap = d.additionalPremium;
	if (typeof ap === "number" && ap > 0) {
		return true;
	}
	return false;
}

/** Matches `20260416163356_nostalgic_sasquatch.sql` backfill: non-zero premium signal in endorsement JSON. */
export function policyEndorsementDataHasQualifyingMidTermPremium (data: unknown): boolean {
	if (!data || typeof data !== "object") {
		return false;
	}
	const d = data as Record<string, unknown>;
	const nonZeroNumber = (v: unknown) => typeof v === "number" && v !== 0;
	return (
		nonZeroNumber(d.additionalPremium)
		|| nonZeroNumber(d.premiumAdjustment)
		|| nonZeroNumber(d.premium)
		|| nonZeroNumber(d.invoicedPremium)
		|| nonZeroNumber(d.deletionAdditionalPremium)
		|| nonZeroNumber(d.deletionInvoicedPremium)
	);
}

/** Sums `additionalPremium` on quote endorsement payloads (v2 endorsements). */
export function sumQuoteEndorsementsAdditionalPremium (endorsements: { data: unknown }[]): number {
	return endorsements.reduce((acc, e) => {
		const d = e.data as Record<string, unknown>;
		const ap = d.additionalPremium;
		return acc + (typeof ap === "number" ? ap : 0);
	}, 0);
}

/** Filename / custom order key — used for endorsement ordering (policy PDF sequence, quotes). */
const compareEndorsementsByTemplateDocumentOrder = <T extends { slug: string; data: unknown }>(a: T, b: T): number => {
	let aDocName = endorsementConfig?.[a.slug]?.documentName ?? "a";
	let bDocName = endorsementConfig?.[b.slug]?.documentName ?? "b";
	if (a.slug === "custom_endorsement" && a.data && typeof a.data === "object" && "order" in a.data) {
		const orderString = `${a.data.order}`.padStart(4, "0");
		aDocName = `CLASSA ${orderString}`;
	}
	if (b.slug === "custom_endorsement" && b.data && typeof b.data === "object" && "order" in b.data) {
		const orderString = `${b.data.order}`.padStart(4, "0");
		bDocName = `CLASSA ${orderString}`;
	}

	return docNumber(aDocName).localeCompare(docNumber(bDocName));
};

export const orderEndorsements = <T extends { slug: string, data: unknown }>(endorsements: T[]): T[] => {
	return endorsements.toSorted((a, b) => {
		if (a.slug === "pilot_warranty" && b.slug !== "pilot_warranty") {
			return -1;
		}
		if (b.slug === "pilot_warranty" && a.slug !== "pilot_warranty") {
			return 1;
		}
		if (a.slug === "broad_form" && b.slug !== "broad_form") {
			return -1;
		}
		if (b.slug === "broad_form" && a.slug !== "broad_form") {
			return 1;
		}
		return compareEndorsementsByTemplateDocumentOrder(a, b);
	});
};

export const CGL_MANDATORY_ENDORSEMENT_SLUGS = [
	"cgl_supplementary_payments",
	"cgl_communicable_disease_exclusion",
	"cgl_date_change_recognition",
	"cgl_data_event_clause",
	"cgl_pfas_exclusion",
] as const;

const mandatoryEndorsementSlugsByLob: Partial<Record<LOBEnumType, readonly string[]>> = {
	commercial_general_liability: CGL_MANDATORY_ENDORSEMENT_SLUGS,
};

interface StateMandatoryRule {
	add?: readonly string[];
	/** Countrywide slugs this state supersedes or has no filed form for. */
	remove?: readonly string[];
}

const CGL_STATE_MANDATORY_RULES: Record<string, StateMandatoryRule> = {
	MO: {
		add: ["cgl_amendatory_mo"],
	},
	NH: {
		add: ["cgl_amendatory_nh"],
	},
	NY: {
		add: ["cgl_amendatory_ny"],
		remove: ["cgl_communicable_disease_exclusion"],
	},
	SD: {
		add: ["cgl_amendatory_sd", "cgl_communicable_disease_exclusion_sd"],
		remove: ["cgl_communicable_disease_exclusion"],
	},
	VA: {
		add: ["cgl_amendatory_va"],
	},
	VT: {
		add: ["cgl_communicable_disease_exclusion_vt"],
		remove: ["cgl_communicable_disease_exclusion"],
	},
};

const PL_STATE_MANDATORY_RULES: Record<string, StateMandatoryRule> = {
	IL: {
		add: ["pl_other_insurance_il"],
	},
	NY: {
		add: ["pl_amendatory_ny"],
	},
};

const stateMandatoryRulesByLob: Partial<Record<LOBEnumType, Record<string, StateMandatoryRule>>> = {
	commercial_general_liability: CGL_STATE_MANDATORY_RULES,
	product_liability: PL_STATE_MANDATORY_RULES,
};

const stateCode = (state: string | null | undefined): string | null => {
	const trimmed = state?.trim().toUpperCase();
	return trimmed && trimmed.length === 2 ? trimmed : null;
};

export const mandatoryEndorsementSlugsForLob = (
	lob: LOBEnumType | null | undefined,
	state?: string | null,
): readonly string[] => {
	if (!lob) {
		return [];
	}
	const base = mandatoryEndorsementSlugsByLob[lob] ?? [];
	const code = stateCode(state);
	const rule = code ? stateMandatoryRulesByLob[lob]?.[code] : undefined;
	if (!rule) {
		return base;
	}
	const removed = new Set(rule.remove ?? []);
	return [...base.filter(slug => !removed.has(slug)), ...(rule.add ?? [])];
};

export const isMandatoryEndorsementForLob = (
	slug: string,
	lob: LOBEnumType | null | undefined,
	state?: string | null,
): boolean => mandatoryEndorsementSlugsForLob(lob, state).includes(slug);

export const endorsementDocumentNameForState = (
	config: Pick<EndorsementConfig, "documentName" | "stateDocuments">,
	state?: string | null,
): string => {
	const code = stateCode(state);
	return (code ? config.stateDocuments?.[code]?.documentName : undefined) ?? config.documentName;
};

export const endorsementFormNumberForState = (
	config: Pick<EndorsementConfig, "formNumber" | "stateDocuments">,
	state?: string | null,
): string => {
	const code = stateCode(state);
	return (code ? config.stateDocuments?.[code]?.formNumber : undefined) ?? config.formNumber;
};

export const endorsementDocumentForLob = (
	config: Pick<EndorsementConfig, "documentName" | "documentsByLob" | "formNumber">,
	lob?: LOBEnumType | null,
): StateFormVariant => (lob ? config.documentsByLob?.[lob] : undefined)
	?? { documentName: config.documentName, formNumber: config.formNumber };

export const endorsementLabelForLob = (slug: string, lob?: LOBEnumType | null): string => {
	const config = endorsementConfig[slug];
	return (lob ? config?.labelsByLob?.[lob] : undefined) ?? config?.label ?? slug;
};

export const isEndorsementOfferable = (
	slug: string,
	lob?: LOBEnumType | null,
	state?: string | null,
): boolean => {
	const config = endorsementConfig[slug];
	if (!config) {
		return false;
	}
	if (lob && !config.lob.includes(lob)) {
		return false;
	}
	const code = stateCode(state);
	return !(code && config.excludedStates?.includes(code));
};

const defaultEndorsementSlugsForLob = (lob: LOBEnumType, state?: string | null): Array<keyof typeof endorsementConfig> => {
	if (lob === "general_aviation") {
		return [
			"broad_form",
			"broad_named_insured",
			"war_hijacking_perils",
			"war_hijacking_physical_damage",
			"tria_liability",
			"tria_hull_write_back",
			"automatic_attachment_for_newly_acquired_aircraft",
			"airport_ops_area_auto",
			"damage_to_premises",
			"garagekeepers",
			"automatic_increase_spare_parts",
			"broad_form_contractual_liability",
			"personal_injury_extension",
			"advertising_injury_extension",
			"family_assistance_expense",
			"passenger_voluntary_settlement",
			"non_owned_aircraft_liability",
			"incidental_medical_malpractice",
			"mechanics_tools",
			"knowledge_of_occurrence",
			"layup",
			"lienholders_interest",
			"mexican_liability",
			"aircraft_relocation_expense",
			"date_change_recognition",
			"cancellation_clause_ammendment",
		];
	}
	if (lob === "product_liability") {
		return [
			"pl_date_change_recognition",
			"pl_war_hijacking_write_back",
			"tria_liability",
			...mandatoryEndorsementSlugsForLob(lob, state),
		];
	}
	if (lob === "commercial_general_liability") {
		return [...mandatoryEndorsementSlugsForLob(lob, state)] as Array<keyof typeof endorsementConfig>;
	}
	return [];
};

export const defaultEndorsementInitParams = (
	aircraft: ReadonlyArray<{ insuredValue: number, liabilityLimit: number }>,
	namedInsured: string,
): InitializeParams => ({
	liabilityLimit: aircraft.reduce((acc, item) => Math.max(acc, item.liabilityLimit), 0),
	hullValue: aircraft.reduce((acc, item) => Math.max(acc, item.insuredValue), 0),
	namedInsured,
	aircraftCount: aircraft.length,
});

export const defaultEndorsements = ({
	initParams, lob, state, 
}: { initParams: InitializeParams, lob: LOBEnumType, state?: string | null }): Array<typeof GenericEndorsementSchema.static> => {
	const endorsements: Array<typeof GenericEndorsementSchema.static> = [];
    

	const slugs = defaultEndorsementSlugsForLob(lob, state);

	for (const slug of slugs) {
		const config = endorsementConfig[slug];
		if (!config) {
			continue;
		}
		if (config.initialize) {
			const endorsement = config.initialize(initParams);
			endorsements.push(endorsement as typeof GenericEndorsementSchema.static);
			continue;
		}
		const endorsement = Value.Default(config.schema, {});
		if (Value.Check(config.schema, endorsement)) {
			endorsements.push(endorsement);
		}
	}

	const aircraftCount = initParams.aircraftCount ?? 0;
	if (aircraftCount > 1) {
		const slugsToNullFields: Record<string, string[]> = {
			host_liquor_liability: ["limitAggregate"],
			mobile_equipment_liability: ["limitEachOccurrence"],
			products_liability: ["liabilityLimit"],
			personal_injury_extension: ["limitOfLiabilityEachOffense", "limitOfLiabilityAnnualAggregate"],
			advertising_injury_extension: ["limitOfLiabilityEachOffense", "limitOfLiabilityAnnualAggregate"],
			incidental_medical_malpractice: ["liabilityLimitEachOccurrence"],
			airport_ops_area_auto: ["liabilityLimit"],
			broad_form: ["hostLiquorLimit", "mobileEquipmentLimit", "productsLiabilitySaleLimit"],
		};
		for (const endorsement of endorsements) {
			const fields = slugsToNullFields[(endorsement as { slug: string }).slug];
			if (fields?.length) {
				const data = endorsement as Record<string, unknown>;
				for (const field of fields) {
					data[field] = null;
				}
			}
		}
	}

	return endorsements;
};
